/**
 * @license
 * Copyright 2010-2024 Three.js Authors
 * SPDX-License-Identifier: MIT
 */
const REVISION = '166';
const FrontSide = 0;
const NormalBlending = 1;
const AddEquation = 100;
const SrcAlphaFactor = 204;
const OneMinusSrcAlphaFactor = 205;
const LessEqualDepth = 3;

const UVMapping = 300;
const RepeatWrapping = 1000;
const ClampToEdgeWrapping = 1001;
const MirroredRepeatWrapping = 1002;
const LinearFilter = 1006;
const LinearMipmapLinearFilter = 1008;
const UnsignedByteType = 1009;
const FloatType = 1015;
const RGBAFormat = 1023;

// Color space string identifiers, matching CSS Color Module Level 4 and WebGPU names where available.
const NoColorSpace = '';
const SRGBColorSpace = 'srgb';
const LinearSRGBColorSpace = 'srgb-linear';
const DisplayP3ColorSpace = 'display-p3';
const LinearDisplayP3ColorSpace = 'display-p3-linear';

const LinearTransfer = 'linear';
const SRGBTransfer = 'srgb';

const Rec709Primaries = 'rec709';
const P3Primaries = 'p3';
const KeepStencilOp = 7680;
const AlwaysStencilFunc = 519;

const StaticDrawUsage = 35044;

const WebGLCoordinateSystem = 2000;
const WebGPUCoordinateSystem = 2001;

/**
 * https://github.com/mrdoob/eventdispatcher.js/
 */

class EventDispatcher {

	addEventListener( type, listener ) {

		if ( this._listeners === undefined ) this._listeners = {};

		const listeners = this._listeners;

		if ( listeners[ type ] === undefined ) {

			listeners[ type ] = [];

		}

		if ( listeners[ type ].indexOf( listener ) === - 1 ) {

			listeners[ type ].push( listener );

		}

	}

	hasEventListener( type, listener ) {

		if ( this._listeners === undefined ) return false;

		const listeners = this._listeners;

		return listeners[ type ] !== undefined && listeners[ type ].indexOf( listener ) !== - 1;

	}

	removeEventListener( type, listener ) {

		if ( this._listeners === undefined ) return;

		const listeners = this._listeners;
		const listenerArray = listeners[ type ];

		if ( listenerArray !== undefined ) {

			const index = listenerArray.indexOf( listener );

			if ( index !== - 1 ) {

				listenerArray.splice( index, 1 );

			}

		}

	}

	dispatchEvent( event ) {

		if ( this._listeners === undefined ) return;

		const listeners = this._listeners;
		const listenerArray = listeners[ event.type ];

		if ( listenerArray !== undefined ) {

			event.target = this;

			// Make a copy, in case listeners are removed while iterating.
			const array = listenerArray.slice( 0 );

			for ( let i = 0, l = array.length; i < l; i ++ ) {

				array[ i ].call( this, event );

			}

			event.target = null;

		}

	}

}

const _lut = [ '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '0a', '0b', '0c', '0d', '0e', '0f', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '1a', '1b', '1c', '1d', '1e', '1f', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '2a', '2b', '2c', '2d', '2e', '2f', '30', '31', '32', '33', '34', '35', '36', '37', '38', '39', '3a', '3b', '3c', '3d', '3e', '3f', '40', '41', '42', '43', '44', '45', '46', '47', '48', '49', '4a', '4b', '4c', '4d', '4e', '4f', '50', '51', '52', '53', '54', '55', '56', '57', '58', '59', '5a', '5b', '5c', '5d', '5e', '5f', '60', '61', '62', '63', '64', '65', '66', '67', '68', '69', '6a', '6b', '6c', '6d', '6e', '6f', '70', '71', '72', '73', '74', '75', '76', '77', '78', '79', '7a', '7b', '7c', '7d', '7e', '7f', '80', '81', '82', '83', '84', '85', '86', '87', '88', '89', '8a', '8b', '8c', '8d', '8e', '8f', '90', '91', '92', '93', '94', '95', '96', '97', '98', '99', '9a', '9b', '9c', '9d', '9e', '9f', 'a0', 'a1', 'a2', 'a3', 'a4', 'a5', 'a6', 'a7', 'a8', 'a9', 'aa', 'ab', 'ac', 'ad', 'ae', 'af', 'b0', 'b1', 'b2', 'b3', 'b4', 'b5', 'b6', 'b7', 'b8', 'b9', 'ba', 'bb', 'bc', 'bd', 'be', 'bf', 'c0', 'c1', 'c2', 'c3', 'c4', 'c5', 'c6', 'c7', 'c8', 'c9', 'ca', 'cb', 'cc', 'cd', 'ce', 'cf', 'd0', 'd1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8', 'd9', 'da', 'db', 'dc', 'dd', 'de', 'df', 'e0', 'e1', 'e2', 'e3', 'e4', 'e5', 'e6', 'e7', 'e8', 'e9', 'ea', 'eb', 'ec', 'ed', 'ee', 'ef', 'f0', 'f1', 'f2', 'f3', 'f4', 'f5', 'f6', 'f7', 'f8', 'f9', 'fa', 'fb', 'fc', 'fd', 'fe', 'ff' ];

// http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/21963136#21963136
function generateUUID() {

	const d0 = Math.random() * 0xffffffff | 0;
	const d1 = Math.random() * 0xffffffff | 0;
	const d2 = Math.random() * 0xffffffff | 0;
	const d3 = Math.random() * 0xffffffff | 0;
	const uuid = _lut[ d0 & 0xff ] + _lut[ d0 >> 8 & 0xff ] + _lut[ d0 >> 16 & 0xff ] + _lut[ d0 >> 24 & 0xff ] + '-' +
			_lut[ d1 & 0xff ] + _lut[ d1 >> 8 & 0xff ] + '-' + _lut[ d1 >> 16 & 0x0f | 0x40 ] + _lut[ d1 >> 24 & 0xff ] + '-' +
			_lut[ d2 & 0x3f | 0x80 ] + _lut[ d2 >> 8 & 0xff ] + '-' + _lut[ d2 >> 16 & 0xff ] + _lut[ d2 >> 24 & 0xff ] +
			_lut[ d3 & 0xff ] + _lut[ d3 >> 8 & 0xff ] + _lut[ d3 >> 16 & 0xff ] + _lut[ d3 >> 24 & 0xff ];

	// .toLowerCase() here flattens concatenated strings to save heap memory space.
	return uuid.toLowerCase();

}

function clamp( value, min, max ) {

	return Math.max( min, Math.min( max, value ) );

}

// compute euclidean modulo of m % n
// https://en.wikipedia.org/wiki/Modulo_operation
function euclideanModulo( n, m ) {

	return ( ( n % m ) + m ) % m;

}

// https://en.wikipedia.org/wiki/Linear_interpolation
function lerp( x, y, t ) {

	return ( 1 - t ) * x + t * y;

}

function denormalize( value, array ) {

	switch ( array.constructor ) {

		case Float32Array:

			return value;

		case Uint32Array:

			return value / 4294967295.0;

		case Uint16Array:

			return value / 65535.0;

		case Uint8Array:

			return value / 255.0;

		case Int32Array:

			return Math.max( value / 2147483647.0, - 1.0 );

		case Int16Array:

			return Math.max( value / 32767.0, - 1.0 );

		case Int8Array:

			return Math.max( value / 127.0, - 1.0 );

		default:

			throw new Error( 'Invalid component type.' );

	}

}

function normalize( value, array ) {

	switch ( array.constructor ) {

		case Float32Array:

			return value;

		case Uint32Array:

			return Math.round( value * 4294967295.0 );

		case Uint16Array:

			return Math.round( value * 65535.0 );

		case Uint8Array:

			return Math.round( value * 255.0 );

		case Int32Array:

			return Math.round( value * 2147483647.0 );

		case Int16Array:

			return Math.round( value * 32767.0 );

		case Int8Array:

			return Math.round( value * 127.0 );

		default:

			throw new Error( 'Invalid component type.' );

	}

}

class Vector2 {

	constructor( x = 0, y = 0 ) {

		Vector2.prototype.isVector2 = true;

		this.x = x;
		this.y = y;

	}

	get width() {

		return this.x;

	}

	set width( value ) {

		this.x = value;

	}

	get height() {

		return this.y;

	}

	set height( value ) {

		this.y = value;

	}

	set( x, y ) {

		this.x = x;
		this.y = y;

		return this;

	}

	setScalar( scalar ) {

		this.x = scalar;
		this.y = scalar;

		return this;

	}

	setX( x ) {

		this.x = x;

		return this;

	}

	setY( y ) {

		this.y = y;

		return this;

	}

	setComponent( index, value ) {

		switch ( index ) {

			case 0: this.x = value; break;
			case 1: this.y = value; break;
			default: throw new Error( 'index is out of range: ' + index );

		}

		return this;

	}

	getComponent( index ) {

		switch ( index ) {

			case 0: return this.x;
			case 1: return this.y;
			default: throw new Error( 'index is out of range: ' + index );

		}

	}

	clone() {

		return new this.constructor( this.x, this.y );

	}

	copy( v ) {

		this.x = v.x;
		this.y = v.y;

		return this;

	}

	add( v ) {

		this.x += v.x;
		this.y += v.y;

		return this;

	}

	addScalar( s ) {

		this.x += s;
		this.y += s;

		return this;

	}

	addVectors( a, b ) {

		this.x = a.x + b.x;
		this.y = a.y + b.y;

		return this;

	}

	addScaledVector( v, s ) {

		this.x += v.x * s;
		this.y += v.y * s;

		return this;

	}

	sub( v ) {

		this.x -= v.x;
		this.y -= v.y;

		return this;

	}

	subScalar( s ) {

		this.x -= s;
		this.y -= s;

		return this;

	}

	subVectors( a, b ) {

		this.x = a.x - b.x;
		this.y = a.y - b.y;

		return this;

	}

	multiply( v ) {

		this.x *= v.x;
		this.y *= v.y;

		return this;

	}

	multiplyScalar( scalar ) {

		this.x *= scalar;
		this.y *= scalar;

		return this;

	}

	divide( v ) {

		this.x /= v.x;
		this.y /= v.y;

		return this;

	}

	divideScalar( scalar ) {

		return this.multiplyScalar( 1 / scalar );

	}

	applyMatrix3( m ) {

		const x = this.x, y = this.y;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 3 ] * y + e[ 6 ];
		this.y = e[ 1 ] * x + e[ 4 ] * y + e[ 7 ];

		return this;

	}

	min( v ) {

		this.x = Math.min( this.x, v.x );
		this.y = Math.min( this.y, v.y );

		return this;

	}

	max( v ) {

		this.x = Math.max( this.x, v.x );
		this.y = Math.max( this.y, v.y );

		return this;

	}

	clamp( min, max ) {

		// assumes min < max, componentwise

		this.x = Math.max( min.x, Math.min( max.x, this.x ) );
		this.y = Math.max( min.y, Math.min( max.y, this.y ) );

		return this;

	}

	clampScalar( minVal, maxVal ) {

		this.x = Math.max( minVal, Math.min( maxVal, this.x ) );
		this.y = Math.max( minVal, Math.min( maxVal, this.y ) );

		return this;

	}

	clampLength( min, max ) {

		const length = this.length();

		return this.divideScalar( length || 1 ).multiplyScalar( Math.max( min, Math.min( max, length ) ) );

	}

	floor() {

		this.x = Math.floor( this.x );
		this.y = Math.floor( this.y );

		return this;

	}

	ceil() {

		this.x = Math.ceil( this.x );
		this.y = Math.ceil( this.y );

		return this;

	}

	round() {

		this.x = Math.round( this.x );
		this.y = Math.round( this.y );

		return this;

	}

	roundToZero() {

		this.x = Math.trunc( this.x );
		this.y = Math.trunc( this.y );

		return this;

	}

	negate() {

		this.x = - this.x;
		this.y = - this.y;

		return this;

	}

	dot( v ) {

		return this.x * v.x + this.y * v.y;

	}

	cross( v ) {

		return this.x * v.y - this.y * v.x;

	}

	lengthSq() {

		return this.x * this.x + this.y * this.y;

	}

	length() {

		return Math.sqrt( this.x * this.x + this.y * this.y );

	}

	manhattanLength() {

		return Math.abs( this.x ) + Math.abs( this.y );

	}

	normalize() {

		return this.divideScalar( this.length() || 1 );

	}

	angle() {

		// computes the angle in radians with respect to the positive x-axis

		const angle = Math.atan2( - this.y, - this.x ) + Math.PI;

		return angle;

	}

	angleTo( v ) {

		const denominator = Math.sqrt( this.lengthSq() * v.lengthSq() );

		if ( denominator === 0 ) return Math.PI / 2;

		const theta = this.dot( v ) / denominator;

		// clamp, to handle numerical problems

		return Math.acos( clamp( theta, - 1, 1 ) );

	}

	distanceTo( v ) {

		return Math.sqrt( this.distanceToSquared( v ) );

	}

	distanceToSquared( v ) {

		const dx = this.x - v.x, dy = this.y - v.y;
		return dx * dx + dy * dy;

	}

	manhattanDistanceTo( v ) {

		return Math.abs( this.x - v.x ) + Math.abs( this.y - v.y );

	}

	setLength( length ) {

		return this.normalize().multiplyScalar( length );

	}

	lerp( v, alpha ) {

		this.x += ( v.x - this.x ) * alpha;
		this.y += ( v.y - this.y ) * alpha;

		return this;

	}

	lerpVectors( v1, v2, alpha ) {

		this.x = v1.x + ( v2.x - v1.x ) * alpha;
		this.y = v1.y + ( v2.y - v1.y ) * alpha;

		return this;

	}

	equals( v ) {

		return ( ( v.x === this.x ) && ( v.y === this.y ) );

	}

	fromArray( array, offset = 0 ) {

		this.x = array[ offset ];
		this.y = array[ offset + 1 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.x;
		array[ offset + 1 ] = this.y;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.x = attribute.getX( index );
		this.y = attribute.getY( index );

		return this;

	}

	rotateAround( center, angle ) {

		const c = Math.cos( angle ), s = Math.sin( angle );

		const x = this.x - center.x;
		const y = this.y - center.y;

		this.x = x * c - y * s + center.x;
		this.y = x * s + y * c + center.y;

		return this;

	}

	random() {

		this.x = Math.random();
		this.y = Math.random();

		return this;

	}

	*[ Symbol.iterator ]() {

		yield this.x;
		yield this.y;

	}

}

class Matrix3 {

	constructor( n11, n12, n13, n21, n22, n23, n31, n32, n33 ) {

		Matrix3.prototype.isMatrix3 = true;

		this.elements = [

			1, 0, 0,
			0, 1, 0,
			0, 0, 1

		];

		if ( n11 !== undefined ) {

			this.set( n11, n12, n13, n21, n22, n23, n31, n32, n33 );

		}

	}

	set( n11, n12, n13, n21, n22, n23, n31, n32, n33 ) {

		const te = this.elements;

		te[ 0 ] = n11; te[ 1 ] = n21; te[ 2 ] = n31;
		te[ 3 ] = n12; te[ 4 ] = n22; te[ 5 ] = n32;
		te[ 6 ] = n13; te[ 7 ] = n23; te[ 8 ] = n33;

		return this;

	}

	identity() {

		this.set(

			1, 0, 0,
			0, 1, 0,
			0, 0, 1

		);

		return this;

	}

	copy( m ) {

		const te = this.elements;
		const me = m.elements;

		te[ 0 ] = me[ 0 ]; te[ 1 ] = me[ 1 ]; te[ 2 ] = me[ 2 ];
		te[ 3 ] = me[ 3 ]; te[ 4 ] = me[ 4 ]; te[ 5 ] = me[ 5 ];
		te[ 6 ] = me[ 6 ]; te[ 7 ] = me[ 7 ]; te[ 8 ] = me[ 8 ];

		return this;

	}

	extractBasis( xAxis, yAxis, zAxis ) {

		xAxis.setFromMatrix3Column( this, 0 );
		yAxis.setFromMatrix3Column( this, 1 );
		zAxis.setFromMatrix3Column( this, 2 );

		return this;

	}

	setFromMatrix4( m ) {

		const me = m.elements;

		this.set(

			me[ 0 ], me[ 4 ], me[ 8 ],
			me[ 1 ], me[ 5 ], me[ 9 ],
			me[ 2 ], me[ 6 ], me[ 10 ]

		);

		return this;

	}

	multiply( m ) {

		return this.multiplyMatrices( this, m );

	}

	premultiply( m ) {

		return this.multiplyMatrices( m, this );

	}

	multiplyMatrices( a, b ) {

		const ae = a.elements;
		const be = b.elements;
		const te = this.elements;

		const a11 = ae[ 0 ], a12 = ae[ 3 ], a13 = ae[ 6 ];
		const a21 = ae[ 1 ], a22 = ae[ 4 ], a23 = ae[ 7 ];
		const a31 = ae[ 2 ], a32 = ae[ 5 ], a33 = ae[ 8 ];

		const b11 = be[ 0 ], b12 = be[ 3 ], b13 = be[ 6 ];
		const b21 = be[ 1 ], b22 = be[ 4 ], b23 = be[ 7 ];
		const b31 = be[ 2 ], b32 = be[ 5 ], b33 = be[ 8 ];

		te[ 0 ] = a11 * b11 + a12 * b21 + a13 * b31;
		te[ 3 ] = a11 * b12 + a12 * b22 + a13 * b32;
		te[ 6 ] = a11 * b13 + a12 * b23 + a13 * b33;

		te[ 1 ] = a21 * b11 + a22 * b21 + a23 * b31;
		te[ 4 ] = a21 * b12 + a22 * b22 + a23 * b32;
		te[ 7 ] = a21 * b13 + a22 * b23 + a23 * b33;

		te[ 2 ] = a31 * b11 + a32 * b21 + a33 * b31;
		te[ 5 ] = a31 * b12 + a32 * b22 + a33 * b32;
		te[ 8 ] = a31 * b13 + a32 * b23 + a33 * b33;

		return this;

	}

	multiplyScalar( s ) {

		const te = this.elements;

		te[ 0 ] *= s; te[ 3 ] *= s; te[ 6 ] *= s;
		te[ 1 ] *= s; te[ 4 ] *= s; te[ 7 ] *= s;
		te[ 2 ] *= s; te[ 5 ] *= s; te[ 8 ] *= s;

		return this;

	}

	determinant() {

		const te = this.elements;

		const a = te[ 0 ], b = te[ 1 ], c = te[ 2 ],
			d = te[ 3 ], e = te[ 4 ], f = te[ 5 ],
			g = te[ 6 ], h = te[ 7 ], i = te[ 8 ];

		return a * e * i - a * f * h - b * d * i + b * f * g + c * d * h - c * e * g;

	}

	invert() {

		const te = this.elements,

			n11 = te[ 0 ], n21 = te[ 1 ], n31 = te[ 2 ],
			n12 = te[ 3 ], n22 = te[ 4 ], n32 = te[ 5 ],
			n13 = te[ 6 ], n23 = te[ 7 ], n33 = te[ 8 ],

			t11 = n33 * n22 - n32 * n23,
			t12 = n32 * n13 - n33 * n12,
			t13 = n23 * n12 - n22 * n13,

			det = n11 * t11 + n21 * t12 + n31 * t13;

		if ( det === 0 ) return this.set( 0, 0, 0, 0, 0, 0, 0, 0, 0 );

		const detInv = 1 / det;

		te[ 0 ] = t11 * detInv;
		te[ 1 ] = ( n31 * n23 - n33 * n21 ) * detInv;
		te[ 2 ] = ( n32 * n21 - n31 * n22 ) * detInv;

		te[ 3 ] = t12 * detInv;
		te[ 4 ] = ( n33 * n11 - n31 * n13 ) * detInv;
		te[ 5 ] = ( n31 * n12 - n32 * n11 ) * detInv;

		te[ 6 ] = t13 * detInv;
		te[ 7 ] = ( n21 * n13 - n23 * n11 ) * detInv;
		te[ 8 ] = ( n22 * n11 - n21 * n12 ) * detInv;

		return this;

	}

	transpose() {

		let tmp;
		const m = this.elements;

		tmp = m[ 1 ]; m[ 1 ] = m[ 3 ]; m[ 3 ] = tmp;
		tmp = m[ 2 ]; m[ 2 ] = m[ 6 ]; m[ 6 ] = tmp;
		tmp = m[ 5 ]; m[ 5 ] = m[ 7 ]; m[ 7 ] = tmp;

		return this;

	}

	getNormalMatrix( matrix4 ) {

		return this.setFromMatrix4( matrix4 ).invert().transpose();

	}

	transposeIntoArray( r ) {

		const m = this.elements;

		r[ 0 ] = m[ 0 ];
		r[ 1 ] = m[ 3 ];
		r[ 2 ] = m[ 6 ];
		r[ 3 ] = m[ 1 ];
		r[ 4 ] = m[ 4 ];
		r[ 5 ] = m[ 7 ];
		r[ 6 ] = m[ 2 ];
		r[ 7 ] = m[ 5 ];
		r[ 8 ] = m[ 8 ];

		return this;

	}

	setUvTransform( tx, ty, sx, sy, rotation, cx, cy ) {

		const c = Math.cos( rotation );
		const s = Math.sin( rotation );

		this.set(
			sx * c, sx * s, - sx * ( c * cx + s * cy ) + cx + tx,
			- sy * s, sy * c, - sy * ( - s * cx + c * cy ) + cy + ty,
			0, 0, 1
		);

		return this;

	}

	//

	scale( sx, sy ) {

		this.premultiply( _m3.makeScale( sx, sy ) );

		return this;

	}

	rotate( theta ) {

		this.premultiply( _m3.makeRotation( - theta ) );

		return this;

	}

	translate( tx, ty ) {

		this.premultiply( _m3.makeTranslation( tx, ty ) );

		return this;

	}

	// for 2D Transforms

	makeTranslation( x, y ) {

		if ( x.isVector2 ) {

			this.set(

				1, 0, x.x,
				0, 1, x.y,
				0, 0, 1

			);

		} else {

			this.set(

				1, 0, x,
				0, 1, y,
				0, 0, 1

			);

		}

		return this;

	}

	makeRotation( theta ) {

		// counterclockwise

		const c = Math.cos( theta );
		const s = Math.sin( theta );

		this.set(

			c, - s, 0,
			s, c, 0,
			0, 0, 1

		);

		return this;

	}

	makeScale( x, y ) {

		this.set(

			x, 0, 0,
			0, y, 0,
			0, 0, 1

		);

		return this;

	}

	//

	equals( matrix ) {

		const te = this.elements;
		const me = matrix.elements;

		for ( let i = 0; i < 9; i ++ ) {

			if ( te[ i ] !== me[ i ] ) return false;

		}

		return true;

	}

	fromArray( array, offset = 0 ) {

		for ( let i = 0; i < 9; i ++ ) {

			this.elements[ i ] = array[ i + offset ];

		}

		return this;

	}

	toArray( array = [], offset = 0 ) {

		const te = this.elements;

		array[ offset ] = te[ 0 ];
		array[ offset + 1 ] = te[ 1 ];
		array[ offset + 2 ] = te[ 2 ];

		array[ offset + 3 ] = te[ 3 ];
		array[ offset + 4 ] = te[ 4 ];
		array[ offset + 5 ] = te[ 5 ];

		array[ offset + 6 ] = te[ 6 ];
		array[ offset + 7 ] = te[ 7 ];
		array[ offset + 8 ] = te[ 8 ];

		return array;

	}

	clone() {

		return new this.constructor().fromArray( this.elements );

	}

}

const _m3 = /*@__PURE__*/ new Matrix3();

function arrayNeedsUint32( array ) {

	// assumes larger values usually on last

	for ( let i = array.length - 1; i >= 0; -- i ) {

		if ( array[ i ] >= 65535 ) return true; // account for PRIMITIVE_RESTART_FIXED_INDEX, #24565

	}

	return false;

}

function createElementNS( name ) {

	return document.createElementNS( 'http://www.w3.org/1999/xhtml', name );

}

const _cache = {};

function warnOnce( message ) {

	if ( message in _cache ) return;

	_cache[ message ] = true;

	console.warn( message );

}

/**
 * Matrices converting P3 <-> Rec. 709 primaries, without gamut mapping
 * or clipping. Based on W3C specifications for sRGB and Display P3,
 * and ICC specifications for the D50 connection space. Values in/out
 * are _linear_ sRGB and _linear_ Display P3.
 *
 * Note that both sRGB and Display P3 use the sRGB transfer functions.
 *
 * Reference:
 * - http://www.russellcottrell.com/photo/matrixCalculator.htm
 */

const LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 = /*@__PURE__*/ new Matrix3().set(
	0.8224621, 0.177538, 0.0,
	0.0331941, 0.9668058, 0.0,
	0.0170827, 0.0723974, 0.9105199,
);

const LINEAR_DISPLAY_P3_TO_LINEAR_SRGB = /*@__PURE__*/ new Matrix3().set(
	1.2249401, - 0.2249404, 0.0,
	- 0.0420569, 1.0420571, 0.0,
	- 0.0196376, - 0.0786361, 1.0982735
);

/**
 * Defines supported color spaces by transfer function and primaries,
 * and provides conversions to/from the Linear-sRGB reference space.
 */
const COLOR_SPACES = {
	[ LinearSRGBColorSpace ]: {
		transfer: LinearTransfer,
		primaries: Rec709Primaries,
		toReference: ( color ) => color,
		fromReference: ( color ) => color,
	},
	[ SRGBColorSpace ]: {
		transfer: SRGBTransfer,
		primaries: Rec709Primaries,
		toReference: ( color ) => color.convertSRGBToLinear(),
		fromReference: ( color ) => color.convertLinearToSRGB(),
	},
	[ LinearDisplayP3ColorSpace ]: {
		transfer: LinearTransfer,
		primaries: P3Primaries,
		toReference: ( color ) => color.applyMatrix3( LINEAR_DISPLAY_P3_TO_LINEAR_SRGB ),
		fromReference: ( color ) => color.applyMatrix3( LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 ),
	},
	[ DisplayP3ColorSpace ]: {
		transfer: SRGBTransfer,
		primaries: P3Primaries,
		toReference: ( color ) => color.convertSRGBToLinear().applyMatrix3( LINEAR_DISPLAY_P3_TO_LINEAR_SRGB ),
		fromReference: ( color ) => color.applyMatrix3( LINEAR_SRGB_TO_LINEAR_DISPLAY_P3 ).convertLinearToSRGB(),
	},
};

const SUPPORTED_WORKING_COLOR_SPACES = new Set( [ LinearSRGBColorSpace, LinearDisplayP3ColorSpace ] );

const ColorManagement = {

	enabled: true,

	_workingColorSpace: LinearSRGBColorSpace,

	get workingColorSpace() {

		return this._workingColorSpace;

	},

	set workingColorSpace( colorSpace ) {

		if ( ! SUPPORTED_WORKING_COLOR_SPACES.has( colorSpace ) ) {

			throw new Error( `Unsupported working color space, "${ colorSpace }".` );

		}

		this._workingColorSpace = colorSpace;

	},

	convert: function ( color, sourceColorSpace, targetColorSpace ) {

		if ( this.enabled === false || sourceColorSpace === targetColorSpace || ! sourceColorSpace || ! targetColorSpace ) {

			return color;

		}

		const sourceToReference = COLOR_SPACES[ sourceColorSpace ].toReference;
		const targetFromReference = COLOR_SPACES[ targetColorSpace ].fromReference;

		return targetFromReference( sourceToReference( color ) );

	},

	fromWorkingColorSpace: function ( color, targetColorSpace ) {

		return this.convert( color, this._workingColorSpace, targetColorSpace );

	},

	toWorkingColorSpace: function ( color, sourceColorSpace ) {

		return this.convert( color, sourceColorSpace, this._workingColorSpace );

	},

	getPrimaries: function ( colorSpace ) {

		return COLOR_SPACES[ colorSpace ].primaries;

	},

	getTransfer: function ( colorSpace ) {

		if ( colorSpace === NoColorSpace ) return LinearTransfer;

		return COLOR_SPACES[ colorSpace ].transfer;

	},

};


function SRGBToLinear( c ) {

	return ( c < 0.04045 ) ? c * 0.0773993808 : Math.pow( c * 0.9478672986 + 0.0521327014, 2.4 );

}

function LinearToSRGB( c ) {

	return ( c < 0.0031308 ) ? c * 12.92 : 1.055 * ( Math.pow( c, 0.41666 ) ) - 0.055;

}

let _canvas;

class ImageUtils {

	static getDataURL( image ) {

		if ( /^data:/i.test( image.src ) ) {

			return image.src;

		}

		if ( typeof HTMLCanvasElement === 'undefined' ) {

			return image.src;

		}

		let canvas;

		if ( image instanceof HTMLCanvasElement ) {

			canvas = image;

		} else {

			if ( _canvas === undefined ) _canvas = createElementNS( 'canvas' );

			_canvas.width = image.width;
			_canvas.height = image.height;

			const context = _canvas.getContext( '2d' );

			if ( image instanceof ImageData ) {

				context.putImageData( image, 0, 0 );

			} else {

				context.drawImage( image, 0, 0, image.width, image.height );

			}

			canvas = _canvas;

		}

		if ( canvas.width > 2048 || canvas.height > 2048 ) {

			console.warn( 'THREE.ImageUtils.getDataURL: Image converted to jpg for performance reasons', image );

			return canvas.toDataURL( 'image/jpeg', 0.6 );

		} else {

			return canvas.toDataURL( 'image/png' );

		}

	}

	static sRGBToLinear( image ) {

		if ( ( typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement ) ||
			( typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement ) ||
			( typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap ) ) {

			const canvas = createElementNS( 'canvas' );

			canvas.width = image.width;
			canvas.height = image.height;

			const context = canvas.getContext( '2d' );
			context.drawImage( image, 0, 0, image.width, image.height );

			const imageData = context.getImageData( 0, 0, image.width, image.height );
			const data = imageData.data;

			for ( let i = 0; i < data.length; i ++ ) {

				data[ i ] = SRGBToLinear( data[ i ] / 255 ) * 255;

			}

			context.putImageData( imageData, 0, 0 );

			return canvas;

		} else if ( image.data ) {

			const data = image.data.slice( 0 );

			for ( let i = 0; i < data.length; i ++ ) {

				if ( data instanceof Uint8Array || data instanceof Uint8ClampedArray ) {

					data[ i ] = Math.floor( SRGBToLinear( data[ i ] / 255 ) * 255 );

				} else {

					// assuming float

					data[ i ] = SRGBToLinear( data[ i ] );

				}

			}

			return {
				data: data,
				width: image.width,
				height: image.height
			};

		} else {

			console.warn( 'THREE.ImageUtils.sRGBToLinear(): Unsupported image type. No color space conversion applied.' );
			return image;

		}

	}

}

let _sourceId = 0;

class Source {

	constructor( data = null ) {

		this.isSource = true;

		Object.defineProperty( this, 'id', { value: _sourceId ++ } );

		this.uuid = generateUUID();

		this.data = data;
		this.dataReady = true;

		this.version = 0;

	}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	toJSON( meta ) {

		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		if ( ! isRootObject && meta.images[ this.uuid ] !== undefined ) {

			return meta.images[ this.uuid ];

		}

		const output = {
			uuid: this.uuid,
			url: ''
		};

		const data = this.data;

		if ( data !== null ) {

			let url;

			if ( Array.isArray( data ) ) {

				// cube texture

				url = [];

				for ( let i = 0, l = data.length; i < l; i ++ ) {

					if ( data[ i ].isDataTexture ) {

						url.push( serializeImage( data[ i ].image ) );

					} else {

						url.push( serializeImage( data[ i ] ) );

					}

				}

			} else {

				// texture

				url = serializeImage( data );

			}

			output.url = url;

		}

		if ( ! isRootObject ) {

			meta.images[ this.uuid ] = output;

		}

		return output;

	}

}

function serializeImage( image ) {

	if ( ( typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement ) ||
		( typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement ) ||
		( typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap ) ) {

		// default images

		return ImageUtils.getDataURL( image );

	} else {

		if ( image.data ) {

			// images of DataTexture

			return {
				data: Array.from( image.data ),
				width: image.width,
				height: image.height,
				type: image.data.constructor.name
			};

		} else {

			console.warn( 'THREE.Texture: Unable to serialize Texture.' );
			return {};

		}

	}

}

let _textureId = 0;

class Texture extends EventDispatcher {

	constructor( image = Texture.DEFAULT_IMAGE, mapping = Texture.DEFAULT_MAPPING, wrapS = ClampToEdgeWrapping, wrapT = ClampToEdgeWrapping, magFilter = LinearFilter, minFilter = LinearMipmapLinearFilter, format = RGBAFormat, type = UnsignedByteType, anisotropy = Texture.DEFAULT_ANISOTROPY, colorSpace = NoColorSpace ) {

		super();

		this.isTexture = true;

		Object.defineProperty( this, 'id', { value: _textureId ++ } );

		this.uuid = generateUUID();

		this.name = '';

		this.source = new Source( image );
		this.mipmaps = [];

		this.mapping = mapping;
		this.channel = 0;

		this.wrapS = wrapS;
		this.wrapT = wrapT;

		this.magFilter = magFilter;
		this.minFilter = minFilter;

		this.anisotropy = anisotropy;

		this.format = format;
		this.internalFormat = null;
		this.type = type;

		this.offset = new Vector2( 0, 0 );
		this.repeat = new Vector2( 1, 1 );
		this.center = new Vector2( 0, 0 );
		this.rotation = 0;

		this.matrixAutoUpdate = true;
		this.matrix = new Matrix3();

		this.generateMipmaps = true;
		this.premultiplyAlpha = false;
		this.flipY = true;
		this.unpackAlignment = 4;	// valid values: 1, 2, 4, 8 (see http://www.khronos.org/opengles/sdk/docs/man/xhtml/glPixelStorei.xml)

		this.colorSpace = colorSpace;

		this.userData = {};

		this.version = 0;
		this.onUpdate = null;

		this.isRenderTargetTexture = false; // indicates whether a texture belongs to a render target or not
		this.pmremVersion = 0; // indicates whether this texture should be processed by PMREMGenerator or not (only relevant for render target textures)

	}

	get image() {

		return this.source.data;

	}

	set image( value = null ) {

		this.source.data = value;

	}

	updateMatrix() {

		this.matrix.setUvTransform( this.offset.x, this.offset.y, this.repeat.x, this.repeat.y, this.rotation, this.center.x, this.center.y );

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		this.name = source.name;

		this.source = source.source;
		this.mipmaps = source.mipmaps.slice( 0 );

		this.mapping = source.mapping;
		this.channel = source.channel;

		this.wrapS = source.wrapS;
		this.wrapT = source.wrapT;

		this.magFilter = source.magFilter;
		this.minFilter = source.minFilter;

		this.anisotropy = source.anisotropy;

		this.format = source.format;
		this.internalFormat = source.internalFormat;
		this.type = source.type;

		this.offset.copy( source.offset );
		this.repeat.copy( source.repeat );
		this.center.copy( source.center );
		this.rotation = source.rotation;

		this.matrixAutoUpdate = source.matrixAutoUpdate;
		this.matrix.copy( source.matrix );

		this.generateMipmaps = source.generateMipmaps;
		this.premultiplyAlpha = source.premultiplyAlpha;
		this.flipY = source.flipY;
		this.unpackAlignment = source.unpackAlignment;
		this.colorSpace = source.colorSpace;

		this.userData = JSON.parse( JSON.stringify( source.userData ) );

		this.needsUpdate = true;

		return this;

	}

	toJSON( meta ) {

		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		if ( ! isRootObject && meta.textures[ this.uuid ] !== undefined ) {

			return meta.textures[ this.uuid ];

		}

		const output = {

			metadata: {
				version: 4.6,
				type: 'Texture',
				generator: 'Texture.toJSON'
			},

			uuid: this.uuid,
			name: this.name,

			image: this.source.toJSON( meta ).uuid,

			mapping: this.mapping,
			channel: this.channel,

			repeat: [ this.repeat.x, this.repeat.y ],
			offset: [ this.offset.x, this.offset.y ],
			center: [ this.center.x, this.center.y ],
			rotation: this.rotation,

			wrap: [ this.wrapS, this.wrapT ],

			format: this.format,
			internalFormat: this.internalFormat,
			type: this.type,
			colorSpace: this.colorSpace,

			minFilter: this.minFilter,
			magFilter: this.magFilter,
			anisotropy: this.anisotropy,

			flipY: this.flipY,

			generateMipmaps: this.generateMipmaps,
			premultiplyAlpha: this.premultiplyAlpha,
			unpackAlignment: this.unpackAlignment

		};

		if ( Object.keys( this.userData ).length > 0 ) output.userData = this.userData;

		if ( ! isRootObject ) {

			meta.textures[ this.uuid ] = output;

		}

		return output;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

	}

	transformUv( uv ) {

		if ( this.mapping !== UVMapping ) return uv;

		uv.applyMatrix3( this.matrix );

		if ( uv.x < 0 || uv.x > 1 ) {

			switch ( this.wrapS ) {

				case RepeatWrapping:

					uv.x = uv.x - Math.floor( uv.x );
					break;

				case ClampToEdgeWrapping:

					uv.x = uv.x < 0 ? 0 : 1;
					break;

				case MirroredRepeatWrapping:

					if ( Math.abs( Math.floor( uv.x ) % 2 ) === 1 ) {

						uv.x = Math.ceil( uv.x ) - uv.x;

					} else {

						uv.x = uv.x - Math.floor( uv.x );

					}

					break;

			}

		}

		if ( uv.y < 0 || uv.y > 1 ) {

			switch ( this.wrapT ) {

				case RepeatWrapping:

					uv.y = uv.y - Math.floor( uv.y );
					break;

				case ClampToEdgeWrapping:

					uv.y = uv.y < 0 ? 0 : 1;
					break;

				case MirroredRepeatWrapping:

					if ( Math.abs( Math.floor( uv.y ) % 2 ) === 1 ) {

						uv.y = Math.ceil( uv.y ) - uv.y;

					} else {

						uv.y = uv.y - Math.floor( uv.y );

					}

					break;

			}

		}

		if ( this.flipY ) {

			uv.y = 1 - uv.y;

		}

		return uv;

	}

	set needsUpdate( value ) {

		if ( value === true ) {

			this.version ++;
			this.source.needsUpdate = true;

		}

	}

	set needsPMREMUpdate( value ) {

		if ( value === true ) {

			this.pmremVersion ++;

		}

	}

}

Texture.DEFAULT_IMAGE = null;
Texture.DEFAULT_MAPPING = UVMapping;
Texture.DEFAULT_ANISOTROPY = 1;

class Quaternion {

	constructor( x = 0, y = 0, z = 0, w = 1 ) {

		this.isQuaternion = true;

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

	}

	static slerpFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1, t ) {

		// fuzz-free, array-based Quaternion SLERP operation

		let x0 = src0[ srcOffset0 + 0 ],
			y0 = src0[ srcOffset0 + 1 ],
			z0 = src0[ srcOffset0 + 2 ],
			w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 + 0 ],
			y1 = src1[ srcOffset1 + 1 ],
			z1 = src1[ srcOffset1 + 2 ],
			w1 = src1[ srcOffset1 + 3 ];

		if ( t === 0 ) {

			dst[ dstOffset + 0 ] = x0;
			dst[ dstOffset + 1 ] = y0;
			dst[ dstOffset + 2 ] = z0;
			dst[ dstOffset + 3 ] = w0;
			return;

		}

		if ( t === 1 ) {

			dst[ dstOffset + 0 ] = x1;
			dst[ dstOffset + 1 ] = y1;
			dst[ dstOffset + 2 ] = z1;
			dst[ dstOffset + 3 ] = w1;
			return;

		}

		if ( w0 !== w1 || x0 !== x1 || y0 !== y1 || z0 !== z1 ) {

			let s = 1 - t;
			const cos = x0 * x1 + y0 * y1 + z0 * z1 + w0 * w1,
				dir = ( cos >= 0 ? 1 : - 1 ),
				sqrSin = 1 - cos * cos;

			// Skip the Slerp for tiny steps to avoid numeric problems:
			if ( sqrSin > Number.EPSILON ) {

				const sin = Math.sqrt( sqrSin ),
					len = Math.atan2( sin, cos * dir );

				s = Math.sin( s * len ) / sin;
				t = Math.sin( t * len ) / sin;

			}

			const tDir = t * dir;

			x0 = x0 * s + x1 * tDir;
			y0 = y0 * s + y1 * tDir;
			z0 = z0 * s + z1 * tDir;
			w0 = w0 * s + w1 * tDir;

			// Normalize in case we just did a lerp:
			if ( s === 1 - t ) {

				const f = 1 / Math.sqrt( x0 * x0 + y0 * y0 + z0 * z0 + w0 * w0 );

				x0 *= f;
				y0 *= f;
				z0 *= f;
				w0 *= f;

			}

		}

		dst[ dstOffset ] = x0;
		dst[ dstOffset + 1 ] = y0;
		dst[ dstOffset + 2 ] = z0;
		dst[ dstOffset + 3 ] = w0;

	}

	static multiplyQuaternionsFlat( dst, dstOffset, src0, srcOffset0, src1, srcOffset1 ) {

		const x0 = src0[ srcOffset0 ];
		const y0 = src0[ srcOffset0 + 1 ];
		const z0 = src0[ srcOffset0 + 2 ];
		const w0 = src0[ srcOffset0 + 3 ];

		const x1 = src1[ srcOffset1 ];
		const y1 = src1[ srcOffset1 + 1 ];
		const z1 = src1[ srcOffset1 + 2 ];
		const w1 = src1[ srcOffset1 + 3 ];

		dst[ dstOffset ] = x0 * w1 + w0 * x1 + y0 * z1 - z0 * y1;
		dst[ dstOffset + 1 ] = y0 * w1 + w0 * y1 + z0 * x1 - x0 * z1;
		dst[ dstOffset + 2 ] = z0 * w1 + w0 * z1 + x0 * y1 - y0 * x1;
		dst[ dstOffset + 3 ] = w0 * w1 - x0 * x1 - y0 * y1 - z0 * z1;

		return dst;

	}

	get x() {

		return this._x;

	}

	set x( value ) {

		this._x = value;
		this._onChangeCallback();

	}

	get y() {

		return this._y;

	}

	set y( value ) {

		this._y = value;
		this._onChangeCallback();

	}

	get z() {

		return this._z;

	}

	set z( value ) {

		this._z = value;
		this._onChangeCallback();

	}

	get w() {

		return this._w;

	}

	set w( value ) {

		this._w = value;
		this._onChangeCallback();

	}

	set( x, y, z, w ) {

		this._x = x;
		this._y = y;
		this._z = z;
		this._w = w;

		this._onChangeCallback();

		return this;

	}

	clone() {

		return new this.constructor( this._x, this._y, this._z, this._w );

	}

	copy( quaternion ) {

		this._x = quaternion.x;
		this._y = quaternion.y;
		this._z = quaternion.z;
		this._w = quaternion.w;

		this._onChangeCallback();

		return this;

	}

	setFromEuler( euler, update = true ) {

		const x = euler._x, y = euler._y, z = euler._z, order = euler._order;

		// http://www.mathworks.com/matlabcentral/fileexchange/
		// 	20696-function-to-convert-between-dcm-euler-angles-quaternions-and-euler-vectors/
		//	content/SpinCalc.m

		const cos = Math.cos;
		const sin = Math.sin;

		const c1 = cos( x / 2 );
		const c2 = cos( y / 2 );
		const c3 = cos( z / 2 );

		const s1 = sin( x / 2 );
		const s2 = sin( y / 2 );
		const s3 = sin( z / 2 );

		switch ( order ) {

			case 'XYZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'YXZ':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'ZXY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'ZYX':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			case 'YZX':
				this._x = s1 * c2 * c3 + c1 * s2 * s3;
				this._y = c1 * s2 * c3 + s1 * c2 * s3;
				this._z = c1 * c2 * s3 - s1 * s2 * c3;
				this._w = c1 * c2 * c3 - s1 * s2 * s3;
				break;

			case 'XZY':
				this._x = s1 * c2 * c3 - c1 * s2 * s3;
				this._y = c1 * s2 * c3 - s1 * c2 * s3;
				this._z = c1 * c2 * s3 + s1 * s2 * c3;
				this._w = c1 * c2 * c3 + s1 * s2 * s3;
				break;

			default:
				console.warn( 'THREE.Quaternion: .setFromEuler() encountered an unknown order: ' + order );

		}

		if ( update === true ) this._onChangeCallback();

		return this;

	}

	setFromAxisAngle( axis, angle ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/angleToQuaternion/index.htm

		// assumes axis is normalized

		const halfAngle = angle / 2, s = Math.sin( halfAngle );

		this._x = axis.x * s;
		this._y = axis.y * s;
		this._z = axis.z * s;
		this._w = Math.cos( halfAngle );

		this._onChangeCallback();

		return this;

	}

	setFromRotationMatrix( m ) {

		// http://www.euclideanspace.com/maths/geometry/rotations/conversions/matrixToQuaternion/index.htm

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		const te = m.elements,

			m11 = te[ 0 ], m12 = te[ 4 ], m13 = te[ 8 ],
			m21 = te[ 1 ], m22 = te[ 5 ], m23 = te[ 9 ],
			m31 = te[ 2 ], m32 = te[ 6 ], m33 = te[ 10 ],

			trace = m11 + m22 + m33;

		if ( trace > 0 ) {

			const s = 0.5 / Math.sqrt( trace + 1.0 );

			this._w = 0.25 / s;
			this._x = ( m32 - m23 ) * s;
			this._y = ( m13 - m31 ) * s;
			this._z = ( m21 - m12 ) * s;

		} else if ( m11 > m22 && m11 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m11 - m22 - m33 );

			this._w = ( m32 - m23 ) / s;
			this._x = 0.25 * s;
			this._y = ( m12 + m21 ) / s;
			this._z = ( m13 + m31 ) / s;

		} else if ( m22 > m33 ) {

			const s = 2.0 * Math.sqrt( 1.0 + m22 - m11 - m33 );

			this._w = ( m13 - m31 ) / s;
			this._x = ( m12 + m21 ) / s;
			this._y = 0.25 * s;
			this._z = ( m23 + m32 ) / s;

		} else {

			const s = 2.0 * Math.sqrt( 1.0 + m33 - m11 - m22 );

			this._w = ( m21 - m12 ) / s;
			this._x = ( m13 + m31 ) / s;
			this._y = ( m23 + m32 ) / s;
			this._z = 0.25 * s;

		}

		this._onChangeCallback();

		return this;

	}

	setFromUnitVectors( vFrom, vTo ) {

		// assumes direction vectors vFrom and vTo are normalized

		let r = vFrom.dot( vTo ) + 1;

		if ( r < Number.EPSILON ) {

			// vFrom and vTo point in opposite directions

			r = 0;

			if ( Math.abs( vFrom.x ) > Math.abs( vFrom.z ) ) {

				this._x = - vFrom.y;
				this._y = vFrom.x;
				this._z = 0;
				this._w = r;

			} else {

				this._x = 0;
				this._y = - vFrom.z;
				this._z = vFrom.y;
				this._w = r;

			}

		} else {

			// crossVectors( vFrom, vTo ); // inlined to avoid cyclic dependency on Vector3

			this._x = vFrom.y * vTo.z - vFrom.z * vTo.y;
			this._y = vFrom.z * vTo.x - vFrom.x * vTo.z;
			this._z = vFrom.x * vTo.y - vFrom.y * vTo.x;
			this._w = r;

		}

		return this.normalize();

	}

	angleTo( q ) {

		return 2 * Math.acos( Math.abs( clamp( this.dot( q ), - 1, 1 ) ) );

	}

	rotateTowards( q, step ) {

		const angle = this.angleTo( q );

		if ( angle === 0 ) return this;

		const t = Math.min( 1, step / angle );

		this.slerp( q, t );

		return this;

	}

	identity() {

		return this.set( 0, 0, 0, 1 );

	}

	invert() {

		// quaternion is assumed to have unit length

		return this.conjugate();

	}

	conjugate() {

		this._x *= - 1;
		this._y *= - 1;
		this._z *= - 1;

		this._onChangeCallback();

		return this;

	}

	dot( v ) {

		return this._x * v._x + this._y * v._y + this._z * v._z + this._w * v._w;

	}

	lengthSq() {

		return this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w;

	}

	length() {

		return Math.sqrt( this._x * this._x + this._y * this._y + this._z * this._z + this._w * this._w );

	}

	normalize() {

		let l = this.length();

		if ( l === 0 ) {

			this._x = 0;
			this._y = 0;
			this._z = 0;
			this._w = 1;

		} else {

			l = 1 / l;

			this._x = this._x * l;
			this._y = this._y * l;
			this._z = this._z * l;
			this._w = this._w * l;

		}

		this._onChangeCallback();

		return this;

	}

	multiply( q ) {

		return this.multiplyQuaternions( this, q );

	}

	premultiply( q ) {

		return this.multiplyQuaternions( q, this );

	}

	multiplyQuaternions( a, b ) {

		// from http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/code/index.htm

		const qax = a._x, qay = a._y, qaz = a._z, qaw = a._w;
		const qbx = b._x, qby = b._y, qbz = b._z, qbw = b._w;

		this._x = qax * qbw + qaw * qbx + qay * qbz - qaz * qby;
		this._y = qay * qbw + qaw * qby + qaz * qbx - qax * qbz;
		this._z = qaz * qbw + qaw * qbz + qax * qby - qay * qbx;
		this._w = qaw * qbw - qax * qbx - qay * qby - qaz * qbz;

		this._onChangeCallback();

		return this;

	}

	slerp( qb, t ) {

		if ( t === 0 ) return this;
		if ( t === 1 ) return this.copy( qb );

		const x = this._x, y = this._y, z = this._z, w = this._w;

		// http://www.euclideanspace.com/maths/algebra/realNormedAlgebra/quaternions/slerp/

		let cosHalfTheta = w * qb._w + x * qb._x + y * qb._y + z * qb._z;

		if ( cosHalfTheta < 0 ) {

			this._w = - qb._w;
			this._x = - qb._x;
			this._y = - qb._y;
			this._z = - qb._z;

			cosHalfTheta = - cosHalfTheta;

		} else {

			this.copy( qb );

		}

		if ( cosHalfTheta >= 1.0 ) {

			this._w = w;
			this._x = x;
			this._y = y;
			this._z = z;

			return this;

		}

		const sqrSinHalfTheta = 1.0 - cosHalfTheta * cosHalfTheta;

		if ( sqrSinHalfTheta <= Number.EPSILON ) {

			const s = 1 - t;
			this._w = s * w + t * this._w;
			this._x = s * x + t * this._x;
			this._y = s * y + t * this._y;
			this._z = s * z + t * this._z;

			this.normalize(); // normalize calls _onChangeCallback()

			return this;

		}

		const sinHalfTheta = Math.sqrt( sqrSinHalfTheta );
		const halfTheta = Math.atan2( sinHalfTheta, cosHalfTheta );
		const ratioA = Math.sin( ( 1 - t ) * halfTheta ) / sinHalfTheta,
			ratioB = Math.sin( t * halfTheta ) / sinHalfTheta;

		this._w = ( w * ratioA + this._w * ratioB );
		this._x = ( x * ratioA + this._x * ratioB );
		this._y = ( y * ratioA + this._y * ratioB );
		this._z = ( z * ratioA + this._z * ratioB );

		this._onChangeCallback();

		return this;

	}

	slerpQuaternions( qa, qb, t ) {

		return this.copy( qa ).slerp( qb, t );

	}

	random() {

		// sets this quaternion to a uniform random unit quaternnion

		// Ken Shoemake
		// Uniform random rotations
		// D. Kirk, editor, Graphics Gems III, pages 124-132. Academic Press, New York, 1992.

		const theta1 = 2 * Math.PI * Math.random();
		const theta2 = 2 * Math.PI * Math.random();

		const x0 = Math.random();
		const r1 = Math.sqrt( 1 - x0 );
		const r2 = Math.sqrt( x0 );

		return this.set(
			r1 * Math.sin( theta1 ),
			r1 * Math.cos( theta1 ),
			r2 * Math.sin( theta2 ),
			r2 * Math.cos( theta2 ),
		);

	}

	equals( quaternion ) {

		return ( quaternion._x === this._x ) && ( quaternion._y === this._y ) && ( quaternion._z === this._z ) && ( quaternion._w === this._w );

	}

	fromArray( array, offset = 0 ) {

		this._x = array[ offset ];
		this._y = array[ offset + 1 ];
		this._z = array[ offset + 2 ];
		this._w = array[ offset + 3 ];

		this._onChangeCallback();

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this._x;
		array[ offset + 1 ] = this._y;
		array[ offset + 2 ] = this._z;
		array[ offset + 3 ] = this._w;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this._x = attribute.getX( index );
		this._y = attribute.getY( index );
		this._z = attribute.getZ( index );
		this._w = attribute.getW( index );

		this._onChangeCallback();

		return this;

	}

	toJSON() {

		return this.toArray();

	}

	_onChange( callback ) {

		this._onChangeCallback = callback;

		return this;

	}

	_onChangeCallback() {}

	*[ Symbol.iterator ]() {

		yield this._x;
		yield this._y;
		yield this._z;
		yield this._w;

	}

}

class Vector3 {

	constructor( x = 0, y = 0, z = 0 ) {

		Vector3.prototype.isVector3 = true;

		this.x = x;
		this.y = y;
		this.z = z;

	}

	set( x, y, z ) {

		if ( z === undefined ) z = this.z; // sprite.scale.set(x,y)

		this.x = x;
		this.y = y;
		this.z = z;

		return this;

	}

	setScalar( scalar ) {

		this.x = scalar;
		this.y = scalar;
		this.z = scalar;

		return this;

	}

	setX( x ) {

		this.x = x;

		return this;

	}

	setY( y ) {

		this.y = y;

		return this;

	}

	setZ( z ) {

		this.z = z;

		return this;

	}

	setComponent( index, value ) {

		switch ( index ) {

			case 0: this.x = value; break;
			case 1: this.y = value; break;
			case 2: this.z = value; break;
			default: throw new Error( 'index is out of range: ' + index );

		}

		return this;

	}

	getComponent( index ) {

		switch ( index ) {

			case 0: return this.x;
			case 1: return this.y;
			case 2: return this.z;
			default: throw new Error( 'index is out of range: ' + index );

		}

	}

	clone() {

		return new this.constructor( this.x, this.y, this.z );

	}

	copy( v ) {

		this.x = v.x;
		this.y = v.y;
		this.z = v.z;

		return this;

	}

	add( v ) {

		this.x += v.x;
		this.y += v.y;
		this.z += v.z;

		return this;

	}

	addScalar( s ) {

		this.x += s;
		this.y += s;
		this.z += s;

		return this;

	}

	addVectors( a, b ) {

		this.x = a.x + b.x;
		this.y = a.y + b.y;
		this.z = a.z + b.z;

		return this;

	}

	addScaledVector( v, s ) {

		this.x += v.x * s;
		this.y += v.y * s;
		this.z += v.z * s;

		return this;

	}

	sub( v ) {

		this.x -= v.x;
		this.y -= v.y;
		this.z -= v.z;

		return this;

	}

	subScalar( s ) {

		this.x -= s;
		this.y -= s;
		this.z -= s;

		return this;

	}

	subVectors( a, b ) {

		this.x = a.x - b.x;
		this.y = a.y - b.y;
		this.z = a.z - b.z;

		return this;

	}

	multiply( v ) {

		this.x *= v.x;
		this.y *= v.y;
		this.z *= v.z;

		return this;

	}

	multiplyScalar( scalar ) {

		this.x *= scalar;
		this.y *= scalar;
		this.z *= scalar;

		return this;

	}

	multiplyVectors( a, b ) {

		this.x = a.x * b.x;
		this.y = a.y * b.y;
		this.z = a.z * b.z;

		return this;

	}

	applyEuler( euler ) {

		return this.applyQuaternion( _quaternion$4.setFromEuler( euler ) );

	}

	applyAxisAngle( axis, angle ) {

		return this.applyQuaternion( _quaternion$4.setFromAxisAngle( axis, angle ) );

	}

	applyMatrix3( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 3 ] * y + e[ 6 ] * z;
		this.y = e[ 1 ] * x + e[ 4 ] * y + e[ 7 ] * z;
		this.z = e[ 2 ] * x + e[ 5 ] * y + e[ 8 ] * z;

		return this;

	}

	applyNormalMatrix( m ) {

		return this.applyMatrix3( m ).normalize();

	}

	applyMatrix4( m ) {

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		const w = 1 / ( e[ 3 ] * x + e[ 7 ] * y + e[ 11 ] * z + e[ 15 ] );

		this.x = ( e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z + e[ 12 ] ) * w;
		this.y = ( e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z + e[ 13 ] ) * w;
		this.z = ( e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z + e[ 14 ] ) * w;

		return this;

	}

	applyQuaternion( q ) {

		// quaternion q is assumed to have unit length

		const vx = this.x, vy = this.y, vz = this.z;
		const qx = q.x, qy = q.y, qz = q.z, qw = q.w;

		// t = 2 * cross( q.xyz, v );
		const tx = 2 * ( qy * vz - qz * vy );
		const ty = 2 * ( qz * vx - qx * vz );
		const tz = 2 * ( qx * vy - qy * vx );

		// v + q.w * t + cross( q.xyz, t );
		this.x = vx + qw * tx + qy * tz - qz * ty;
		this.y = vy + qw * ty + qz * tx - qx * tz;
		this.z = vz + qw * tz + qx * ty - qy * tx;

		return this;

	}

	project( camera ) {

		return this.applyMatrix4( camera.matrixWorldInverse ).applyMatrix4( camera.projectionMatrix );

	}

	unproject( camera ) {

		return this.applyMatrix4( camera.projectionMatrixInverse ).applyMatrix4( camera.matrixWorld );

	}

	transformDirection( m ) {

		// input: THREE.Matrix4 affine matrix
		// vector interpreted as a direction

		const x = this.x, y = this.y, z = this.z;
		const e = m.elements;

		this.x = e[ 0 ] * x + e[ 4 ] * y + e[ 8 ] * z;
		this.y = e[ 1 ] * x + e[ 5 ] * y + e[ 9 ] * z;
		this.z = e[ 2 ] * x + e[ 6 ] * y + e[ 10 ] * z;

		return this.normalize();

	}

	divide( v ) {

		this.x /= v.x;
		this.y /= v.y;
		this.z /= v.z;

		return this;

	}

	divideScalar( scalar ) {

		return this.multiplyScalar( 1 / scalar );

	}

	min( v ) {

		this.x = Math.min( this.x, v.x );
		this.y = Math.min( this.y, v.y );
		this.z = Math.min( this.z, v.z );

		return this;

	}

	max( v ) {

		this.x = Math.max( this.x, v.x );
		this.y = Math.max( this.y, v.y );
		this.z = Math.max( this.z, v.z );

		return this;

	}

	clamp( min, max ) {

		// assumes min < max, componentwise

		this.x = Math.max( min.x, Math.min( max.x, this.x ) );
		this.y = Math.max( min.y, Math.min( max.y, this.y ) );
		this.z = Math.max( min.z, Math.min( max.z, this.z ) );

		return this;

	}

	clampScalar( minVal, maxVal ) {

		this.x = Math.max( minVal, Math.min( maxVal, this.x ) );
		this.y = Math.max( minVal, Math.min( maxVal, this.y ) );
		this.z = Math.max( minVal, Math.min( maxVal, this.z ) );

		return this;

	}

	clampLength( min, max ) {

		const length = this.length();

		return this.divideScalar( length || 1 ).multiplyScalar( Math.max( min, Math.min( max, length ) ) );

	}

	floor() {

		this.x = Math.floor( this.x );
		this.y = Math.floor( this.y );
		this.z = Math.floor( this.z );

		return this;

	}

	ceil() {

		this.x = Math.ceil( this.x );
		this.y = Math.ceil( this.y );
		this.z = Math.ceil( this.z );

		return this;

	}

	round() {

		this.x = Math.round( this.x );
		this.y = Math.round( this.y );
		this.z = Math.round( this.z );

		return this;

	}

	roundToZero() {

		this.x = Math.trunc( this.x );
		this.y = Math.trunc( this.y );
		this.z = Math.trunc( this.z );

		return this;

	}

	negate() {

		this.x = - this.x;
		this.y = - this.y;
		this.z = - this.z;

		return this;

	}

	dot( v ) {

		return this.x * v.x + this.y * v.y + this.z * v.z;

	}

	// TODO lengthSquared?

	lengthSq() {

		return this.x * this.x + this.y * this.y + this.z * this.z;

	}

	length() {

		return Math.sqrt( this.x * this.x + this.y * this.y + this.z * this.z );

	}

	manhattanLength() {

		return Math.abs( this.x ) + Math.abs( this.y ) + Math.abs( this.z );

	}

	normalize() {

		return this.divideScalar( this.length() || 1 );

	}

	setLength( length ) {

		return this.normalize().multiplyScalar( length );

	}

	lerp( v, alpha ) {

		this.x += ( v.x - this.x ) * alpha;
		this.y += ( v.y - this.y ) * alpha;
		this.z += ( v.z - this.z ) * alpha;

		return this;

	}

	lerpVectors( v1, v2, alpha ) {

		this.x = v1.x + ( v2.x - v1.x ) * alpha;
		this.y = v1.y + ( v2.y - v1.y ) * alpha;
		this.z = v1.z + ( v2.z - v1.z ) * alpha;

		return this;

	}

	cross( v ) {

		return this.crossVectors( this, v );

	}

	crossVectors( a, b ) {

		const ax = a.x, ay = a.y, az = a.z;
		const bx = b.x, by = b.y, bz = b.z;

		this.x = ay * bz - az * by;
		this.y = az * bx - ax * bz;
		this.z = ax * by - ay * bx;

		return this;

	}

	projectOnVector( v ) {

		const denominator = v.lengthSq();

		if ( denominator === 0 ) return this.set( 0, 0, 0 );

		const scalar = v.dot( this ) / denominator;

		return this.copy( v ).multiplyScalar( scalar );

	}

	projectOnPlane( planeNormal ) {

		_vector$c.copy( this ).projectOnVector( planeNormal );

		return this.sub( _vector$c );

	}

	reflect( normal ) {

		// reflect incident vector off plane orthogonal to normal
		// normal is assumed to have unit length

		return this.sub( _vector$c.copy( normal ).multiplyScalar( 2 * this.dot( normal ) ) );

	}

	angleTo( v ) {

		const denominator = Math.sqrt( this.lengthSq() * v.lengthSq() );

		if ( denominator === 0 ) return Math.PI / 2;

		const theta = this.dot( v ) / denominator;

		// clamp, to handle numerical problems

		return Math.acos( clamp( theta, - 1, 1 ) );

	}

	distanceTo( v ) {

		return Math.sqrt( this.distanceToSquared( v ) );

	}

	distanceToSquared( v ) {

		const dx = this.x - v.x, dy = this.y - v.y, dz = this.z - v.z;

		return dx * dx + dy * dy + dz * dz;

	}

	manhattanDistanceTo( v ) {

		return Math.abs( this.x - v.x ) + Math.abs( this.y - v.y ) + Math.abs( this.z - v.z );

	}

	setFromSpherical( s ) {

		return this.setFromSphericalCoords( s.radius, s.phi, s.theta );

	}

	setFromSphericalCoords( radius, phi, theta ) {

		const sinPhiRadius = Math.sin( phi ) * radius;

		this.x = sinPhiRadius * Math.sin( theta );
		this.y = Math.cos( phi ) * radius;
		this.z = sinPhiRadius * Math.cos( theta );

		return this;

	}

	setFromCylindrical( c ) {

		return this.setFromCylindricalCoords( c.radius, c.theta, c.y );

	}

	setFromCylindricalCoords( radius, theta, y ) {

		this.x = radius * Math.sin( theta );
		this.y = y;
		this.z = radius * Math.cos( theta );

		return this;

	}

	setFromMatrixPosition( m ) {

		const e = m.elements;

		this.x = e[ 12 ];
		this.y = e[ 13 ];
		this.z = e[ 14 ];

		return this;

	}

	setFromMatrixScale( m ) {

		const sx = this.setFromMatrixColumn( m, 0 ).length();
		const sy = this.setFromMatrixColumn( m, 1 ).length();
		const sz = this.setFromMatrixColumn( m, 2 ).length();

		this.x = sx;
		this.y = sy;
		this.z = sz;

		return this;

	}

	setFromMatrixColumn( m, index ) {

		return this.fromArray( m.elements, index * 4 );

	}

	setFromMatrix3Column( m, index ) {

		return this.fromArray( m.elements, index * 3 );

	}

	setFromEuler( e ) {

		this.x = e._x;
		this.y = e._y;
		this.z = e._z;

		return this;

	}

	setFromColor( c ) {

		this.x = c.r;
		this.y = c.g;
		this.z = c.b;

		return this;

	}

	equals( v ) {

		return ( ( v.x === this.x ) && ( v.y === this.y ) && ( v.z === this.z ) );

	}

	fromArray( array, offset = 0 ) {

		this.x = array[ offset ];
		this.y = array[ offset + 1 ];
		this.z = array[ offset + 2 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.x;
		array[ offset + 1 ] = this.y;
		array[ offset + 2 ] = this.z;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.x = attribute.getX( index );
		this.y = attribute.getY( index );
		this.z = attribute.getZ( index );

		return this;

	}

	random() {

		this.x = Math.random();
		this.y = Math.random();
		this.z = Math.random();

		return this;

	}

	randomDirection() {

		// https://mathworld.wolfram.com/SpherePointPicking.html

		const theta = Math.random() * Math.PI * 2;
		const u = Math.random() * 2 - 1;
		const c = Math.sqrt( 1 - u * u );

		this.x = c * Math.cos( theta );
		this.y = u;
		this.z = c * Math.sin( theta );

		return this;

	}

	*[ Symbol.iterator ]() {

		yield this.x;
		yield this.y;
		yield this.z;

	}

}

const _vector$c = /*@__PURE__*/ new Vector3();
const _quaternion$4 = /*@__PURE__*/ new Quaternion();

class Box3 {

	constructor( min = new Vector3( + Infinity, + Infinity, + Infinity ), max = new Vector3( - Infinity, - Infinity, - Infinity ) ) {

		this.isBox3 = true;

		this.min = min;
		this.max = max;

	}

	set( min, max ) {

		this.min.copy( min );
		this.max.copy( max );

		return this;

	}

	setFromArray( array ) {

		this.makeEmpty();

		for ( let i = 0, il = array.length; i < il; i += 3 ) {

			this.expandByPoint( _vector$b.fromArray( array, i ) );

		}

		return this;

	}

	setFromBufferAttribute( attribute ) {

		this.makeEmpty();

		for ( let i = 0, il = attribute.count; i < il; i ++ ) {

			this.expandByPoint( _vector$b.fromBufferAttribute( attribute, i ) );

		}

		return this;

	}

	setFromPoints( points ) {

		this.makeEmpty();

		for ( let i = 0, il = points.length; i < il; i ++ ) {

			this.expandByPoint( points[ i ] );

		}

		return this;

	}

	setFromCenterAndSize( center, size ) {

		const halfSize = _vector$b.copy( size ).multiplyScalar( 0.5 );

		this.min.copy( center ).sub( halfSize );
		this.max.copy( center ).add( halfSize );

		return this;

	}

	setFromObject( object, precise = false ) {

		this.makeEmpty();

		return this.expandByObject( object, precise );

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( box ) {

		this.min.copy( box.min );
		this.max.copy( box.max );

		return this;

	}

	makeEmpty() {

		this.min.x = this.min.y = this.min.z = + Infinity;
		this.max.x = this.max.y = this.max.z = - Infinity;

		return this;

	}

	isEmpty() {

		// this is a more robust check for empty than ( volume <= 0 ) because volume can get positive with two negative axes

		return ( this.max.x < this.min.x ) || ( this.max.y < this.min.y ) || ( this.max.z < this.min.z );

	}

	getCenter( target ) {

		return this.isEmpty() ? target.set( 0, 0, 0 ) : target.addVectors( this.min, this.max ).multiplyScalar( 0.5 );

	}

	getSize( target ) {

		return this.isEmpty() ? target.set( 0, 0, 0 ) : target.subVectors( this.max, this.min );

	}

	expandByPoint( point ) {

		this.min.min( point );
		this.max.max( point );

		return this;

	}

	expandByVector( vector ) {

		this.min.sub( vector );
		this.max.add( vector );

		return this;

	}

	expandByScalar( scalar ) {

		this.min.addScalar( - scalar );
		this.max.addScalar( scalar );

		return this;

	}

	expandByObject( object, precise = false ) {

		// Computes the world-axis-aligned bounding box of an object (including its children),
		// accounting for both the object's, and children's, world transforms

		object.updateWorldMatrix( false, false );

		const geometry = object.geometry;

		if ( geometry !== undefined ) {

			const positionAttribute = geometry.getAttribute( 'position' );

			// precise AABB computation based on vertex data requires at least a position attribute.
			// instancing isn't supported so far and uses the normal (conservative) code path.

			if ( precise === true && positionAttribute !== undefined && object.isInstancedMesh !== true ) {

				for ( let i = 0, l = positionAttribute.count; i < l; i ++ ) {

					if ( object.isMesh === true ) {

						object.getVertexPosition( i, _vector$b );

					} else {

						_vector$b.fromBufferAttribute( positionAttribute, i );

					}

					_vector$b.applyMatrix4( object.matrixWorld );
					this.expandByPoint( _vector$b );

				}

			} else {

				if ( object.boundingBox !== undefined ) {

					// object-level bounding box

					if ( object.boundingBox === null ) {

						object.computeBoundingBox();

					}

					_box$4.copy( object.boundingBox );


				} else {

					// geometry-level bounding box

					if ( geometry.boundingBox === null ) {

						geometry.computeBoundingBox();

					}

					_box$4.copy( geometry.boundingBox );

				}

				_box$4.applyMatrix4( object.matrixWorld );

				this.union( _box$4 );

			}

		}

		const children = object.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			this.expandByObject( children[ i ], precise );

		}

		return this;

	}

	containsPoint( point ) {

		return point.x < this.min.x || point.x > this.max.x ||
			point.y < this.min.y || point.y > this.max.y ||
			point.z < this.min.z || point.z > this.max.z ? false : true;

	}

	containsBox( box ) {

		return this.min.x <= box.min.x && box.max.x <= this.max.x &&
			this.min.y <= box.min.y && box.max.y <= this.max.y &&
			this.min.z <= box.min.z && box.max.z <= this.max.z;

	}

	getParameter( point, target ) {

		// This can potentially have a divide by zero if the box
		// has a size dimension of 0.

		return target.set(
			( point.x - this.min.x ) / ( this.max.x - this.min.x ),
			( point.y - this.min.y ) / ( this.max.y - this.min.y ),
			( point.z - this.min.z ) / ( this.max.z - this.min.z )
		);

	}

	intersectsBox( box ) {

		// using 6 splitting planes to rule out intersections.
		return box.max.x < this.min.x || box.min.x > this.max.x ||
			box.max.y < this.min.y || box.min.y > this.max.y ||
			box.max.z < this.min.z || box.min.z > this.max.z ? false : true;

	}

	intersectsSphere( sphere ) {

		// Find the point on the AABB closest to the sphere center.
		this.clampPoint( sphere.center, _vector$b );

		// If that point is inside the sphere, the AABB and sphere intersect.
		return _vector$b.distanceToSquared( sphere.center ) <= ( sphere.radius * sphere.radius );

	}

	intersectsPlane( plane ) {

		// We compute the minimum and maximum dot product values. If those values
		// are on the same side (back or front) of the plane, then there is no intersection.

		let min, max;

		if ( plane.normal.x > 0 ) {

			min = plane.normal.x * this.min.x;
			max = plane.normal.x * this.max.x;

		} else {

			min = plane.normal.x * this.max.x;
			max = plane.normal.x * this.min.x;

		}

		if ( plane.normal.y > 0 ) {

			min += plane.normal.y * this.min.y;
			max += plane.normal.y * this.max.y;

		} else {

			min += plane.normal.y * this.max.y;
			max += plane.normal.y * this.min.y;

		}

		if ( plane.normal.z > 0 ) {

			min += plane.normal.z * this.min.z;
			max += plane.normal.z * this.max.z;

		} else {

			min += plane.normal.z * this.max.z;
			max += plane.normal.z * this.min.z;

		}

		return ( min <= - plane.constant && max >= - plane.constant );

	}

	intersectsTriangle( triangle ) {

		if ( this.isEmpty() ) {

			return false;

		}

		// compute box center and extents
		this.getCenter( _center );
		_extents.subVectors( this.max, _center );

		// translate triangle to aabb origin
		_v0$2.subVectors( triangle.a, _center );
		_v1$7.subVectors( triangle.b, _center );
		_v2$4.subVectors( triangle.c, _center );

		// compute edge vectors for triangle
		_f0.subVectors( _v1$7, _v0$2 );
		_f1.subVectors( _v2$4, _v1$7 );
		_f2.subVectors( _v0$2, _v2$4 );

		// test against axes that are given by cross product combinations of the edges of the triangle and the edges of the aabb
		// make an axis testing of each of the 3 sides of the aabb against each of the 3 sides of the triangle = 9 axis of separation
		// axis_ij = u_i x f_j (u0, u1, u2 = face normals of aabb = x,y,z axes vectors since aabb is axis aligned)
		let axes = [
			0, - _f0.z, _f0.y, 0, - _f1.z, _f1.y, 0, - _f2.z, _f2.y,
			_f0.z, 0, - _f0.x, _f1.z, 0, - _f1.x, _f2.z, 0, - _f2.x,
			- _f0.y, _f0.x, 0, - _f1.y, _f1.x, 0, - _f2.y, _f2.x, 0
		];
		if ( ! satForAxes( axes, _v0$2, _v1$7, _v2$4, _extents ) ) {

			return false;

		}

		// test 3 face normals from the aabb
		axes = [ 1, 0, 0, 0, 1, 0, 0, 0, 1 ];
		if ( ! satForAxes( axes, _v0$2, _v1$7, _v2$4, _extents ) ) {

			return false;

		}

		// finally testing the face normal of the triangle
		// use already existing triangle edge vectors here
		_triangleNormal.crossVectors( _f0, _f1 );
		axes = [ _triangleNormal.x, _triangleNormal.y, _triangleNormal.z ];

		return satForAxes( axes, _v0$2, _v1$7, _v2$4, _extents );

	}

	clampPoint( point, target ) {

		return target.copy( point ).clamp( this.min, this.max );

	}

	distanceToPoint( point ) {

		return this.clampPoint( point, _vector$b ).distanceTo( point );

	}

	getBoundingSphere( target ) {

		if ( this.isEmpty() ) {

			target.makeEmpty();

		} else {

			this.getCenter( target.center );

			target.radius = this.getSize( _vector$b ).length() * 0.5;

		}

		return target;

	}

	intersect( box ) {

		this.min.max( box.min );
		this.max.min( box.max );

		// ensure that if there is no overlap, the result is fully empty, not slightly empty with non-inf/+inf values that will cause subsequence intersects to erroneously return valid values.
		if ( this.isEmpty() ) this.makeEmpty();

		return this;

	}

	union( box ) {

		this.min.min( box.min );
		this.max.max( box.max );

		return this;

	}

	applyMatrix4( matrix ) {

		// transform of empty box is an empty box.
		if ( this.isEmpty() ) return this;

		// NOTE: I am using a binary pattern to specify all 2^3 combinations below
		_points[ 0 ].set( this.min.x, this.min.y, this.min.z ).applyMatrix4( matrix ); // 000
		_points[ 1 ].set( this.min.x, this.min.y, this.max.z ).applyMatrix4( matrix ); // 001
		_points[ 2 ].set( this.min.x, this.max.y, this.min.z ).applyMatrix4( matrix ); // 010
		_points[ 3 ].set( this.min.x, this.max.y, this.max.z ).applyMatrix4( matrix ); // 011
		_points[ 4 ].set( this.max.x, this.min.y, this.min.z ).applyMatrix4( matrix ); // 100
		_points[ 5 ].set( this.max.x, this.min.y, this.max.z ).applyMatrix4( matrix ); // 101
		_points[ 6 ].set( this.max.x, this.max.y, this.min.z ).applyMatrix4( matrix ); // 110
		_points[ 7 ].set( this.max.x, this.max.y, this.max.z ).applyMatrix4( matrix ); // 111

		this.setFromPoints( _points );

		return this;

	}

	translate( offset ) {

		this.min.add( offset );
		this.max.add( offset );

		return this;

	}

	equals( box ) {

		return box.min.equals( this.min ) && box.max.equals( this.max );

	}

}

const _points = [
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3(),
	/*@__PURE__*/ new Vector3()
];

const _vector$b = /*@__PURE__*/ new Vector3();

const _box$4 = /*@__PURE__*/ new Box3();

// triangle centered vertices

const _v0$2 = /*@__PURE__*/ new Vector3();
const _v1$7 = /*@__PURE__*/ new Vector3();
const _v2$4 = /*@__PURE__*/ new Vector3();

// triangle edge vectors

const _f0 = /*@__PURE__*/ new Vector3();
const _f1 = /*@__PURE__*/ new Vector3();
const _f2 = /*@__PURE__*/ new Vector3();

const _center = /*@__PURE__*/ new Vector3();
const _extents = /*@__PURE__*/ new Vector3();
const _triangleNormal = /*@__PURE__*/ new Vector3();
const _testAxis = /*@__PURE__*/ new Vector3();

function satForAxes( axes, v0, v1, v2, extents ) {

	for ( let i = 0, j = axes.length - 3; i <= j; i += 3 ) {

		_testAxis.fromArray( axes, i );
		// project the aabb onto the separating axis
		const r = extents.x * Math.abs( _testAxis.x ) + extents.y * Math.abs( _testAxis.y ) + extents.z * Math.abs( _testAxis.z );
		// project all 3 vertices of the triangle onto the separating axis
		const p0 = v0.dot( _testAxis );
		const p1 = v1.dot( _testAxis );
		const p2 = v2.dot( _testAxis );
		// actual test, basically see if either of the most extreme of the triangle points intersects r
		if ( Math.max( - Math.max( p0, p1, p2 ), Math.min( p0, p1, p2 ) ) > r ) {

			// points of the projected triangle are outside the projected half-length of the aabb
			// the axis is separating and we can exit
			return false;

		}

	}

	return true;

}

const _box$3 = /*@__PURE__*/ new Box3();
const _v1$6 = /*@__PURE__*/ new Vector3();
const _v2$3 = /*@__PURE__*/ new Vector3();

let Sphere$1=class Sphere {

	constructor( center = new Vector3(), radius = - 1 ) {

		this.isSphere = true;

		this.center = center;
		this.radius = radius;

	}

	set( center, radius ) {

		this.center.copy( center );
		this.radius = radius;

		return this;

	}

	setFromPoints( points, optionalCenter ) {

		const center = this.center;

		if ( optionalCenter !== undefined ) {

			center.copy( optionalCenter );

		} else {

			_box$3.setFromPoints( points ).getCenter( center );

		}

		let maxRadiusSq = 0;

		for ( let i = 0, il = points.length; i < il; i ++ ) {

			maxRadiusSq = Math.max( maxRadiusSq, center.distanceToSquared( points[ i ] ) );

		}

		this.radius = Math.sqrt( maxRadiusSq );

		return this;

	}

	copy( sphere ) {

		this.center.copy( sphere.center );
		this.radius = sphere.radius;

		return this;

	}

	isEmpty() {

		return ( this.radius < 0 );

	}

	makeEmpty() {

		this.center.set( 0, 0, 0 );
		this.radius = - 1;

		return this;

	}

	containsPoint( point ) {

		return ( point.distanceToSquared( this.center ) <= ( this.radius * this.radius ) );

	}

	distanceToPoint( point ) {

		return ( point.distanceTo( this.center ) - this.radius );

	}

	intersectsSphere( sphere ) {

		const radiusSum = this.radius + sphere.radius;

		return sphere.center.distanceToSquared( this.center ) <= ( radiusSum * radiusSum );

	}

	intersectsBox( box ) {

		return box.intersectsSphere( this );

	}

	intersectsPlane( plane ) {

		return Math.abs( plane.distanceToPoint( this.center ) ) <= this.radius;

	}

	clampPoint( point, target ) {

		const deltaLengthSq = this.center.distanceToSquared( point );

		target.copy( point );

		if ( deltaLengthSq > ( this.radius * this.radius ) ) {

			target.sub( this.center ).normalize();
			target.multiplyScalar( this.radius ).add( this.center );

		}

		return target;

	}

	getBoundingBox( target ) {

		if ( this.isEmpty() ) {

			// Empty sphere produces empty bounding box
			target.makeEmpty();
			return target;

		}

		target.set( this.center, this.center );
		target.expandByScalar( this.radius );

		return target;

	}

	applyMatrix4( matrix ) {

		this.center.applyMatrix4( matrix );
		this.radius = this.radius * matrix.getMaxScaleOnAxis();

		return this;

	}

	translate( offset ) {

		this.center.add( offset );

		return this;

	}

	expandByPoint( point ) {

		if ( this.isEmpty() ) {

			this.center.copy( point );

			this.radius = 0;

			return this;

		}

		_v1$6.subVectors( point, this.center );

		const lengthSq = _v1$6.lengthSq();

		if ( lengthSq > ( this.radius * this.radius ) ) {

			// calculate the minimal sphere

			const length = Math.sqrt( lengthSq );

			const delta = ( length - this.radius ) * 0.5;

			this.center.addScaledVector( _v1$6, delta / length );

			this.radius += delta;

		}

		return this;

	}

	union( sphere ) {

		if ( sphere.isEmpty() ) {

			return this;

		}

		if ( this.isEmpty() ) {

			this.copy( sphere );

			return this;

		}

		if ( this.center.equals( sphere.center ) === true ) {

			 this.radius = Math.max( this.radius, sphere.radius );

		} else {

			_v2$3.subVectors( sphere.center, this.center ).setLength( sphere.radius );

			this.expandByPoint( _v1$6.copy( sphere.center ).add( _v2$3 ) );

			this.expandByPoint( _v1$6.copy( sphere.center ).sub( _v2$3 ) );

		}

		return this;

	}

	equals( sphere ) {

		return sphere.center.equals( this.center ) && ( sphere.radius === this.radius );

	}

	clone() {

		return new this.constructor().copy( this );

	}

};

const _vector$a = /*@__PURE__*/ new Vector3();
const _segCenter = /*@__PURE__*/ new Vector3();
const _segDir = /*@__PURE__*/ new Vector3();
const _diff = /*@__PURE__*/ new Vector3();

const _edge1 = /*@__PURE__*/ new Vector3();
const _edge2 = /*@__PURE__*/ new Vector3();
const _normal$1 = /*@__PURE__*/ new Vector3();

let Ray$1=class Ray {

	constructor( origin = new Vector3(), direction = new Vector3( 0, 0, - 1 ) ) {

		this.origin = origin;
		this.direction = direction;

	}

	set( origin, direction ) {

		this.origin.copy( origin );
		this.direction.copy( direction );

		return this;

	}

	copy( ray ) {

		this.origin.copy( ray.origin );
		this.direction.copy( ray.direction );

		return this;

	}

	at( t, target ) {

		return target.copy( this.origin ).addScaledVector( this.direction, t );

	}

	lookAt( v ) {

		this.direction.copy( v ).sub( this.origin ).normalize();

		return this;

	}

	recast( t ) {

		this.origin.copy( this.at( t, _vector$a ) );

		return this;

	}

	closestPointToPoint( point, target ) {

		target.subVectors( point, this.origin );

		const directionDistance = target.dot( this.direction );

		if ( directionDistance < 0 ) {

			return target.copy( this.origin );

		}

		return target.copy( this.origin ).addScaledVector( this.direction, directionDistance );

	}

	distanceToPoint( point ) {

		return Math.sqrt( this.distanceSqToPoint( point ) );

	}

	distanceSqToPoint( point ) {

		const directionDistance = _vector$a.subVectors( point, this.origin ).dot( this.direction );

		// point behind the ray

		if ( directionDistance < 0 ) {

			return this.origin.distanceToSquared( point );

		}

		_vector$a.copy( this.origin ).addScaledVector( this.direction, directionDistance );

		return _vector$a.distanceToSquared( point );

	}

	distanceSqToSegment( v0, v1, optionalPointOnRay, optionalPointOnSegment ) {

		// from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteDistRaySegment.h
		// It returns the min distance between the ray and the segment
		// defined by v0 and v1
		// It can also set two optional targets :
		// - The closest point on the ray
		// - The closest point on the segment

		_segCenter.copy( v0 ).add( v1 ).multiplyScalar( 0.5 );
		_segDir.copy( v1 ).sub( v0 ).normalize();
		_diff.copy( this.origin ).sub( _segCenter );

		const segExtent = v0.distanceTo( v1 ) * 0.5;
		const a01 = - this.direction.dot( _segDir );
		const b0 = _diff.dot( this.direction );
		const b1 = - _diff.dot( _segDir );
		const c = _diff.lengthSq();
		const det = Math.abs( 1 - a01 * a01 );
		let s0, s1, sqrDist, extDet;

		if ( det > 0 ) {

			// The ray and segment are not parallel.

			s0 = a01 * b1 - b0;
			s1 = a01 * b0 - b1;
			extDet = segExtent * det;

			if ( s0 >= 0 ) {

				if ( s1 >= - extDet ) {

					if ( s1 <= extDet ) {

						// region 0
						// Minimum at interior points of ray and segment.

						const invDet = 1 / det;
						s0 *= invDet;
						s1 *= invDet;
						sqrDist = s0 * ( s0 + a01 * s1 + 2 * b0 ) + s1 * ( a01 * s0 + s1 + 2 * b1 ) + c;

					} else {

						// region 1

						s1 = segExtent;
						s0 = Math.max( 0, - ( a01 * s1 + b0 ) );
						sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

					}

				} else {

					// region 5

					s1 = - segExtent;
					s0 = Math.max( 0, - ( a01 * s1 + b0 ) );
					sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

				}

			} else {

				if ( s1 <= - extDet ) {

					// region 4

					s0 = Math.max( 0, - ( - a01 * segExtent + b0 ) );
					s1 = ( s0 > 0 ) ? - segExtent : Math.min( Math.max( - segExtent, - b1 ), segExtent );
					sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

				} else if ( s1 <= extDet ) {

					// region 3

					s0 = 0;
					s1 = Math.min( Math.max( - segExtent, - b1 ), segExtent );
					sqrDist = s1 * ( s1 + 2 * b1 ) + c;

				} else {

					// region 2

					s0 = Math.max( 0, - ( a01 * segExtent + b0 ) );
					s1 = ( s0 > 0 ) ? segExtent : Math.min( Math.max( - segExtent, - b1 ), segExtent );
					sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

				}

			}

		} else {

			// Ray and segment are parallel.

			s1 = ( a01 > 0 ) ? - segExtent : segExtent;
			s0 = Math.max( 0, - ( a01 * s1 + b0 ) );
			sqrDist = - s0 * s0 + s1 * ( s1 + 2 * b1 ) + c;

		}

		if ( optionalPointOnRay ) {

			optionalPointOnRay.copy( this.origin ).addScaledVector( this.direction, s0 );

		}

		if ( optionalPointOnSegment ) {

			optionalPointOnSegment.copy( _segCenter ).addScaledVector( _segDir, s1 );

		}

		return sqrDist;

	}

	intersectSphere( sphere, target ) {

		_vector$a.subVectors( sphere.center, this.origin );
		const tca = _vector$a.dot( this.direction );
		const d2 = _vector$a.dot( _vector$a ) - tca * tca;
		const radius2 = sphere.radius * sphere.radius;

		if ( d2 > radius2 ) return null;

		const thc = Math.sqrt( radius2 - d2 );

		// t0 = first intersect point - entrance on front of sphere
		const t0 = tca - thc;

		// t1 = second intersect point - exit point on back of sphere
		const t1 = tca + thc;

		// test to see if t1 is behind the ray - if so, return null
		if ( t1 < 0 ) return null;

		// test to see if t0 is behind the ray:
		// if it is, the ray is inside the sphere, so return the second exit point scaled by t1,
		// in order to always return an intersect point that is in front of the ray.
		if ( t0 < 0 ) return this.at( t1, target );

		// else t0 is in front of the ray, so return the first collision point scaled by t0
		return this.at( t0, target );

	}

	intersectsSphere( sphere ) {

		return this.distanceSqToPoint( sphere.center ) <= ( sphere.radius * sphere.radius );

	}

	distanceToPlane( plane ) {

		const denominator = plane.normal.dot( this.direction );

		if ( denominator === 0 ) {

			// line is coplanar, return origin
			if ( plane.distanceToPoint( this.origin ) === 0 ) {

				return 0;

			}

			// Null is preferable to undefined since undefined means.... it is undefined

			return null;

		}

		const t = - ( this.origin.dot( plane.normal ) + plane.constant ) / denominator;

		// Return if the ray never intersects the plane

		return t >= 0 ? t : null;

	}

	intersectPlane( plane, target ) {

		const t = this.distanceToPlane( plane );

		if ( t === null ) {

			return null;

		}

		return this.at( t, target );

	}

	intersectsPlane( plane ) {

		// check if the ray lies on the plane first

		const distToPoint = plane.distanceToPoint( this.origin );

		if ( distToPoint === 0 ) {

			return true;

		}

		const denominator = plane.normal.dot( this.direction );

		if ( denominator * distToPoint < 0 ) {

			return true;

		}

		// ray origin is behind the plane (and is pointing behind it)

		return false;

	}

	intersectBox( box, target ) {

		let tmin, tmax, tymin, tymax, tzmin, tzmax;

		const invdirx = 1 / this.direction.x,
			invdiry = 1 / this.direction.y,
			invdirz = 1 / this.direction.z;

		const origin = this.origin;

		if ( invdirx >= 0 ) {

			tmin = ( box.min.x - origin.x ) * invdirx;
			tmax = ( box.max.x - origin.x ) * invdirx;

		} else {

			tmin = ( box.max.x - origin.x ) * invdirx;
			tmax = ( box.min.x - origin.x ) * invdirx;

		}

		if ( invdiry >= 0 ) {

			tymin = ( box.min.y - origin.y ) * invdiry;
			tymax = ( box.max.y - origin.y ) * invdiry;

		} else {

			tymin = ( box.max.y - origin.y ) * invdiry;
			tymax = ( box.min.y - origin.y ) * invdiry;

		}

		if ( ( tmin > tymax ) || ( tymin > tmax ) ) return null;

		if ( tymin > tmin || isNaN( tmin ) ) tmin = tymin;

		if ( tymax < tmax || isNaN( tmax ) ) tmax = tymax;

		if ( invdirz >= 0 ) {

			tzmin = ( box.min.z - origin.z ) * invdirz;
			tzmax = ( box.max.z - origin.z ) * invdirz;

		} else {

			tzmin = ( box.max.z - origin.z ) * invdirz;
			tzmax = ( box.min.z - origin.z ) * invdirz;

		}

		if ( ( tmin > tzmax ) || ( tzmin > tmax ) ) return null;

		if ( tzmin > tmin || tmin !== tmin ) tmin = tzmin;

		if ( tzmax < tmax || tmax !== tmax ) tmax = tzmax;

		//return point closest to the ray (positive side)

		if ( tmax < 0 ) return null;

		return this.at( tmin >= 0 ? tmin : tmax, target );

	}

	intersectsBox( box ) {

		return this.intersectBox( box, _vector$a ) !== null;

	}

	intersectTriangle( a, b, c, backfaceCulling, target ) {

		// Compute the offset origin, edges, and normal.

		// from https://github.com/pmjoniak/GeometricTools/blob/master/GTEngine/Include/Mathematics/GteIntrRay3Triangle3.h

		_edge1.subVectors( b, a );
		_edge2.subVectors( c, a );
		_normal$1.crossVectors( _edge1, _edge2 );

		// Solve Q + t*D = b1*E1 + b2*E2 (Q = kDiff, D = ray direction,
		// E1 = kEdge1, E2 = kEdge2, N = Cross(E1,E2)) by
		//   |Dot(D,N)|*b1 = sign(Dot(D,N))*Dot(D,Cross(Q,E2))
		//   |Dot(D,N)|*b2 = sign(Dot(D,N))*Dot(D,Cross(E1,Q))
		//   |Dot(D,N)|*t = -sign(Dot(D,N))*Dot(Q,N)
		let DdN = this.direction.dot( _normal$1 );
		let sign;

		if ( DdN > 0 ) {

			if ( backfaceCulling ) return null;
			sign = 1;

		} else if ( DdN < 0 ) {

			sign = - 1;
			DdN = - DdN;

		} else {

			return null;

		}

		_diff.subVectors( this.origin, a );
		const DdQxE2 = sign * this.direction.dot( _edge2.crossVectors( _diff, _edge2 ) );

		// b1 < 0, no intersection
		if ( DdQxE2 < 0 ) {

			return null;

		}

		const DdE1xQ = sign * this.direction.dot( _edge1.cross( _diff ) );

		// b2 < 0, no intersection
		if ( DdE1xQ < 0 ) {

			return null;

		}

		// b1+b2 > 1, no intersection
		if ( DdQxE2 + DdE1xQ > DdN ) {

			return null;

		}

		// Line intersects triangle, check if ray does.
		const QdN = - sign * _diff.dot( _normal$1 );

		// t < 0, no intersection
		if ( QdN < 0 ) {

			return null;

		}

		// Ray intersects triangle.
		return this.at( QdN / DdN, target );

	}

	applyMatrix4( matrix4 ) {

		this.origin.applyMatrix4( matrix4 );
		this.direction.transformDirection( matrix4 );

		return this;

	}

	equals( ray ) {

		return ray.origin.equals( this.origin ) && ray.direction.equals( this.direction );

	}

	clone() {

		return new this.constructor().copy( this );

	}

};

class Matrix4 {

	constructor( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		Matrix4.prototype.isMatrix4 = true;

		this.elements = [

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		];

		if ( n11 !== undefined ) {

			this.set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 );

		}

	}

	set( n11, n12, n13, n14, n21, n22, n23, n24, n31, n32, n33, n34, n41, n42, n43, n44 ) {

		const te = this.elements;

		te[ 0 ] = n11; te[ 4 ] = n12; te[ 8 ] = n13; te[ 12 ] = n14;
		te[ 1 ] = n21; te[ 5 ] = n22; te[ 9 ] = n23; te[ 13 ] = n24;
		te[ 2 ] = n31; te[ 6 ] = n32; te[ 10 ] = n33; te[ 14 ] = n34;
		te[ 3 ] = n41; te[ 7 ] = n42; te[ 11 ] = n43; te[ 15 ] = n44;

		return this;

	}

	identity() {

		this.set(

			1, 0, 0, 0,
			0, 1, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	clone() {

		return new Matrix4().fromArray( this.elements );

	}

	copy( m ) {

		const te = this.elements;
		const me = m.elements;

		te[ 0 ] = me[ 0 ]; te[ 1 ] = me[ 1 ]; te[ 2 ] = me[ 2 ]; te[ 3 ] = me[ 3 ];
		te[ 4 ] = me[ 4 ]; te[ 5 ] = me[ 5 ]; te[ 6 ] = me[ 6 ]; te[ 7 ] = me[ 7 ];
		te[ 8 ] = me[ 8 ]; te[ 9 ] = me[ 9 ]; te[ 10 ] = me[ 10 ]; te[ 11 ] = me[ 11 ];
		te[ 12 ] = me[ 12 ]; te[ 13 ] = me[ 13 ]; te[ 14 ] = me[ 14 ]; te[ 15 ] = me[ 15 ];

		return this;

	}

	copyPosition( m ) {

		const te = this.elements, me = m.elements;

		te[ 12 ] = me[ 12 ];
		te[ 13 ] = me[ 13 ];
		te[ 14 ] = me[ 14 ];

		return this;

	}

	setFromMatrix3( m ) {

		const me = m.elements;

		this.set(

			me[ 0 ], me[ 3 ], me[ 6 ], 0,
			me[ 1 ], me[ 4 ], me[ 7 ], 0,
			me[ 2 ], me[ 5 ], me[ 8 ], 0,
			0, 0, 0, 1

		);

		return this;

	}

	extractBasis( xAxis, yAxis, zAxis ) {

		xAxis.setFromMatrixColumn( this, 0 );
		yAxis.setFromMatrixColumn( this, 1 );
		zAxis.setFromMatrixColumn( this, 2 );

		return this;

	}

	makeBasis( xAxis, yAxis, zAxis ) {

		this.set(
			xAxis.x, yAxis.x, zAxis.x, 0,
			xAxis.y, yAxis.y, zAxis.y, 0,
			xAxis.z, yAxis.z, zAxis.z, 0,
			0, 0, 0, 1
		);

		return this;

	}

	extractRotation( m ) {

		// this method does not support reflection matrices

		const te = this.elements;
		const me = m.elements;

		const scaleX = 1 / _v1$5.setFromMatrixColumn( m, 0 ).length();
		const scaleY = 1 / _v1$5.setFromMatrixColumn( m, 1 ).length();
		const scaleZ = 1 / _v1$5.setFromMatrixColumn( m, 2 ).length();

		te[ 0 ] = me[ 0 ] * scaleX;
		te[ 1 ] = me[ 1 ] * scaleX;
		te[ 2 ] = me[ 2 ] * scaleX;
		te[ 3 ] = 0;

		te[ 4 ] = me[ 4 ] * scaleY;
		te[ 5 ] = me[ 5 ] * scaleY;
		te[ 6 ] = me[ 6 ] * scaleY;
		te[ 7 ] = 0;

		te[ 8 ] = me[ 8 ] * scaleZ;
		te[ 9 ] = me[ 9 ] * scaleZ;
		te[ 10 ] = me[ 10 ] * scaleZ;
		te[ 11 ] = 0;

		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromEuler( euler ) {

		const te = this.elements;

		const x = euler.x, y = euler.y, z = euler.z;
		const a = Math.cos( x ), b = Math.sin( x );
		const c = Math.cos( y ), d = Math.sin( y );
		const e = Math.cos( z ), f = Math.sin( z );

		if ( euler.order === 'XYZ' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = - c * f;
			te[ 8 ] = d;

			te[ 1 ] = af + be * d;
			te[ 5 ] = ae - bf * d;
			te[ 9 ] = - b * c;

			te[ 2 ] = bf - ae * d;
			te[ 6 ] = be + af * d;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YXZ' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce + df * b;
			te[ 4 ] = de * b - cf;
			te[ 8 ] = a * d;

			te[ 1 ] = a * f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b;

			te[ 2 ] = cf * b - de;
			te[ 6 ] = df + ce * b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZXY' ) {

			const ce = c * e, cf = c * f, de = d * e, df = d * f;

			te[ 0 ] = ce - df * b;
			te[ 4 ] = - a * f;
			te[ 8 ] = de + cf * b;

			te[ 1 ] = cf + de * b;
			te[ 5 ] = a * e;
			te[ 9 ] = df - ce * b;

			te[ 2 ] = - a * d;
			te[ 6 ] = b;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'ZYX' ) {

			const ae = a * e, af = a * f, be = b * e, bf = b * f;

			te[ 0 ] = c * e;
			te[ 4 ] = be * d - af;
			te[ 8 ] = ae * d + bf;

			te[ 1 ] = c * f;
			te[ 5 ] = bf * d + ae;
			te[ 9 ] = af * d - be;

			te[ 2 ] = - d;
			te[ 6 ] = b * c;
			te[ 10 ] = a * c;

		} else if ( euler.order === 'YZX' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = bd - ac * f;
			te[ 8 ] = bc * f + ad;

			te[ 1 ] = f;
			te[ 5 ] = a * e;
			te[ 9 ] = - b * e;

			te[ 2 ] = - d * e;
			te[ 6 ] = ad * f + bc;
			te[ 10 ] = ac - bd * f;

		} else if ( euler.order === 'XZY' ) {

			const ac = a * c, ad = a * d, bc = b * c, bd = b * d;

			te[ 0 ] = c * e;
			te[ 4 ] = - f;
			te[ 8 ] = d * e;

			te[ 1 ] = ac * f + bd;
			te[ 5 ] = a * e;
			te[ 9 ] = ad * f - bc;

			te[ 2 ] = bc * f - ad;
			te[ 6 ] = b * e;
			te[ 10 ] = bd * f + ac;

		}

		// bottom row
		te[ 3 ] = 0;
		te[ 7 ] = 0;
		te[ 11 ] = 0;

		// last column
		te[ 12 ] = 0;
		te[ 13 ] = 0;
		te[ 14 ] = 0;
		te[ 15 ] = 1;

		return this;

	}

	makeRotationFromQuaternion( q ) {

		return this.compose( _zero, q, _one );

	}

	lookAt( eye, target, up ) {

		const te = this.elements;

		_z.subVectors( eye, target );

		if ( _z.lengthSq() === 0 ) {

			// eye and target are in the same position

			_z.z = 1;

		}

		_z.normalize();
		_x.crossVectors( up, _z );

		if ( _x.lengthSq() === 0 ) {

			// up and z are parallel

			if ( Math.abs( up.z ) === 1 ) {

				_z.x += 0.0001;

			} else {

				_z.z += 0.0001;

			}

			_z.normalize();
			_x.crossVectors( up, _z );

		}

		_x.normalize();
		_y.crossVectors( _z, _x );

		te[ 0 ] = _x.x; te[ 4 ] = _y.x; te[ 8 ] = _z.x;
		te[ 1 ] = _x.y; te[ 5 ] = _y.y; te[ 9 ] = _z.y;
		te[ 2 ] = _x.z; te[ 6 ] = _y.z; te[ 10 ] = _z.z;

		return this;

	}

	multiply( m ) {

		return this.multiplyMatrices( this, m );

	}

	premultiply( m ) {

		return this.multiplyMatrices( m, this );

	}

	multiplyMatrices( a, b ) {

		const ae = a.elements;
		const be = b.elements;
		const te = this.elements;

		const a11 = ae[ 0 ], a12 = ae[ 4 ], a13 = ae[ 8 ], a14 = ae[ 12 ];
		const a21 = ae[ 1 ], a22 = ae[ 5 ], a23 = ae[ 9 ], a24 = ae[ 13 ];
		const a31 = ae[ 2 ], a32 = ae[ 6 ], a33 = ae[ 10 ], a34 = ae[ 14 ];
		const a41 = ae[ 3 ], a42 = ae[ 7 ], a43 = ae[ 11 ], a44 = ae[ 15 ];

		const b11 = be[ 0 ], b12 = be[ 4 ], b13 = be[ 8 ], b14 = be[ 12 ];
		const b21 = be[ 1 ], b22 = be[ 5 ], b23 = be[ 9 ], b24 = be[ 13 ];
		const b31 = be[ 2 ], b32 = be[ 6 ], b33 = be[ 10 ], b34 = be[ 14 ];
		const b41 = be[ 3 ], b42 = be[ 7 ], b43 = be[ 11 ], b44 = be[ 15 ];

		te[ 0 ] = a11 * b11 + a12 * b21 + a13 * b31 + a14 * b41;
		te[ 4 ] = a11 * b12 + a12 * b22 + a13 * b32 + a14 * b42;
		te[ 8 ] = a11 * b13 + a12 * b23 + a13 * b33 + a14 * b43;
		te[ 12 ] = a11 * b14 + a12 * b24 + a13 * b34 + a14 * b44;

		te[ 1 ] = a21 * b11 + a22 * b21 + a23 * b31 + a24 * b41;
		te[ 5 ] = a21 * b12 + a22 * b22 + a23 * b32 + a24 * b42;
		te[ 9 ] = a21 * b13 + a22 * b23 + a23 * b33 + a24 * b43;
		te[ 13 ] = a21 * b14 + a22 * b24 + a23 * b34 + a24 * b44;

		te[ 2 ] = a31 * b11 + a32 * b21 + a33 * b31 + a34 * b41;
		te[ 6 ] = a31 * b12 + a32 * b22 + a33 * b32 + a34 * b42;
		te[ 10 ] = a31 * b13 + a32 * b23 + a33 * b33 + a34 * b43;
		te[ 14 ] = a31 * b14 + a32 * b24 + a33 * b34 + a34 * b44;

		te[ 3 ] = a41 * b11 + a42 * b21 + a43 * b31 + a44 * b41;
		te[ 7 ] = a41 * b12 + a42 * b22 + a43 * b32 + a44 * b42;
		te[ 11 ] = a41 * b13 + a42 * b23 + a43 * b33 + a44 * b43;
		te[ 15 ] = a41 * b14 + a42 * b24 + a43 * b34 + a44 * b44;

		return this;

	}

	multiplyScalar( s ) {

		const te = this.elements;

		te[ 0 ] *= s; te[ 4 ] *= s; te[ 8 ] *= s; te[ 12 ] *= s;
		te[ 1 ] *= s; te[ 5 ] *= s; te[ 9 ] *= s; te[ 13 ] *= s;
		te[ 2 ] *= s; te[ 6 ] *= s; te[ 10 ] *= s; te[ 14 ] *= s;
		te[ 3 ] *= s; te[ 7 ] *= s; te[ 11 ] *= s; te[ 15 ] *= s;

		return this;

	}

	determinant() {

		const te = this.elements;

		const n11 = te[ 0 ], n12 = te[ 4 ], n13 = te[ 8 ], n14 = te[ 12 ];
		const n21 = te[ 1 ], n22 = te[ 5 ], n23 = te[ 9 ], n24 = te[ 13 ];
		const n31 = te[ 2 ], n32 = te[ 6 ], n33 = te[ 10 ], n34 = te[ 14 ];
		const n41 = te[ 3 ], n42 = te[ 7 ], n43 = te[ 11 ], n44 = te[ 15 ];

		//TODO: make this more efficient
		//( based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm )

		return (
			n41 * (
				+ n14 * n23 * n32
				 - n13 * n24 * n32
				 - n14 * n22 * n33
				 + n12 * n24 * n33
				 + n13 * n22 * n34
				 - n12 * n23 * n34
			) +
			n42 * (
				+ n11 * n23 * n34
				 - n11 * n24 * n33
				 + n14 * n21 * n33
				 - n13 * n21 * n34
				 + n13 * n24 * n31
				 - n14 * n23 * n31
			) +
			n43 * (
				+ n11 * n24 * n32
				 - n11 * n22 * n34
				 - n14 * n21 * n32
				 + n12 * n21 * n34
				 + n14 * n22 * n31
				 - n12 * n24 * n31
			) +
			n44 * (
				- n13 * n22 * n31
				 - n11 * n23 * n32
				 + n11 * n22 * n33
				 + n13 * n21 * n32
				 - n12 * n21 * n33
				 + n12 * n23 * n31
			)

		);

	}

	transpose() {

		const te = this.elements;
		let tmp;

		tmp = te[ 1 ]; te[ 1 ] = te[ 4 ]; te[ 4 ] = tmp;
		tmp = te[ 2 ]; te[ 2 ] = te[ 8 ]; te[ 8 ] = tmp;
		tmp = te[ 6 ]; te[ 6 ] = te[ 9 ]; te[ 9 ] = tmp;

		tmp = te[ 3 ]; te[ 3 ] = te[ 12 ]; te[ 12 ] = tmp;
		tmp = te[ 7 ]; te[ 7 ] = te[ 13 ]; te[ 13 ] = tmp;
		tmp = te[ 11 ]; te[ 11 ] = te[ 14 ]; te[ 14 ] = tmp;

		return this;

	}

	setPosition( x, y, z ) {

		const te = this.elements;

		if ( x.isVector3 ) {

			te[ 12 ] = x.x;
			te[ 13 ] = x.y;
			te[ 14 ] = x.z;

		} else {

			te[ 12 ] = x;
			te[ 13 ] = y;
			te[ 14 ] = z;

		}

		return this;

	}

	invert() {

		// based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
		const te = this.elements,

			n11 = te[ 0 ], n21 = te[ 1 ], n31 = te[ 2 ], n41 = te[ 3 ],
			n12 = te[ 4 ], n22 = te[ 5 ], n32 = te[ 6 ], n42 = te[ 7 ],
			n13 = te[ 8 ], n23 = te[ 9 ], n33 = te[ 10 ], n43 = te[ 11 ],
			n14 = te[ 12 ], n24 = te[ 13 ], n34 = te[ 14 ], n44 = te[ 15 ],

			t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44,
			t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44,
			t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44,
			t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;

		const det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;

		if ( det === 0 ) return this.set( 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0 );

		const detInv = 1 / det;

		te[ 0 ] = t11 * detInv;
		te[ 1 ] = ( n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44 ) * detInv;
		te[ 2 ] = ( n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44 ) * detInv;
		te[ 3 ] = ( n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43 ) * detInv;

		te[ 4 ] = t12 * detInv;
		te[ 5 ] = ( n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44 ) * detInv;
		te[ 6 ] = ( n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44 ) * detInv;
		te[ 7 ] = ( n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43 ) * detInv;

		te[ 8 ] = t13 * detInv;
		te[ 9 ] = ( n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44 ) * detInv;
		te[ 10 ] = ( n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44 ) * detInv;
		te[ 11 ] = ( n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43 ) * detInv;

		te[ 12 ] = t14 * detInv;
		te[ 13 ] = ( n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34 ) * detInv;
		te[ 14 ] = ( n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34 ) * detInv;
		te[ 15 ] = ( n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33 ) * detInv;

		return this;

	}

	scale( v ) {

		const te = this.elements;
		const x = v.x, y = v.y, z = v.z;

		te[ 0 ] *= x; te[ 4 ] *= y; te[ 8 ] *= z;
		te[ 1 ] *= x; te[ 5 ] *= y; te[ 9 ] *= z;
		te[ 2 ] *= x; te[ 6 ] *= y; te[ 10 ] *= z;
		te[ 3 ] *= x; te[ 7 ] *= y; te[ 11 ] *= z;

		return this;

	}

	getMaxScaleOnAxis() {

		const te = this.elements;

		const scaleXSq = te[ 0 ] * te[ 0 ] + te[ 1 ] * te[ 1 ] + te[ 2 ] * te[ 2 ];
		const scaleYSq = te[ 4 ] * te[ 4 ] + te[ 5 ] * te[ 5 ] + te[ 6 ] * te[ 6 ];
		const scaleZSq = te[ 8 ] * te[ 8 ] + te[ 9 ] * te[ 9 ] + te[ 10 ] * te[ 10 ];

		return Math.sqrt( Math.max( scaleXSq, scaleYSq, scaleZSq ) );

	}

	makeTranslation( x, y, z ) {

		if ( x.isVector3 ) {

			this.set(

				1, 0, 0, x.x,
				0, 1, 0, x.y,
				0, 0, 1, x.z,
				0, 0, 0, 1

			);

		} else {

			this.set(

				1, 0, 0, x,
				0, 1, 0, y,
				0, 0, 1, z,
				0, 0, 0, 1

			);

		}

		return this;

	}

	makeRotationX( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			1, 0, 0, 0,
			0, c, - s, 0,
			0, s, c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationY( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			 c, 0, s, 0,
			 0, 1, 0, 0,
			- s, 0, c, 0,
			 0, 0, 0, 1

		);

		return this;

	}

	makeRotationZ( theta ) {

		const c = Math.cos( theta ), s = Math.sin( theta );

		this.set(

			c, - s, 0, 0,
			s, c, 0, 0,
			0, 0, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeRotationAxis( axis, angle ) {

		// Based on http://www.gamedev.net/reference/articles/article1199.asp

		const c = Math.cos( angle );
		const s = Math.sin( angle );
		const t = 1 - c;
		const x = axis.x, y = axis.y, z = axis.z;
		const tx = t * x, ty = t * y;

		this.set(

			tx * x + c, tx * y - s * z, tx * z + s * y, 0,
			tx * y + s * z, ty * y + c, ty * z - s * x, 0,
			tx * z - s * y, ty * z + s * x, t * z * z + c, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeScale( x, y, z ) {

		this.set(

			x, 0, 0, 0,
			0, y, 0, 0,
			0, 0, z, 0,
			0, 0, 0, 1

		);

		return this;

	}

	makeShear( xy, xz, yx, yz, zx, zy ) {

		this.set(

			1, yx, zx, 0,
			xy, 1, zy, 0,
			xz, yz, 1, 0,
			0, 0, 0, 1

		);

		return this;

	}

	compose( position, quaternion, scale ) {

		const te = this.elements;

		const x = quaternion._x, y = quaternion._y, z = quaternion._z, w = quaternion._w;
		const x2 = x + x,	y2 = y + y, z2 = z + z;
		const xx = x * x2, xy = x * y2, xz = x * z2;
		const yy = y * y2, yz = y * z2, zz = z * z2;
		const wx = w * x2, wy = w * y2, wz = w * z2;

		const sx = scale.x, sy = scale.y, sz = scale.z;

		te[ 0 ] = ( 1 - ( yy + zz ) ) * sx;
		te[ 1 ] = ( xy + wz ) * sx;
		te[ 2 ] = ( xz - wy ) * sx;
		te[ 3 ] = 0;

		te[ 4 ] = ( xy - wz ) * sy;
		te[ 5 ] = ( 1 - ( xx + zz ) ) * sy;
		te[ 6 ] = ( yz + wx ) * sy;
		te[ 7 ] = 0;

		te[ 8 ] = ( xz + wy ) * sz;
		te[ 9 ] = ( yz - wx ) * sz;
		te[ 10 ] = ( 1 - ( xx + yy ) ) * sz;
		te[ 11 ] = 0;

		te[ 12 ] = position.x;
		te[ 13 ] = position.y;
		te[ 14 ] = position.z;
		te[ 15 ] = 1;

		return this;

	}

	decompose( position, quaternion, scale ) {

		const te = this.elements;

		let sx = _v1$5.set( te[ 0 ], te[ 1 ], te[ 2 ] ).length();
		const sy = _v1$5.set( te[ 4 ], te[ 5 ], te[ 6 ] ).length();
		const sz = _v1$5.set( te[ 8 ], te[ 9 ], te[ 10 ] ).length();

		// if determine is negative, we need to invert one scale
		const det = this.determinant();
		if ( det < 0 ) sx = - sx;

		position.x = te[ 12 ];
		position.y = te[ 13 ];
		position.z = te[ 14 ];

		// scale the rotation part
		_m1$4.copy( this );

		const invSX = 1 / sx;
		const invSY = 1 / sy;
		const invSZ = 1 / sz;

		_m1$4.elements[ 0 ] *= invSX;
		_m1$4.elements[ 1 ] *= invSX;
		_m1$4.elements[ 2 ] *= invSX;

		_m1$4.elements[ 4 ] *= invSY;
		_m1$4.elements[ 5 ] *= invSY;
		_m1$4.elements[ 6 ] *= invSY;

		_m1$4.elements[ 8 ] *= invSZ;
		_m1$4.elements[ 9 ] *= invSZ;
		_m1$4.elements[ 10 ] *= invSZ;

		quaternion.setFromRotationMatrix( _m1$4 );

		scale.x = sx;
		scale.y = sy;
		scale.z = sz;

		return this;

	}

	makePerspective( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const x = 2 * near / ( right - left );
		const y = 2 * near / ( top - bottom );

		const a = ( right + left ) / ( right - left );
		const b = ( top + bottom ) / ( top - bottom );

		let c, d;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			c = - ( far + near ) / ( far - near );
			d = ( - 2 * far * near ) / ( far - near );

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			c = - far / ( far - near );
			d = ( - far * near ) / ( far - near );

		} else {

			throw new Error( 'THREE.Matrix4.makePerspective(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = x;	te[ 4 ] = 0;	te[ 8 ] = a; 	te[ 12 ] = 0;
		te[ 1 ] = 0;	te[ 5 ] = y;	te[ 9 ] = b; 	te[ 13 ] = 0;
		te[ 2 ] = 0;	te[ 6 ] = 0;	te[ 10 ] = c; 	te[ 14 ] = d;
		te[ 3 ] = 0;	te[ 7 ] = 0;	te[ 11 ] = - 1;	te[ 15 ] = 0;

		return this;

	}

	makeOrthographic( left, right, top, bottom, near, far, coordinateSystem = WebGLCoordinateSystem ) {

		const te = this.elements;
		const w = 1.0 / ( right - left );
		const h = 1.0 / ( top - bottom );
		const p = 1.0 / ( far - near );

		const x = ( right + left ) * w;
		const y = ( top + bottom ) * h;

		let z, zInv;

		if ( coordinateSystem === WebGLCoordinateSystem ) {

			z = ( far + near ) * p;
			zInv = - 2 * p;

		} else if ( coordinateSystem === WebGPUCoordinateSystem ) {

			z = near * p;
			zInv = - 1 * p;

		} else {

			throw new Error( 'THREE.Matrix4.makeOrthographic(): Invalid coordinate system: ' + coordinateSystem );

		}

		te[ 0 ] = 2 * w;	te[ 4 ] = 0;		te[ 8 ] = 0; 		te[ 12 ] = - x;
		te[ 1 ] = 0; 		te[ 5 ] = 2 * h;	te[ 9 ] = 0; 		te[ 13 ] = - y;
		te[ 2 ] = 0; 		te[ 6 ] = 0;		te[ 10 ] = zInv;	te[ 14 ] = - z;
		te[ 3 ] = 0; 		te[ 7 ] = 0;		te[ 11 ] = 0;		te[ 15 ] = 1;

		return this;

	}

	equals( matrix ) {

		const te = this.elements;
		const me = matrix.elements;

		for ( let i = 0; i < 16; i ++ ) {

			if ( te[ i ] !== me[ i ] ) return false;

		}

		return true;

	}

	fromArray( array, offset = 0 ) {

		for ( let i = 0; i < 16; i ++ ) {

			this.elements[ i ] = array[ i + offset ];

		}

		return this;

	}

	toArray( array = [], offset = 0 ) {

		const te = this.elements;

		array[ offset ] = te[ 0 ];
		array[ offset + 1 ] = te[ 1 ];
		array[ offset + 2 ] = te[ 2 ];
		array[ offset + 3 ] = te[ 3 ];

		array[ offset + 4 ] = te[ 4 ];
		array[ offset + 5 ] = te[ 5 ];
		array[ offset + 6 ] = te[ 6 ];
		array[ offset + 7 ] = te[ 7 ];

		array[ offset + 8 ] = te[ 8 ];
		array[ offset + 9 ] = te[ 9 ];
		array[ offset + 10 ] = te[ 10 ];
		array[ offset + 11 ] = te[ 11 ];

		array[ offset + 12 ] = te[ 12 ];
		array[ offset + 13 ] = te[ 13 ];
		array[ offset + 14 ] = te[ 14 ];
		array[ offset + 15 ] = te[ 15 ];

		return array;

	}

}

const _v1$5 = /*@__PURE__*/ new Vector3();
const _m1$4 = /*@__PURE__*/ new Matrix4();
const _zero = /*@__PURE__*/ new Vector3( 0, 0, 0 );
const _one = /*@__PURE__*/ new Vector3( 1, 1, 1 );
const _x = /*@__PURE__*/ new Vector3();
const _y = /*@__PURE__*/ new Vector3();
const _z = /*@__PURE__*/ new Vector3();

const _matrix$2 = /*@__PURE__*/ new Matrix4();
const _quaternion$3 = /*@__PURE__*/ new Quaternion();

class Euler {

	constructor( x = 0, y = 0, z = 0, order = Euler.DEFAULT_ORDER ) {

		this.isEuler = true;

		this._x = x;
		this._y = y;
		this._z = z;
		this._order = order;

	}

	get x() {

		return this._x;

	}

	set x( value ) {

		this._x = value;
		this._onChangeCallback();

	}

	get y() {

		return this._y;

	}

	set y( value ) {

		this._y = value;
		this._onChangeCallback();

	}

	get z() {

		return this._z;

	}

	set z( value ) {

		this._z = value;
		this._onChangeCallback();

	}

	get order() {

		return this._order;

	}

	set order( value ) {

		this._order = value;
		this._onChangeCallback();

	}

	set( x, y, z, order = this._order ) {

		this._x = x;
		this._y = y;
		this._z = z;
		this._order = order;

		this._onChangeCallback();

		return this;

	}

	clone() {

		return new this.constructor( this._x, this._y, this._z, this._order );

	}

	copy( euler ) {

		this._x = euler._x;
		this._y = euler._y;
		this._z = euler._z;
		this._order = euler._order;

		this._onChangeCallback();

		return this;

	}

	setFromRotationMatrix( m, order = this._order, update = true ) {

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		const te = m.elements;
		const m11 = te[ 0 ], m12 = te[ 4 ], m13 = te[ 8 ];
		const m21 = te[ 1 ], m22 = te[ 5 ], m23 = te[ 9 ];
		const m31 = te[ 2 ], m32 = te[ 6 ], m33 = te[ 10 ];

		switch ( order ) {

			case 'XYZ':

				this._y = Math.asin( clamp( m13, - 1, 1 ) );

				if ( Math.abs( m13 ) < 0.9999999 ) {

					this._x = Math.atan2( - m23, m33 );
					this._z = Math.atan2( - m12, m11 );

				} else {

					this._x = Math.atan2( m32, m22 );
					this._z = 0;

				}

				break;

			case 'YXZ':

				this._x = Math.asin( - clamp( m23, - 1, 1 ) );

				if ( Math.abs( m23 ) < 0.9999999 ) {

					this._y = Math.atan2( m13, m33 );
					this._z = Math.atan2( m21, m22 );

				} else {

					this._y = Math.atan2( - m31, m11 );
					this._z = 0;

				}

				break;

			case 'ZXY':

				this._x = Math.asin( clamp( m32, - 1, 1 ) );

				if ( Math.abs( m32 ) < 0.9999999 ) {

					this._y = Math.atan2( - m31, m33 );
					this._z = Math.atan2( - m12, m22 );

				} else {

					this._y = 0;
					this._z = Math.atan2( m21, m11 );

				}

				break;

			case 'ZYX':

				this._y = Math.asin( - clamp( m31, - 1, 1 ) );

				if ( Math.abs( m31 ) < 0.9999999 ) {

					this._x = Math.atan2( m32, m33 );
					this._z = Math.atan2( m21, m11 );

				} else {

					this._x = 0;
					this._z = Math.atan2( - m12, m22 );

				}

				break;

			case 'YZX':

				this._z = Math.asin( clamp( m21, - 1, 1 ) );

				if ( Math.abs( m21 ) < 0.9999999 ) {

					this._x = Math.atan2( - m23, m22 );
					this._y = Math.atan2( - m31, m11 );

				} else {

					this._x = 0;
					this._y = Math.atan2( m13, m33 );

				}

				break;

			case 'XZY':

				this._z = Math.asin( - clamp( m12, - 1, 1 ) );

				if ( Math.abs( m12 ) < 0.9999999 ) {

					this._x = Math.atan2( m32, m22 );
					this._y = Math.atan2( m13, m11 );

				} else {

					this._x = Math.atan2( - m23, m33 );
					this._y = 0;

				}

				break;

			default:

				console.warn( 'THREE.Euler: .setFromRotationMatrix() encountered an unknown order: ' + order );

		}

		this._order = order;

		if ( update === true ) this._onChangeCallback();

		return this;

	}

	setFromQuaternion( q, order, update ) {

		_matrix$2.makeRotationFromQuaternion( q );

		return this.setFromRotationMatrix( _matrix$2, order, update );

	}

	setFromVector3( v, order = this._order ) {

		return this.set( v.x, v.y, v.z, order );

	}

	reorder( newOrder ) {

		// WARNING: this discards revolution information -bhouston

		_quaternion$3.setFromEuler( this );

		return this.setFromQuaternion( _quaternion$3, newOrder );

	}

	equals( euler ) {

		return ( euler._x === this._x ) && ( euler._y === this._y ) && ( euler._z === this._z ) && ( euler._order === this._order );

	}

	fromArray( array ) {

		this._x = array[ 0 ];
		this._y = array[ 1 ];
		this._z = array[ 2 ];
		if ( array[ 3 ] !== undefined ) this._order = array[ 3 ];

		this._onChangeCallback();

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this._x;
		array[ offset + 1 ] = this._y;
		array[ offset + 2 ] = this._z;
		array[ offset + 3 ] = this._order;

		return array;

	}

	_onChange( callback ) {

		this._onChangeCallback = callback;

		return this;

	}

	_onChangeCallback() {}

	*[ Symbol.iterator ]() {

		yield this._x;
		yield this._y;
		yield this._z;
		yield this._order;

	}

}

Euler.DEFAULT_ORDER = 'XYZ';

class Layers {

	constructor() {

		this.mask = 1 | 0;

	}

	set( channel ) {

		this.mask = ( 1 << channel | 0 ) >>> 0;

	}

	enable( channel ) {

		this.mask |= 1 << channel | 0;

	}

	enableAll() {

		this.mask = 0xffffffff | 0;

	}

	toggle( channel ) {

		this.mask ^= 1 << channel | 0;

	}

	disable( channel ) {

		this.mask &= ~ ( 1 << channel | 0 );

	}

	disableAll() {

		this.mask = 0;

	}

	test( layers ) {

		return ( this.mask & layers.mask ) !== 0;

	}

	isEnabled( channel ) {

		return ( this.mask & ( 1 << channel | 0 ) ) !== 0;

	}

}

let _object3DId = 0;

const _v1$4 = /*@__PURE__*/ new Vector3();
const _q1 = /*@__PURE__*/ new Quaternion();
const _m1$3 = /*@__PURE__*/ new Matrix4();
const _target = /*@__PURE__*/ new Vector3();

const _position$3 = /*@__PURE__*/ new Vector3();
const _scale$2 = /*@__PURE__*/ new Vector3();
const _quaternion$2 = /*@__PURE__*/ new Quaternion();

const _xAxis = /*@__PURE__*/ new Vector3( 1, 0, 0 );
const _yAxis = /*@__PURE__*/ new Vector3( 0, 1, 0 );
const _zAxis = /*@__PURE__*/ new Vector3( 0, 0, 1 );

const _addedEvent = { type: 'added' };
const _removedEvent = { type: 'removed' };

const _childaddedEvent = { type: 'childadded', child: null };
const _childremovedEvent = { type: 'childremoved', child: null };

class Object3D extends EventDispatcher {

	constructor() {

		super();

		this.isObject3D = true;

		Object.defineProperty( this, 'id', { value: _object3DId ++ } );

		this.uuid = generateUUID();

		this.name = '';
		this.type = 'Object3D';

		this.parent = null;
		this.children = [];

		this.up = Object3D.DEFAULT_UP.clone();

		const position = new Vector3();
		const rotation = new Euler();
		const quaternion = new Quaternion();
		const scale = new Vector3( 1, 1, 1 );

		function onRotationChange() {

			quaternion.setFromEuler( rotation, false );

		}

		function onQuaternionChange() {

			rotation.setFromQuaternion( quaternion, undefined, false );

		}

		rotation._onChange( onRotationChange );
		quaternion._onChange( onQuaternionChange );

		Object.defineProperties( this, {
			position: {
				configurable: true,
				enumerable: true,
				value: position
			},
			rotation: {
				configurable: true,
				enumerable: true,
				value: rotation
			},
			quaternion: {
				configurable: true,
				enumerable: true,
				value: quaternion
			},
			scale: {
				configurable: true,
				enumerable: true,
				value: scale
			},
			modelViewMatrix: {
				value: new Matrix4()
			},
			normalMatrix: {
				value: new Matrix3()
			}
		} );

		this.matrix = new Matrix4();
		this.matrixWorld = new Matrix4();

		this.matrixAutoUpdate = Object3D.DEFAULT_MATRIX_AUTO_UPDATE;

		this.matrixWorldAutoUpdate = Object3D.DEFAULT_MATRIX_WORLD_AUTO_UPDATE; // checked by the renderer
		this.matrixWorldNeedsUpdate = false;

		this.layers = new Layers();
		this.visible = true;

		this.castShadow = false;
		this.receiveShadow = false;

		this.frustumCulled = true;
		this.renderOrder = 0;

		this.animations = [];

		this.userData = {};

	}

	onBeforeShadow( /* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */ ) {}

	onAfterShadow( /* renderer, object, camera, shadowCamera, geometry, depthMaterial, group */ ) {}

	onBeforeRender( /* renderer, scene, camera, geometry, material, group */ ) {}

	onAfterRender( /* renderer, scene, camera, geometry, material, group */ ) {}

	applyMatrix4( matrix ) {

		if ( this.matrixAutoUpdate ) this.updateMatrix();

		this.matrix.premultiply( matrix );

		this.matrix.decompose( this.position, this.quaternion, this.scale );

	}

	applyQuaternion( q ) {

		this.quaternion.premultiply( q );

		return this;

	}

	setRotationFromAxisAngle( axis, angle ) {

		// assumes axis is normalized

		this.quaternion.setFromAxisAngle( axis, angle );

	}

	setRotationFromEuler( euler ) {

		this.quaternion.setFromEuler( euler, true );

	}

	setRotationFromMatrix( m ) {

		// assumes the upper 3x3 of m is a pure rotation matrix (i.e, unscaled)

		this.quaternion.setFromRotationMatrix( m );

	}

	setRotationFromQuaternion( q ) {

		// assumes q is normalized

		this.quaternion.copy( q );

	}

	rotateOnAxis( axis, angle ) {

		// rotate object on axis in object space
		// axis is assumed to be normalized

		_q1.setFromAxisAngle( axis, angle );

		this.quaternion.multiply( _q1 );

		return this;

	}

	rotateOnWorldAxis( axis, angle ) {

		// rotate object on axis in world space
		// axis is assumed to be normalized
		// method assumes no rotated parent

		_q1.setFromAxisAngle( axis, angle );

		this.quaternion.premultiply( _q1 );

		return this;

	}

	rotateX( angle ) {

		return this.rotateOnAxis( _xAxis, angle );

	}

	rotateY( angle ) {

		return this.rotateOnAxis( _yAxis, angle );

	}

	rotateZ( angle ) {

		return this.rotateOnAxis( _zAxis, angle );

	}

	translateOnAxis( axis, distance ) {

		// translate object by distance along axis in object space
		// axis is assumed to be normalized

		_v1$4.copy( axis ).applyQuaternion( this.quaternion );

		this.position.add( _v1$4.multiplyScalar( distance ) );

		return this;

	}

	translateX( distance ) {

		return this.translateOnAxis( _xAxis, distance );

	}

	translateY( distance ) {

		return this.translateOnAxis( _yAxis, distance );

	}

	translateZ( distance ) {

		return this.translateOnAxis( _zAxis, distance );

	}

	localToWorld( vector ) {

		this.updateWorldMatrix( true, false );

		return vector.applyMatrix4( this.matrixWorld );

	}

	worldToLocal( vector ) {

		this.updateWorldMatrix( true, false );

		return vector.applyMatrix4( _m1$3.copy( this.matrixWorld ).invert() );

	}

	lookAt( x, y, z ) {

		// This method does not support objects having non-uniformly-scaled parent(s)

		if ( x.isVector3 ) {

			_target.copy( x );

		} else {

			_target.set( x, y, z );

		}

		const parent = this.parent;

		this.updateWorldMatrix( true, false );

		_position$3.setFromMatrixPosition( this.matrixWorld );

		if ( this.isCamera || this.isLight ) {

			_m1$3.lookAt( _position$3, _target, this.up );

		} else {

			_m1$3.lookAt( _target, _position$3, this.up );

		}

		this.quaternion.setFromRotationMatrix( _m1$3 );

		if ( parent ) {

			_m1$3.extractRotation( parent.matrixWorld );
			_q1.setFromRotationMatrix( _m1$3 );
			this.quaternion.premultiply( _q1.invert() );

		}

	}

	add( object ) {

		if ( arguments.length > 1 ) {

			for ( let i = 0; i < arguments.length; i ++ ) {

				this.add( arguments[ i ] );

			}

			return this;

		}

		if ( object === this ) {

			console.error( 'THREE.Object3D.add: object can\'t be added as a child of itself.', object );
			return this;

		}

		if ( object && object.isObject3D ) {

			object.removeFromParent();
			object.parent = this;
			this.children.push( object );

			object.dispatchEvent( _addedEvent );

			_childaddedEvent.child = object;
			this.dispatchEvent( _childaddedEvent );
			_childaddedEvent.child = null;

		} else {

			console.error( 'THREE.Object3D.add: object not an instance of THREE.Object3D.', object );

		}

		return this;

	}

	remove( object ) {

		if ( arguments.length > 1 ) {

			for ( let i = 0; i < arguments.length; i ++ ) {

				this.remove( arguments[ i ] );

			}

			return this;

		}

		const index = this.children.indexOf( object );

		if ( index !== - 1 ) {

			object.parent = null;
			this.children.splice( index, 1 );

			object.dispatchEvent( _removedEvent );

			_childremovedEvent.child = object;
			this.dispatchEvent( _childremovedEvent );
			_childremovedEvent.child = null;

		}

		return this;

	}

	removeFromParent() {

		const parent = this.parent;

		if ( parent !== null ) {

			parent.remove( this );

		}

		return this;

	}

	clear() {

		return this.remove( ... this.children );

	}

	attach( object ) {

		// adds object as a child of this, while maintaining the object's world transform

		// Note: This method does not support scene graphs having non-uniformly-scaled nodes(s)

		this.updateWorldMatrix( true, false );

		_m1$3.copy( this.matrixWorld ).invert();

		if ( object.parent !== null ) {

			object.parent.updateWorldMatrix( true, false );

			_m1$3.multiply( object.parent.matrixWorld );

		}

		object.applyMatrix4( _m1$3 );

		object.removeFromParent();
		object.parent = this;
		this.children.push( object );

		object.updateWorldMatrix( false, true );

		object.dispatchEvent( _addedEvent );

		_childaddedEvent.child = object;
		this.dispatchEvent( _childaddedEvent );
		_childaddedEvent.child = null;

		return this;

	}

	getObjectById( id ) {

		return this.getObjectByProperty( 'id', id );

	}

	getObjectByName( name ) {

		return this.getObjectByProperty( 'name', name );

	}

	getObjectByProperty( name, value ) {

		if ( this[ name ] === value ) return this;

		for ( let i = 0, l = this.children.length; i < l; i ++ ) {

			const child = this.children[ i ];
			const object = child.getObjectByProperty( name, value );

			if ( object !== undefined ) {

				return object;

			}

		}

		return undefined;

	}

	getObjectsByProperty( name, value, result = [] ) {

		if ( this[ name ] === value ) result.push( this );

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			children[ i ].getObjectsByProperty( name, value, result );

		}

		return result;

	}

	getWorldPosition( target ) {

		this.updateWorldMatrix( true, false );

		return target.setFromMatrixPosition( this.matrixWorld );

	}

	getWorldQuaternion( target ) {

		this.updateWorldMatrix( true, false );

		this.matrixWorld.decompose( _position$3, target, _scale$2 );

		return target;

	}

	getWorldScale( target ) {

		this.updateWorldMatrix( true, false );

		this.matrixWorld.decompose( _position$3, _quaternion$2, target );

		return target;

	}

	getWorldDirection( target ) {

		this.updateWorldMatrix( true, false );

		const e = this.matrixWorld.elements;

		return target.set( e[ 8 ], e[ 9 ], e[ 10 ] ).normalize();

	}

	raycast( /* raycaster, intersects */ ) {}

	traverse( callback ) {

		callback( this );

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			children[ i ].traverse( callback );

		}

	}

	traverseVisible( callback ) {

		if ( this.visible === false ) return;

		callback( this );

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			children[ i ].traverseVisible( callback );

		}

	}

	traverseAncestors( callback ) {

		const parent = this.parent;

		if ( parent !== null ) {

			callback( parent );

			parent.traverseAncestors( callback );

		}

	}

	updateMatrix() {

		this.matrix.compose( this.position, this.quaternion, this.scale );

		this.matrixWorldNeedsUpdate = true;

	}

	updateMatrixWorld( force ) {

		if ( this.matrixAutoUpdate ) this.updateMatrix();

		if ( this.matrixWorldNeedsUpdate || force ) {

			if ( this.matrixWorldAutoUpdate === true ) {

				if ( this.parent === null ) {

					this.matrixWorld.copy( this.matrix );

				} else {

					this.matrixWorld.multiplyMatrices( this.parent.matrixWorld, this.matrix );

				}

			}

			this.matrixWorldNeedsUpdate = false;

			force = true;

		}

		// make sure descendants are updated if required

		const children = this.children;

		for ( let i = 0, l = children.length; i < l; i ++ ) {

			const child = children[ i ];

			child.updateMatrixWorld( force );

		}

	}

	updateWorldMatrix( updateParents, updateChildren ) {

		const parent = this.parent;

		if ( updateParents === true && parent !== null ) {

			parent.updateWorldMatrix( true, false );

		}

		if ( this.matrixAutoUpdate ) this.updateMatrix();

		if ( this.matrixWorldAutoUpdate === true ) {

			if ( this.parent === null ) {

				this.matrixWorld.copy( this.matrix );

			} else {

				this.matrixWorld.multiplyMatrices( this.parent.matrixWorld, this.matrix );

			}

		}

		// make sure descendants are updated

		if ( updateChildren === true ) {

			const children = this.children;

			for ( let i = 0, l = children.length; i < l; i ++ ) {

				const child = children[ i ];

				child.updateWorldMatrix( false, true );

			}

		}

	}

	toJSON( meta ) {

		// meta is a string when called from JSON.stringify
		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		const output = {};

		// meta is a hash used to collect geometries, materials.
		// not providing it implies that this is the root object
		// being serialized.
		if ( isRootObject ) {

			// initialize meta obj
			meta = {
				geometries: {},
				materials: {},
				textures: {},
				images: {},
				shapes: {},
				skeletons: {},
				animations: {},
				nodes: {}
			};

			output.metadata = {
				version: 4.6,
				type: 'Object',
				generator: 'Object3D.toJSON'
			};

		}

		// standard Object3D serialization

		const object = {};

		object.uuid = this.uuid;
		object.type = this.type;

		if ( this.name !== '' ) object.name = this.name;
		if ( this.castShadow === true ) object.castShadow = true;
		if ( this.receiveShadow === true ) object.receiveShadow = true;
		if ( this.visible === false ) object.visible = false;
		if ( this.frustumCulled === false ) object.frustumCulled = false;
		if ( this.renderOrder !== 0 ) object.renderOrder = this.renderOrder;
		if ( Object.keys( this.userData ).length > 0 ) object.userData = this.userData;

		object.layers = this.layers.mask;
		object.matrix = this.matrix.toArray();
		object.up = this.up.toArray();

		if ( this.matrixAutoUpdate === false ) object.matrixAutoUpdate = false;

		// object specific properties

		if ( this.isInstancedMesh ) {

			object.type = 'InstancedMesh';
			object.count = this.count;
			object.instanceMatrix = this.instanceMatrix.toJSON();
			if ( this.instanceColor !== null ) object.instanceColor = this.instanceColor.toJSON();

		}

		if ( this.isBatchedMesh ) {

			object.type = 'BatchedMesh';
			object.perObjectFrustumCulled = this.perObjectFrustumCulled;
			object.sortObjects = this.sortObjects;

			object.drawRanges = this._drawRanges;
			object.reservedRanges = this._reservedRanges;

			object.visibility = this._visibility;
			object.active = this._active;
			object.bounds = this._bounds.map( bound => ( {
				boxInitialized: bound.boxInitialized,
				boxMin: bound.box.min.toArray(),
				boxMax: bound.box.max.toArray(),

				sphereInitialized: bound.sphereInitialized,
				sphereRadius: bound.sphere.radius,
				sphereCenter: bound.sphere.center.toArray()
			} ) );

			object.maxInstanceCount = this._maxInstanceCount;
			object.maxVertexCount = this._maxVertexCount;
			object.maxIndexCount = this._maxIndexCount;

			object.geometryInitialized = this._geometryInitialized;
			object.geometryCount = this._geometryCount;

			object.matricesTexture = this._matricesTexture.toJSON( meta );

			if ( this._colorsTexture !== null ) object.colorsTexture = this._colorsTexture.toJSON( meta );

			if ( this.boundingSphere !== null ) {

				object.boundingSphere = {
					center: object.boundingSphere.center.toArray(),
					radius: object.boundingSphere.radius
				};

			}

			if ( this.boundingBox !== null ) {

				object.boundingBox = {
					min: object.boundingBox.min.toArray(),
					max: object.boundingBox.max.toArray()
				};

			}

		}

		//

		function serialize( library, element ) {

			if ( library[ element.uuid ] === undefined ) {

				library[ element.uuid ] = element.toJSON( meta );

			}

			return element.uuid;

		}

		if ( this.isScene ) {

			if ( this.background ) {

				if ( this.background.isColor ) {

					object.background = this.background.toJSON();

				} else if ( this.background.isTexture ) {

					object.background = this.background.toJSON( meta ).uuid;

				}

			}

			if ( this.environment && this.environment.isTexture && this.environment.isRenderTargetTexture !== true ) {

				object.environment = this.environment.toJSON( meta ).uuid;

			}

		} else if ( this.isMesh || this.isLine || this.isPoints ) {

			object.geometry = serialize( meta.geometries, this.geometry );

			const parameters = this.geometry.parameters;

			if ( parameters !== undefined && parameters.shapes !== undefined ) {

				const shapes = parameters.shapes;

				if ( Array.isArray( shapes ) ) {

					for ( let i = 0, l = shapes.length; i < l; i ++ ) {

						const shape = shapes[ i ];

						serialize( meta.shapes, shape );

					}

				} else {

					serialize( meta.shapes, shapes );

				}

			}

		}

		if ( this.isSkinnedMesh ) {

			object.bindMode = this.bindMode;
			object.bindMatrix = this.bindMatrix.toArray();

			if ( this.skeleton !== undefined ) {

				serialize( meta.skeletons, this.skeleton );

				object.skeleton = this.skeleton.uuid;

			}

		}

		if ( this.material !== undefined ) {

			if ( Array.isArray( this.material ) ) {

				const uuids = [];

				for ( let i = 0, l = this.material.length; i < l; i ++ ) {

					uuids.push( serialize( meta.materials, this.material[ i ] ) );

				}

				object.material = uuids;

			} else {

				object.material = serialize( meta.materials, this.material );

			}

		}

		//

		if ( this.children.length > 0 ) {

			object.children = [];

			for ( let i = 0; i < this.children.length; i ++ ) {

				object.children.push( this.children[ i ].toJSON( meta ).object );

			}

		}

		//

		if ( this.animations.length > 0 ) {

			object.animations = [];

			for ( let i = 0; i < this.animations.length; i ++ ) {

				const animation = this.animations[ i ];

				object.animations.push( serialize( meta.animations, animation ) );

			}

		}

		if ( isRootObject ) {

			const geometries = extractFromCache( meta.geometries );
			const materials = extractFromCache( meta.materials );
			const textures = extractFromCache( meta.textures );
			const images = extractFromCache( meta.images );
			const shapes = extractFromCache( meta.shapes );
			const skeletons = extractFromCache( meta.skeletons );
			const animations = extractFromCache( meta.animations );
			const nodes = extractFromCache( meta.nodes );

			if ( geometries.length > 0 ) output.geometries = geometries;
			if ( materials.length > 0 ) output.materials = materials;
			if ( textures.length > 0 ) output.textures = textures;
			if ( images.length > 0 ) output.images = images;
			if ( shapes.length > 0 ) output.shapes = shapes;
			if ( skeletons.length > 0 ) output.skeletons = skeletons;
			if ( animations.length > 0 ) output.animations = animations;
			if ( nodes.length > 0 ) output.nodes = nodes;

		}

		output.object = object;

		return output;

		// extract data from the cache hash
		// remove metadata on each item
		// and return as array
		function extractFromCache( cache ) {

			const values = [];
			for ( const key in cache ) {

				const data = cache[ key ];
				delete data.metadata;
				values.push( data );

			}

			return values;

		}

	}

	clone( recursive ) {

		return new this.constructor().copy( this, recursive );

	}

	copy( source, recursive = true ) {

		this.name = source.name;

		this.up.copy( source.up );

		this.position.copy( source.position );
		this.rotation.order = source.rotation.order;
		this.quaternion.copy( source.quaternion );
		this.scale.copy( source.scale );

		this.matrix.copy( source.matrix );
		this.matrixWorld.copy( source.matrixWorld );

		this.matrixAutoUpdate = source.matrixAutoUpdate;

		this.matrixWorldAutoUpdate = source.matrixWorldAutoUpdate;
		this.matrixWorldNeedsUpdate = source.matrixWorldNeedsUpdate;

		this.layers.mask = source.layers.mask;
		this.visible = source.visible;

		this.castShadow = source.castShadow;
		this.receiveShadow = source.receiveShadow;

		this.frustumCulled = source.frustumCulled;
		this.renderOrder = source.renderOrder;

		this.animations = source.animations.slice();

		this.userData = JSON.parse( JSON.stringify( source.userData ) );

		if ( recursive === true ) {

			for ( let i = 0; i < source.children.length; i ++ ) {

				const child = source.children[ i ];
				this.add( child.clone() );

			}

		}

		return this;

	}

}

Object3D.DEFAULT_UP = /*@__PURE__*/ new Vector3( 0, 1, 0 );
Object3D.DEFAULT_MATRIX_AUTO_UPDATE = true;
Object3D.DEFAULT_MATRIX_WORLD_AUTO_UPDATE = true;

const _colorKeywords = { 'aliceblue': 0xF0F8FF, 'antiquewhite': 0xFAEBD7, 'aqua': 0x00FFFF, 'aquamarine': 0x7FFFD4, 'azure': 0xF0FFFF,
	'beige': 0xF5F5DC, 'bisque': 0xFFE4C4, 'black': 0x000000, 'blanchedalmond': 0xFFEBCD, 'blue': 0x0000FF, 'blueviolet': 0x8A2BE2,
	'brown': 0xA52A2A, 'burlywood': 0xDEB887, 'cadetblue': 0x5F9EA0, 'chartreuse': 0x7FFF00, 'chocolate': 0xD2691E, 'coral': 0xFF7F50,
	'cornflowerblue': 0x6495ED, 'cornsilk': 0xFFF8DC, 'crimson': 0xDC143C, 'cyan': 0x00FFFF, 'darkblue': 0x00008B, 'darkcyan': 0x008B8B,
	'darkgoldenrod': 0xB8860B, 'darkgray': 0xA9A9A9, 'darkgreen': 0x006400, 'darkgrey': 0xA9A9A9, 'darkkhaki': 0xBDB76B, 'darkmagenta': 0x8B008B,
	'darkolivegreen': 0x556B2F, 'darkorange': 0xFF8C00, 'darkorchid': 0x9932CC, 'darkred': 0x8B0000, 'darksalmon': 0xE9967A, 'darkseagreen': 0x8FBC8F,
	'darkslateblue': 0x483D8B, 'darkslategray': 0x2F4F4F, 'darkslategrey': 0x2F4F4F, 'darkturquoise': 0x00CED1, 'darkviolet': 0x9400D3,
	'deeppink': 0xFF1493, 'deepskyblue': 0x00BFFF, 'dimgray': 0x696969, 'dimgrey': 0x696969, 'dodgerblue': 0x1E90FF, 'firebrick': 0xB22222,
	'floralwhite': 0xFFFAF0, 'forestgreen': 0x228B22, 'fuchsia': 0xFF00FF, 'gainsboro': 0xDCDCDC, 'ghostwhite': 0xF8F8FF, 'gold': 0xFFD700,
	'goldenrod': 0xDAA520, 'gray': 0x808080, 'green': 0x008000, 'greenyellow': 0xADFF2F, 'grey': 0x808080, 'honeydew': 0xF0FFF0, 'hotpink': 0xFF69B4,
	'indianred': 0xCD5C5C, 'indigo': 0x4B0082, 'ivory': 0xFFFFF0, 'khaki': 0xF0E68C, 'lavender': 0xE6E6FA, 'lavenderblush': 0xFFF0F5, 'lawngreen': 0x7CFC00,
	'lemonchiffon': 0xFFFACD, 'lightblue': 0xADD8E6, 'lightcoral': 0xF08080, 'lightcyan': 0xE0FFFF, 'lightgoldenrodyellow': 0xFAFAD2, 'lightgray': 0xD3D3D3,
	'lightgreen': 0x90EE90, 'lightgrey': 0xD3D3D3, 'lightpink': 0xFFB6C1, 'lightsalmon': 0xFFA07A, 'lightseagreen': 0x20B2AA, 'lightskyblue': 0x87CEFA,
	'lightslategray': 0x778899, 'lightslategrey': 0x778899, 'lightsteelblue': 0xB0C4DE, 'lightyellow': 0xFFFFE0, 'lime': 0x00FF00, 'limegreen': 0x32CD32,
	'linen': 0xFAF0E6, 'magenta': 0xFF00FF, 'maroon': 0x800000, 'mediumaquamarine': 0x66CDAA, 'mediumblue': 0x0000CD, 'mediumorchid': 0xBA55D3,
	'mediumpurple': 0x9370DB, 'mediumseagreen': 0x3CB371, 'mediumslateblue': 0x7B68EE, 'mediumspringgreen': 0x00FA9A, 'mediumturquoise': 0x48D1CC,
	'mediumvioletred': 0xC71585, 'midnightblue': 0x191970, 'mintcream': 0xF5FFFA, 'mistyrose': 0xFFE4E1, 'moccasin': 0xFFE4B5, 'navajowhite': 0xFFDEAD,
	'navy': 0x000080, 'oldlace': 0xFDF5E6, 'olive': 0x808000, 'olivedrab': 0x6B8E23, 'orange': 0xFFA500, 'orangered': 0xFF4500, 'orchid': 0xDA70D6,
	'palegoldenrod': 0xEEE8AA, 'palegreen': 0x98FB98, 'paleturquoise': 0xAFEEEE, 'palevioletred': 0xDB7093, 'papayawhip': 0xFFEFD5, 'peachpuff': 0xFFDAB9,
	'peru': 0xCD853F, 'pink': 0xFFC0CB, 'plum': 0xDDA0DD, 'powderblue': 0xB0E0E6, 'purple': 0x800080, 'rebeccapurple': 0x663399, 'red': 0xFF0000, 'rosybrown': 0xBC8F8F,
	'royalblue': 0x4169E1, 'saddlebrown': 0x8B4513, 'salmon': 0xFA8072, 'sandybrown': 0xF4A460, 'seagreen': 0x2E8B57, 'seashell': 0xFFF5EE,
	'sienna': 0xA0522D, 'silver': 0xC0C0C0, 'skyblue': 0x87CEEB, 'slateblue': 0x6A5ACD, 'slategray': 0x708090, 'slategrey': 0x708090, 'snow': 0xFFFAFA,
	'springgreen': 0x00FF7F, 'steelblue': 0x4682B4, 'tan': 0xD2B48C, 'teal': 0x008080, 'thistle': 0xD8BFD8, 'tomato': 0xFF6347, 'turquoise': 0x40E0D0,
	'violet': 0xEE82EE, 'wheat': 0xF5DEB3, 'white': 0xFFFFFF, 'whitesmoke': 0xF5F5F5, 'yellow': 0xFFFF00, 'yellowgreen': 0x9ACD32 };

const _hslA = { h: 0, s: 0, l: 0 };
const _hslB = { h: 0, s: 0, l: 0 };

function hue2rgb( p, q, t ) {

	if ( t < 0 ) t += 1;
	if ( t > 1 ) t -= 1;
	if ( t < 1 / 6 ) return p + ( q - p ) * 6 * t;
	if ( t < 1 / 2 ) return q;
	if ( t < 2 / 3 ) return p + ( q - p ) * 6 * ( 2 / 3 - t );
	return p;

}

class Color {

	constructor( r, g, b ) {

		this.isColor = true;

		this.r = 1;
		this.g = 1;
		this.b = 1;

		return this.set( r, g, b );

	}

	set( r, g, b ) {

		if ( g === undefined && b === undefined ) {

			// r is THREE.Color, hex or string

			const value = r;

			if ( value && value.isColor ) {

				this.copy( value );

			} else if ( typeof value === 'number' ) {

				this.setHex( value );

			} else if ( typeof value === 'string' ) {

				this.setStyle( value );

			}

		} else {

			this.setRGB( r, g, b );

		}

		return this;

	}

	setScalar( scalar ) {

		this.r = scalar;
		this.g = scalar;
		this.b = scalar;

		return this;

	}

	setHex( hex, colorSpace = SRGBColorSpace ) {

		hex = Math.floor( hex );

		this.r = ( hex >> 16 & 255 ) / 255;
		this.g = ( hex >> 8 & 255 ) / 255;
		this.b = ( hex & 255 ) / 255;

		ColorManagement.toWorkingColorSpace( this, colorSpace );

		return this;

	}

	setRGB( r, g, b, colorSpace = ColorManagement.workingColorSpace ) {

		this.r = r;
		this.g = g;
		this.b = b;

		ColorManagement.toWorkingColorSpace( this, colorSpace );

		return this;

	}

	setHSL( h, s, l, colorSpace = ColorManagement.workingColorSpace ) {

		// h,s,l ranges are in 0.0 - 1.0
		h = euclideanModulo( h, 1 );
		s = clamp( s, 0, 1 );
		l = clamp( l, 0, 1 );

		if ( s === 0 ) {

			this.r = this.g = this.b = l;

		} else {

			const p = l <= 0.5 ? l * ( 1 + s ) : l + s - ( l * s );
			const q = ( 2 * l ) - p;

			this.r = hue2rgb( q, p, h + 1 / 3 );
			this.g = hue2rgb( q, p, h );
			this.b = hue2rgb( q, p, h - 1 / 3 );

		}

		ColorManagement.toWorkingColorSpace( this, colorSpace );

		return this;

	}

	setStyle( style, colorSpace = SRGBColorSpace ) {

		function handleAlpha( string ) {

			if ( string === undefined ) return;

			if ( parseFloat( string ) < 1 ) {

				console.warn( 'THREE.Color: Alpha component of ' + style + ' will be ignored.' );

			}

		}


		let m;

		if ( m = /^(\w+)\(([^\)]*)\)/.exec( style ) ) {

			// rgb / hsl

			let color;
			const name = m[ 1 ];
			const components = m[ 2 ];

			switch ( name ) {

				case 'rgb':
				case 'rgba':

					if ( color = /^\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec( components ) ) {

						// rgb(255,0,0) rgba(255,0,0,0.5)

						handleAlpha( color[ 4 ] );

						return this.setRGB(
							Math.min( 255, parseInt( color[ 1 ], 10 ) ) / 255,
							Math.min( 255, parseInt( color[ 2 ], 10 ) ) / 255,
							Math.min( 255, parseInt( color[ 3 ], 10 ) ) / 255,
							colorSpace
						);

					}

					if ( color = /^\s*(\d+)\%\s*,\s*(\d+)\%\s*,\s*(\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec( components ) ) {

						// rgb(100%,0%,0%) rgba(100%,0%,0%,0.5)

						handleAlpha( color[ 4 ] );

						return this.setRGB(
							Math.min( 100, parseInt( color[ 1 ], 10 ) ) / 100,
							Math.min( 100, parseInt( color[ 2 ], 10 ) ) / 100,
							Math.min( 100, parseInt( color[ 3 ], 10 ) ) / 100,
							colorSpace
						);

					}

					break;

				case 'hsl':
				case 'hsla':

					if ( color = /^\s*(\d*\.?\d+)\s*,\s*(\d*\.?\d+)\%\s*,\s*(\d*\.?\d+)\%\s*(?:,\s*(\d*\.?\d+)\s*)?$/.exec( components ) ) {

						// hsl(120,50%,50%) hsla(120,50%,50%,0.5)

						handleAlpha( color[ 4 ] );

						return this.setHSL(
							parseFloat( color[ 1 ] ) / 360,
							parseFloat( color[ 2 ] ) / 100,
							parseFloat( color[ 3 ] ) / 100,
							colorSpace
						);

					}

					break;

				default:

					console.warn( 'THREE.Color: Unknown color model ' + style );

			}

		} else if ( m = /^\#([A-Fa-f\d]+)$/.exec( style ) ) {

			// hex color

			const hex = m[ 1 ];
			const size = hex.length;

			if ( size === 3 ) {

				// #ff0
				return this.setRGB(
					parseInt( hex.charAt( 0 ), 16 ) / 15,
					parseInt( hex.charAt( 1 ), 16 ) / 15,
					parseInt( hex.charAt( 2 ), 16 ) / 15,
					colorSpace
				);

			} else if ( size === 6 ) {

				// #ff0000
				return this.setHex( parseInt( hex, 16 ), colorSpace );

			} else {

				console.warn( 'THREE.Color: Invalid hex color ' + style );

			}

		} else if ( style && style.length > 0 ) {

			return this.setColorName( style, colorSpace );

		}

		return this;

	}

	setColorName( style, colorSpace = SRGBColorSpace ) {

		// color keywords
		const hex = _colorKeywords[ style.toLowerCase() ];

		if ( hex !== undefined ) {

			// red
			this.setHex( hex, colorSpace );

		} else {

			// unknown color
			console.warn( 'THREE.Color: Unknown color ' + style );

		}

		return this;

	}

	clone() {

		return new this.constructor( this.r, this.g, this.b );

	}

	copy( color ) {

		this.r = color.r;
		this.g = color.g;
		this.b = color.b;

		return this;

	}

	copySRGBToLinear( color ) {

		this.r = SRGBToLinear( color.r );
		this.g = SRGBToLinear( color.g );
		this.b = SRGBToLinear( color.b );

		return this;

	}

	copyLinearToSRGB( color ) {

		this.r = LinearToSRGB( color.r );
		this.g = LinearToSRGB( color.g );
		this.b = LinearToSRGB( color.b );

		return this;

	}

	convertSRGBToLinear() {

		this.copySRGBToLinear( this );

		return this;

	}

	convertLinearToSRGB() {

		this.copyLinearToSRGB( this );

		return this;

	}

	getHex( colorSpace = SRGBColorSpace ) {

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		return Math.round( clamp( _color.r * 255, 0, 255 ) ) * 65536 + Math.round( clamp( _color.g * 255, 0, 255 ) ) * 256 + Math.round( clamp( _color.b * 255, 0, 255 ) );

	}

	getHexString( colorSpace = SRGBColorSpace ) {

		return ( '000000' + this.getHex( colorSpace ).toString( 16 ) ).slice( - 6 );

	}

	getHSL( target, colorSpace = ColorManagement.workingColorSpace ) {

		// h,s,l ranges are in 0.0 - 1.0

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		const r = _color.r, g = _color.g, b = _color.b;

		const max = Math.max( r, g, b );
		const min = Math.min( r, g, b );

		let hue, saturation;
		const lightness = ( min + max ) / 2.0;

		if ( min === max ) {

			hue = 0;
			saturation = 0;

		} else {

			const delta = max - min;

			saturation = lightness <= 0.5 ? delta / ( max + min ) : delta / ( 2 - max - min );

			switch ( max ) {

				case r: hue = ( g - b ) / delta + ( g < b ? 6 : 0 ); break;
				case g: hue = ( b - r ) / delta + 2; break;
				case b: hue = ( r - g ) / delta + 4; break;

			}

			hue /= 6;

		}

		target.h = hue;
		target.s = saturation;
		target.l = lightness;

		return target;

	}

	getRGB( target, colorSpace = ColorManagement.workingColorSpace ) {

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		target.r = _color.r;
		target.g = _color.g;
		target.b = _color.b;

		return target;

	}

	getStyle( colorSpace = SRGBColorSpace ) {

		ColorManagement.fromWorkingColorSpace( _color.copy( this ), colorSpace );

		const r = _color.r, g = _color.g, b = _color.b;

		if ( colorSpace !== SRGBColorSpace ) {

			// Requires CSS Color Module Level 4 (https://www.w3.org/TR/css-color-4/).
			return `color(${ colorSpace } ${ r.toFixed( 3 ) } ${ g.toFixed( 3 ) } ${ b.toFixed( 3 ) })`;

		}

		return `rgb(${ Math.round( r * 255 ) },${ Math.round( g * 255 ) },${ Math.round( b * 255 ) })`;

	}

	offsetHSL( h, s, l ) {

		this.getHSL( _hslA );

		return this.setHSL( _hslA.h + h, _hslA.s + s, _hslA.l + l );

	}

	add( color ) {

		this.r += color.r;
		this.g += color.g;
		this.b += color.b;

		return this;

	}

	addColors( color1, color2 ) {

		this.r = color1.r + color2.r;
		this.g = color1.g + color2.g;
		this.b = color1.b + color2.b;

		return this;

	}

	addScalar( s ) {

		this.r += s;
		this.g += s;
		this.b += s;

		return this;

	}

	sub( color ) {

		this.r = Math.max( 0, this.r - color.r );
		this.g = Math.max( 0, this.g - color.g );
		this.b = Math.max( 0, this.b - color.b );

		return this;

	}

	multiply( color ) {

		this.r *= color.r;
		this.g *= color.g;
		this.b *= color.b;

		return this;

	}

	multiplyScalar( s ) {

		this.r *= s;
		this.g *= s;
		this.b *= s;

		return this;

	}

	lerp( color, alpha ) {

		this.r += ( color.r - this.r ) * alpha;
		this.g += ( color.g - this.g ) * alpha;
		this.b += ( color.b - this.b ) * alpha;

		return this;

	}

	lerpColors( color1, color2, alpha ) {

		this.r = color1.r + ( color2.r - color1.r ) * alpha;
		this.g = color1.g + ( color2.g - color1.g ) * alpha;
		this.b = color1.b + ( color2.b - color1.b ) * alpha;

		return this;

	}

	lerpHSL( color, alpha ) {

		this.getHSL( _hslA );
		color.getHSL( _hslB );

		const h = lerp( _hslA.h, _hslB.h, alpha );
		const s = lerp( _hslA.s, _hslB.s, alpha );
		const l = lerp( _hslA.l, _hslB.l, alpha );

		this.setHSL( h, s, l );

		return this;

	}

	setFromVector3( v ) {

		this.r = v.x;
		this.g = v.y;
		this.b = v.z;

		return this;

	}

	applyMatrix3( m ) {

		const r = this.r, g = this.g, b = this.b;
		const e = m.elements;

		this.r = e[ 0 ] * r + e[ 3 ] * g + e[ 6 ] * b;
		this.g = e[ 1 ] * r + e[ 4 ] * g + e[ 7 ] * b;
		this.b = e[ 2 ] * r + e[ 5 ] * g + e[ 8 ] * b;

		return this;

	}

	equals( c ) {

		return ( c.r === this.r ) && ( c.g === this.g ) && ( c.b === this.b );

	}

	fromArray( array, offset = 0 ) {

		this.r = array[ offset ];
		this.g = array[ offset + 1 ];
		this.b = array[ offset + 2 ];

		return this;

	}

	toArray( array = [], offset = 0 ) {

		array[ offset ] = this.r;
		array[ offset + 1 ] = this.g;
		array[ offset + 2 ] = this.b;

		return array;

	}

	fromBufferAttribute( attribute, index ) {

		this.r = attribute.getX( index );
		this.g = attribute.getY( index );
		this.b = attribute.getZ( index );

		return this;

	}

	toJSON() {

		return this.getHex();

	}

	*[ Symbol.iterator ]() {

		yield this.r;
		yield this.g;
		yield this.b;

	}

}

const _color = /*@__PURE__*/ new Color();

Color.NAMES = _colorKeywords;

let _materialId = 0;

class Material extends EventDispatcher {

	constructor() {

		super();

		this.isMaterial = true;

		Object.defineProperty( this, 'id', { value: _materialId ++ } );

		this.uuid = generateUUID();

		this.name = '';
		this.type = 'Material';

		this.blending = NormalBlending;
		this.side = FrontSide;
		this.vertexColors = false;

		this.opacity = 1;
		this.transparent = false;
		this.alphaHash = false;

		this.blendSrc = SrcAlphaFactor;
		this.blendDst = OneMinusSrcAlphaFactor;
		this.blendEquation = AddEquation;
		this.blendSrcAlpha = null;
		this.blendDstAlpha = null;
		this.blendEquationAlpha = null;
		this.blendColor = new Color( 0, 0, 0 );
		this.blendAlpha = 0;

		this.depthFunc = LessEqualDepth;
		this.depthTest = true;
		this.depthWrite = true;

		this.stencilWriteMask = 0xff;
		this.stencilFunc = AlwaysStencilFunc;
		this.stencilRef = 0;
		this.stencilFuncMask = 0xff;
		this.stencilFail = KeepStencilOp;
		this.stencilZFail = KeepStencilOp;
		this.stencilZPass = KeepStencilOp;
		this.stencilWrite = false;

		this.clippingPlanes = null;
		this.clipIntersection = false;
		this.clipShadows = false;

		this.shadowSide = null;

		this.colorWrite = true;

		this.precision = null; // override the renderer's default precision for this material

		this.polygonOffset = false;
		this.polygonOffsetFactor = 0;
		this.polygonOffsetUnits = 0;

		this.dithering = false;

		this.alphaToCoverage = false;
		this.premultipliedAlpha = false;
		this.forceSinglePass = false;

		this.visible = true;

		this.toneMapped = true;

		this.userData = {};

		this.version = 0;

		this._alphaTest = 0;

	}

	get alphaTest() {

		return this._alphaTest;

	}

	set alphaTest( value ) {

		if ( this._alphaTest > 0 !== value > 0 ) {

			this.version ++;

		}

		this._alphaTest = value;

	}

	onBeforeCompile( /* shaderobject, renderer */ ) {}

	customProgramCacheKey() {

		return this.onBeforeCompile.toString();

	}

	setValues( values ) {

		if ( values === undefined ) return;

		for ( const key in values ) {

			const newValue = values[ key ];

			if ( newValue === undefined ) {

				console.warn( `THREE.Material: parameter '${ key }' has value of undefined.` );
				continue;

			}

			const currentValue = this[ key ];

			if ( currentValue === undefined ) {

				console.warn( `THREE.Material: '${ key }' is not a property of THREE.${ this.type }.` );
				continue;

			}

			if ( currentValue && currentValue.isColor ) {

				currentValue.set( newValue );

			} else if ( ( currentValue && currentValue.isVector3 ) && ( newValue && newValue.isVector3 ) ) {

				currentValue.copy( newValue );

			} else {

				this[ key ] = newValue;

			}

		}

	}

	toJSON( meta ) {

		const isRootObject = ( meta === undefined || typeof meta === 'string' );

		if ( isRootObject ) {

			meta = {
				textures: {},
				images: {}
			};

		}

		const data = {
			metadata: {
				version: 4.6,
				type: 'Material',
				generator: 'Material.toJSON'
			}
		};

		// standard Material serialization
		data.uuid = this.uuid;
		data.type = this.type;

		if ( this.name !== '' ) data.name = this.name;

		if ( this.color && this.color.isColor ) data.color = this.color.getHex();

		if ( this.roughness !== undefined ) data.roughness = this.roughness;
		if ( this.metalness !== undefined ) data.metalness = this.metalness;

		if ( this.sheen !== undefined ) data.sheen = this.sheen;
		if ( this.sheenColor && this.sheenColor.isColor ) data.sheenColor = this.sheenColor.getHex();
		if ( this.sheenRoughness !== undefined ) data.sheenRoughness = this.sheenRoughness;
		if ( this.emissive && this.emissive.isColor ) data.emissive = this.emissive.getHex();
		if ( this.emissiveIntensity !== undefined && this.emissiveIntensity !== 1 ) data.emissiveIntensity = this.emissiveIntensity;

		if ( this.specular && this.specular.isColor ) data.specular = this.specular.getHex();
		if ( this.specularIntensity !== undefined ) data.specularIntensity = this.specularIntensity;
		if ( this.specularColor && this.specularColor.isColor ) data.specularColor = this.specularColor.getHex();
		if ( this.shininess !== undefined ) data.shininess = this.shininess;
		if ( this.clearcoat !== undefined ) data.clearcoat = this.clearcoat;
		if ( this.clearcoatRoughness !== undefined ) data.clearcoatRoughness = this.clearcoatRoughness;

		if ( this.clearcoatMap && this.clearcoatMap.isTexture ) {

			data.clearcoatMap = this.clearcoatMap.toJSON( meta ).uuid;

		}

		if ( this.clearcoatRoughnessMap && this.clearcoatRoughnessMap.isTexture ) {

			data.clearcoatRoughnessMap = this.clearcoatRoughnessMap.toJSON( meta ).uuid;

		}

		if ( this.clearcoatNormalMap && this.clearcoatNormalMap.isTexture ) {

			data.clearcoatNormalMap = this.clearcoatNormalMap.toJSON( meta ).uuid;
			data.clearcoatNormalScale = this.clearcoatNormalScale.toArray();

		}

		if ( this.dispersion !== undefined ) data.dispersion = this.dispersion;

		if ( this.iridescence !== undefined ) data.iridescence = this.iridescence;
		if ( this.iridescenceIOR !== undefined ) data.iridescenceIOR = this.iridescenceIOR;
		if ( this.iridescenceThicknessRange !== undefined ) data.iridescenceThicknessRange = this.iridescenceThicknessRange;

		if ( this.iridescenceMap && this.iridescenceMap.isTexture ) {

			data.iridescenceMap = this.iridescenceMap.toJSON( meta ).uuid;

		}

		if ( this.iridescenceThicknessMap && this.iridescenceThicknessMap.isTexture ) {

			data.iridescenceThicknessMap = this.iridescenceThicknessMap.toJSON( meta ).uuid;

		}

		if ( this.anisotropy !== undefined ) data.anisotropy = this.anisotropy;
		if ( this.anisotropyRotation !== undefined ) data.anisotropyRotation = this.anisotropyRotation;

		if ( this.anisotropyMap && this.anisotropyMap.isTexture ) {

			data.anisotropyMap = this.anisotropyMap.toJSON( meta ).uuid;

		}

		if ( this.map && this.map.isTexture ) data.map = this.map.toJSON( meta ).uuid;
		if ( this.matcap && this.matcap.isTexture ) data.matcap = this.matcap.toJSON( meta ).uuid;
		if ( this.alphaMap && this.alphaMap.isTexture ) data.alphaMap = this.alphaMap.toJSON( meta ).uuid;

		if ( this.lightMap && this.lightMap.isTexture ) {

			data.lightMap = this.lightMap.toJSON( meta ).uuid;
			data.lightMapIntensity = this.lightMapIntensity;

		}

		if ( this.aoMap && this.aoMap.isTexture ) {

			data.aoMap = this.aoMap.toJSON( meta ).uuid;
			data.aoMapIntensity = this.aoMapIntensity;

		}

		if ( this.bumpMap && this.bumpMap.isTexture ) {

			data.bumpMap = this.bumpMap.toJSON( meta ).uuid;
			data.bumpScale = this.bumpScale;

		}

		if ( this.normalMap && this.normalMap.isTexture ) {

			data.normalMap = this.normalMap.toJSON( meta ).uuid;
			data.normalMapType = this.normalMapType;
			data.normalScale = this.normalScale.toArray();

		}

		if ( this.displacementMap && this.displacementMap.isTexture ) {

			data.displacementMap = this.displacementMap.toJSON( meta ).uuid;
			data.displacementScale = this.displacementScale;
			data.displacementBias = this.displacementBias;

		}

		if ( this.roughnessMap && this.roughnessMap.isTexture ) data.roughnessMap = this.roughnessMap.toJSON( meta ).uuid;
		if ( this.metalnessMap && this.metalnessMap.isTexture ) data.metalnessMap = this.metalnessMap.toJSON( meta ).uuid;

		if ( this.emissiveMap && this.emissiveMap.isTexture ) data.emissiveMap = this.emissiveMap.toJSON( meta ).uuid;
		if ( this.specularMap && this.specularMap.isTexture ) data.specularMap = this.specularMap.toJSON( meta ).uuid;
		if ( this.specularIntensityMap && this.specularIntensityMap.isTexture ) data.specularIntensityMap = this.specularIntensityMap.toJSON( meta ).uuid;
		if ( this.specularColorMap && this.specularColorMap.isTexture ) data.specularColorMap = this.specularColorMap.toJSON( meta ).uuid;

		if ( this.envMap && this.envMap.isTexture ) {

			data.envMap = this.envMap.toJSON( meta ).uuid;

			if ( this.combine !== undefined ) data.combine = this.combine;

		}

		if ( this.envMapRotation !== undefined ) data.envMapRotation = this.envMapRotation.toArray();
		if ( this.envMapIntensity !== undefined ) data.envMapIntensity = this.envMapIntensity;
		if ( this.reflectivity !== undefined ) data.reflectivity = this.reflectivity;
		if ( this.refractionRatio !== undefined ) data.refractionRatio = this.refractionRatio;

		if ( this.gradientMap && this.gradientMap.isTexture ) {

			data.gradientMap = this.gradientMap.toJSON( meta ).uuid;

		}

		if ( this.transmission !== undefined ) data.transmission = this.transmission;
		if ( this.transmissionMap && this.transmissionMap.isTexture ) data.transmissionMap = this.transmissionMap.toJSON( meta ).uuid;
		if ( this.thickness !== undefined ) data.thickness = this.thickness;
		if ( this.thicknessMap && this.thicknessMap.isTexture ) data.thicknessMap = this.thicknessMap.toJSON( meta ).uuid;
		if ( this.attenuationDistance !== undefined && this.attenuationDistance !== Infinity ) data.attenuationDistance = this.attenuationDistance;
		if ( this.attenuationColor !== undefined ) data.attenuationColor = this.attenuationColor.getHex();

		if ( this.size !== undefined ) data.size = this.size;
		if ( this.shadowSide !== null ) data.shadowSide = this.shadowSide;
		if ( this.sizeAttenuation !== undefined ) data.sizeAttenuation = this.sizeAttenuation;

		if ( this.blending !== NormalBlending ) data.blending = this.blending;
		if ( this.side !== FrontSide ) data.side = this.side;
		if ( this.vertexColors === true ) data.vertexColors = true;

		if ( this.opacity < 1 ) data.opacity = this.opacity;
		if ( this.transparent === true ) data.transparent = true;

		if ( this.blendSrc !== SrcAlphaFactor ) data.blendSrc = this.blendSrc;
		if ( this.blendDst !== OneMinusSrcAlphaFactor ) data.blendDst = this.blendDst;
		if ( this.blendEquation !== AddEquation ) data.blendEquation = this.blendEquation;
		if ( this.blendSrcAlpha !== null ) data.blendSrcAlpha = this.blendSrcAlpha;
		if ( this.blendDstAlpha !== null ) data.blendDstAlpha = this.blendDstAlpha;
		if ( this.blendEquationAlpha !== null ) data.blendEquationAlpha = this.blendEquationAlpha;
		if ( this.blendColor && this.blendColor.isColor ) data.blendColor = this.blendColor.getHex();
		if ( this.blendAlpha !== 0 ) data.blendAlpha = this.blendAlpha;

		if ( this.depthFunc !== LessEqualDepth ) data.depthFunc = this.depthFunc;
		if ( this.depthTest === false ) data.depthTest = this.depthTest;
		if ( this.depthWrite === false ) data.depthWrite = this.depthWrite;
		if ( this.colorWrite === false ) data.colorWrite = this.colorWrite;

		if ( this.stencilWriteMask !== 0xff ) data.stencilWriteMask = this.stencilWriteMask;
		if ( this.stencilFunc !== AlwaysStencilFunc ) data.stencilFunc = this.stencilFunc;
		if ( this.stencilRef !== 0 ) data.stencilRef = this.stencilRef;
		if ( this.stencilFuncMask !== 0xff ) data.stencilFuncMask = this.stencilFuncMask;
		if ( this.stencilFail !== KeepStencilOp ) data.stencilFail = this.stencilFail;
		if ( this.stencilZFail !== KeepStencilOp ) data.stencilZFail = this.stencilZFail;
		if ( this.stencilZPass !== KeepStencilOp ) data.stencilZPass = this.stencilZPass;
		if ( this.stencilWrite === true ) data.stencilWrite = this.stencilWrite;

		// rotation (SpriteMaterial)
		if ( this.rotation !== undefined && this.rotation !== 0 ) data.rotation = this.rotation;

		if ( this.polygonOffset === true ) data.polygonOffset = true;
		if ( this.polygonOffsetFactor !== 0 ) data.polygonOffsetFactor = this.polygonOffsetFactor;
		if ( this.polygonOffsetUnits !== 0 ) data.polygonOffsetUnits = this.polygonOffsetUnits;

		if ( this.linewidth !== undefined && this.linewidth !== 1 ) data.linewidth = this.linewidth;
		if ( this.dashSize !== undefined ) data.dashSize = this.dashSize;
		if ( this.gapSize !== undefined ) data.gapSize = this.gapSize;
		if ( this.scale !== undefined ) data.scale = this.scale;

		if ( this.dithering === true ) data.dithering = true;

		if ( this.alphaTest > 0 ) data.alphaTest = this.alphaTest;
		if ( this.alphaHash === true ) data.alphaHash = true;
		if ( this.alphaToCoverage === true ) data.alphaToCoverage = true;
		if ( this.premultipliedAlpha === true ) data.premultipliedAlpha = true;
		if ( this.forceSinglePass === true ) data.forceSinglePass = true;

		if ( this.wireframe === true ) data.wireframe = true;
		if ( this.wireframeLinewidth > 1 ) data.wireframeLinewidth = this.wireframeLinewidth;
		if ( this.wireframeLinecap !== 'round' ) data.wireframeLinecap = this.wireframeLinecap;
		if ( this.wireframeLinejoin !== 'round' ) data.wireframeLinejoin = this.wireframeLinejoin;

		if ( this.flatShading === true ) data.flatShading = true;

		if ( this.visible === false ) data.visible = false;

		if ( this.toneMapped === false ) data.toneMapped = false;

		if ( this.fog === false ) data.fog = false;

		if ( Object.keys( this.userData ).length > 0 ) data.userData = this.userData;

		// TODO: Copied from Object3D.toJSON

		function extractFromCache( cache ) {

			const values = [];

			for ( const key in cache ) {

				const data = cache[ key ];
				delete data.metadata;
				values.push( data );

			}

			return values;

		}

		if ( isRootObject ) {

			const textures = extractFromCache( meta.textures );
			const images = extractFromCache( meta.images );

			if ( textures.length > 0 ) data.textures = textures;
			if ( images.length > 0 ) data.images = images;

		}

		return data;

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		this.name = source.name;

		this.blending = source.blending;
		this.side = source.side;
		this.vertexColors = source.vertexColors;

		this.opacity = source.opacity;
		this.transparent = source.transparent;

		this.blendSrc = source.blendSrc;
		this.blendDst = source.blendDst;
		this.blendEquation = source.blendEquation;
		this.blendSrcAlpha = source.blendSrcAlpha;
		this.blendDstAlpha = source.blendDstAlpha;
		this.blendEquationAlpha = source.blendEquationAlpha;
		this.blendColor.copy( source.blendColor );
		this.blendAlpha = source.blendAlpha;

		this.depthFunc = source.depthFunc;
		this.depthTest = source.depthTest;
		this.depthWrite = source.depthWrite;

		this.stencilWriteMask = source.stencilWriteMask;
		this.stencilFunc = source.stencilFunc;
		this.stencilRef = source.stencilRef;
		this.stencilFuncMask = source.stencilFuncMask;
		this.stencilFail = source.stencilFail;
		this.stencilZFail = source.stencilZFail;
		this.stencilZPass = source.stencilZPass;
		this.stencilWrite = source.stencilWrite;

		const srcPlanes = source.clippingPlanes;
		let dstPlanes = null;

		if ( srcPlanes !== null ) {

			const n = srcPlanes.length;
			dstPlanes = new Array( n );

			for ( let i = 0; i !== n; ++ i ) {

				dstPlanes[ i ] = srcPlanes[ i ].clone();

			}

		}

		this.clippingPlanes = dstPlanes;
		this.clipIntersection = source.clipIntersection;
		this.clipShadows = source.clipShadows;

		this.shadowSide = source.shadowSide;

		this.colorWrite = source.colorWrite;

		this.precision = source.precision;

		this.polygonOffset = source.polygonOffset;
		this.polygonOffsetFactor = source.polygonOffsetFactor;
		this.polygonOffsetUnits = source.polygonOffsetUnits;

		this.dithering = source.dithering;

		this.alphaTest = source.alphaTest;
		this.alphaHash = source.alphaHash;
		this.alphaToCoverage = source.alphaToCoverage;
		this.premultipliedAlpha = source.premultipliedAlpha;
		this.forceSinglePass = source.forceSinglePass;

		this.visible = source.visible;

		this.toneMapped = source.toneMapped;

		this.userData = JSON.parse( JSON.stringify( source.userData ) );

		return this;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

	}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	onBuild( /* shaderobject, renderer */ ) {

		console.warn( 'Material: onBuild() has been removed.' ); // @deprecated, r166

	}

	onBeforeRender( /* renderer, scene, camera, geometry, object, group */ ) {

		console.warn( 'Material: onBeforeRender() has been removed.' ); // @deprecated, r166

	}


}

const _vector$9 = /*@__PURE__*/ new Vector3();
const _vector2$1$1 = /*@__PURE__*/ new Vector2();

class BufferAttribute {

	constructor( array, itemSize, normalized = false ) {

		if ( Array.isArray( array ) ) {

			throw new TypeError( 'THREE.BufferAttribute: array should be a Typed Array.' );

		}

		this.isBufferAttribute = true;

		this.name = '';

		this.array = array;
		this.itemSize = itemSize;
		this.count = array !== undefined ? array.length / itemSize : 0;
		this.normalized = normalized;

		this.usage = StaticDrawUsage;
		this._updateRange = { offset: 0, count: - 1 };
		this.updateRanges = [];
		this.gpuType = FloatType;

		this.version = 0;

	}

	onUploadCallback() {}

	set needsUpdate( value ) {

		if ( value === true ) this.version ++;

	}

	get updateRange() {

		warnOnce( 'THREE.BufferAttribute: updateRange() is deprecated and will be removed in r169. Use addUpdateRange() instead.' ); // @deprecated, r159
		return this._updateRange;

	}

	setUsage( value ) {

		this.usage = value;

		return this;

	}

	addUpdateRange( start, count ) {

		this.updateRanges.push( { start, count } );

	}

	clearUpdateRanges() {

		this.updateRanges.length = 0;

	}

	copy( source ) {

		this.name = source.name;
		this.array = new source.array.constructor( source.array );
		this.itemSize = source.itemSize;
		this.count = source.count;
		this.normalized = source.normalized;

		this.usage = source.usage;
		this.gpuType = source.gpuType;

		return this;

	}

	copyAt( index1, attribute, index2 ) {

		index1 *= this.itemSize;
		index2 *= attribute.itemSize;

		for ( let i = 0, l = this.itemSize; i < l; i ++ ) {

			this.array[ index1 + i ] = attribute.array[ index2 + i ];

		}

		return this;

	}

	copyArray( array ) {

		this.array.set( array );

		return this;

	}

	applyMatrix3( m ) {

		if ( this.itemSize === 2 ) {

			for ( let i = 0, l = this.count; i < l; i ++ ) {

				_vector2$1$1.fromBufferAttribute( this, i );
				_vector2$1$1.applyMatrix3( m );

				this.setXY( i, _vector2$1$1.x, _vector2$1$1.y );

			}

		} else if ( this.itemSize === 3 ) {

			for ( let i = 0, l = this.count; i < l; i ++ ) {

				_vector$9.fromBufferAttribute( this, i );
				_vector$9.applyMatrix3( m );

				this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

			}

		}

		return this;

	}

	applyMatrix4( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$9.fromBufferAttribute( this, i );

			_vector$9.applyMatrix4( m );

			this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

		}

		return this;

	}

	applyNormalMatrix( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$9.fromBufferAttribute( this, i );

			_vector$9.applyNormalMatrix( m );

			this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

		}

		return this;

	}

	transformDirection( m ) {

		for ( let i = 0, l = this.count; i < l; i ++ ) {

			_vector$9.fromBufferAttribute( this, i );

			_vector$9.transformDirection( m );

			this.setXYZ( i, _vector$9.x, _vector$9.y, _vector$9.z );

		}

		return this;

	}

	set( value, offset = 0 ) {

		// Matching BufferAttribute constructor, do not normalize the array.
		this.array.set( value, offset );

		return this;

	}

	getComponent( index, component ) {

		let value = this.array[ index * this.itemSize + component ];

		if ( this.normalized ) value = denormalize( value, this.array );

		return value;

	}

	setComponent( index, component, value ) {

		if ( this.normalized ) value = normalize( value, this.array );

		this.array[ index * this.itemSize + component ] = value;

		return this;

	}

	getX( index ) {

		let x = this.array[ index * this.itemSize ];

		if ( this.normalized ) x = denormalize( x, this.array );

		return x;

	}

	setX( index, x ) {

		if ( this.normalized ) x = normalize( x, this.array );

		this.array[ index * this.itemSize ] = x;

		return this;

	}

	getY( index ) {

		let y = this.array[ index * this.itemSize + 1 ];

		if ( this.normalized ) y = denormalize( y, this.array );

		return y;

	}

	setY( index, y ) {

		if ( this.normalized ) y = normalize( y, this.array );

		this.array[ index * this.itemSize + 1 ] = y;

		return this;

	}

	getZ( index ) {

		let z = this.array[ index * this.itemSize + 2 ];

		if ( this.normalized ) z = denormalize( z, this.array );

		return z;

	}

	setZ( index, z ) {

		if ( this.normalized ) z = normalize( z, this.array );

		this.array[ index * this.itemSize + 2 ] = z;

		return this;

	}

	getW( index ) {

		let w = this.array[ index * this.itemSize + 3 ];

		if ( this.normalized ) w = denormalize( w, this.array );

		return w;

	}

	setW( index, w ) {

		if ( this.normalized ) w = normalize( w, this.array );

		this.array[ index * this.itemSize + 3 ] = w;

		return this;

	}

	setXY( index, x, y ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );

		}

		this.array[ index + 0 ] = x;
		this.array[ index + 1 ] = y;

		return this;

	}

	setXYZ( index, x, y, z ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );

		}

		this.array[ index + 0 ] = x;
		this.array[ index + 1 ] = y;
		this.array[ index + 2 ] = z;

		return this;

	}

	setXYZW( index, x, y, z, w ) {

		index *= this.itemSize;

		if ( this.normalized ) {

			x = normalize( x, this.array );
			y = normalize( y, this.array );
			z = normalize( z, this.array );
			w = normalize( w, this.array );

		}

		this.array[ index + 0 ] = x;
		this.array[ index + 1 ] = y;
		this.array[ index + 2 ] = z;
		this.array[ index + 3 ] = w;

		return this;

	}

	onUpload( callback ) {

		this.onUploadCallback = callback;

		return this;

	}

	clone() {

		return new this.constructor( this.array, this.itemSize ).copy( this );

	}

	toJSON() {

		const data = {
			itemSize: this.itemSize,
			type: this.array.constructor.name,
			array: Array.from( this.array ),
			normalized: this.normalized
		};

		if ( this.name !== '' ) data.name = this.name;
		if ( this.usage !== StaticDrawUsage ) data.usage = this.usage;

		return data;

	}

}

class Uint16BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Uint16Array( array ), itemSize, normalized );

	}

}

class Uint32BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Uint32Array( array ), itemSize, normalized );

	}

}


class Float32BufferAttribute extends BufferAttribute {

	constructor( array, itemSize, normalized ) {

		super( new Float32Array( array ), itemSize, normalized );

	}

}

let _id$2 = 0;

const _m1$2 = /*@__PURE__*/ new Matrix4();
const _obj = /*@__PURE__*/ new Object3D();
const _offset = /*@__PURE__*/ new Vector3();
const _box$2 = /*@__PURE__*/ new Box3();
const _boxMorphTargets = /*@__PURE__*/ new Box3();
const _vector$8 = /*@__PURE__*/ new Vector3();

class BufferGeometry extends EventDispatcher {

	constructor() {

		super();

		this.isBufferGeometry = true;

		Object.defineProperty( this, 'id', { value: _id$2 ++ } );

		this.uuid = generateUUID();

		this.name = '';
		this.type = 'BufferGeometry';

		this.index = null;
		this.attributes = {};

		this.morphAttributes = {};
		this.morphTargetsRelative = false;

		this.groups = [];

		this.boundingBox = null;
		this.boundingSphere = null;

		this.drawRange = { start: 0, count: Infinity };

		this.userData = {};

	}

	getIndex() {

		return this.index;

	}

	setIndex( index ) {

		if ( Array.isArray( index ) ) {

			this.index = new ( arrayNeedsUint32( index ) ? Uint32BufferAttribute : Uint16BufferAttribute )( index, 1 );

		} else {

			this.index = index;

		}

		return this;

	}

	getAttribute( name ) {

		return this.attributes[ name ];

	}

	setAttribute( name, attribute ) {

		this.attributes[ name ] = attribute;

		return this;

	}

	deleteAttribute( name ) {

		delete this.attributes[ name ];

		return this;

	}

	hasAttribute( name ) {

		return this.attributes[ name ] !== undefined;

	}

	addGroup( start, count, materialIndex = 0 ) {

		this.groups.push( {

			start: start,
			count: count,
			materialIndex: materialIndex

		} );

	}

	clearGroups() {

		this.groups = [];

	}

	setDrawRange( start, count ) {

		this.drawRange.start = start;
		this.drawRange.count = count;

	}

	applyMatrix4( matrix ) {

		const position = this.attributes.position;

		if ( position !== undefined ) {

			position.applyMatrix4( matrix );

			position.needsUpdate = true;

		}

		const normal = this.attributes.normal;

		if ( normal !== undefined ) {

			const normalMatrix = new Matrix3().getNormalMatrix( matrix );

			normal.applyNormalMatrix( normalMatrix );

			normal.needsUpdate = true;

		}

		const tangent = this.attributes.tangent;

		if ( tangent !== undefined ) {

			tangent.transformDirection( matrix );

			tangent.needsUpdate = true;

		}

		if ( this.boundingBox !== null ) {

			this.computeBoundingBox();

		}

		if ( this.boundingSphere !== null ) {

			this.computeBoundingSphere();

		}

		return this;

	}

	applyQuaternion( q ) {

		_m1$2.makeRotationFromQuaternion( q );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	rotateX( angle ) {

		// rotate geometry around world x-axis

		_m1$2.makeRotationX( angle );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	rotateY( angle ) {

		// rotate geometry around world y-axis

		_m1$2.makeRotationY( angle );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	rotateZ( angle ) {

		// rotate geometry around world z-axis

		_m1$2.makeRotationZ( angle );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	translate( x, y, z ) {

		// translate geometry

		_m1$2.makeTranslation( x, y, z );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	scale( x, y, z ) {

		// scale geometry

		_m1$2.makeScale( x, y, z );

		this.applyMatrix4( _m1$2 );

		return this;

	}

	lookAt( vector ) {

		_obj.lookAt( vector );

		_obj.updateMatrix();

		this.applyMatrix4( _obj.matrix );

		return this;

	}

	center() {

		this.computeBoundingBox();

		this.boundingBox.getCenter( _offset ).negate();

		this.translate( _offset.x, _offset.y, _offset.z );

		return this;

	}

	setFromPoints( points ) {

		const position = [];

		for ( let i = 0, l = points.length; i < l; i ++ ) {

			const point = points[ i ];
			position.push( point.x, point.y, point.z || 0 );

		}

		this.setAttribute( 'position', new Float32BufferAttribute( position, 3 ) );

		return this;

	}

	computeBoundingBox() {

		if ( this.boundingBox === null ) {

			this.boundingBox = new Box3();

		}

		const position = this.attributes.position;
		const morphAttributesPosition = this.morphAttributes.position;

		if ( position && position.isGLBufferAttribute ) {

			console.error( 'THREE.BufferGeometry.computeBoundingBox(): GLBufferAttribute requires a manual bounding box.', this );

			this.boundingBox.set(
				new Vector3( - Infinity, - Infinity, - Infinity ),
				new Vector3( + Infinity, + Infinity, + Infinity )
			);

			return;

		}

		if ( position !== undefined ) {

			this.boundingBox.setFromBufferAttribute( position );

			// process morph attributes if present

			if ( morphAttributesPosition ) {

				for ( let i = 0, il = morphAttributesPosition.length; i < il; i ++ ) {

					const morphAttribute = morphAttributesPosition[ i ];
					_box$2.setFromBufferAttribute( morphAttribute );

					if ( this.morphTargetsRelative ) {

						_vector$8.addVectors( this.boundingBox.min, _box$2.min );
						this.boundingBox.expandByPoint( _vector$8 );

						_vector$8.addVectors( this.boundingBox.max, _box$2.max );
						this.boundingBox.expandByPoint( _vector$8 );

					} else {

						this.boundingBox.expandByPoint( _box$2.min );
						this.boundingBox.expandByPoint( _box$2.max );

					}

				}

			}

		} else {

			this.boundingBox.makeEmpty();

		}

		if ( isNaN( this.boundingBox.min.x ) || isNaN( this.boundingBox.min.y ) || isNaN( this.boundingBox.min.z ) ) {

			console.error( 'THREE.BufferGeometry.computeBoundingBox(): Computed min/max have NaN values. The "position" attribute is likely to have NaN values.', this );

		}

	}

	computeBoundingSphere() {

		if ( this.boundingSphere === null ) {

			this.boundingSphere = new Sphere$1();

		}

		const position = this.attributes.position;
		const morphAttributesPosition = this.morphAttributes.position;

		if ( position && position.isGLBufferAttribute ) {

			console.error( 'THREE.BufferGeometry.computeBoundingSphere(): GLBufferAttribute requires a manual bounding sphere.', this );

			this.boundingSphere.set( new Vector3(), Infinity );

			return;

		}

		if ( position ) {

			// first, find the center of the bounding sphere

			const center = this.boundingSphere.center;

			_box$2.setFromBufferAttribute( position );

			// process morph attributes if present

			if ( morphAttributesPosition ) {

				for ( let i = 0, il = morphAttributesPosition.length; i < il; i ++ ) {

					const morphAttribute = morphAttributesPosition[ i ];
					_boxMorphTargets.setFromBufferAttribute( morphAttribute );

					if ( this.morphTargetsRelative ) {

						_vector$8.addVectors( _box$2.min, _boxMorphTargets.min );
						_box$2.expandByPoint( _vector$8 );

						_vector$8.addVectors( _box$2.max, _boxMorphTargets.max );
						_box$2.expandByPoint( _vector$8 );

					} else {

						_box$2.expandByPoint( _boxMorphTargets.min );
						_box$2.expandByPoint( _boxMorphTargets.max );

					}

				}

			}

			_box$2.getCenter( center );

			// second, try to find a boundingSphere with a radius smaller than the
			// boundingSphere of the boundingBox: sqrt(3) smaller in the best case

			let maxRadiusSq = 0;

			for ( let i = 0, il = position.count; i < il; i ++ ) {

				_vector$8.fromBufferAttribute( position, i );

				maxRadiusSq = Math.max( maxRadiusSq, center.distanceToSquared( _vector$8 ) );

			}

			// process morph attributes if present

			if ( morphAttributesPosition ) {

				for ( let i = 0, il = morphAttributesPosition.length; i < il; i ++ ) {

					const morphAttribute = morphAttributesPosition[ i ];
					const morphTargetsRelative = this.morphTargetsRelative;

					for ( let j = 0, jl = morphAttribute.count; j < jl; j ++ ) {

						_vector$8.fromBufferAttribute( morphAttribute, j );

						if ( morphTargetsRelative ) {

							_offset.fromBufferAttribute( position, j );
							_vector$8.add( _offset );

						}

						maxRadiusSq = Math.max( maxRadiusSq, center.distanceToSquared( _vector$8 ) );

					}

				}

			}

			this.boundingSphere.radius = Math.sqrt( maxRadiusSq );

			if ( isNaN( this.boundingSphere.radius ) ) {

				console.error( 'THREE.BufferGeometry.computeBoundingSphere(): Computed radius is NaN. The "position" attribute is likely to have NaN values.', this );

			}

		}

	}

	computeTangents() {

		const index = this.index;
		const attributes = this.attributes;

		// based on http://www.terathon.com/code/tangent.html
		// (per vertex tangents)

		if ( index === null ||
			 attributes.position === undefined ||
			 attributes.normal === undefined ||
			 attributes.uv === undefined ) {

			console.error( 'THREE.BufferGeometry: .computeTangents() failed. Missing required attributes (index, position, normal or uv)' );
			return;

		}

		const positionAttribute = attributes.position;
		const normalAttribute = attributes.normal;
		const uvAttribute = attributes.uv;

		if ( this.hasAttribute( 'tangent' ) === false ) {

			this.setAttribute( 'tangent', new BufferAttribute( new Float32Array( 4 * positionAttribute.count ), 4 ) );

		}

		const tangentAttribute = this.getAttribute( 'tangent' );

		const tan1 = [], tan2 = [];

		for ( let i = 0; i < positionAttribute.count; i ++ ) {

			tan1[ i ] = new Vector3();
			tan2[ i ] = new Vector3();

		}

		const vA = new Vector3(),
			vB = new Vector3(),
			vC = new Vector3(),

			uvA = new Vector2(),
			uvB = new Vector2(),
			uvC = new Vector2(),

			sdir = new Vector3(),
			tdir = new Vector3();

		function handleTriangle( a, b, c ) {

			vA.fromBufferAttribute( positionAttribute, a );
			vB.fromBufferAttribute( positionAttribute, b );
			vC.fromBufferAttribute( positionAttribute, c );

			uvA.fromBufferAttribute( uvAttribute, a );
			uvB.fromBufferAttribute( uvAttribute, b );
			uvC.fromBufferAttribute( uvAttribute, c );

			vB.sub( vA );
			vC.sub( vA );

			uvB.sub( uvA );
			uvC.sub( uvA );

			const r = 1.0 / ( uvB.x * uvC.y - uvC.x * uvB.y );

			// silently ignore degenerate uv triangles having coincident or colinear vertices

			if ( ! isFinite( r ) ) return;

			sdir.copy( vB ).multiplyScalar( uvC.y ).addScaledVector( vC, - uvB.y ).multiplyScalar( r );
			tdir.copy( vC ).multiplyScalar( uvB.x ).addScaledVector( vB, - uvC.x ).multiplyScalar( r );

			tan1[ a ].add( sdir );
			tan1[ b ].add( sdir );
			tan1[ c ].add( sdir );

			tan2[ a ].add( tdir );
			tan2[ b ].add( tdir );
			tan2[ c ].add( tdir );

		}

		let groups = this.groups;

		if ( groups.length === 0 ) {

			groups = [ {
				start: 0,
				count: index.count
			} ];

		}

		for ( let i = 0, il = groups.length; i < il; ++ i ) {

			const group = groups[ i ];

			const start = group.start;
			const count = group.count;

			for ( let j = start, jl = start + count; j < jl; j += 3 ) {

				handleTriangle(
					index.getX( j + 0 ),
					index.getX( j + 1 ),
					index.getX( j + 2 )
				);

			}

		}

		const tmp = new Vector3(), tmp2 = new Vector3();
		const n = new Vector3(), n2 = new Vector3();

		function handleVertex( v ) {

			n.fromBufferAttribute( normalAttribute, v );
			n2.copy( n );

			const t = tan1[ v ];

			// Gram-Schmidt orthogonalize

			tmp.copy( t );
			tmp.sub( n.multiplyScalar( n.dot( t ) ) ).normalize();

			// Calculate handedness

			tmp2.crossVectors( n2, t );
			const test = tmp2.dot( tan2[ v ] );
			const w = ( test < 0.0 ) ? - 1.0 : 1.0;

			tangentAttribute.setXYZW( v, tmp.x, tmp.y, tmp.z, w );

		}

		for ( let i = 0, il = groups.length; i < il; ++ i ) {

			const group = groups[ i ];

			const start = group.start;
			const count = group.count;

			for ( let j = start, jl = start + count; j < jl; j += 3 ) {

				handleVertex( index.getX( j + 0 ) );
				handleVertex( index.getX( j + 1 ) );
				handleVertex( index.getX( j + 2 ) );

			}

		}

	}

	computeVertexNormals() {

		const index = this.index;
		const positionAttribute = this.getAttribute( 'position' );

		if ( positionAttribute !== undefined ) {

			let normalAttribute = this.getAttribute( 'normal' );

			if ( normalAttribute === undefined ) {

				normalAttribute = new BufferAttribute( new Float32Array( positionAttribute.count * 3 ), 3 );
				this.setAttribute( 'normal', normalAttribute );

			} else {

				// reset existing normals to zero

				for ( let i = 0, il = normalAttribute.count; i < il; i ++ ) {

					normalAttribute.setXYZ( i, 0, 0, 0 );

				}

			}

			const pA = new Vector3(), pB = new Vector3(), pC = new Vector3();
			const nA = new Vector3(), nB = new Vector3(), nC = new Vector3();
			const cb = new Vector3(), ab = new Vector3();

			// indexed elements

			if ( index ) {

				for ( let i = 0, il = index.count; i < il; i += 3 ) {

					const vA = index.getX( i + 0 );
					const vB = index.getX( i + 1 );
					const vC = index.getX( i + 2 );

					pA.fromBufferAttribute( positionAttribute, vA );
					pB.fromBufferAttribute( positionAttribute, vB );
					pC.fromBufferAttribute( positionAttribute, vC );

					cb.subVectors( pC, pB );
					ab.subVectors( pA, pB );
					cb.cross( ab );

					nA.fromBufferAttribute( normalAttribute, vA );
					nB.fromBufferAttribute( normalAttribute, vB );
					nC.fromBufferAttribute( normalAttribute, vC );

					nA.add( cb );
					nB.add( cb );
					nC.add( cb );

					normalAttribute.setXYZ( vA, nA.x, nA.y, nA.z );
					normalAttribute.setXYZ( vB, nB.x, nB.y, nB.z );
					normalAttribute.setXYZ( vC, nC.x, nC.y, nC.z );

				}

			} else {

				// non-indexed elements (unconnected triangle soup)

				for ( let i = 0, il = positionAttribute.count; i < il; i += 3 ) {

					pA.fromBufferAttribute( positionAttribute, i + 0 );
					pB.fromBufferAttribute( positionAttribute, i + 1 );
					pC.fromBufferAttribute( positionAttribute, i + 2 );

					cb.subVectors( pC, pB );
					ab.subVectors( pA, pB );
					cb.cross( ab );

					normalAttribute.setXYZ( i + 0, cb.x, cb.y, cb.z );
					normalAttribute.setXYZ( i + 1, cb.x, cb.y, cb.z );
					normalAttribute.setXYZ( i + 2, cb.x, cb.y, cb.z );

				}

			}

			this.normalizeNormals();

			normalAttribute.needsUpdate = true;

		}

	}

	normalizeNormals() {

		const normals = this.attributes.normal;

		for ( let i = 0, il = normals.count; i < il; i ++ ) {

			_vector$8.fromBufferAttribute( normals, i );

			_vector$8.normalize();

			normals.setXYZ( i, _vector$8.x, _vector$8.y, _vector$8.z );

		}

	}

	toNonIndexed() {

		function convertBufferAttribute( attribute, indices ) {

			const array = attribute.array;
			const itemSize = attribute.itemSize;
			const normalized = attribute.normalized;

			const array2 = new array.constructor( indices.length * itemSize );

			let index = 0, index2 = 0;

			for ( let i = 0, l = indices.length; i < l; i ++ ) {

				if ( attribute.isInterleavedBufferAttribute ) {

					index = indices[ i ] * attribute.data.stride + attribute.offset;

				} else {

					index = indices[ i ] * itemSize;

				}

				for ( let j = 0; j < itemSize; j ++ ) {

					array2[ index2 ++ ] = array[ index ++ ];

				}

			}

			return new BufferAttribute( array2, itemSize, normalized );

		}

		//

		if ( this.index === null ) {

			console.warn( 'THREE.BufferGeometry.toNonIndexed(): BufferGeometry is already non-indexed.' );
			return this;

		}

		const geometry2 = new BufferGeometry();

		const indices = this.index.array;
		const attributes = this.attributes;

		// attributes

		for ( const name in attributes ) {

			const attribute = attributes[ name ];

			const newAttribute = convertBufferAttribute( attribute, indices );

			geometry2.setAttribute( name, newAttribute );

		}

		// morph attributes

		const morphAttributes = this.morphAttributes;

		for ( const name in morphAttributes ) {

			const morphArray = [];
			const morphAttribute = morphAttributes[ name ]; // morphAttribute: array of Float32BufferAttributes

			for ( let i = 0, il = morphAttribute.length; i < il; i ++ ) {

				const attribute = morphAttribute[ i ];

				const newAttribute = convertBufferAttribute( attribute, indices );

				morphArray.push( newAttribute );

			}

			geometry2.morphAttributes[ name ] = morphArray;

		}

		geometry2.morphTargetsRelative = this.morphTargetsRelative;

		// groups

		const groups = this.groups;

		for ( let i = 0, l = groups.length; i < l; i ++ ) {

			const group = groups[ i ];
			geometry2.addGroup( group.start, group.count, group.materialIndex );

		}

		return geometry2;

	}

	toJSON() {

		const data = {
			metadata: {
				version: 4.6,
				type: 'BufferGeometry',
				generator: 'BufferGeometry.toJSON'
			}
		};

		// standard BufferGeometry serialization

		data.uuid = this.uuid;
		data.type = this.type;
		if ( this.name !== '' ) data.name = this.name;
		if ( Object.keys( this.userData ).length > 0 ) data.userData = this.userData;

		if ( this.parameters !== undefined ) {

			const parameters = this.parameters;

			for ( const key in parameters ) {

				if ( parameters[ key ] !== undefined ) data[ key ] = parameters[ key ];

			}

			return data;

		}

		// for simplicity the code assumes attributes are not shared across geometries, see #15811

		data.data = { attributes: {} };

		const index = this.index;

		if ( index !== null ) {

			data.data.index = {
				type: index.array.constructor.name,
				array: Array.prototype.slice.call( index.array )
			};

		}

		const attributes = this.attributes;

		for ( const key in attributes ) {

			const attribute = attributes[ key ];

			data.data.attributes[ key ] = attribute.toJSON( data.data );

		}

		const morphAttributes = {};
		let hasMorphAttributes = false;

		for ( const key in this.morphAttributes ) {

			const attributeArray = this.morphAttributes[ key ];

			const array = [];

			for ( let i = 0, il = attributeArray.length; i < il; i ++ ) {

				const attribute = attributeArray[ i ];

				array.push( attribute.toJSON( data.data ) );

			}

			if ( array.length > 0 ) {

				morphAttributes[ key ] = array;

				hasMorphAttributes = true;

			}

		}

		if ( hasMorphAttributes ) {

			data.data.morphAttributes = morphAttributes;
			data.data.morphTargetsRelative = this.morphTargetsRelative;

		}

		const groups = this.groups;

		if ( groups.length > 0 ) {

			data.data.groups = JSON.parse( JSON.stringify( groups ) );

		}

		const boundingSphere = this.boundingSphere;

		if ( boundingSphere !== null ) {

			data.data.boundingSphere = {
				center: boundingSphere.center.toArray(),
				radius: boundingSphere.radius
			};

		}

		return data;

	}

	clone() {

		return new this.constructor().copy( this );

	}

	copy( source ) {

		// reset

		this.index = null;
		this.attributes = {};
		this.morphAttributes = {};
		this.groups = [];
		this.boundingBox = null;
		this.boundingSphere = null;

		// used for storing cloned, shared data

		const data = {};

		// name

		this.name = source.name;

		// index

		const index = source.index;

		if ( index !== null ) {

			this.setIndex( index.clone( data ) );

		}

		// attributes

		const attributes = source.attributes;

		for ( const name in attributes ) {

			const attribute = attributes[ name ];
			this.setAttribute( name, attribute.clone( data ) );

		}

		// morph attributes

		const morphAttributes = source.morphAttributes;

		for ( const name in morphAttributes ) {

			const array = [];
			const morphAttribute = morphAttributes[ name ]; // morphAttribute: array of Float32BufferAttributes

			for ( let i = 0, l = morphAttribute.length; i < l; i ++ ) {

				array.push( morphAttribute[ i ].clone( data ) );

			}

			this.morphAttributes[ name ] = array;

		}

		this.morphTargetsRelative = source.morphTargetsRelative;

		// groups

		const groups = source.groups;

		for ( let i = 0, l = groups.length; i < l; i ++ ) {

			const group = groups[ i ];
			this.addGroup( group.start, group.count, group.materialIndex );

		}

		// bounding box

		const boundingBox = source.boundingBox;

		if ( boundingBox !== null ) {

			this.boundingBox = boundingBox.clone();

		}

		// bounding sphere

		const boundingSphere = source.boundingSphere;

		if ( boundingSphere !== null ) {

			this.boundingSphere = boundingSphere.clone();

		}

		// draw range

		this.drawRange.start = source.drawRange.start;
		this.drawRange.count = source.drawRange.count;

		// user data

		this.userData = source.userData;

		return this;

	}

	dispose() {

		this.dispatchEvent( { type: 'dispose' } );

	}

}

class LineBasicMaterial extends Material {

	constructor( parameters ) {

		super();

		this.isLineBasicMaterial = true;

		this.type = 'LineBasicMaterial';

		this.color = new Color( 0xffffff );

		this.map = null;

		this.linewidth = 1;
		this.linecap = 'round';
		this.linejoin = 'round';

		this.fog = true;

		this.setValues( parameters );

	}


	copy( source ) {

		super.copy( source );

		this.color.copy( source.color );

		this.map = source.map;

		this.linewidth = source.linewidth;
		this.linecap = source.linecap;
		this.linejoin = source.linejoin;

		this.fog = source.fog;

		return this;

	}

}

const _vStart = /*@__PURE__*/ new Vector3();
const _vEnd = /*@__PURE__*/ new Vector3();

const _inverseMatrix$1 = /*@__PURE__*/ new Matrix4();
const _ray$1 = /*@__PURE__*/ new Ray$1();
const _sphere$1 = /*@__PURE__*/ new Sphere$1();

const _intersectPointOnRay = /*@__PURE__*/ new Vector3();
const _intersectPointOnSegment = /*@__PURE__*/ new Vector3();

let Line$1=class Line extends Object3D {

	constructor( geometry = new BufferGeometry(), material = new LineBasicMaterial() ) {

		super();

		this.isLine = true;

		this.type = 'Line';

		this.geometry = geometry;
		this.material = material;

		this.updateMorphTargets();

	}

	copy( source, recursive ) {

		super.copy( source, recursive );

		this.material = Array.isArray( source.material ) ? source.material.slice() : source.material;
		this.geometry = source.geometry;

		return this;

	}

	computeLineDistances() {

		const geometry = this.geometry;

		// we assume non-indexed geometry

		if ( geometry.index === null ) {

			const positionAttribute = geometry.attributes.position;
			const lineDistances = [ 0 ];

			for ( let i = 1, l = positionAttribute.count; i < l; i ++ ) {

				_vStart.fromBufferAttribute( positionAttribute, i - 1 );
				_vEnd.fromBufferAttribute( positionAttribute, i );

				lineDistances[ i ] = lineDistances[ i - 1 ];
				lineDistances[ i ] += _vStart.distanceTo( _vEnd );

			}

			geometry.setAttribute( 'lineDistance', new Float32BufferAttribute( lineDistances, 1 ) );

		} else {

			console.warn( 'THREE.Line.computeLineDistances(): Computation only possible with non-indexed BufferGeometry.' );

		}

		return this;

	}

	raycast( raycaster, intersects ) {

		const geometry = this.geometry;
		const matrixWorld = this.matrixWorld;
		const threshold = raycaster.params.Line.threshold;
		const drawRange = geometry.drawRange;

		// Checking boundingSphere distance to ray

		if ( geometry.boundingSphere === null ) geometry.computeBoundingSphere();

		_sphere$1.copy( geometry.boundingSphere );
		_sphere$1.applyMatrix4( matrixWorld );
		_sphere$1.radius += threshold;

		if ( raycaster.ray.intersectsSphere( _sphere$1 ) === false ) return;

		//

		_inverseMatrix$1.copy( matrixWorld ).invert();
		_ray$1.copy( raycaster.ray ).applyMatrix4( _inverseMatrix$1 );

		const localThreshold = threshold / ( ( this.scale.x + this.scale.y + this.scale.z ) / 3 );
		const localThresholdSq = localThreshold * localThreshold;

		const step = this.isLineSegments ? 2 : 1;

		const index = geometry.index;
		const attributes = geometry.attributes;
		const positionAttribute = attributes.position;

		if ( index !== null ) {

			const start = Math.max( 0, drawRange.start );
			const end = Math.min( index.count, ( drawRange.start + drawRange.count ) );

			for ( let i = start, l = end - 1; i < l; i += step ) {

				const a = index.getX( i );
				const b = index.getX( i + 1 );

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, a, b );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

			if ( this.isLineLoop ) {

				const a = index.getX( end - 1 );
				const b = index.getX( start );

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, a, b );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

		} else {

			const start = Math.max( 0, drawRange.start );
			const end = Math.min( positionAttribute.count, ( drawRange.start + drawRange.count ) );

			for ( let i = start, l = end - 1; i < l; i += step ) {

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, i, i + 1 );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

			if ( this.isLineLoop ) {

				const intersect = checkIntersection( this, raycaster, _ray$1, localThresholdSq, end - 1, start );

				if ( intersect ) {

					intersects.push( intersect );

				}

			}

		}

	}

	updateMorphTargets() {

		const geometry = this.geometry;

		const morphAttributes = geometry.morphAttributes;
		const keys = Object.keys( morphAttributes );

		if ( keys.length > 0 ) {

			const morphAttribute = morphAttributes[ keys[ 0 ] ];

			if ( morphAttribute !== undefined ) {

				this.morphTargetInfluences = [];
				this.morphTargetDictionary = {};

				for ( let m = 0, ml = morphAttribute.length; m < ml; m ++ ) {

					const name = morphAttribute[ m ].name || String( m );

					this.morphTargetInfluences.push( 0 );
					this.morphTargetDictionary[ name ] = m;

				}

			}

		}

	}

};

function checkIntersection( object, raycaster, ray, thresholdSq, a, b ) {

	const positionAttribute = object.geometry.attributes.position;

	_vStart.fromBufferAttribute( positionAttribute, a );
	_vEnd.fromBufferAttribute( positionAttribute, b );

	const distSq = ray.distanceSqToSegment( _vStart, _vEnd, _intersectPointOnRay, _intersectPointOnSegment );

	if ( distSq > thresholdSq ) return;

	_intersectPointOnRay.applyMatrix4( object.matrixWorld ); // Move back to world space for distance calculation

	const distance = raycaster.ray.origin.distanceTo( _intersectPointOnRay );

	if ( distance < raycaster.near || distance > raycaster.far ) return;

	return {

		distance: distance,
		// What do we want? intersection point on the ray or on the segment??
		// point: raycaster.ray.at( distance ),
		point: _intersectPointOnSegment.clone().applyMatrix4( object.matrixWorld ),
		index: a,
		face: null,
		faceIndex: null,
		object: object

	};

}

if ( typeof __THREE_DEVTOOLS__ !== 'undefined' ) {

	__THREE_DEVTOOLS__.dispatchEvent( new CustomEvent( 'register', { detail: {
		revision: REVISION,
	} } ) );

}

if ( typeof window !== 'undefined' ) {

	if ( window.__THREE__ ) {

		console.warn( 'WARNING: Multiple instances of Three.js being imported.' );

	} else {

		window.__THREE__ = REVISION;

	}

}Vector3.prototype.equals = function (v) {
    return (Math.abs(this.x - v.x) < 1e-6 && Math.abs(this.y - v.y) < 1e-6 && Math.abs(this.z - v.z) < 1e-6);
};
/**
 * 空间几何点
 */
class Point extends Vector3 {
    equals(point, tolerance = 1e-6) {
        return (Math.abs(this.x - point.x) < tolerance && Math.abs(this.y - point.y) < tolerance && Math.abs(this.z - point.z) < tolerance);
    }
}/**
 * 声明几何特征点类型
 */
var FEATURE_POINT_TYPE;
(function (FEATURE_POINT_TYPE) {
    /**
     * 端点
     */
    FEATURE_POINT_TYPE[FEATURE_POINT_TYPE["END"] = 0] = "END";
    /**
     * 中点
     */
    FEATURE_POINT_TYPE[FEATURE_POINT_TYPE["MIDDLE"] = 1] = "MIDDLE";
    /**
     * 中心点
     */
    FEATURE_POINT_TYPE[FEATURE_POINT_TYPE["CENTER"] = 2] = "CENTER";
    /**
     * 面中心点
     */
    FEATURE_POINT_TYPE[FEATURE_POINT_TYPE["FACE_CENTER"] = 3] = "FACE_CENTER";
    /**
     * 象限点
     */
    FEATURE_POINT_TYPE[FEATURE_POINT_TYPE["QUADRANTAL"] = 4] = "QUADRANTAL";
    /**
     * 交点
     */
    FEATURE_POINT_TYPE[FEATURE_POINT_TYPE["INTERSECTION"] = 5] = "INTERSECTION";
})(FEATURE_POINT_TYPE || (FEATURE_POINT_TYPE = {}));/**
 * 几何基类
 */
class GeometryBase {
    /**
     * 获取X轴方向
     * @returns
     */
    getNormalizeX(target = null) {
        if (target == null)
            target = new Vector3();
        target.set(this.transformMatrix.elements[0], this.transformMatrix.elements[1], this.transformMatrix.elements[2]).normalize();
        // return new Vector3(this.transformMatrix.elements[0], this.transformMatrix.elements[1], this.transformMatrix.elements[2]).normalize();
        return target;
    }
    /**
     * 获取Y轴方向
     * @returns
     */
    getNormalizeY(target = null) {
        if (target == null)
            target = new Vector3();
        target.set(this.transformMatrix.elements[4], this.transformMatrix.elements[5], this.transformMatrix.elements[6]).normalize();
        return target;
        // return new Vector3(this.transformMatrix.elements[4], this.transformMatrix.elements[5], this.transformMatrix.elements[6]).normalize();
    }
    /**
     * 获取Z轴方向
     * @returns
     */
    getNormalizeZ(target = null) {
        if (target == null)
            target = new Vector3();
        target.set(this.transformMatrix.elements[8], this.transformMatrix.elements[9], this.transformMatrix.elements[10]).normalize();
        return target;
        // return new Vector3(this.transformMatrix.elements[8], this.transformMatrix.elements[9], this.transformMatrix.elements[10]).normalize();
    }
    /**
     * 获取X轴方向 带长度
     * @returns
     */
    getX(target = null) {
        if (target == null)
            target = new Vector3();
        target.set(this.transformMatrix.elements[0], this.transformMatrix.elements[1], this.transformMatrix.elements[2]);
        // return new Vector3(this.transformMatrix.elements[0], this.transformMatrix.elements[1], this.transformMatrix.elements[2]).normalize();
        return target;
    }
    /**
     * 获取Y轴方向 带长度
     * @returns
     */
    getY(target = null) {
        if (target == null)
            target = new Vector3();
        target.set(this.transformMatrix.elements[4], this.transformMatrix.elements[5], this.transformMatrix.elements[6]);
        return target;
        // return new Vector3(this.transformMatrix.elements[4], this.transformMatrix.elements[5], this.transformMatrix.elements[6]).normalize();
    }
    /**
     * 获取Z轴方向 带长度
     * @returns
     */
    getZ(target = null) {
        if (target == null)
            target = new Vector3();
        target.set(this.transformMatrix.elements[8], this.transformMatrix.elements[9], this.transformMatrix.elements[10]);
        return target;
        // return new Vector3(this.transformMatrix.elements[8], this.transformMatrix.elements[9], this.transformMatrix.elements[10]).normalize();
    }
    /**
     * 获取基点
     * @returns
     */
    getP(target = null) {
        if (target == null)
            target = new Point();
        target.set(this.transformMatrix.elements[12], this.transformMatrix.elements[13], this.transformMatrix.elements[14]);
        return target;
        // return new Point(this.transformMatrix.elements[12], this.transformMatrix.elements[13], this.transformMatrix.elements[14]);
    }
    /**
     * 设置基点
     * @param value
     */
    setP(value) {
        this.transformMatrix.elements[12] = value.x;
        this.transformMatrix.elements[13] = value.y;
        this.transformMatrix.elements[14] = value.z;
    }
    /**
     * 设置局部坐标系
     * @param x x轴方向
     * @param z z轴方向
     * @param [keepZ=true]
     */
    setXYZ(x, z, keepZ = true) {
        _vec1$1.copy(x).normalize();
        _vec3$1.copy(z).normalize();
        _vec2$1.copy(z).cross(_vec1$1).normalize();
        if (Math.abs(_vec1$1.dot(_vec3$1)) > 1e-6) {
            //x与z不垂直，需要根据keepZ的值设置x或者z
            if (keepZ) {
                _vec1$1.crossVectors(_vec2$1, _vec3$1);
            }
            else {
                _vec3$1.crossVectors(_vec1$1, _vec2$1);
            }
        }
        const scaleX = this.scaleX;
        const scaleY = this.scaleY;
        const scaleZ = this.scaleZ;
        this.transformMatrix.elements[0] = _vec1$1.x * scaleX;
        this.transformMatrix.elements[1] = _vec1$1.y * scaleX;
        this.transformMatrix.elements[2] = _vec1$1.z * scaleX;
        this.transformMatrix.elements[4] = _vec2$1.x * scaleY;
        this.transformMatrix.elements[5] = _vec2$1.y * scaleY;
        this.transformMatrix.elements[6] = _vec2$1.z * scaleY;
        this.transformMatrix.elements[8] = _vec3$1.x * scaleZ;
        this.transformMatrix.elements[9] = _vec3$1.y * scaleZ;
        this.transformMatrix.elements[10] = _vec3$1.z * scaleZ;
    }
    /**
     * x轴缩放因子
     */
    get scaleX() {
        return _vec$1.set(this.transformMatrix.elements[0], this.transformMatrix.elements[1], this.transformMatrix.elements[2]).length();
    }
    /**
     * y轴缩放因子
     */
    get scaleY() {
        return _vec$1.set(this.transformMatrix.elements[4], this.transformMatrix.elements[5], this.transformMatrix.elements[6]).length();
    }
    /**
     * z轴缩放因子
     */
    get scaleZ() {
        return _vec$1.set(this.transformMatrix.elements[8], this.transformMatrix.elements[9], this.transformMatrix.elements[10]).length();
    }
    /**
     * 构造函数
     */
    constructor() {
        /**
         * 几何类型
         */
        this.type = 'base';
        this.transformMatrix = new Matrix4();
        // this.setP(new Point(0, 0, 0));
        // this.setXYZ(new Vector3(1, 0, 0), new Vector3(0, 0, 1));
    }
    /**
     * 放大
     * @param point
     * @param factor
     */
    scale(point, factorX, factorY = null, factorZ = null) {
        if (factorY == null)
            factorY = factorX;
        if (factorZ == null)
            factorZ = factorX;
        _transformMatrix.set(factorX, 0, 0, point.x * (1 - factorX), 0, factorY, 0, point.y * (1 - factorY), 0, 0, factorZ, point.z * (1 - factorZ), 0, 0, 0, 1);
        this.transformMatrix.premultiply(_transformMatrix);
        return this;
        // _beforeMatrix.set(
        //     1, 0, 0, -point.x,
        //     0, 1, 0, -point.y,
        //     0, 0, 1, -point.z,
        //     0, 0, 0, 1
        // )
        // _afterMatrix.set(
        //     1, 0, 0, point.x,
        //     0, 1, 0, point.y,
        //     0, 0, 1, point.z,
        //     0, 0, 0, 1
        // )
        // _transformMatrix.set(
        //     factor, 0, 0, 0,
        //     0, factor, 0, 0,
        //     0, 0, factor, 0,
        //     0, 0, 0, 1
        // );
        // 第一个是错误写法，后面两种都行
        // this.transformMatrix = _beforeMatrix.multiply(_transformMatrix).multiply(_afterMatrix).multiply(this.transformMatrix);
        // this.transformMatrix = this.transformMatrix.multiply(_afterMatrix.multiply(_transformMatrix.multiply(_beforeMatrix)));
        // this.transformMatrix = _beforeMatrix.premultiply(_transformMatrix).premultiply(_afterMatrix).premultiply(this.transformMatrix);
    }
    /**
     * 移动图元
     * @param vec 位移向量,长度表示距离
     */
    move(vec) {
        _transformMatrix.set(1, 0, 0, vec.x, 0, 1, 0, vec.y, 0, 0, 1, vec.z, 0, 0, 0, 1);
        this.transformMatrix.premultiply(_transformMatrix);
        return this;
    }
    /**
     * 旋转图元
     * @param point 旋转中心
     * @param axis  旋转轴
     * @param angle 旋转角度
     */
    rotate(point, axis, angle) {
        _beforeMatrix.set(1, 0, 0, -point.x, 0, 1, 0, -point.y, 0, 0, 1, -point.z, 0, 0, 0, 1);
        _afterMatrix.set(1, 0, 0, point.x, 0, 1, 0, point.y, 0, 0, 1, point.z, 0, 0, 0, 1);
        _quaternion.setFromAxisAngle(axis, angle);
        _transformMatrix.makeRotationFromQuaternion(_quaternion);
        // this.transformMatrix = _beforeMatrix.premultiply(_transformMatrix).premultiply(_afterMatrix).premultiply(this.transformMatrix);
        this.transformMatrix.premultiply(_beforeMatrix).premultiply(_transformMatrix).premultiply(_afterMatrix);
        return this;
    }
    /**
     * 复制方法     need to override
     * @param geo
     * @returns
     */
    copy(geo) {
        this.transformMatrix.copy(geo.transformMatrix);
        return this;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new GeometryBase().copy(this);
    }
    /**
     * 获取中心点
     * @param target 用以防止对象重复创建
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        this.getP(target);
        return target;
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        return {
            transformMatrix: this.transformMatrix.elements,
        };
    }
    /**
     * 解析数据成对象
     */
    fromJson(json) {
        if (json.transformMatrix) {
            this.transformMatrix.fromArray(json.transformMatrix);
        }
        return this;
    }
    /**
     * 判断矩阵相等
     * @param another 另一个几何基类的矩阵
     * @param eplison 判断相等的精度
     * @returns
     */
    matrixEquals(another, eplison = 1e-6) {
        for (let i = 0; i < 16; i++) {
            if (Math.abs(this.transformMatrix.elements[i] - another.elements[i]) > eplison)
                return false;
        }
        return true;
    }
    /**
     * 判定相等
     * @param another
     * @returns
     */
    equals(another) {
        return false;
    }
    /**
     * X轴更新，同步更新剩下2个轴
     * @param vecX 新的X轴
     * @param withScale scale值是否覆盖
     */
    updateMatrixByX(vecX, withScale = true) {
        if (vecX == null)
            return this;
        _vec1$1.copy(vecX);
        let scaleX = _vec1$1.length();
        if (!withScale) {
            scaleX = this.scaleX;
        }
        _vec1$1.normalize();
        this.getNormalizeY(_vec2$1);
        const scaleY = this.scaleY;
        this.getNormalizeZ(_vec3$1);
        const scaleZ = this.scaleZ;
        // let copyN = _vec3.clone().normalize().sub(_vec1.normalize());
        let angle = Math.abs(_vec3$1.clone().angleTo(_vec1$1));
        if (Math.abs(Math.PI - angle) < 1 / 64 || angle < 1 / 64) {
            // 和新的x轴和原来的z轴靠很近，需要先计算新的z轴
            _vec3$1.crossVectors(_vec1$1, _vec2$1).normalize();
            _vec2$1.crossVectors(_vec3$1, _vec1$1).normalize();
        }
        else {
            // 否则，先计算新的y轴
            _vec2$1.crossVectors(_vec3$1, _vec1$1).normalize();
            _vec3$1.crossVectors(_vec1$1, _vec2$1).normalize();
        }
        this.transformMatrix.elements[0] = _vec1$1.x * scaleX;
        this.transformMatrix.elements[1] = _vec1$1.y * scaleX;
        this.transformMatrix.elements[2] = _vec1$1.z * scaleX;
        this.transformMatrix.elements[4] = _vec2$1.x * scaleY;
        this.transformMatrix.elements[5] = _vec2$1.y * scaleY;
        this.transformMatrix.elements[6] = _vec2$1.z * scaleY;
        this.transformMatrix.elements[8] = _vec3$1.x * scaleZ;
        this.transformMatrix.elements[9] = _vec3$1.y * scaleZ;
        this.transformMatrix.elements[10] = _vec3$1.z * scaleZ;
        return this;
    }
    /**
     * Y轴更新，同步更新剩下2个轴
     * @param vecY 新的Y轴，带ScaleY
     * @returns
     */
    updateMatrixByY(vecY, withScale = true) {
        if (vecY == null)
            return this;
        _vec2$1.copy(vecY);
        let scaleY = _vec2$1.length();
        if (!withScale) {
            scaleY = this.scaleY;
        }
        _vec2$1.normalize();
        this.getNormalizeX(_vec1$1);
        const scaleX = this.scaleX;
        this.getNormalizeZ(_vec3$1);
        const scaleZ = this.scaleZ;
        // let copyN = _vec3.clone().normalize().sub(_vec2.normalize());
        let angle = Math.abs(_vec3$1.clone().angleTo(_vec2$1));
        if (Math.abs(Math.PI - angle) < 1 / 64 || angle < 1 / 64) {
            // 新的y轴和原来的z轴靠很近，需要先计算新的z轴
            _vec3$1.crossVectors(_vec1$1, _vec2$1).normalize();
            _vec1$1.crossVectors(_vec2$1, _vec3$1).normalize();
        }
        else {
            // 否则，先计算新的x轴
            _vec1$1.crossVectors(_vec2$1, _vec3$1).normalize();
            _vec3$1.crossVectors(_vec1$1, _vec2$1).normalize();
        }
        this.transformMatrix.elements[0] = _vec1$1.x * scaleX;
        this.transformMatrix.elements[1] = _vec1$1.y * scaleX;
        this.transformMatrix.elements[2] = _vec1$1.z * scaleX;
        this.transformMatrix.elements[4] = _vec2$1.x * scaleY;
        this.transformMatrix.elements[5] = _vec2$1.y * scaleY;
        this.transformMatrix.elements[6] = _vec2$1.z * scaleY;
        this.transformMatrix.elements[8] = _vec3$1.x * scaleZ;
        this.transformMatrix.elements[9] = _vec3$1.y * scaleZ;
        this.transformMatrix.elements[10] = _vec3$1.z * scaleZ;
        return this;
    }
    /**
     * Z轴更新，同步更新剩下2个轴
     * @param vecZ 新的Z轴，带ScaleZ
     * @returns
     */
    updateMatrixByZ(vecZ, withScale = true) {
        if (vecZ == null)
            return this;
        _vec3$1.copy(vecZ);
        let scaleZ = _vec3$1.length();
        if (!withScale) {
            scaleZ = this.scaleZ;
        }
        _vec3$1.normalize();
        this.getNormalizeX(_vec1$1);
        const scaleX = this.scaleX;
        this.getNormalizeY(_vec2$1);
        const scaleY = this.scaleY;
        // let copyN = _vec1.clone().normalize().sub(_vec3.normalize());
        let angle = Math.abs(_vec1$1.clone().angleTo(_vec3$1));
        if (Math.abs(Math.PI - angle) < 1 / 64 || angle < 1 / 64) {
            // 新的z轴和原来的x轴靠很近，需要先计算新的x轴
            _vec1$1.crossVectors(_vec2$1, _vec3$1).normalize();
            _vec2$1.crossVectors(_vec3$1, _vec1$1).normalize();
        }
        else {
            // 否则，先计算新的y轴
            _vec2$1.crossVectors(_vec3$1, _vec1$1).normalize();
            _vec1$1.crossVectors(_vec2$1, _vec3$1).normalize();
        }
        this.transformMatrix.elements[0] = _vec1$1.x * scaleX;
        this.transformMatrix.elements[1] = _vec1$1.y * scaleX;
        this.transformMatrix.elements[2] = _vec1$1.z * scaleX;
        this.transformMatrix.elements[4] = _vec2$1.x * scaleY;
        this.transformMatrix.elements[5] = _vec2$1.y * scaleY;
        this.transformMatrix.elements[6] = _vec2$1.z * scaleY;
        this.transformMatrix.elements[8] = _vec3$1.x * scaleZ;
        this.transformMatrix.elements[9] = _vec3$1.y * scaleZ;
        this.transformMatrix.elements[10] = _vec3$1.z * scaleZ;
        return this;
    }
    /**
     * 获取基元列表
     * @returns
     */
    getGeoElements() {
        return [];
    }
    /**
     * 获取基元索引列表 用于绑定
     */
    getGeoElementsIdxList() {
        return [];
    }
    /**
     * 获取特征点列表
     * @returns
     */
    getFeaturePoints() {
        return [];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        return {
            min: null,
            max: null,
        };
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        return [];
    }
    /**
     * 通过控制点修改几何
     */
    setByControlPoint(controlPoints = [], vec) {
        let center = controlPoints.find(item => item.type === FEATURE_POINT_TYPE.CENTER);
        if (center != null) {
            this.move(vec);
            return true;
        }
        return false;
    }
}
//全局临时存储
const _beforeMatrix = new Matrix4();
const _transformMatrix = new Matrix4();
const _afterMatrix = new Matrix4();
//临时四元数
const _quaternion = new Quaternion();
const _vec1$1 = new Vector3();
const _vec2$1 = new Vector3();
const _vec3$1 = new Vector3();
const _vec$1 = new Vector3();/**
 * 空间曲线基类
 */
class Curve extends GeometryBase {
    constructor() {
        super(...arguments);
        this.type = 'Curve';
        /**
         * 为了解决instanceof在引用库时可能失败的问题，强制增加一条
         */
        this.isCurve = true;
    }
    /**
     * 获取插值点
     * @param t
     * @returns
     */
    getPointAt(t, target = null) {
        return null;
    }
    /**
     * 获取曲线长度
     * @returns
     */
    getPerimeter() {
        return 0;
    }
    /**
     * 获取曲线的插值点序列
     * @returns
     */
    getPoints(division) {
        return [];
    }
    /**
     * 获取起点和终点
     * @returns
     */
    getEndPoints() {
        let start = this.getPointAt(0);
        let end = this.getPointAt(1);
        return [start, end];
    }
    clone() {
        return new Curve().copy(this);
    }
    /**
     * 获取曲线上某点的切线方向
     * @param point
     * @returns
     */
    getCutLineDir(point, target) {
        if (target == null) {
            target = new Vector3();
        }
        return target;
    }
    /**
     * 获取基元列表
     * @returns
     */
    getGeoElements() {
        return [this];
    }
    /**
     * 获取基元索引列表 用于绑定
     */
    getGeoElementsIdxList() {
        return [0];
    }
    /**
     * 获取控制点 可用于stretch和夹点编辑
     * @returns
     */
    getControlPoints() {
        return [];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let min = new Point(0, 0, 0);
        let max = new Point(0, 0, 0);
        return {
            min: min,
            max: max,
        };
    }
    /**
     * 反向线的方向
     */
    reverse() {
        return this;
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        return [];
    }
}/**
 * 直线段
 * 其局部坐标系定义为起点为基点p，方向为局部x轴，长度为x轴向量长度
 */
class Line extends Curve {
    /**
     * 起点
     */
    get start() {
        return this.getP();
    }
    /**
     * 设置起点
     */
    set start(point) {
        this.set(point, this.end);
    }
    /**
     * 终点
     */
    get end() {
        this.getX(_vec);
        return this.start.clone().add(_vec);
    }
    /**
     * 设置终点
     */
    set end(point) {
        this.set(this.start, point);
    }
    /**
     * 构造函数
     * @param start
     * @param end
     */
    constructor(start = new Point(), end = new Point()) {
        super();
        /**
         * 类型
         */
        this.type = 'Line';
        this.set(start, end);
    }
    /**
     * 设置起点和终点
     * @param start
     * @param end
     * @returns
     */
    set(start, end) {
        this.setP(start);
        _vec.subVectors(end, start);
        let length = _vec.length();
        if (length < 1e-6) {
            _vec.set(1e-5, 0, 0);
        }
        this.updateMatrixByX(_vec, true);
        return this;
    }
    /**
     * 获取切线方向
     * @param point
     */
    getCutLineDir(point, target) {
        if (target == null) {
            target = new Vector3();
        }
        this.getNormalizeX(target);
        return target;
    }
    /**
     * 获取端点
     * @returns
     */
    getEndPoints() {
        return [this.start, this.end];
    }
    /**
     * 获取插值点
     * @param t 0-1
     * @returns
     */
    getPointAt(t, target = null) {
        if (target == null) {
            target = new Point();
        }
        this.getX(target).multiplyScalar(t).add(this.start);
        return target;
    }
    /**
     * 获取中心点
     * @param target
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.set(0.5, 0, 0).applyMatrix4(this.transformMatrix);
        return target;
    }
    /**
     * 克隆一个新的线对象
     * @returns
     */
    clone() {
        return new Line().copy(this);
    }
    /**
     * 转成json格式数据
     * @returns
     */
    toJson() {
        let data = Object.assign({ type: this.type }, super.toJson());
        return data;
    }
    /**
     * 判定相等
     * @param another
     * @returns
     */
    equals(another) {
        return this.start.equals(another.start) && this.end.equals(another.end);
    }
    /**
     * 获取特征点列表
     * @returns
     */
    getFeaturePoints() {
        return [
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1), geoType: this.type, uvtPosition: 1 },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: this.getPointAt(0.5), geoType: this.type },
        ];
    }
    /**
     * 获取控制点 可用于stretch和夹点编辑
     * @returns
     */
    getControlPoints() {
        return [
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1), geoType: this.type, uvtPosition: 1 },
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getPointAt(0.5), geoType: this.type },
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let min = new Point();
        let max = new Point(1, 0, 0);
        min.applyMatrix4(this.transformMatrix);
        max.applyMatrix4(this.transformMatrix);
        return {
            min: min,
            max: max,
        };
    }
    /**
     * 线翻转
     */
    negate() {
        let start = this.getP();
        let end = this.getPointAt(1);
        this.set(end, start);
    }
    reverse() {
        let start = this.getP();
        let end = this.getPointAt(1);
        this.set(end, start);
        return this;
    }
    /**
     * 获取曲线的插值点序列
     * @returns
     */
    getPoints(division) {
        return [
            this.start,
            this.end,
        ];
    }
}
const _vec = new Vector3();class Surface extends GeometryBase {
    constructor() {
        super(...arguments);
        /**
         * 为了解决instanceof在引用库时可能失败的问题，强制增加一条
         */
        this.isSurface = true;
    }
    /**
     * 获得曲面的插值点列表
     * @param div_t
     * @param div_u
     * @returns
     */
    getPoints(div_t = 20, div_u = 8) {
        const points = [];
        for (let u = 0; u <= div_u; u++) {
            for (let t = 0; t <= div_t; t++) {
                let point = this.getPointAt(t / div_t, u / div_u);
                if (!point)
                    continue;
                points.push(point);
            }
        }
        return points;
    }
    /**
     * 获得指定位置比例的曲面插值点， t u 0...1 override
     * @param t
     * @param u
     * @param target
     * @returns
     */
    getPointAt(t, u, target = null) {
        return target;
    }
    getGeoElements() {
        return [this];
    }
    /**
     * 获取基元索引列表 用于绑定
     */
    getGeoElementsIdxList() {
        return [0];
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Surface().copy(this);
    }
    /**
     * 获取面的边线
     * @returns
     */
    getSideEdges() {
        // throw new Error("Method 'getSideEdges' must be implemented in Surface!")
        return [];
    }
}var GeoMeasurement;
(function (GeoMeasurement) {
    /**
    * 求点到平面的距离
    * @param point 三维点
    * @param plane 平面
    * @returns
    */
    function PointDistanceToPlane(point, plane) {
        return plane.normal.dot(point) + plane.constant;
    }
    GeoMeasurement.PointDistanceToPlane = PointDistanceToPlane;
    /**
     * 获取点在平面上的投影点
     * @param point
     * @param plane
     */
    function getPointProjectToPlane(point, plane, target = null) {
        if (target == null) {
            target = new Point();
        }
        target.copy(point);
        _matrixInverse.copy(plane.transformMatrix).invert();
        target.applyMatrix4(_matrixInverse);
        target.z = 0;
        target.applyMatrix4(plane.transformMatrix);
        return target;
    }
    GeoMeasurement.getPointProjectToPlane = getPointProjectToPlane;
    const _matrixInverse = new Matrix4();
    // /**
    //  * 求成环的edges的面积
    //  * @param curves 
    //  * @returns 
    //  */
    // static GetAreaByEdges(curves: Curve[]) {
    //     const _prevVector: Vector3 = new Vector3();
    //     const _nextVector: Vector3 = new Vector3();
    //     // 由有序的边得到的顶点
    //     let points: Point[] = [];
    //     let noRepeatPoints: Point[] = [];
    //     curves.forEach(edge => {
    //         points.push(...edge.getPoints());
    //     })
    //     // 对顶点去重
    //     points.forEach(point => {
    //         if (!noRepeatPoints.some(item => item.equals(point))) {
    //             noRepeatPoints.push(point);
    //         }
    //     })
    //     /**         
    //      * 计算多边形面积，参考文献：[1]韦品瑚,晓伟.三维空间多边形的面积计算[J].数学通报,1984(02):18-21. 引理2
    //      * 空间多边形A1A2...An的面积S=1/2 * |A1A2 * A1A3 + A1A3 * A1A4 + ... + A1An-1 * A1An|
    //      */
    //     if (noRepeatPoints.length < 3) return 0;
    //     // 第一个点
    //     let firstPoint = noRepeatPoints[0];
    //     // 矢量积
    //     let vectorProduct = new Vector3();
    //     for (let i = 2; i <= noRepeatPoints.length - 1; i++) {
    //         // A1Ai-1
    //         _prevVector.subVectors(noRepeatPoints[i - 1], firstPoint);
    //         // A1Ai
    //         _nextVector.subVectors(noRepeatPoints[i], firstPoint);
    //         // A1Ai-1 * A1Ai
    //         vectorProduct.add(_prevVector.cross(_nextVector));
    //     }
    //     return vectorProduct.length() / 2
    // }
    /**
     * 射线起点沿射线方向与平面的距离
     * @param ray
     * @param plane
     * @returns
     */
    function RayOriginDistanceToPlane(ray, plane) {
        const denominator = plane.normal.dot(ray.direction);
        if (denominator === 0) {
            // line is coplanar, return origin
            if (PointDistanceToPlane(ray.origin, plane) === 0) {
                return 0;
            }
            // Null is preferable to undefined since undefined means.... it is undefined
            return null;
        }
        const t = -(ray.origin.dot(plane.normal) + plane.constant) / denominator;
        // Return if the ray never intersects the plane
        return t >= 1e-6 ? t : null;
    }
    GeoMeasurement.RayOriginDistanceToPlane = RayOriginDistanceToPlane;
    //求v1到v2的旋转角，两个向量都是单位向量
    function getRotateAngle(v1, v2) {
        let npi = Math.PI;
        let dot = v1.clone().dot(v2);
        let angle = 0;
        if (Math.abs(dot - 1.0) <= 1e-6) {
            angle = 0.0;
        }
        else if (Math.abs(dot + 1.0) <= 1e-6) {
            angle = npi;
        }
        else {
            angle = Math.acos(dot);
            let crossX = v1.y * v2.z - v1.z * v2.y;
            let crossY = v1.z * v2.x - v1.x * v2.z;
            let crossZ = v1.x * v2.y - v1.y * v2.x;
            // 向量 p2 是相对于向量 p1 的顺时针方向（根据右手定则）
            if (crossX < 0 || crossY < 0 || crossZ < 0) {
                angle = 2 * npi - angle;
            }
        }
        return angle;
    }
    GeoMeasurement.getRotateAngle = getRotateAngle;
})(GeoMeasurement || (GeoMeasurement = {}));/**
 * 几何平面
 */
class Plane extends Surface {
    constructor(normal = new Vector3(0, 0, 1), constant = 0) {
        super();
        /**
         * 为了解决instanceof在引用库时可能失败的问题，强制增加一条
         */
        this.isPlane = true;
        if (this.transformMatrix == null) {
            this.transformMatrix = new Matrix4();
        }
        this.set(normal, constant);
    }
    /**
     * 按平面方程定义构造平面
     * @param normal
     * @param constant
     */
    set(normal = new Vector3(0, 0, 1), constant = 0) {
        normal.normalize();
        this.normal = normal;
        this.constant = constant;
    }
    /**
     * 平面法向量
     */
    set normal(value) {
        this.setXYZ(this.getNormalizeX(), value);
    }
    /**
     * 平面法向量
     */
    get normal() {
        return this.getNormalizeZ();
    }
    /**
     * 平面常数
     */
    set constant(value) {
        _point$2.copy(this.normal).multiplyScalar(-value);
        this.setP(_point$2);
    }
    /**
     * 平面常数
     */
    get constant() {
        let value = -this.getP().dot(this.normal);
        return value;
    }
    /**
     * 获取平面上一点
     * @param target 若target不为空，则原位设置返回
     * @returns
     */
    coplanarPoint(target = null) {
        if (target == null) {
            target = new Point();
        }
        target.set(this.getP().x, this.getP().y, this.getP().z);
        return target;
    }
    equals(plane) {
        return plane.normal.equals(this.normal) && (plane.constant === this.constant);
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Plane().copy(this);
    }
    /**
     * 根据平面上三个点构造平面
     * @param a
     * @param b
     * @param c
     * @returns
     */
    setFromCoplanarPoints(a, b, c) {
        const normal = _vector1$1.subVectors(c, b).cross(_vector2$1.subVectors(a, b)).normalize();
        // Q: should an error be thrown if normal is zero (e.g. degenerate plane)?
        this.setFromNormalAndCoplanarPoint(normal, a);
        return this;
    }
    /**
     * 根据平面法向量和平面上一点构造平面
     * @param normal
     * @param point
     * @returns
     */
    setFromNormalAndCoplanarPoint(normal, point) {
        _vector1$1.copy(normal);
        if (_vector1$1.length() < 1e-6) {
            _vector1$1.set(0, 0, 1);
        }
        else {
            _vector1$1.normalize();
        }
        this.setXYZ(this.getNormalizeX(), _vector1$1, true);
        this.setP(point);
        return this;
    }
    /**
     * 计算点在平面的投影点
     * @param normal
     * @param point
     * @returns
     */
    projectPoint(point, target = null) {
        if (target == null) {
            target = new Point();
        }
        return target.copy(this.normal).multiplyScalar(-GeoMeasurement.PointDistanceToPlane(point, this)).add(point);
    }
    getSideEdges() {
        return [];
    }
    /**
     * 转化为一般面域
     * @returns
     */
    toGeneralRegion() {
        return null;
    }
}
const _point$2 = new Point();
const _vector1$1 = new Vector3();
const _vector2$1 = new Vector3();Vector2.prototype.equals = function (v) {
    return Math.abs(this.x - v.x) < 1e-6 && Math.abs(this.y - v.y) < 1e-6;
};
/**
 * 平面几何点
 */
class Point2 extends Vector2 {
    equals(point, tolerance = 1e-6) {
        return (Math.abs(this.x - point.x) < tolerance && Math.abs(this.y - point.y) < tolerance);
    }
}/**
 * 直线
 */
class StraightLine extends Curve {
    /**
     * 经过点
     */
    get origin() {
        return this.getP();
    }
    /**
     * 设置经过点
     */
    set origin(value) {
        this.setP(value);
    }
    /**
     * 方向
     */
    get direction() {
        return this.getNormalizeX();
    }
    /**
     * 设置方向
     */
    set direction(value) {
        if (value.length() < 1e-6) {
            return;
        }
        this.updateMatrixByX(value, false);
    }
    /**
     * 返回直线上某点切线
     * @param point
     */
    getCutLineDir(point, target) {
        if (target == null) {
            target = new Vector3();
        }
        this.getNormalizeX(target);
        return target;
    }
    /**
     * 构造函数
     * @param origin
     * @param direction
     */
    constructor(origin = new Point(0, 0, 0), direction = new Vector3(1, 0, 0)) {
        super();
        /**
         * 类型
         */
        this.type = 'StraightLine';
        this.origin = origin;
        this.direction = direction.clone().normalize();
    }
    /**
     * 利用经过点和方向构造直线
     * @param origin
     * @param direction
     */
    setFromDirection(origin, direction) {
        this.origin = origin;
        if (direction.length() < 1e-6) {
            return;
        }
        this.direction = direction.clone().normalize();
    }
    /**
     * 利用两点构造直线
     * @param start
     * @param end
     * @returns
     */
    setFromPoints(start, end) {
        let dir = end.clone().sub(start);
        if (dir.length() <= 1e-6)
            return;
        this.origin = start;
        this.direction = dir.normalize();
    }
    /**
     * 直线的中心，由于没有现实意义的中心，等效成获取origin
     * @param target
     * @returns
     */
    getCenter(target = null) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.origin);
        return target;
    }
    /**
     * 直线没有端点
     * @returns
     */
    getEndPoints() {
        return [];
    }
    /**
     * 获取直线上与经过点距离为t的点
     * @param t 距离
     * @param target
     * @returns
     */
    getPointAt(t, target = null) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.origin).add(this.direction.clone().multiplyScalar(t));
        return target;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new StraightLine().copy(this);
    }
    /**
     * 复制
     * @param source
     * @returns
     */
    copy(source) {
        this.origin = source.origin;
        this.direction = source.direction;
        return this;
    }
    /**
     * 判断相等
     * @param curve
     * @returns
     */
    equals(curve) {
        if (!this.direction.equals(curve.direction) || !this.direction.equals(curve.direction.multiplyScalar(-1)))
            return false;
        let dir = this.origin.sub(curve.origin).normalize();
        if ((1 - Math.abs(dir.dot(this.direction))) > 1e-6)
            return false;
        return true;
    }
}/**
 * 一般多边形
 */
class GeneralRegion extends Plane {
    /**
     * @param rings 三维点构成的二维数组，表示多边形的环（环的方向需延法向量逆时针）
     * @param normal 法向量
     */
    constructor(rings = [], normal = new Vector3(0, 0, 1)) {
        super(normal);
        /**
         * 一般多边形
         */
        this.type = 'GeneralRegion';
        this.rings = [];
        this.setByRingsAndNormal(rings, normal);
    }
    /**
     * 通过空间三维点与法向量设置一般多边形
     * @param rings
     * @param normal
     * @param useNormal 是否使用传入的normal
     */
    setByRingsAndNormal(rings = [], normal = new Vector3(0, 0, 1), useNormal = true) {
        let regionNormal;
        if (useNormal) {
            regionNormal = normal;
        }
        else {
            let points = [];
            for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
                let ring = rings[ringIndex];
                for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                    points.push(ring[pointIndex]);
                    if (points.length >= 3) {
                        break;
                    }
                }
                if (points.length >= 3) {
                    break;
                }
            }
            if (points.length < 3) {
                regionNormal = new Vector3(0, 0, 1);
            }
            regionNormal = new Plane().setFromCoplanarPoints(points[0], points[1], points[2]).getNormalizeZ();
        }
        if (!GeneralRegion.isEmptyRing(rings)) { //如果不是空环，根据环中的点设置多边形
            this.setFromNormalAndCoplanarPoint(regionNormal, rings[0][0]);
            const matrixInverse = new Matrix4().copy(this.transformMatrix).invert();
            for (let ringIndex = 0; ringIndex < rings.length; ringIndex++) {
                let ring = rings[ringIndex];
                this.rings.push([]);
                for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                    _point$1.copy(ring[pointIndex]).applyMatrix4(matrixInverse);
                    this.rings[ringIndex].push(new Point2(_point$1.x, _point$1.y)); //直接舍弃掉z轴，默认将所有点拍平到一个平面上
                }
            }
        }
    }
    /**
     * 判断是否为空环
     * @param rings
     * @returns
     */
    static isEmptyRing(rings) {
        if (rings && rings.length > 0 && rings[0].length > 0) {
            return false;
        }
        return true;
    }
    /**
     * 多边形反向，所有线的方向与法向量方向均反
     */
    negate() {
        this.scale(this.getP(), -1, -1, -1);
        let newRings = [];
        for (let i = 0; i < this.rings.length; i++) {
            let newRing = [];
            for (let j = this.rings[i].length - 1; j >= 0; j--) {
                newRing.push(newRing[j]);
            }
            newRings.push(newRing);
        }
        this.rings = newRings;
    }
    /**
     * 判断是否为一个有效多边形
     * @returns
     */
    isVaild() {
        return this.transformMatrix && this.rings && this.rings.length > 0 && this.rings[0].length > 2;
    }
    /**
     * 获取反向的面
     * @returns
     */
    getNegate() {
        let res = this.clone();
        res.negate();
        return res;
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.set(0, 0, 0);
        let count = 0;
        if (this.rings) {
            this.rings.forEach(ring => {
                ring.forEach(point2 => {
                    target.set(point2.x + target.x, point2.y + target.y, 0);
                    count += 1;
                });
            });
        }
        if (count === 0)
            return target;
        target.multiplyScalar(count);
        target.applyMatrix4(this.transformMatrix);
        return target;
    }
    /**
     * 获得指定位置比例的曲面插值点， t u 0...1 override
     * @param t
     * @param u
     * @param target
     * @returns
     */
    getPointAt(t, u, target = null) {
        if (target == null) {
            target = new Point();
        }
        target.set(t, u, 0).applyMatrix4(this.transformMatrix);
        return target;
    }
    copy(geo) {
        super.copy(geo);
        if (geo instanceof GeneralRegion) {
            this.rings = [];
            geo.rings.forEach(ring => {
                let newRing = [];
                ring.forEach(point2 => {
                    newRing.push(point2.clone());
                });
                this.rings.push(newRing);
            });
        }
        return this;
    }
    clone() {
        return new GeneralRegion().copy(this);
    }
    getSideEdges() {
        let res = [];
        if (GeneralRegion.isEmptyRing(this.rings)) {
            return res;
        }
        this.rings.forEach(ring => {
            if (ring.length <= 1)
                return;
            for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                if (pointIndex != ring.length - 1) {
                    res.push(new Line(this.getPointAt(ring[pointIndex].x, ring[pointIndex].y), this.getPointAt(ring[pointIndex + 1].x, ring[pointIndex + 1].y)));
                }
                else {
                    res.push(new Line(this.getPointAt(ring[pointIndex].x, ring[pointIndex].y), this.getPointAt(ring[0].x, ring[0].y)));
                }
            }
        });
        return res;
    }
    getBoundingBox() {
        let min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, 0);
        let max = new Vector3(Number.MIN_VALUE, Number.MIN_VALUE, 0);
        this.rings.forEach(ring => {
            ring.forEach(point2 => {
                min.x = Math.min(min.x, point2.x);
                min.y = Math.min(min.y, point2.y);
                max.x = Math.max(max.x, point2.x);
                max.y = Math.max(max.y, point2.y);
            });
        });
        let box = new Box3(min, max);
        box.applyMatrix4(this.transformMatrix);
        return box;
    }
    toJson() {
        let data = Object.assign(Object.assign({}, super.toJson()), { type: this.type });
        let rings = this.rings.map(ring => {
            return ring.map(point2 => {
                return { x: point2.x, y: point2.y };
            });
        });
        data['rings'] = rings;
        return data;
    }
    fromJson(json) {
        super.fromJson(json);
        if (json.rings) {
            let newRings = [];
            json.rings.forEach(ring => {
                let newRing = [];
                ring.forEach(point2 => {
                    newRing.push(new Point2(point2.x, point2.y));
                });
                newRings.push(newRing);
            });
            this.rings = newRings;
        }
        return this;
    }
}
const _point$1 = new Point();const EPSILON = 1e-6;var GeoRelation;
(function (GeoRelation) {
    /**
    * 求直线上距离空间中任意一点最近的点
    * @param straightLine
    * @param point
    * @param target
    * @returns
    */
    function StraightLineClosestPointToPoint(straightLine, point, target = null) {
        if (target == null) {
            target = new Point();
        }
        target.subVectors(point, straightLine.origin);
        const directionDistance = target.dot(straightLine.direction);
        return target.copy(straightLine.direction).multiplyScalar(directionDistance).add(straightLine.origin);
    }
    GeoRelation.StraightLineClosestPointToPoint = StraightLineClosestPointToPoint;
    /**
     * 线上距离一点最近的点
     * @param line
     * @param point
     * @param clampToLine
     * @param target
     * @returns
     */
    function LineClosestPointToPoint(line, point, clampToLine, target) {
        const t = GeoRelation.LineClosestPointToPointParameter(line, point, clampToLine);
        if (target === undefined) {
            target = new Point();
        }
        return line.getPointAt(t, target);
    }
    GeoRelation.LineClosestPointToPoint = LineClosestPointToPoint;
    /**
     * 线上距离一点最近的点的参数
     * @param line
     * @param point
     * @param clampToLine
     * @returns
     */
    function LineClosestPointToPointParameter(line, point, clampToLine = false) {
        if (point == null)
            return null;
        const _startP = new Vector3();
        const _startEnd = new Vector3();
        _startP.subVectors(point, line.start);
        _startEnd.subVectors(line.end, line.start);
        const startEnd2 = _startEnd.dot(_startEnd);
        const startEnd_startP = _startEnd.dot(_startP);
        let t = startEnd_startP / startEnd2;
        if (clampToLine) {
            t = Math.max(0, Math.min(1, t));
        }
        return t;
    }
    GeoRelation.LineClosestPointToPointParameter = LineClosestPointToPointParameter;
    /**
     * 射线上距离一点最近的点
     * @param ray
     * @param point
     * @param target
     * @returns
     */
    function RayClosestPointToPoint(ray, point, target = null) {
        if (target == null) {
            target = new Point();
        }
        target.subVectors(point, ray.origin);
        const directionDistance = target.dot(ray.direction);
        if (directionDistance < 0) {
            return target.copy(ray.origin);
        }
        return target.copy(ray.direction).multiplyScalar(directionDistance).add(ray.origin);
    }
    GeoRelation.RayClosestPointToPoint = RayClosestPointToPoint;
    /**
     * 圆弧上距离一点最近的点
     * @param arc
     * @param point
     * @param clampToArc 所求点是否限制在圆弧上
     * @param target
     * @returns
     */
    function ArcClosestPointToPoint(arc, point, clampToArc = false, target = null) {
        if (target == null) {
            target = new Point();
        }
        let plane = new Plane();
        plane.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
        let distToPlane = GeoMeasurement.PointDistanceToPlane(point, plane);
        if (Math.abs(distToPlane) > 1e-6) {
            // point不在圆弧所在平面
            plane.projectPoint(point.clone(), point);
        }
        let pDir = point.clone().sub(arc.center).normalize();
        let foot = new Point();
        foot.copy(pDir.clone().multiplyScalar(arc.radius).add(arc.center));
        let t = arc.getPointParameter(foot);
        if ((t >= -1e-6 && t <= 1 + 1e-6) || !clampToArc) {
            return target.copy(foot);
        }
        // 判断point距离起点还是端点更近
        let endPoints = arc.getEndPoints();
        let startPoint = endPoints[0];
        let endPoint = endPoints[1];
        let dirStart = startPoint.clone().sub(arc.center);
        let dirEnd = endPoint.clone().sub(arc.center);
        let angleToStart = pDir.angleTo(dirStart);
        let angleToEnd = pDir.angleTo(dirEnd);
        if (angleToStart <= angleToEnd) {
            return target.copy(startPoint);
        }
        return target.copy(endPoint);
    }
    GeoRelation.ArcClosestPointToPoint = ArcClosestPointToPoint;
    /**
     * 圆弧上距离射线最近的点
     * @param ray
     * @param arc
     * @param target
     * @returns
     */
    function RayClosestPointInArc(ray, arc, target = null) {
        if (target == null) {
            target = new Point();
        }
        let arcPlane = new Plane();
        arcPlane.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
        if (Math.abs(ray.direction.dot(arc.normal)) < 1e-6) {
            // 射线平行于圆弧面 
            return null;
        }
        // 不共面
        let point = GeoIntersection.RayIntersectPlane(ray, arcPlane);
        if (!point) {
            // 射线与圆弧面无交点
            point = ray.origin;
            // return null;
        }
        GeoRelation.ArcClosestPointToPoint(arc, point, true, target);
        return target;
    }
    GeoRelation.RayClosestPointInArc = RayClosestPointInArc;
    /**
     * 逆时针计算角度
     * @param start
     * @param end
     * @param normal
     * @returns
     */
    function Vector3AngleTo(start, end, normal) {
        let theta = Math.acos(start.dot(end) / start.length() / end.length());
        let mark = start.clone().cross(end).dot(normal);
        if (mark < 0) {
            return 2 * Math.PI - theta;
        }
        return theta;
    }
    GeoRelation.Vector3AngleTo = Vector3AngleTo;
    /**
     * 判断两个是否接近
     * @param v1
     * @param v2
     * @returns
     */
    function Vector3IsClose(v1, v2) {
        if (v1.distanceTo(v2) < 1e-2) {
            return true;
        }
        return false;
    }
    GeoRelation.Vector3IsClose = Vector3IsClose;
    /**
     * 计算点到平面的距离
     * @param plane
     * @param point
     * @returns
     */
    function DistanceToPoint(plane, point) {
        return plane.normal.dot(point) + plane.constant;
    }
    GeoRelation.DistanceToPoint = DistanceToPoint;
    /**
     * 枚举类型，用于判定点与多边形的关系
     */
    let PointInRegion;
    (function (PointInRegion) {
        PointInRegion[PointInRegion["in"] = 1] = "in";
        PointInRegion[PointInRegion["on"] = 2] = "on";
        PointInRegion[PointInRegion["out"] = 3] = "out";
    })(PointInRegion = GeoRelation.PointInRegion || (GeoRelation.PointInRegion = {}));
    /**
     * 判断二维空间中的点c是否在a与b连成的线段之间（包括点a与点b）
     * @param a
     * @param b
     * @param c
     * @returns
     */
    function pointBetweenTwoPoints2D(a, b, c) {
        if (Math.abs(a[0] * b[1] + b[0] * c[1] + c[0] * a[1] - a[0] * c[1] - b[0] * a[1] - c[0] * b[1]) < EPSILON) {
            // 判断平行
            const x1 = [a[0] - c[0], a[1] - c[1]];
            const x2 = [b[0] - c[0], b[1] - c[1]];
            if (x1[0] * x2[0] + x1[1] * x2[1] <= 0) {
                // 判断是否在中间
                return true;
            }
        }
        return false;
    }
    /**
     * 获取下一点的索引，如果current是最后一个节点，则返回第一个点
     * @param current 当前索引
     * @param length 数组长度
     * @returns
     */
    function getNextIndex(current, length) {
        return current === length - 1 ? 0 : current + 1;
    }
    /**
     * 获取上一点的索引，如果current是第一个节点，则返回最后一点
     * @param current 当前索引
     * @param length 数组长度
     * @returns
     */
    function getLastIndex(current, length) {
        return current === 0 ? length - 1 : current - 1;
    }
    /**
     * 获取一个点在一条二维直线段上的定比分点的数值， 参考：https://zhuanlan.zhihu.com/p/422377860， 图片：点到直线定比分点.png
     * @param point
     * @param segment
     */
    function ratioToLine2(point, start, end) {
        _delta.subVectors(end, start);
        return (_delta.x * (point.x - start.x) + _delta.y * (point.y - start.y)) / (_delta.lengthSq());
    }
    GeoRelation.ratioToLine2 = ratioToLine2;
    const _delta = new Point2();
    /**
     * 判断二维点是否在多边形内部，注意此处的点和多边形在同一个坐标系下
     * 如果不能保证，请使用Point3InGeneralRegion进行转化
     * 采用射线法进行计算，默认发射一条从点point开始，延x轴正方向的射线。
     * 射线与图元交点为奇数，则在多边形内，否则在多边形外。
     * @param region
     * @param point
     * @returns
     */
    function Point2InGeneralRegion(region, point) {
        //1.如果是空环，直接认为不属于
        if (GeneralRegion.isEmptyRing(region.rings)) {
            return PointInRegion.out;
        }
        //2.定义交点个数
        let count = 0;
        //3.遍历所有环
        for (let ringIndex = 0; ringIndex < region.rings.length; ringIndex++) {
            let ring = region.rings[ringIndex];
            //4.如果环的长度小于等于2，直接舍弃
            if (ring.length <= 2) {
                continue;
            }
            //5.遍历一个环中的所有点
            let haveDone = new Array(ring.length).fill(false); //标记某条边被处理过
            for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                if (haveDone[pointIndex]) { //如果被处理过，则直接跳过
                    continue;
                }
                //5.1获取环中当前点point1与下一点point2与上一点point0
                let point1 = ring[pointIndex];
                let lastPointIndex = getLastIndex(pointIndex, ring.length);
                let nextPointIndex = getNextIndex(pointIndex, ring.length);
                let point2 = ring[nextPointIndex];
                let point0 = ring[lastPointIndex];
                //5.1判断point1和point2是否重合，如果重合直接不考虑这条线
                if (point1.equals(point2)) {
                    haveDone[pointIndex] = true;
                    continue;
                }
                //5.3判断传入的点是否位于多边形的某一条边，如果是，直接得出点在多边形上
                if (pointBetweenTwoPoints2D([point1.x, point1.y], [point2.x, point2.y], [point.x, point.y])) {
                    return PointInRegion.on;
                }
                //判断point1与point2这条线是否与射线y=point.y(x>=point.x)平行
                if (Math.abs(point1.y - point2.y) < EPSILON) {
                    if (Math.abs(point.y - point2.y) < EPSILON) { //重合
                        let maxX = Math.max(point1.x, point2.x);
                        if (point.x > maxX + EPSILON) ;
                        else {
                            //TODO，可能有很多条线平行的病态情况
                            //如果平行直线段两端反向，算一个交点
                            let point3 = ring[getNextIndex(nextPointIndex, ring.length)];
                            if ((point3.y - point2.y) * (point0.y - point1.y) < 0) {
                                count += 1;
                            }
                        }
                        haveDone[lastPointIndex] = true;
                        haveDone[pointIndex] = true;
                        haveDone[nextPointIndex] = true;
                        continue;
                    }
                    else { //平行不重合，直接返回
                        haveDone[pointIndex] = true;
                        continue;
                    }
                }
                //判断是否与起点重合
                if (point1.equals(point)) {
                    //每条线段只考虑起点，不考虑终点，以防重复计算
                    if ((point2.y - point1.y) * (point0.y - point1.y) < 0) {
                        count += 1;
                    }
                    haveDone[lastPointIndex] = true;
                    haveDone[pointIndex] = true;
                    continue;
                }
                //既不平行，也不重合，只用计算是否有交点即可
                //(已知(x1, y1), (x2, y2)后，直线方程为(y2-y1)*x+(x1-x2)*y+(x2*y1-x1*y2)=0)
                let intersectionX = ((point1.x * point2.y - point2.x * point1.y) + point.y * (point2.x - point1.x)) / (point2.y - point1.y);
                let maxX = Math.max(point1.x, point2.x);
                let minX = Math.min(point1.x, point2.x);
                let maxY = Math.max(point1.y, point2.y);
                let minY = Math.min(point1.y, point2.y);
                // console.log("intersectionX", intersectionX, "point1.x", point1.x, "point1.y", point1.y, "point2.x", point2.x, "point2.y", point2.y)
                if (point.x > intersectionX + EPSILON || intersectionX < minX - EPSILON || intersectionX > maxX + EPSILON || point.y < minY - EPSILON || point.y > maxY + EPSILON) ;
                else {
                    _point2Intersection.set(intersectionX, point.y); //设置交点
                    if (_point2Intersection.equals(point1)) {
                        //交点等于起点
                        if ((point2.y - point1.y) * (point0.y - point1.y) < 0) {
                            count += 1;
                            // console.log("pointIndex", pointIndex, "交点等于起点");
                            haveDone[pointIndex] = true;
                            haveDone[lastPointIndex] = true;
                        }
                        continue;
                    }
                    if (_point2Intersection.equals(point2)) {
                        //交点等于终点
                        let point3 = ring[getNextIndex(nextPointIndex, ring.length)];
                        if ((point3.y - point2.y) * (point1.y - point2.y) < 0) {
                            count += 1;
                            // console.log("pointIndex", pointIndex, "交点等于终点");
                            haveDone[pointIndex] = true;
                            haveDone[nextPointIndex] = true;
                        }
                        continue;
                    }
                    // console.log("pointIndex", pointIndex, "正常相交", intersectionX, "_point2Intersection", _point2Intersection.x, _point2Intersection.y); 
                    count += 1;
                }
                haveDone[pointIndex] = true;
                continue;
            }
        }
        // console.log("count", count)
        if (count % 2 == 0) {
            return PointInRegion.out;
        }
        return PointInRegion.in;
    }
    GeoRelation.Point2InGeneralRegion = Point2InGeneralRegion;
    /**
     * 判断三维点是否在多边形内部
     * @param region
     * @param point
     * @returns
     */
    function Point3InGeneralRegion(region, point) {
        if (GeoRelation.DistanceToPoint(region, point) > 1e-6) { //如果不共面，直接认为不属于
            return GeoRelation.PointInRegion.out;
        }
        _matrix.copy(region.transformMatrix).invert();
        _point3$1.copy(point).applyMatrix4(_matrix);
        _point2$1.set(_point3$1.x, _point3$1.y);
        return GeoRelation.Point2InGeneralRegion(region, _point2$1);
    }
    GeoRelation.Point3InGeneralRegion = Point3InGeneralRegion;
    /**
     * 从平面上的二维点中，提取所有环
     * @param lines 线段集合
     * @param precision 判断相等的小数位数精度
     * @returns 环的集合，每个环是一个 Point2 的数组
     */
    function extractAllLoops(lines, precision = 6) {
        console.log("lines", lines.map(loop => loop.map(p => `(${p.x}, ${p.y})`)));
        // 构建邻接表
        const adjMap = new Map();
        const pointKey = (p) => `${p.x.toFixed(precision)},${p.y.toFixed(precision)}`;
        for (const [p1, p2] of lines) {
            const key1 = pointKey(p1);
            const key2 = pointKey(p2);
            if (!adjMap.has(key1))
                adjMap.set(key1, []);
            if (!adjMap.has(key2))
                adjMap.set(key2, []);
            adjMap.get(key1).push(p2);
            adjMap.get(key2).push(p1);
        }
        // 存储环的集合
        const loops = [];
        const visitedEdges = new Set(); // 已访问的边避免重复计算
        // 深度优先搜索
        const dfs = (current, start, path, visitedNodes) => {
            const key = pointKey(current);
            visitedNodes.add(key);
            path.push(current);
            for (const neighbor of adjMap.get(key) || []) {
                const edgeKey = [pointKey(current), pointKey(neighbor)].sort().join("-");
                if (path.length > 2 && neighbor.equals(start)) {
                    // 找到一个环
                    loops.push([...path]);
                    continue;
                }
                if (!visitedEdges.has(edgeKey)) {
                    visitedEdges.add(edgeKey);
                    if (!visitedNodes.has(pointKey(neighbor))) {
                        dfs(neighbor, start, path, visitedNodes);
                    }
                    visitedEdges.delete(edgeKey);
                }
            }
            // 回溯
            path.pop();
            visitedNodes.delete(key);
        };
        // 遍历所有顶点并启动 DFS
        for (const [p1] of lines) {
            const visitedNodes = new Set();
            dfs(p1, p1, [], visitedNodes);
        }
        // 去重环
        const uniqueLoops = new Set();
        const result = [];
        for (const loop of loops) {
            const sortedLoop = loop
                .slice()
                .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x))
                .map(pointKey)
                .join("-");
            if (!uniqueLoops.has(sortedLoop)) {
                uniqueLoops.add(sortedLoop);
                result.push(loop);
            }
        }
        return result;
    }
    GeoRelation.extractAllLoops = extractAllLoops;
    /**
     * 从空间中的三维点中，提取所有环
     * @param lines 线段集合
     * @param precision 判断相等的小数位数精度
     * @returns 环的集合，每个环是一个 Point2 的数组
     */
    function extractAllLoops3D(lines, precision = 6) {
        // 构建邻接表
        const adjMap = new Map();
        const pointKey = (p) => `${p.x.toFixed(precision)},${p.y.toFixed(precision)},${p.z.toFixed(precision)}`;
        for (const [p1, p2] of lines) {
            const key1 = pointKey(p1);
            const key2 = pointKey(p2);
            if (!adjMap.has(key1))
                adjMap.set(key1, []);
            if (!adjMap.has(key2))
                adjMap.set(key2, []);
            adjMap.get(key1).push(p2);
            adjMap.get(key2).push(p1);
        }
        // 存储环的集合
        const loops = [];
        const visitedEdges = new Set(); // 已访问的边避免重复计算
        // 深度优先搜索
        const dfs = (current, start, path, visitedNodes) => {
            const key = pointKey(current);
            visitedNodes.add(key);
            path.push(current);
            for (const neighbor of adjMap.get(key) || []) {
                const edgeKey = [pointKey(current), pointKey(neighbor)].sort().join("-");
                if (path.length > 2 && neighbor.equals(start)) {
                    // 找到一个环
                    loops.push([...path]);
                    continue;
                }
                if (!visitedEdges.has(edgeKey)) {
                    visitedEdges.add(edgeKey);
                    if (!visitedNodes.has(pointKey(neighbor))) {
                        dfs(neighbor, start, path, visitedNodes);
                    }
                    visitedEdges.delete(edgeKey);
                }
            }
            // 回溯
            path.pop();
            visitedNodes.delete(key);
        };
        // 遍历所有顶点并启动 DFS
        for (const [p1] of lines) {
            const visitedNodes = new Set();
            dfs(p1, p1, [], visitedNodes);
        }
        // 去重环
        const uniqueLoops = new Set();
        const result = [];
        for (const loop of loops) {
            const sortedLoop = loop
                .slice()
                .sort((a, b) => {
                if (a.x === b.x && a.y === b.y) {
                    return a.z - b.z;
                }
                else if (a.x === b.x) {
                    return a.y - b.y;
                }
                return a.x - b.x;
            })
                .map(pointKey)
                .join("-");
            if (!uniqueLoops.has(sortedLoop)) {
                uniqueLoops.add(sortedLoop);
                result.push(loop);
            }
        }
        return result;
    }
    GeoRelation.extractAllLoops3D = extractAllLoops3D;
    /**
     * 判断点是否在线上
     * @param line
     * @param point
     * @returns
     */
    function getT(line, point) {
        const lineLength = line.start.distanceTo(line.end);
        const startToPoint = line.start.distanceTo(point);
        const endToPoint = line.end.distanceTo(point);
        // 判断点是否在线段上：如果点到起点和终点的距离之和等于线段的总长度，说明点在该线段上
        if (Math.abs((startToPoint + endToPoint) - lineLength) > 1e-6) {
            return null;
        }
        // 计算 t 值
        return startToPoint / lineLength; // 返回 t 值，范围 [0, 1]
    }
    GeoRelation.getT = getT;
})(GeoRelation || (GeoRelation = {}));
const _point3$1 = new Point();
const _point2$1 = new Point2();
const _point2Intersection = new Point2();
const _matrix = new Matrix4();/**
 * 布尔操作枚举
 */
var BoolOpeType;
(function (BoolOpeType) {
    /**
     * 布尔-交
     */
    BoolOpeType[BoolOpeType["INTERSECT"] = 1] = "INTERSECT";
    /**
     * 布尔-并
     */
    BoolOpeType[BoolOpeType["UNION"] = 2] = "UNION";
    /**
     * 布尔-差
     */
    BoolOpeType[BoolOpeType["SUBSTRACT"] = 3] = "SUBSTRACT";
})(BoolOpeType || (BoolOpeType = {}));/**
 * 几何体基类
 */
class Solid extends GeometryBase {
    constructor() {
        super(...arguments);
        /**
         * 几何类熊
         */
        this.type = 'Solid';
        /**
         * 为了解决instanceof在引用库时可能失败的问题，强制增加一条
         */
        this.isSolid = true;
        /**
         * 面索引
         */
        this.surfaceIndex = [];
    }
    clone() {
        return new Solid().copy(this);
    }
    equals(solid) {
        return false;
    }
    getCenter(target = null) {
        return target;
    }
    /**
     * 获取基元索引列表 用于绑定
     */
    getGeoElementsIdxList() {
        return this.surfaceIndex;
    }
}/**
 * 索引面，用以减少存储空间
 */
class PolygonIndex {
    constructor(rings = [], normal = new Vector3(0, 0, 1)) {
        this.rings = rings;
        this.normal = normal.clone();
        this.normal.normalize();
    }
    copy(polygonIndex) {
        this.normal = polygonIndex.normal.clone();
        this.rings = [];
        for (let i = 0; i < polygonIndex.rings.length; i++) {
            let tmpRing = [];
            for (let j = 0; j < polygonIndex.rings[i].length; j++) {
                tmpRing.push(polygonIndex.rings[i][j]);
            }
            this.rings.push(tmpRing);
        }
        return this;
    }
    getCenter(points, target = null) {
        let len = 0;
        if (target == null) {
            target = new Point();
        }
        this.rings.forEach(ring => {
            ring.forEach(line => {
                target.add(points[line]);
                len += 1;
            });
        });
        if (len > 0) {
            target.multiplyScalar(1 / len);
        }
        return target;
    }
    negate() {
        this.normal.negate();
        this.negateRings();
    }
    negateRings() {
        let newRings = [];
        this.rings.forEach(ring => {
            let newRing = [];
            for (let i = ring.length - 1; i >= 0; i--) {
                newRing.push(ring[i]);
            }
            newRings.push(newRing);
        });
        this.rings = newRings;
    }
    clone() {
        let res = new PolygonIndex();
        res.copy(this);
        return res;
    }
    fromJson(json) {
        this.normal.set(json.normal.x, json.normal.y, json.normal.z);
        this.rings = json.rings;
    }
    toJson() {
        let data = {
            version: "1.0",
            normal: {
                x: this.normal.x,
                y: this.normal.y,
                z: this.normal.z
            },
            rings: this.rings
        };
        return data;
    }
}
/**
 * 一般几何体
 */
class GeneralSolid extends Solid {
    constructor(points = [], faces = [], transformMatrix = new Matrix4()) {
        super();
        /**
         * 一般多边形
         */
        this.type = 'GeneralSolid';
        this.points = [];
        this.faces = [];
        this.points = [...points];
        this.faces = [...faces];
        this.transformMatrix.copy(transformMatrix);
    }
    copy(geo) {
        super.copy(geo);
        if (geo instanceof GeneralSolid) {
            this.points = [];
            geo.points.forEach(point => {
                this.points.push(point.clone());
            });
            this.faces = [];
            geo.faces.forEach(face => {
                this.faces.push(face.clone());
            });
        }
        return this;
    }
    clone() {
        return new GeneralSolid().copy(this);
    }
    equals(geo) {
        return false;
    }
    /**
     * 每个面生成实际多边形
     * @returns
     */
    getAllPolygons() {
        let res = [];
        for (let i = 0; i < this.faces.length; i++) {
            let polygon = this.getSurface(i);
            res.push(polygon);
        }
        return res;
    }
    /**
     * 获取基元索引列表 用于绑定
     */
    getGeoElementsIdxList() {
        return this.surfaceIndex;
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, 0);
        let max = new Vector3(Number.MIN_VALUE, Number.MIN_VALUE, 0);
        this.points.forEach(point => {
            min.x = Math.min(min.x, point.x);
            min.y = Math.min(min.y, point.y);
            max.x = Math.max(max.x, point.x);
            max.y = Math.max(max.y, point.y);
        });
        let box = new Box3(min, max);
        box.applyMatrix4(this.transformMatrix);
        return box;
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        return this.getAllPolygons();
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index) {
        if (index >= this.faces.length)
            return null;
        let polygon = new GeneralRegion([], this.faces[index].normal.clone());
        let rings = [];
        this.faces[index].rings.forEach(ring => {
            let realRing = [];
            ring.forEach(pointIndex => {
                realRing.push(new Point().copy(this.points[pointIndex]).applyMatrix4(this.transformMatrix));
            });
            rings.push(realRing);
        });
        _matrix3.setFromMatrix4(this.transformMatrix);
        const normal = this.faces[index].normal.clone().applyMatrix3(_matrix3);
        polygon.setByRingsAndNormal(rings, normal);
        return polygon;
    }
    fromJson(json) {
        super.fromJson(json);
        this.points = [];
        this.faces = [];
        json.points.forEach(point => {
            let newPoint = new Point(point.x, point.y, point.z);
            this.points.push(newPoint);
        });
        json.faces.forEach(face => {
            let newFace = new PolygonIndex();
            newFace.normal.set(face.normal.x, face.normal.y, face.normal.z);
            newFace.rings = face.rings;
            this.faces.push(newFace);
        });
        return this;
    }
    toJson() {
        let data = Object.assign(Object.assign({}, super.toJson()), { version: "1.0", type: 'GeneralSolid', points: this.points.map(point => { return { x: point.x, y: point.y, z: point.z }; }), faces: this.faces.map(face => { return face.toJson(); }) });
        return data;
    }
}
const _matrix3 = new Matrix3();/**
 * 计算机编程的通用基础库
 */
var BaseUtils;
(function (BaseUtils) {
    /**
     * 保留小数点后n位
     * @param value
     * @param n
     * @returns
     */
    BaseUtils.NumFixed = (value, n) => {
        return Math.round(value * Math.pow(10, n)) / Math.pow(10, n);
    };
    const _lut = [];
    /**
     * 随机生成全局唯一uuid
     * @returns
     */
    function genUUID() {
        if (_lut.length == 0) {
            for (let i = 0; i < 256; i++) {
                _lut[i] = (i < 16 ? '0' : '') + (i).toString(16);
            }
        }
        const d0 = Math.random() * 0xffffffff | 0;
        const d1 = Math.random() * 0xffffffff | 0;
        const d2 = Math.random() * 0xffffffff | 0;
        const d3 = Math.random() * 0xffffffff | 0;
        const uuid = _lut[d0 & 0xff] + _lut[(d0 >> 8) & 0xff] + _lut[(d0 >> 16) & 0xff] + _lut[(d0 >> 24) & 0xff] + '-' +
            _lut[d1 & 0xff] + _lut[(d1 >> 8) & 0xff] + '-' + _lut[((d1 >> 16) & 0x0f) | 0x40] + _lut[(d1 >> 24) & 0xff] + '-' +
            _lut[(d2 & 0x3f) | 0x80] + _lut[(d2 >> 8) & 0xff] + '-' + _lut[(d2 >> 16) & 0xff] + _lut[(d2 >> 24) & 0xff] +
            _lut[d3 & 0xff] + _lut[(d3 >> 8) & 0xff] + _lut[(d3 >> 16) & 0xff] + _lut[(d3 >> 24) & 0xff];
        // .toUpperCase() here flattens concatenated strings to save heap memory space.
        // return uuid.toUpperCase().replace(/-/g, '');
        return uuid;
    }
    BaseUtils.genUUID = genUUID;
    /**
     * 随机生成全局唯一uuid，不带短横线
     * @returns
     */
    function genUUIDNoLine() {
        return genUUID().toLowerCase().replace(/-/g, "");
    }
    BaseUtils.genUUIDNoLine = genUUIDNoLine;
    /**
     * 用以验证antd输入的字符一定是浮点数字符
     * 输入输出示例
     * 1.041  => 1.041
     * -1.30 => -1.30
     * ---2.41 => -2.41
     * ANF-3 => 3
     * ANF-3.4GF5 => 3.45
     * AIEGHQING => 空字符串
     */
    function FloatVerification(value) {
        if (!value)
            return '';
        //按照.对字符串进行分割
        const tempArr = value.split('.');
        let minus = "";
        //如果不是空字符串，且第一位为“-”，说明输入的浮点数为负数，在之后的结果中加
        if ((value === null || value === void 0 ? void 0 : value.length) > 0 && value[0] == "-")
            minus = "-";
        if (tempArr.length >= 2) {
            //则将除了[0-9]和.号的其他字符全部替换为空字符
            return minus + `${tempArr[0]}.${tempArr[1]}`.replace(/([^\d{1,}\.\d{1,}|\d{1,}])|,+/g, '');
        }
        else {
            return minus + value.replace(/([^\d{1,}\.\d{1,}|\d{1,}])|,+/g, '');
        }
    }
    BaseUtils.FloatVerification = FloatVerification;
    /**
     * 用以验证antd输入的字符一定是非负整数字符
     * 输入输出示例
     * 1.041  => 1041
     * -1.30 => 130
     * ---2.41 => 2.41
     * ANF-3 => 3
     * ANF-3.4GF5 => 3.45
     * AIEGHQING => 空字符串
     */
    function NonNegativeIntegerVerification(value) {
        if (!value)
            return '';
        // 将除了 [0-9] 的其他字符全部替换为空字符
        return value.replace(/[^\d]/g, '');
    }
    BaseUtils.NonNegativeIntegerVerification = NonNegativeIntegerVerification;
    /**
     * 将值设置在min和max之间
     * @param value
     * @param min
     * @param max
     * @returns
     */
    function clamp(value, min, max) {
        return Math.max(min, Math.min(max, value));
    }
    BaseUtils.clamp = clamp;
    // compute euclidian modulo of m % n
    // https://en.wikipedia.org/wiki/Modulo_operation
    function euclideanModulo(n, m) {
        return ((n % m) + m) % m;
    }
    BaseUtils.euclideanModulo = euclideanModulo;
    /**
     * 获取下一点的索引，如果current是最后一个节点，则返回第一个点
     * @param current 当前索引
     * @param length 数组长度
     * @returns
     */
    function getNextIndex(current, length) {
        return current === length - 1 ? 0 : current + 1;
    }
    BaseUtils.getNextIndex = getNextIndex;
    /**
     * 获取上一点的索引，如果current是第一个节点，则返回最后一点
     * @param current 当前索引
     * @param length 数组长度
     * @returns
     */
    function getLastIndex(current, length) {
        return current === 0 ? length - 1 : current - 1;
    }
    BaseUtils.getLastIndex = getLastIndex;
})(BaseUtils || (BaseUtils = {}));/**
 * 闭区间
 */
class ClosedInterval {
    constructor(datas = [], needMerge = false) {
        this.datas = [];
        this.datas = datas;
        this.sort();
        if (needMerge) {
            this.merge();
        }
        this.needMerge = needMerge;
    }
    /**
     *
     * @param data 向闭区间中添加区间
     */
    add(data) {
        this.datas.push(data);
        this.sort();
    }
    /**
     * 闭区间排序
     */
    sort() {
        this.datas.sort((a, b) => a[0] - b[0]);
    }
    /**
     * 获取闭区间数据
     * @returns
     */
    getData() {
        return this.datas;
    }
    /**
     * 设置数据
     * @param datas
     */
    setData(datas) {
        this.datas = datas;
    }
    /**
     *  区间类合并函数,可将排好序的区间进行合并区间
     *  eg [[-3, 7], [1, 9], [11, 13]] =>  [[-3, 9], [11, 13]]
     */
    merge() {
        const newDatas = [];
        for (const data of this.datas) {
            if (!newDatas.length || newDatas[newDatas.length - 1][1] < data[0]) {
                // c 大
                newDatas.push(data);
            }
            else {
                // b 大
                // 将 b 和 d 中大的作为后一个元素
                newDatas[newDatas.length - 1][1] = Math.max(newDatas[newDatas.length - 1][1], data[1]);
            }
        }
        this.datas = newDatas;
    }
    /**
     * 求a的补集
     * @param a
     */
    getComplement() {
        const newDatas = [];
        if (this.datas.length === 0) {
            return new ClosedInterval([[-Infinity, Infinity]]);
        }
        newDatas.push([-Infinity, this.datas[0][0]]);
        let i = 0;
        while (i < this.datas.length - 1) {
            newDatas.push([this.datas[i][1], this.datas[i + 1][0]]);
            i += 1;
        }
        newDatas.push([this.datas[i][1], Infinity]);
        return new ClosedInterval(newDatas, this.needMerge);
    }
}
/**
 * 布尔操作算法类
 */
var BoolOperation;
(function (BoolOperation) {
    /**
     * 一维闭区间的布尔操作算法
     * @param a
     * @param b
     * @param ope
     * @returns
     */
    function boolOperation1D(a, b, ope = BoolOpeType.INTERSECT) {
        if (ope === BoolOpeType.UNION) {
            const newDatas = [...a.getData(), ...b.getData()];
            return new ClosedInterval(newDatas, a.needMerge);
        }
        else if (ope === BoolOpeType.SUBSTRACT) {
            return boolOperation1D(a, b.getComplement(), BoolOpeType.INTERSECT);
        }
        else if (ope === BoolOpeType.INTERSECT) {
            const A = a.getData();
            const B = b.getData();
            let i = 0;
            let j = 0;
            let result = [];
            while (i < A.length && j < B.length) {
                const [a1, a2] = A[i];
                const [b1, b2] = B[j];
                // 计算有交集的情况
                const tmpMin = Math.max(a1, b1);
                const tmpMax = Math.min(a2, b2);
                if (b2 >= a1 && a2 >= b1 && tmpMin < tmpMax) {
                    // 计算出交集
                    result.push([tmpMin, tmpMax]);
                }
                // 指针前进
                if (b2 < a2) {
                    j += 1;
                }
                if (b2 >= a2) {
                    i += 1;
                }
            }
            return new ClosedInterval(result, a.needMerge);
        }
        else {
            return new ClosedInterval();
        }
    }
    BoolOperation.boolOperation1D = boolOperation1D;
    /**
     * 二维布尔操作，操作后的图元默认采用left的局部坐标系
     * @param left 操作左侧多边形
     * @param right 操作右侧多边形
     * @param ope 操作类型
     * @returns
     */
    function boolOperation2D(left, right, ope = BoolOpeType.INTERSECT) {
        if (!left && !right)
            return new GeneralRegion(); //left和right都为空，直接返回空
        if (!left)
            return right.clone(); //left为空，返回right
        if (!right)
            return left.clone(); //right为空，返回left
        if (left.normal.cross(right.normal).length() > 1e-6 || GeoRelation.DistanceToPoint(left, right.getP()) > 1e-6) { //如果不共面，直接返回左
            return left.clone();
        }
        let leftResult = _generateInOnOutForPolygon(left, right, true);
        // console.log("=====================================================================================")
        let rightResult = _generateInOnOutForPolygon(right, left);
        // console.log("leftResult", leftResult)
        // console.log("rightResult", rightResult)
        let left_in_right = leftResult.in;
        let left_on_right = leftResult.on;
        let left_out_right = leftResult.out;
        let right_in_left = rightResult.in;
        // let right_on_left = rightResult.on;
        let right_out_left = rightResult.out;
        let res = new GeneralRegion();
        res.transformMatrix.copy(left.transformMatrix);
        if (ope === BoolOpeType.INTERSECT) {
            res.rings = GeoRelation.extractAllLoops([...left_in_right, ...right_in_left, ...left_on_right]);
        }
        else if (ope === BoolOpeType.UNION) {
            res.rings = GeoRelation.extractAllLoops([...left_out_right, ...right_out_left, ...left_on_right]);
        }
        else { //默认作差
            res.rings = GeoRelation.extractAllLoops([...left_out_right, ...right_in_left, ...left_on_right]);
        }
        return res;
    }
    BoolOperation.boolOperation2D = boolOperation2D;
    function boolOperation3D(left, right, ope = BoolOpeType.INTERSECT) {
        let res = new GeneralSolid();
        // console.log("a ---> b ++++");
        let resA = _generateInOnOutForSolid(left, right);
        // console.log("b ---> a ++++");
        let resB = _generateInOnOutForSolid(right, left);
        let a_in_b = resA.in;
        let a_on_b = resA.on;
        let a_out_b = resA.out;
        let b_in_a = resB.in;
        // let b_on_a = resB.on;
        let b_out_a = resB.out;
        let points = [];
        let faces = [];
        if (ope === BoolOpeType.INTERSECT) {
            removeDuplicatePoint(a_in_b, points, faces);
            removeDuplicatePoint(b_in_a, points, faces);
            removeDuplicatePoint(a_on_b, points, faces);
        }
        else if (ope === BoolOpeType.SUBSTRACT) {
            removeDuplicatePoint(a_out_b, points, faces);
            removeDuplicatePoint(a_on_b, points, faces);
            let b_in_a_inverse = [];
            b_in_a.forEach(item => {
                b_in_a_inverse.push(item.getNegate());
            });
            removeDuplicatePoint(b_in_a_inverse, points, faces);
        }
        else if (ope === BoolOpeType.UNION) {
            removeDuplicatePoint(a_out_b, points, faces);
            removeDuplicatePoint(b_out_a, points, faces);
            removeDuplicatePoint(a_on_b, points, faces);
        }
        res.faces = faces;
        res.points = points;
        return res;
    }
    BoolOperation.boolOperation3D = boolOperation3D;
    /**
     * GeneralSolid顶点去重
     * @param polygonItem
     * @param points
     * @param faces
     */
    function removeDuplicatePoint(polygonItem, points, faces) {
        polygonItem.forEach(item => {
            // console.log("item in removeDuplicatePoint", item.virtual, item.polygon);
            let pIndex = new PolygonIndex([], item.normal.clone());
            item.rings.forEach(ring => {
                let ringIndex = [];
                ring.forEach(point2 => {
                    let alreadyInPoints = false;
                    let start = 0;
                    _point3.set(point2.x, point2.y, 0).applyMatrix4(item.transformMatrix);
                    for (let i = 0; i < points.length; i++) {
                        if (_point3.equals(points[i])) {
                            alreadyInPoints = true;
                            start = i;
                            break;
                        }
                    }
                    if (!alreadyInPoints) {
                        start = points.length;
                        points.push(_point3.clone());
                    }
                    ringIndex.push(start);
                });
                pIndex.rings.push(ringIndex);
            });
            faces.push(pIndex);
        });
    }
    BoolOperation.removeDuplicatePoint = removeDuplicatePoint;
    /**
     * 三维布尔操作核心逻辑
     * @param a 一般体a
     * @param b 一般体b
     * @returns 返回一般体a在一般体b的内部、面上、外部的PolygonItem集合
     */
    function _generateInOnOutForSolid(a, b) {
        let res = { out: [], in: [], on: [] };
        let polygonItemOfA = a.getAllPolygons();
        let polygonItemOfB = b.getAllPolygons();
        for (let i = 0; i < polygonItemOfA.length; i++) {
            let itemA = polygonItemOfA[i];
            let shouldBeOut = true;
            let polygonOnPlaneA = [];
            let intersectLines = [];
            let shouldBeIn = false;
            // console.log("==============================================================================================");
            polygonItemOfB.forEach(itemB => {
                let inResultPlaneAAndPolygonB = GeoIntersection.polygonIntersectPlane(itemB, itemA, null, true); //B上多边形与A上多边形所在平面的交线集合
                if (inResultPlaneAAndPolygonB.status === 2) {
                    //重合
                    if (itemA.normal.dot(itemB.normal) < 0) {
                        //如果法向量反向，将polygon_b反转
                        polygonOnPlaneA.push(itemB.getNegate());
                    }
                    else {
                        polygonOnPlaneA.push(itemB);
                    }
                    // console.log("---------------polygonOnPlaneA-----------------", polygonOnPlaneA);
                    shouldBeOut = false;
                }
                else if (inResultPlaneAAndPolygonB.status === 1) {
                    //相交
                    if (inResultPlaneAAndPolygonB.lines.length > 0) {
                        shouldBeOut = false;
                        if (!inResultPlaneAAndPolygonB.onlyOn) {
                            shouldBeIn = true;
                        }
                        // console.log("多边形", itemB.polygon, "与平面", planeA, "相交结果:", inResultPlaneAAndPolygonB.lines)
                        intersectLines = [...intersectLines, ...inResultPlaneAAndPolygonB.lines];
                        // intersectLines.forEach(line => {
                        //     console.log("当前交线集合：：", 'start: ', line.start.x.toFixed(4), line.start.y.toFixed(4), line.start.z.toFixed(4), 'end: ',  line.end.x.toFixed(4), line.end.y.toFixed(4), line.end.z.toFixed(4))
                        // });
                        // console.log('\n');
                    }
                }
                //平行不考虑
            });
            if (shouldBeOut && itemA.isVaild()) {
                res.out.push(itemA);
                continue;
            }
            polygonOnPlaneA.forEach(polygon => {
                let tmpPolygon = boolOperation2D(itemA.clone(), polygon.clone(), BoolOpeType.INTERSECT);
                if (tmpPolygon.isVaild()) {
                    res.on.push(tmpPolygon);
                }
            });
            let allPolygons = [...polygonOnPlaneA];
            if (intersectLines.length > 0 && shouldBeIn) {
                // console.log("bool 3d拼交面")
                // console.log("交线长度大于0了，接下来准备拼交面了!!!");
                // intersectLines.forEach(line => {
                //     console.log("start:", line.start.x.toFixed(6), line.start.y.toFixed(6), line.start.z.toFixed(6), "end:", line.end.x.toFixed(6), line.end.y.toFixed(6), line.end.z.toFixed(6))
                // })
                let intersectLinesPolygon = GeoRelation.extractAllLoops3D(intersectLines);
                if (!!intersectLinesPolygon && !GeneralRegion.isEmptyRing(intersectLinesPolygon)) {
                    let tmpGeneralRegion = new GeneralRegion();
                    tmpGeneralRegion.setByRingsAndNormal(intersectLinesPolygon, null, false);
                    if (tmpGeneralRegion.normal.dot(itemA.normal) < 0) {
                        tmpGeneralRegion.negate();
                    }
                    let tmpPolygon = boolOperation2D(tmpGeneralRegion, itemA, BoolOpeType.INTERSECT);
                    if (tmpPolygon.isVaild()) {
                        res.in.push(tmpPolygon);
                    }
                    allPolygons.push(tmpGeneralRegion);
                }
            }
            let outPolygons = [itemA.clone()]; // 初始化一个包含初始多边形的数组
            allPolygons.forEach(item => {
                // 用全体部分减去在多边形内部的部分，得到更新后的多边形数组
                let updatedPolygons = [];
                outPolygons.forEach(outPolygon => {
                    updatedPolygons = updatedPolygons.concat(boolOperation2D(outPolygon, item, BoolOpeType.SUBSTRACT)); // 将每个 outPolygon 与当前 item 进行布尔运算，得到更新后的多边形数组
                });
                outPolygons = updatedPolygons; // 更新 outPolygons 为更新后的多边形数组
            });
            // 将结果添加到 res.out 数组中
            outPolygons.forEach(outPolygon => {
                if (outPolygon.isVaild()) {
                    res.out.push(outPolygon);
                }
            });
        }
        return res;
    }
    BoolOperation._generateInOnOutForSolid = _generateInOnOutForSolid;
    /**
     * 二维布尔操作核心逻辑
     * @param left 多边形left
     * @param right 多边形right
     * @param needChangeToLeft 将所求得的点转化到left的局部坐标空间中
     * @returns 返回多边形left在多边形right的内部、边上、外部的线段集合
     */
    function _generateInOnOutForPolygon(left, right, needChangeToLeft = false) {
        let res = { out: [], in: [], on: [] }; //返回结果
        //获取左图元所有边（平铺开）
        let allLinesOfLeft = [];
        let ringsOfLeftInRightUcs = []; //left图元的环在right的世界坐标系下的位置
        _matrix1.copy(right.transformMatrix).invert(); //right图元的逆矩阵
        left.rings.forEach(ring => {
            let newRing = [];
            ring.forEach(point2InLeftUcs => {
                // console.log("left point", point2InLeftUcs.x, point2InLeftUcs.y)
                _point3.set(point2InLeftUcs.x, point2InLeftUcs.y, 0).applyMatrix4(left.transformMatrix); //left转世界
                _point3.applyMatrix4(_matrix1); //世界转right
                let point2InRightUcs = new Point2(_point3.x, _point3.y);
                // console.log("right point", _point3.x, _point3.y, "\n")
                newRing.push(point2InRightUcs);
            });
            ringsOfLeftInRightUcs.push(newRing);
        });
        ringsOfLeftInRightUcs.forEach(ring => {
            for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                allLinesOfLeft.push([ring[pointIndex], ring[BaseUtils.getNextIndex(pointIndex, ring.length)]]);
            }
        });
        // console.log("matrix_left", left.transformMatrix.elements);
        // console.log("matrix_right", right.transformMatrix.elements);
        // console.log("matrix_right_inserve", _matrix1.elements);
        // console.log("ringsOfLeftInRightUcs", ringsOfLeftInRightUcs.map(loop => loop.map(p => `(${p.x}, ${p.y})`)));
        // console.log("right.rings", right.rings.map(loop => loop.map(p => `(${p.x}, ${p.y})`)))
        //遍历left中所有铺开的线段
        allLinesOfLeft.forEach(line2 => {
            // console.log("line2 start", line2[0].x, line2[0].y, "line2 end", line2[1].x, line2[1].y)
            if (line2[0].equals(line2[1])) { //两点重合，直接舍弃
                return;
            }
            let tmpRes = _generateInOnOutForLine(line2, right);
            res.out = [...res.out, ...tmpRes.out];
            res.in = [...res.in, ...tmpRes.in];
            res.on = [...res.on, ...tmpRes.on];
        });
        if (needChangeToLeft) {
            _matrix2.copy(left.transformMatrix).invert();
            res.out.forEach(line2 => {
                _point3.set(line2[0].x, line2[0].y, 0);
                _point3.applyMatrix4(right.transformMatrix).applyMatrix4(_matrix2);
                line2[0].set(_point3.x, _point3.y);
                _point3.set(line2[1].x, line2[1].y, 0);
                _point3.applyMatrix4(right.transformMatrix).applyMatrix4(_matrix2);
                line2[1].set(_point3.x, _point3.y);
            });
            res.in.forEach(line2 => {
                _point3.set(line2[0].x, line2[0].y, 0);
                _point3.applyMatrix4(right.transformMatrix).applyMatrix4(_matrix2);
                line2[0].set(_point3.x, _point3.y);
                _point3.set(line2[1].x, line2[1].y, 0);
                _point3.applyMatrix4(right.transformMatrix).applyMatrix4(_matrix2);
                line2[1].set(_point3.x, _point3.y);
            });
            res.on.forEach(line2 => {
                _point3.set(line2[0].x, line2[0].y, 0);
                _point3.applyMatrix4(right.transformMatrix).applyMatrix4(_matrix2);
                line2[0].set(_point3.x, _point3.y);
                _point3.set(line2[1].x, line2[1].y, 0);
                _point3.applyMatrix4(right.transformMatrix).applyMatrix4(_matrix2);
                line2[1].set(_point3.x, _point3.y);
            });
        }
        return res;
    }
    BoolOperation._generateInOnOutForPolygon = _generateInOnOutForPolygon;
    /**
     * 求得一条二维线段在另一个多边形内部、边界与外部的部分
     * @param line2
     * @param region
     * @returns
     */
    function _generateInOnOutForLine(line2, region) {
        let res = { out: [], in: [], on: [] };
        //1.先求region在直线line2上的部分
        let onInterval = new ClosedInterval([]); //a_on_b的所有部分在a上的定比分点值所组成的区间
        region.rings.forEach(ring => {
            for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                //******算法起始：判断两直线是否共线******
                let start = ring[pointIndex];
                let end = ring[BaseUtils.getNextIndex(pointIndex, ring.length)];
                if (start.equals(end)) {
                    continue;
                }
                // console.log("line2 start", line2[0].x, line2[0].y, "line2 end", line2[1].x, line2[1].y, "segment start", start.x, start.y, "segment end", end.x, end.y)
                let A = line2[0].y - line2[1].y;
                let B = line2[1].x - line2[0].x;
                let C = line2[0].x * line2[1].y - line2[1].x * line2[0].y;
                let length = Math.sqrt(A * A + B * B);
                // console.log("A", A, "B", B, "C", C, "length", length, "eee", A * start.x + B * start.y + C, A * end.x + B * end.y + C);
                if (Math.abs(A * start.x + B * start.y + C) / length < EPSILON && Math.abs(A * end.x + B * end.y + C) / length < EPSILON) {
                    //共线
                    let startRatio = GeoRelation.ratioToLine2(start, line2[0], line2[1]);
                    let endRatio = GeoRelation.ratioToLine2(end, line2[0], line2[1]);
                    let tmpClosedInterval = boolOperation1D(new ClosedInterval([[startRatio, endRatio]]), new ClosedInterval([[0, 1]]), BoolOpeType.INTERSECT);
                    onInterval = boolOperation1D(onInterval, tmpClosedInterval, BoolOpeType.UNION);
                }
                //******算法结束：判断两直线是否共线******
            }
        });
        let line2Dir = { x: line2[1].x - line2[0].x, y: line2[1].y - line2[0].y };
        // console.log("onIntervalonIntervalonInterval", onInterval.getData())
        onInterval.getData().forEach(data => {
            res.on.push([
                new Point2(line2[0].x + data[0] * line2Dir.x, line2[0].y + data[0] * line2Dir.y),
                new Point2(line2[0].x + data[1] * line2Dir.x, line2[0].y + data[1] * line2Dir.y)
            ]);
        });
        let intersectionPointsWithLine = _straightLineIntersectPolygon2d(line2, region);
        // console.log("intersectionPointsWithLine", intersectionPointsWithLine.map(item => `p(${item.point.x},${item.point.y})`))
        if (intersectionPointsWithLine.length === 0) {
            //如果没有交点，那么整条a都是位于多边形b的外侧的
            res.out.push([line2[0].clone(), line2[1].clone()]);
            return res;
        }
        let intersectionPointInfos = [];
        intersectionPointsWithLine.forEach(item => {
            intersectionPointInfos.push({
                line: item.line,
                point: item.point,
                t: GeoRelation.ratioToLine2(item.point, line2[0], line2[1])
            });
        });
        intersectionPointInfos.sort((a, b) => a.t - b.t);
        // console.log("intersectionPointInfos", intersectionPointInfos.map(item => `p(${item.point.x},${item.point.y}), t(${item.t}), l(${item.line[0].x},${item.line[0].y}  ,${item.line[1].x},${item.line[1].y})`))
        let totalInterval = new ClosedInterval([[-Infinity, intersectionPointInfos[0].t]]);
        // console.log("totalInterval", totalInterval.getData());
        for (let i = 0; i < intersectionPointInfos.length; i++) {
            if (i < intersectionPointInfos.length - 1) {
                totalInterval = boolOperation1D(totalInterval, new ClosedInterval([[intersectionPointInfos[i].t, intersectionPointInfos[i + 1].t]]), BoolOpeType.UNION);
                // console.log("totalInterval", totalInterval.getData());
            }
            else {
                //最后一个
                totalInterval = boolOperation1D(totalInterval, new ClosedInterval([[intersectionPointInfos[i].t, Infinity]]), BoolOpeType.UNION);
                // console.log("totalInterval", totalInterval.getData());
            }
        }
        totalInterval = boolOperation1D(totalInterval, new ClosedInterval([[0, 1]]), BoolOpeType.INTERSECT);
        // console.log("totalInterval", totalInterval.getData());
        let otherInterval = boolOperation1D(totalInterval, onInterval, BoolOpeType.SUBSTRACT);
        // console.log("otherInterval", otherInterval.getData());
        otherInterval.getData().forEach(data => {
            let t = (data[0] + data[1]) / 2;
            let startpoint = new Point2(line2[0].x + data[0] * line2Dir.x, line2[0].y + data[0] * line2Dir.y);
            let endpoint = new Point2(line2[0].x + data[1] * line2Dir.x, line2[0].y + data[1] * line2Dir.y);
            let midpoint = new Point2(line2[0].x + t * line2Dir.x, line2[0].y + t * line2Dir.y);
            // console.log("midpoint", midpoint.x, midpoint.y, "status", GeoRelation.Point2InGeneralRegion(region, midpoint))
            if (GeoRelation.Point2InGeneralRegion(region, midpoint) === GeoRelation.PointInRegion.out) {
                res.out.push([startpoint, endpoint]);
            }
            else {
                res.in.push([startpoint, endpoint]);
            }
        });
        return res;
    }
    BoolOperation._generateInOnOutForLine = _generateInOnOutForLine;
    // /**
    //  * 根据t获取二维直线段上的点
    //  * @param start 
    //  * @param end 
    //  * @param t 
    //  * @param target 
    //  * @returns 
    //  */
    // function getPointAtByLine2(start: Point2, end: Point2, t: number, target: Point2 = null) {
    //     if (target == null) {
    //         target = new Point2();
    //     }
    //     target.copy(end).multiplyScalar(t).add(start);
    //     return target;
    // }
    /**
     * 在同一平面上的直线与多边形求交，返回直线与多边形交点与交点所在线段。
     * @param a
     * @param b
     * @returns
     */
    function _straightLineIntersectPolygon2d(line2, b) {
        let res = [];
        b.rings.forEach(ring => {
            for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                let start = ring[pointIndex];
                let end = ring[BaseUtils.getNextIndex(pointIndex, ring.length)];
                // console.log("line2 start", line2[0].x, line2[0].y, "line2 end", line2[1].x, line2[1].y);
                // console.log("segment start", start.x, start.y, "segment end", end.x, end.y);
                let intersectPointInfo = GeoIntersection.straightLine2IntersectLine2(line2, [start, end]);
                // console.log("intersectPointInfo", intersectPointInfo.status, intersectPointInfo.t);
                if (intersectPointInfo.status === 1) {
                    res.push({ line: [start, end], point: intersectPointInfo.point });
                }
            }
        });
        return res;
    }
    BoolOperation._straightLineIntersectPolygon2d = _straightLineIntersectPolygon2d;
})(BoolOperation || (BoolOperation = {}));
const _point3 = new Point();
const _matrix1 = new Matrix4();
const _matrix2 = new Matrix4();var GeoIntersection;
(function (GeoIntersection) {
    /**
    * 求直线段和平面的交点
    * @param line
    * @param plane
    * @param target 返回值
    * @returns
    */
    function LineIntersectPlane(line, plane, target = null) {
        if (target == null) {
            target = new Point();
        }
        const start = line.start;
        const end = line.end;
        const normal = plane.normal;
        const constant = plane.constant;
        const direction = end.clone().sub(start);
        // const direction = end.clone().sub(start).normalize();
        const denominator = normal.dot(direction); //TODO 可以考虑做成基本函数，判断共线和垂直
        if (Math.abs(denominator) < 1e-6) {
            // line is coplanar, return origin
            if (Math.abs(GeoMeasurement.PointDistanceToPlane(start, plane)) < 1e-6) {
                return target.copy(start);
            }
            // Unsure if this is the correct method to handle this case.
            return null;
        }
        const t = -(start.dot(normal) + constant) / denominator;
        if (t < -1e-6 || t > 1 + 1e-6) {
            return null;
        }
        return target.copy(direction).multiplyScalar(t).add(start);
    }
    GeoIntersection.LineIntersectPlane = LineIntersectPlane;
    /**
     * 两直线求交
     * @param aline
     * @param sline
     * @param target
     * @returns
     */
    function StraightLineIntersectStraightLine(aline, sline, target = null) {
        let t = LineIntersectLineParameter(aline.origin, aline.direction, sline.origin, sline.direction);
        if (t === null) {
            target = null;
            return null;
        }
        if (target == null) {
            target = new Point();
        }
        target.copy(aline.origin).add(aline.direction.clone().multiplyScalar(t));
        return target;
    }
    GeoIntersection.StraightLineIntersectStraightLine = StraightLineIntersectStraightLine;
    /**
     * 求b线与a线的交点在a线上的参数t
     * @param originP a线起点
     * @param pDir a线方向
     * @param originQ b线起点
     * @param qDir b线方向
     * @returns
     */
    function LineIntersectLineParameter(originP, pDir, originQ, qDir) {
        // https://www.cnblogs.com/xiangtingshen/p/12329951.html
        if (pDir.clone().cross(qDir).length() < 1e-6) {
            // 平行
            return null;
        }
        let AC = originQ.clone().sub(originP);
        if (!(Math.abs(qDir.clone().cross(pDir).normalize().dot(AC)) < 1e-6)) {
            // 不共面
            return null;
        }
        let sACD = AC.clone().cross(qDir); // AC X CD / 2 = sACD
        let sCED = pDir.clone().cross(qDir); // AB X CD / 2 = sCED
        let sign = Math.sign(sACD.dot(sCED));
        let t = sign * sACD.length() / sCED.length(); // t 为AO/AB
        return t;
    }
    GeoIntersection.LineIntersectLineParameter = LineIntersectLineParameter;
    /**
     * 两线交点
     * @param A
     * @param B
     * @param C
     * @param D
     * @returns
     */
    function LineSegmentIntersection(A, B, C, D) {
        // 计算方向向量
        const AB = B.clone().sub(A);
        const CD = D.clone().sub(C);
        const AC = C.clone().sub(A);
        const AD = D.clone().sub(A);
        // 判断是否平行
        const crossABCD = AB.clone().cross(CD);
        if (crossABCD.length() < 1e-6) {
            return null; // 平行
        }
        _ab.copy(AB).normalize();
        _ac.copy(AC).normalize();
        _ad.copy(AD).normalize();
        /**
         * 构造一个 3x3 矩阵并计算行列式，判断是否共面
         */
        const matrix = new Matrix3().set(_ab.x, _ac.x, _ad.x, _ab.y, _ac.y, _ad.y, _ab.z, _ac.z, _ad.z);
        /**
         * 若是线性相关，则共面，继续进行交点判断
         */
        if (Math.abs(matrix.determinant()) > 1e-3) {
            return null;
        }
        // 计算 t 和 s
        const crossACCD = AC.clone().cross(CD);
        const t = crossACCD.dot(crossABCD) / crossABCD.lengthSq();
        const crossACAB = AC.clone().cross(AB);
        const s = crossACAB.dot(crossABCD) / crossABCD.lengthSq();
        // 检查 t 和 s 是否在 [0, 1] 范围内
        if (t < -1e-6 || t > 1 + 1e-6 || s < -1e-6 || s > 1 + 1e-6) {
            return null; // 线段不相交
        }
        // 计算交点
        const intersection = A.clone().add(AB.multiplyScalar(t));
        return intersection;
    }
    GeoIntersection.LineSegmentIntersection = LineSegmentIntersection;
    /**
     * 求圆弧与线的交点
     * @param arc 圆弧
     * @param line 线段
     * @param clampToArc 交点是否限制在圆弧上
     * @param clampToLine 交点是否限制在线段上
     * @returns
     */
    function ArcIntersectLine(arc, line, clampToArc = false, clampToLine = true) {
        let pDir = line.getX();
        if (pDir.length() < 1e-6)
            return [];
        if (Math.abs(arc.radius) < 1e-6)
            return [];
        let denominator = arc.normal.dot(pDir.normalize());
        let plane = new Plane();
        plane.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
        if (Math.abs(denominator) > 1e-3) {
            // 不共面
            let pointInPlane = PlaneIntersectLine(plane, line);
            // 线段与圆弧所在平面不相交
            if (pointInPlane == null)
                return [];
            let dist = pointInPlane.distanceTo(arc.center);
            // 线段的交点至圆弧圆心的距离不等于半径
            if (Math.abs(dist - arc.radius) > 1e-3)
                return [];
            if (clampToArc) {
                // 需要判断在圆弧上
                let s = arc.getPointParameter(pointInPlane);
                if (s < 0 || s > 1)
                    return [];
            }
            return [pointInPlane];
        }
        // 共面或者平行
        let lineDisCheck = GeoMeasurement.PointDistanceToPlane(line.getP(), plane);
        if (Math.abs(lineDisCheck) > 1e-2) {
            // 平行不共面
            return [];
        }
        // 共面
        let foot = GeoRelation.LineClosestPointToPoint(line, arc.center, false);
        let dist = foot.distanceTo(arc.center);
        if (dist > arc.radius) {
            // 无交点
            return [];
        }
        if (Math.abs(dist - arc.radius) < 1e-6) {
            // 相切
            let t = GeoRelation.LineClosestPointToPointParameter(line, foot, false);
            if (clampToLine) {
                //需要判断在线上
                if (t > 1 || t < 0)
                    return [];
            }
            if (clampToArc) {
                // 需要判断在圆弧上
                let s = arc.getPointParameter(foot);
                if (s < 0 || s > 1)
                    return [];
            }
            return [foot];
        }
        // 相交 两个交点
        let len = Math.sqrt(arc.radius ** 2 - dist ** 2);
        let res = [];
        let point1 = foot.clone().sub(pDir.clone().multiplyScalar(len));
        let t1 = GeoRelation.LineClosestPointToPointParameter(line, point1, false);
        if ((t1 >= -1e-6 && t1 <= 1 + 1e-6) || !clampToLine) {
            let s = arc.getPointParameter(point1);
            if ((s >= -1e-6 && s <= 1 + 1e-6) || !clampToArc) {
                res.push(point1);
            }
        }
        let point2 = foot.clone().add(pDir.clone().multiplyScalar(len));
        let t2 = GeoRelation.LineClosestPointToPointParameter(line, point2, false);
        if ((t2 >= -1e-6 && t2 <= 1 + 1e-6) || !clampToLine) {
            let s = arc.getPointParameter(point2);
            if ((s >= -1e-6 && s <= 1 + 1e-6) || !clampToArc) {
                res.push(point2);
            }
        }
        return res;
    }
    GeoIntersection.ArcIntersectLine = ArcIntersectLine;
    /**
     * 平面与线段的交点
     * @param plane 平面
     * @param line 线段
     * @param target
     */
    function PlaneIntersectLine(plane, line, target = null) {
        if (target == null) {
            target = new Point();
        }
        let start = line.start;
        let end = line.end;
        const direction = end.clone().sub(start);
        const denominator = plane.normal.dot(direction);
        if (denominator === 0) {
            // line is coplanar, return origin
            if (GeoMeasurement.PointDistanceToPlane(start, plane) === 0) {
                return target.copy(start);
            }
            // Unsure if this is the correct method to handle this case.
            return null;
        }
        const t = -(start.dot(plane.normal) + plane.constant) / denominator;
        if (t < -1e-6 || t > 1 + 1e-6) {
            return null;
        }
        return target.copy(direction).multiplyScalar(t).add(start);
    }
    GeoIntersection.PlaneIntersectLine = PlaneIntersectLine;
    /**
     * 求两圆弧的交点
     * @param arcA
     * @param arcB
     * @param clampToA 交点是否限制在圆弧A上
     * @param clampToB 交点是否限制在圆弧B上
     * @returns
     */
    function ArcIntersectArc(arcA, arcB, clampToA = false, clampToB = false) {
        if (arcA.radius === 0 || arcB.radius === 0)
            return [];
        let checkDis = arcA.normal.cross(arcB.normal).length();
        if (checkDis < 1e-3) {
            // 圆弧面平行
            let centerDir = arcB.center.clone().sub(arcA.center).normalize();
            let checkDot = centerDir.dot(arcA.normal);
            if (Math.abs(checkDot) > 1e-3) {
                // 平行不共面
                return [];
            }
            // 平行共面
            let centerDist = arcB.center.distanceTo(arcA.center);
            if (centerDist < 1e-6) {
                // 两个圆弧的圆心重合
                return [];
            }
            if (centerDist > arcB.radius + arcA.radius) {
                // 相离
                return [];
            }
            if (centerDist === arcB.radius + arcA.radius || Math.abs(arcA.radius - arcB.radius) === centerDist) {
                // 外切或内切
                if (arcB.radius - arcA.radius === centerDist) {
                    // this内切于arc
                    centerDir.multiplyScalar(-1);
                }
                let target = centerDir.clone().multiplyScalar(arcA.radius).add(arcA.center);
                let s = arcB.getPointParameter(target);
                if (clampToB && (s < 0 || s > 1))
                    return []; // 不在arc圆弧上
                let t = arcA.getPointParameter(target);
                if (clampToA && (t < -1e-6 || t > 1 + 1e-6)) {
                    // 不在this圆弧上
                    return [];
                }
                return [target];
            }
            if (Math.abs(arcA.radius - arcB.radius) > centerDist) {
                // 内相离
                return [];
            }
            // 相交
            // 利用相交圆弧的勾股定理求出圆心连线和相交割线的交点
            let t = (arcA.radius * arcA.radius + centerDist * centerDist - arcB.radius * arcB.radius) / (2 * centerDist);
            let tPoint = centerDir.clone().multiplyScalar(t).add(arcA.center);
            let l_dir = arcA.normal.clone().cross(centerDir).normalize();
            let v_dist = Math.sqrt(arcA.radius * arcA.radius - t * t);
            let target1 = tPoint.clone().add(l_dir.clone().multiplyScalar(v_dist));
            let target2 = tPoint.clone().add(l_dir.clone().multiplyScalar(-v_dist));
            let res = [];
            let s1 = arcB.getPointParameter(target1);
            if ((s1 >= 0 && s1 <= 1) || !clampToB) {
                let t1 = arcA.getPointParameter(target1);
                if ((t1 >= 0 && t1 <= 1) || !clampToA) {
                    res.push(target1);
                }
            }
            let s2 = arcB.getPointParameter(target2);
            if ((s2 >= 0 && s2 <= 1) || !clampToB) {
                let t2 = arcA.getPointParameter(target2);
                if ((t2 >= 0 && t2 <= 1) || !clampToA) {
                    res.push(target2);
                }
            }
            return res;
        }
        // 不共面
        // 两个圆面交线的方向向量
        let dir = arcA.normal.clone().cross(arcB.normal).normalize();
        let arcPlane = new Plane();
        arcPlane.setFromNormalAndCoplanarPoint(arcB.normal, arcB.center);
        const centerDistToArcPlane = GeoMeasurement.PointDistanceToPlane(arcA.center, arcPlane);
        // this中一点到该交线作垂线，垂线的方向向量 乘dist为了保证方向朝着交线
        const l_dir1 = arcA.normal.clone().cross(dir).multiplyScalar(centerDistToArcPlane).normalize();
        const denominator1 = arcB.normal.dot(l_dir1);
        // this圆心到交线的最短距离
        const v_dist1 = Math.abs(centerDistToArcPlane / denominator1);
        let thisPlane = new Plane();
        thisPlane.setFromNormalAndCoplanarPoint(arcA.normal, arcA.center);
        const centerDistToThisPlane = GeoMeasurement.PointDistanceToPlane(arcB.center, thisPlane);
        // circle中一点到该交线作垂线，垂线的方向向量
        const l_dir2 = arcB.normal.clone().cross(dir).multiplyScalar(centerDistToThisPlane).normalize();
        const denominator2 = arcA.normal.dot(l_dir2);
        // circle圆心到交线的最短距离
        const v_dist2 = Math.abs(centerDistToThisPlane / denominator2);
        if (v_dist1 > arcA.radius || v_dist2 > arcB.radius) {
            // 相离
            return [];
        }
        // 交线上离this圆心最近的点
        const target = arcA.center.clone().add(l_dir1.multiplyScalar(v_dist1));
        if (Math.abs(v_dist1 - arcA.radius) < 1e-6) {
            // 相切
            // 切点不在arc圆弧上，不相交
            if (Math.abs(target.distanceTo(arcB.center) - arcB.radius) > 1e-6)
                return [];
            let s = arcB.getPointParameter(target);
            // 切点在arc圆周上，不在arc圆弧上，不相交
            if (clampToB) {
                if (s < 0 || s > 1)
                    return [];
            }
            if (clampToA) {
                let t = arcA.getPointParameter(target);
                // 切点在this的延长线上
                if (t < -1e-6 || t > 1 + 1e-6)
                    return [];
            }
            return [target];
        }
        // 该点target到圆弧与交线的交点之间的距离
        const i_dist = Math.sqrt(arcA.radius * arcA.radius - v_dist1 * v_dist1);
        let res = [];
        // 在交线上的圆弧上的两点
        const target1 = target.clone().add(dir.clone().multiplyScalar(i_dist));
        // 交点在另一个弧的半径上
        if (Math.abs(target1.distanceTo(arcB.center)) < 1e-6) {
            let s = arcB.getPointParameter(target1);
            if ((s >= 0 && s <= 1) || !clampToB) {
                let t = arcA.getPointParameter(target1);
                if ((t >= 0 && t <= 1) || !clampToA) {
                    res.push(target1);
                }
            }
        }
        const target2 = target.clone().add(dir.clone().multiplyScalar(-i_dist));
        if (Math.abs(target2.distanceTo(arcB.center)) < 1e-6) {
            let s = arcB.getPointParameter(target2);
            if ((s >= 0 && s <= 1) || !clampToB) {
                let t = arcA.getPointParameter(target2);
                if ((t >= 0 && t <= 1) || !clampToA) {
                    res.push(target2);
                }
            }
        }
        return res;
    }
    GeoIntersection.ArcIntersectArc = ArcIntersectArc;
    /**
     * 求圆弧与平面的交点
     * @param arc
     * @param plane
     * @param clampToArc 交点是否限制在圆弧上
     * @returns
     */
    function ArcIntersectPlane(arc, plane, clampToArc = false) {
        return GeoIntersection.PlaneIntersectArc(plane, arc, clampToArc);
    }
    GeoIntersection.ArcIntersectPlane = ArcIntersectPlane;
    /**
     * 求平面与圆弧交点
     * @param plane
     * @param arc
     * @param clampToArc 交点是否限制在圆弧上
     * @returns
     */
    function PlaneIntersectArc(plane, arc, clampToArc = false) {
        if (arc.radius === 0)
            return [];
        if (plane.normal.equals(arc.normal) || plane.normal.equals(arc.normal.clone().multiplyScalar(-1))) {
            // 圆弧与面平行
            return [];
        }
        // 不共面
        // 两个面交线的方向向量
        let dir = plane.normal.clone().cross(arc.normal).normalize();
        const centerDistToThisPlane = GeoMeasurement.PointDistanceToPlane(arc.center, plane);
        // arc圆心到该交线作垂线，垂线的方向向量，指向交线
        const l_dir2 = dir.clone().cross(arc.normal).multiplyScalar(centerDistToThisPlane).normalize();
        // arc圆心到交线的最短距离,利用距离沿平面法向投影等于圆心到平面距离
        const v_dist2 = Math.abs(centerDistToThisPlane / plane.normal.dot(l_dir2));
        if (v_dist2 > arc.radius) {
            // 相离
            return [];
        }
        // 交线上离this圆心最近的点
        const target = arc.center.clone().add(l_dir2.multiplyScalar(v_dist2));
        if (Math.abs(v_dist2 - arc.radius) < 1e-6) {
            // 相切
            // 切点不在arc圆弧上，不相交
            let s = arc.getPointParameter(target);
            if (s < 0 || s > 1 && clampToArc)
                return [];
            return [target];
        }
        // 该点target到圆弧与交线的交点之间的距离
        const i_dist = Math.sqrt(arc.radius * arc.radius - v_dist2 * v_dist2);
        let res = [];
        // 在交线上的圆弧上的两点
        const target1 = target.clone().add(dir.clone().multiplyScalar(i_dist));
        let s1 = arc.getPointParameter(target1);
        if ((s1 >= 0 && s1 <= 1) || !clampToArc)
            res.push(target1);
        const target2 = target.clone().add(dir.clone().multiplyScalar(-i_dist));
        let s2 = arc.getPointParameter(target2);
        if ((s2 >= 0 && s2 <= 1) || !clampToArc)
            res.push(target2);
        return res;
    }
    GeoIntersection.PlaneIntersectArc = PlaneIntersectArc;
    /**
     * 求圆弧与射线的交点
     * @param arc
     * @param ray
     * @param clampToArc
     * @returns
     */
    function ArcIntersectRay(arc, ray, clampToArc = false) {
        let arcPlane = new Plane();
        arcPlane.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
        if (Math.abs(ray.direction.dot(arc.normal)) < 1e-6) {
            // 射线平行于圆弧面
            if (Math.abs(GeoMeasurement.PointDistanceToPlane(ray.origin, arcPlane)) > 1e-6) {
                // 平行不共面
                return [];
            }
            // 共面
            let closestPoint = GeoRelation.RayClosestPointToPoint(ray, arc.center);
            let dist = closestPoint.distanceTo(arc.center);
            if (dist > arc.radius) {
                // 无交点
                return [];
            }
            if (Math.abs(dist - arc.radius) < 1e-6) {
                // 相切
                let t = arc.getPointParameter(closestPoint);
                if (clampToArc && (t < -1e-6 || t > 1 + 1e-6)) {
                    // 不在this圆弧上
                    return [];
                }
                return [closestPoint];
            }
            // 相交 两个交点
            let len = Math.sqrt(arc.radius ** 2 - dist ** 2);
            let res = [];
            let point1 = closestPoint.clone().sub(ray.direction.clone().multiplyScalar(len));
            let dir1 = point1.clone().sub(ray.origin);
            let directionDistance1 = dir1.dot(ray.direction);
            if (directionDistance1 > 0) {
                // 需要判断点在圆弧上
                let t = arc.getPointParameter(point1);
                if ((t >= 0 && t <= 1) || !clampToArc) {
                    res.push(point1);
                }
            }
            let point2 = closestPoint.clone().add(ray.direction.clone().multiplyScalar(len));
            let dir2 = point2.clone().sub(ray.origin);
            let directionDistance2 = dir2.dot(ray.direction);
            if (directionDistance2 > 0) {
                // 需要判断点在圆弧上
                let t = arc.getPointParameter(point2);
                if ((t >= 0 && t <= 1) || !clampToArc) {
                    res.push(point2);
                }
            }
            return res;
        }
        // 不共面
        let target = GeoIntersection.RayIntersectPlane(ray, arcPlane);
        if (!target) {
            return [];
        }
        if (Math.abs(target.distanceTo(arc.center) - arc.radius) > 1e-6) {
            // 无命中
            return [];
        }
        let t = arc.getPointParameter(target);
        if (t < -1e-6 || t > 1 + 1e-6)
            return [];
        return [target];
    }
    GeoIntersection.ArcIntersectRay = ArcIntersectRay;
    /**
     * 求射线与平面的交点
     * @param ray
     * @param plane
     * @param target
     * @returns
     */
    function RayIntersectPlane(ray, plane, target = null) {
        const t = GeoMeasurement.RayOriginDistanceToPlane(ray, plane);
        if (t === null) {
            return null;
        }
        return ray.getPointAt(t, target);
    }
    GeoIntersection.RayIntersectPlane = RayIntersectPlane;
    /**
     * 求圆弧与线的延长线上的交点（不在线上）
     * @param arc
     * @param line
     * @returns
     */
    function ArcIntersectExtendLine(arc, line) {
        let intersectPoints = [];
        let points = GeoIntersection.ArcIntersectLine(arc, line);
        points.forEach((point) => {
            let t = arc.getPointParameter(point);
            if (t < -1e-6 || t > 1 + 1e-6) {
                intersectPoints.push(point);
            }
        });
        return intersectPoints;
    }
    GeoIntersection.ArcIntersectExtendLine = ArcIntersectExtendLine;
    /**
     * 求圆弧A与圆弧B的延长线上的交点（不在A,B上）
     * @param arcA
     * @param arcB
     * @returns
     */
    function ArcIntersectExtendArc(arcA, arcB) {
        let intersectPoints = [];
        let points = GeoIntersection.ArcIntersectArc(arcA, arcB);
        points.forEach((point) => {
            let t = arcA.getPointParameter(point);
            if (t < -1e-6 || t > 1 + 1e-6) {
                intersectPoints.push(point);
            }
        });
        return intersectPoints;
    }
    GeoIntersection.ArcIntersectExtendArc = ArcIntersectExtendArc;
    /**
     * 线段P和Q的交点，若clampToLine = false且共面，可返回P的延长线和Q的交点
     * @param q1
     * @param q2
     * @param clampToLine
     * @param clampToLineQ
     * @returns
     */
    function LineIntersectSegment(line, q1, q2, clampToLine = false, clampToLineQ = true) {
        let t = GeoIntersection.LineIntersectSegmentParameter(line, q1, q2, clampToLine);
        let p1 = line.start; // A
        let p2 = line.end;
        let pDir = p2.clone().sub(p1); // B
        let target = p1.clone().add(pDir.multiplyScalar(t));
        let targetInQ = Math.abs((target.distanceTo(q1) + target.distanceTo(q2) - q2.distanceTo(q1))) < 1e-6;
        if (!targetInQ && clampToLineQ)
            return null;
        return target;
    }
    GeoIntersection.LineIntersectSegment = LineIntersectSegment;
    /**
     * 求一条线段l的与一条由q1与q2组成的线段的交点参数 t
     * @param line 线段l
     * @param q1 第二条线段的端点
     * @param q2 第二条线段的端点
     * @param clampToLine1 交点是否限制在line上
     * @param clampToLine2 交点是否限制在由q1与q2组成的线段上
     * @returns
     */
    function LineIntersectSegmentParameter(line, q1, q2, clampToLine1 = false, clampToLine2 = true) {
        // https://www.cnblogs.com/xiangtingshen/p/12329951.html
        let p1 = line.start; // A
        let p2 = line.end; // B
        let pDir = p2.clone().sub(p1); //AB
        let qDir = q2.clone().sub(q1); //CD
        let pqCross = pDir.clone().cross(qDir);
        if (Math.abs(pqCross.lengthSq()) < 1e-6) {
            // 平行
            return null;
        }
        let AC = q1.clone().sub(p1);
        if (!(Math.abs(pqCross.clone().normalize().dot(AC.clone().normalize())) < 1e-6)) {
            // 不共面,如果共面，ABCD的法向量方向相同，AB和CD叉积与
            return null;
        }
        let sACD = AC.clone().cross(qDir); // AC X CD / 2 = sACD // AB X CD / 2 = sCED
        let sign = Math.sign(sACD.dot(pqCross));
        let t = sign * sACD.length() / pqCross.length(); // t 为AO/AB
        if ((t < -1e-6 || t > 1 + 1e-6) && clampToLine1) {
            // 交点不在线段内
            return null;
        }
        if (clampToLine2) {
            // 计算交点 P
            const intersection = p1.clone().add(pDir.multiplyScalar(t));
            // 检查交点是否在线段 CD 上
            const cdLength = q2.clone().sub(q1).length(); // CD 的长度
            const d1 = intersection.clone().sub(q1).length(); // P 到 C 的距离
            const d2 = intersection.clone().sub(q2).length(); // P 到 D 的距离
            if (Math.abs(d1 + d2 - cdLength) > 1e-6) {
                return null; // 交点不在线段 CD 上
            }
        }
        return t;
    }
    GeoIntersection.LineIntersectSegmentParameter = LineIntersectSegmentParameter;
    const _startP = /*@__PURE__*/ new Vector3();
    const _startEnd = /*@__PURE__*/ new Vector3();
    /**
     * 求直线上距离点最近的点 在直线上的t值
     * @param line
     * @param point
     * @param clampToLine
     * @returns
     */
    function closestPointToLineParameter(line, point, clampToLine = false) {
        if (point == null)
            return null;
        _startP.subVectors(point, line.start);
        _startEnd.subVectors(line.end, line.start);
        const startEnd2 = _startEnd.dot(_startEnd);
        const startEnd_startP = _startEnd.dot(_startP);
        let t = startEnd_startP / startEnd2;
        if (clampToLine) {
            t = BaseUtils.clamp(t, 0, 1);
        }
        return t;
    }
    GeoIntersection.closestPointToLineParameter = closestPointToLineParameter;
    const _plane = new Plane();
    const _pDir = new Vector3();
    /**
     * 已知圆弧上一点坐标，求其相对起点的比例t值
     * @param arc
     * @param point
     * @param clamp
     * @returns
     */
    function PointToArcParameter(arc, point, clamp = false) {
        if (point == null)
            return null;
        if (Math.abs(arc.angle) < 1e-5)
            return 0;
        const center = arc.center;
        const normal = arc.normal;
        let distToCenter = point.distanceTo(center);
        if (Math.abs(distToCenter - arc.radius) > 1e-5) {
            return null;
        }
        _plane.setFromNormalAndCoplanarPoint(normal, center);
        let distToPlane = GeoMeasurement.PointDistanceToPlane(point, _plane);
        if (Math.abs(distToPlane) > 1e-5) {
            return null;
        }
        _pDir.subVectors(point, center);
        let axisX = arc.getNormalizeX();
        let angle = _pDir.angleTo(axisX);
        let value = axisX.cross(_pDir).angleTo(normal);
        if (value > Math.PI / 2) {
            angle = Math.PI * 2 - angle;
        }
        let t = angle / arc.angle;
        if (clamp) {
            t = BaseUtils.clamp(t, 0, 1);
        }
        return t;
    }
    GeoIntersection.PointToArcParameter = PointToArcParameter;
    /**
     * 求直线与线段的交点
     * @param sline
     * @param line
     * @param target
     * @returns
     */
    function StraightLineIntersectLine(sline, line, target = null) {
        if (target == null) {
            target = new Point();
        }
        let t = GeoIntersection.StraightLineIntersectLineParameter(sline, line.start, line.getX());
        target.copy(sline.origin).add(sline.direction.clone().multiplyScalar(t));
        let q1 = line.start;
        let q2 = line.end;
        let targetInQ = Math.abs((target.distanceTo(q1) + target.distanceTo(q2) - q2.distanceTo(q1))) < 1e-6;
        if (!targetInQ)
            return null;
        return target;
    }
    GeoIntersection.StraightLineIntersectLine = StraightLineIntersectLine;
    /**
     * 求线与直线的交点
     * @param line
     * @param sline
     * @param target
     * @returns
     */
    function LineIntersectStraightLine(line, sline, target = null) {
        if (target == null) {
            target = new Point();
        }
        return GeoIntersection.StraightLineIntersectLine(sline, line, target);
    }
    GeoIntersection.LineIntersectStraightLine = LineIntersectStraightLine;
    /**
     * 求直线与线段的交点在直线上的参数t
     * @param sline
     * @param originQ
     * @param qDir
     * @returns
     */
    function StraightLineIntersectLineParameter(sline, originQ, qDir) {
        // https://www.cnblogs.com/xiangtingshen/p/12329951.html
        let originP = sline.origin; // A
        let pDir = sline.direction; //AB
        if (pDir.clone().cross(qDir).length() < 1e-6) {
            // 平行
            return null;
        }
        let AC = originQ.clone().sub(originP);
        if (!(Math.abs(qDir.clone().cross(pDir).normalize().dot(AC)) < 1e-6)) {
            // 不共面
            return null;
        }
        let sACD = AC.clone().cross(qDir); // AC X CD / 2 = sACD
        let sCED = pDir.clone().cross(qDir); // AB X CD / 2 = sCED
        let sign = Math.sign(sACD.dot(sCED));
        let t = sign * sACD.length() / sCED.length(); // t 为AO/AB
        return t;
    }
    GeoIntersection.StraightLineIntersectLineParameter = StraightLineIntersectLineParameter;
    /**
     * 平面与直线求交
     * @param plane
     * @param origin
     * @param direction
     * @param target
     * @returns
     */
    function PlaneIntersectStraightLine(plane, origin, direction, target = null) {
        if (target === undefined) {
            target = new Point();
        }
        const denominator = plane.normal.dot(direction);
        if (Math.abs(denominator) < 1e-6) {
            // line is coplanar, return origin
            if (GeoMeasurement.PointDistanceToPlane(origin, plane) === 0) {
                return target.copy(origin);
            }
            // Unsure if this is the correct method to handle this case.
            return null;
        }
        const t = -(origin.dot(plane.normal) + plane.constant) / denominator;
        return target.copy(direction).multiplyScalar(t).add(origin);
    }
    GeoIntersection.PlaneIntersectStraightLine = PlaneIntersectStraightLine;
    /**
     *  求直线与线段的交点。返回值status变量含义：
     *  0：平行，无交点；
     *  1：有交点；
     *  2：有交点，但不在线段内部；
     *  3：segment长度为0，认为没有交点
     *  4：线段与直线重合，这种情况也认为没有交点。
     *  返回值t为交点在线段segment上的定比分点值
     * @param line 直线
     * @param segment 线段
     */
    function straightLine2IntersectLine2(line, segment) {
        if (segment[0].equals(segment[1])) {
            return { status: 3, point: null, t: null };
        }
        // 计算直线方向向量
        _dirLine.subVectors(line[1], line[0]);
        _dirSegment.subVectors(segment[1], segment[0]);
        // 计算行列式 (cross product)
        const determinant = _dirLine.x * _dirSegment.y - _dirLine.y * _dirSegment.x;
        if (Math.abs(determinant) < EPSILON) { //平行或重合
            // 检查是否重合
            _toSegmentStart.subVectors(segment[0], line[0]);
            const crossProduct = _toSegmentStart.x * _dirLine.y - _toSegmentStart.y * _dirLine.x;
            if (Math.abs(crossProduct) < Number.EPSILON) { //重合
                return { status: 4, point: null, t: null };
            }
            return { status: 0, point: null, t: null };
        }
        _toSegmentStart.subVectors(segment[0], line[0]);
        const t1 = (_toSegmentStart.x * _dirSegment.y - _toSegmentStart.y * _dirSegment.x) / determinant;
        const t2 = (_toSegmentStart.x * _dirLine.y - _toSegmentStart.y * _dirLine.x) / determinant;
        // 交点的坐标
        const intersection = new Point2(line[0].x + t1 * _dirLine.x, line[0].y + t1 * _dirLine.y);
        // 判断交点是否在线段内部
        if (t2 >= 0 && t2 <= 1) {
            return { status: 1, point: intersection, t: t2 }; // 有交点且在线段内部
        }
        return { status: 2, point: intersection, t: t2 }; // 有交点但不在线段内部
    }
    GeoIntersection.straightLine2IntersectLine2 = straightLine2IntersectLine2;
    /**
     * 算法4-54PolygonAnyIntersectPlane
     * 返回值第二个数表示状态。
     * 0多边形所在平面与平面平行
     * 1多边形所在平面与平面正常相交
     * 2多边形所在平面与平面重合
     */
    function polygonIntersectPlane(polygon, plane, intersectLine = null, needOn = false) {
        let intersectLineInfo = planeIntersectPlane(polygon, plane);
        if (intersectLineInfo.status === 0 || intersectLineInfo.status === 2) {
            return { status: intersectLineInfo.status, lines: [], onlyOn: false };
        }
        else {
            if (!!intersectLine) {
                intersectLine.copy(intersectLineInfo.line);
            }
            _matrix.copy(polygon.transformMatrix).invert();
            intersectLineInfo.line.getP(_point1);
            intersectLineInfo.line.getX(_point2).add(_point1);
            _point1.applyMatrix4(_matrix);
            _point2.applyMatrix4(_matrix);
            // console.log("_point1", _point1.x, _point1.y);
            // console.log("_point2", _point2.x, _point2.y)
            let resForStraightLineInOnPol = generateInOnForStraightLine([new Point2(_point1.x, _point1.y), new Point2(_point2.x, _point2.y)], polygon.clone());
            if (needOn) {
                return { status: intersectLineInfo.status, lines: [...resForStraightLineInOnPol.in, ...resForStraightLineInOnPol.on], onlyOn: resForStraightLineInOnPol.in.length === 0 };
            }
            else {
                return { status: intersectLineInfo.status, lines: [...resForStraightLineInOnPol.in], onlyOn: resForStraightLineInOnPol.in.length === 0 };
            }
        }
    }
    GeoIntersection.polygonIntersectPlane = polygonIntersectPlane;
    const _point1 = new Point();
    const _point2 = new Point();
    const _matrix = new Matrix4();
    /**
     * 算法4-11平面求交线
     * 返回值第二个数表示状态。
     * 0平面平行
     * 1平面正常相交
     * 2平面重合
     */
    function planeIntersectPlane(p1, p2) {
        const normal1 = p1.normal;
        const a1 = normal1.x;
        const b1 = normal1.y;
        const c1 = normal1.z;
        const d1 = p1.constant;
        const normal2 = p2.normal;
        const a2 = normal2.x;
        const b2 = normal2.y;
        const c2 = normal2.z;
        const d2 = p2.constant;
        const n = p1.normal.clone().cross(p2.normal);
        const nLength = n.lengthSq();
        if (nLength < EPSILON) {
            // 平行或重合
            let point = p2.getP();
            if (Math.abs(a1 * point.x + b1 * point.y + c1 * point.z + d1) < EPSILON) {
                // 如果平面2上的点在平面1上，则重合
                return { line: null, status: 2 };
            }
            return { line: null, status: 0 };
        }
        const delta1 = a1 * b2 - a2 * b1;
        if (Math.abs(delta1) > EPSILON) {
            const x = (-d1 * b2 + d2 * b1) / delta1;
            const y = (-a1 * d2 + a2 * d1) / delta1;
            const z = 0;
            return { line: new StraightLine(new Point(x, y, z), n), status: 1 };
        }
        const delta2 = b1 * c2 - b2 * c1;
        if (Math.abs(delta2) > EPSILON) {
            const x = 0;
            const y = (-d1 * c2 + d2 * c1) / delta2;
            const z = (-b1 * d2 + b2 * d1) / delta2;
            return { line: new StraightLine(new Point(x, y, z), n), status: 1 };
        }
        const delta3 = a1 * c2 - a2 * c1;
        const x = (-d1 * c2 + d2 * c1) / delta3;
        const y = 0;
        const z = (-a1 * d2 + a2 * d1) / delta3;
        return { line: new StraightLine(new Point(x, y, z), n), status: 1 };
    }
    GeoIntersection.planeIntersectPlane = planeIntersectPlane;
    /**
     * 获取下一点的索引，如果current是最后一个节点，则返回第一个点
     * @param current 当前索引
     * @param length 数组长度
     * @returns
     */
    function getNextIndex(current, length) {
        return current === length - 1 ? 0 : current + 1;
    }
    /**
     * 求得一条直线在另一个多边形内部与边界部分，默认共面
     * @param a
     * @param b
     * @returns
     */
    function generateInOnForStraightLine(line2, b) {
        let res = { in: [], on: [] };
        let onInterval = new ClosedInterval([]); //a_on_b的所有部分在a上的定比分点值所组成的区间
        b.rings.forEach(ring => {
            for (let pointIndex = 0; pointIndex < ring.length; pointIndex++) {
                //******算法起始：判断两直线是否共线******
                let start = ring[pointIndex];
                let end = ring[getNextIndex(pointIndex, ring.length)];
                if (start.equals(end)) {
                    continue;
                }
                let A = line2[0].y - line2[1].y;
                let B = line2[1].x - line2[0].x;
                let C = line2[0].x * line2[1].y - line2[1].x * line2[0].y;
                let length = Math.sqrt(A * A + B * B);
                // console.log("A", A, "B", B, "C", C, "length", length, "eee", A * start.x + B * start.y + C, A * end.x + B * end.y + C);
                if (Math.abs(A * start.x + B * start.y + C) / length < EPSILON && Math.abs(A * end.x + B * end.y + C) / length < EPSILON) {
                    //共线
                    let startRatio = GeoRelation.ratioToLine2(start, line2[0], line2[1]);
                    let endRatio = GeoRelation.ratioToLine2(end, line2[0], line2[1]);
                    let tmpClosedInterval = BoolOperation.boolOperation1D(new ClosedInterval([[startRatio, endRatio]]), new ClosedInterval([[0, 1]]), BoolOpeType.INTERSECT);
                    onInterval = BoolOperation.boolOperation1D(onInterval, tmpClosedInterval, BoolOpeType.UNION);
                }
                //******算法结束：判断两直线是否共线******
            }
        });
        let line2Dir = { x: line2[1].x - line2[0].x, y: line2[1].y - line2[0].y };
        onInterval.getData().forEach(data => {
            res.on.push([
                new Point(line2[0].x + data[0] * line2Dir.x, line2[0].y + data[0] * line2Dir.y, 0).applyMatrix4(b.transformMatrix),
                new Point(line2[0].x + data[1] * line2Dir.x, line2[0].y + data[1] * line2Dir.y, 0).applyMatrix4(b.transformMatrix)
            ]);
        });
        let intersectionPointsWithLine = BoolOperation._straightLineIntersectPolygon2d(line2, b);
        if (intersectionPointsWithLine.length <= 1) {
            //如果没有交点，那么整条a都是位于多边形b的外侧的，如果只有一个交点也是（但实际中，这种情况不可能存在）
            return res;
        }
        let intersectionPointInfos = [];
        intersectionPointsWithLine.forEach(item => {
            intersectionPointInfos.push({
                line: item.line,
                point: item.point,
                t: GeoRelation.ratioToLine2(item.point, line2[0], line2[1])
            });
        });
        intersectionPointInfos.sort((c, d) => c.t - d.t);
        let totalInterval = new ClosedInterval([[intersectionPointInfos[0].t, intersectionPointInfos[1].t]]);
        for (let i = 1; i < intersectionPointInfos.length - 1; i++) {
            totalInterval = BoolOperation.boolOperation1D(totalInterval, new ClosedInterval([[intersectionPointInfos[i].t, intersectionPointInfos[i + 1].t]]), BoolOpeType.UNION);
        }
        let otherInterval = BoolOperation.boolOperation1D(totalInterval, onInterval, BoolOpeType.SUBSTRACT);
        otherInterval.getData().forEach(data => {
            let t = (data[0] + data[1]) / 2;
            let midpoint = new Point2(line2[0].x + t * line2Dir.x, line2[0].y + t * line2Dir.y);
            if (GeoRelation.Point2InGeneralRegion(b, midpoint) === GeoRelation.PointInRegion.out) {
                let startpoint = new Point(line2[0].x + data[0] * line2Dir.x, line2[0].y + data[0] * line2Dir.y).applyMatrix4(b.transformMatrix);
                let endpoint = new Point(line2[0].x + data[1] * line2Dir.x, line2[0].y + data[1] * line2Dir.y).applyMatrix4(b.transformMatrix);
                res.in.push([startpoint, endpoint]);
            }
        });
        return res;
    }
    GeoIntersection.generateInOnForStraightLine = generateInOnForStraightLine;
})(GeoIntersection || (GeoIntersection = {}));
// 计算直线方向向量
const _dirLine = new Point2();
const _dirSegment = new Point2();
const _toSegmentStart = new Point2();
const _ab = new Vector3();
const _ac = new Vector3();
const _ad = new Vector3();/**
 * 圆弧
 * 其局部坐标系定义为半径为1，起点在x轴的逆时针的标准圆弧
 */
class Arc extends Curve {
    /**
     * 圆心
     */
    get center() {
        return this.getP();
    }
    /**
     * 设置圆心
     */
    set center(value) {
        this.setP(value);
    }
    /**
     * 法向量
     */
    get normal() {
        return this.getNormalizeZ();
    }
    /**
     * 半径
     */
    get radius() {
        return this.scaleX;
    }
    /**
     * 设置半径
     */
    set radius(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale);
    }
    /**
     * 构造函数
     * @param center 圆心
     * @param radius 半径
     * @param endAngle 终止角度
     * @param normal 法向量
     * @param axisX 局部坐标系x轴
     */
    constructor(center = new Point(), radius = 1, angle = Math.PI, normal = new Vector3(0, 0, 1), axisX = new Vector3(1, 0, 0)) {
        super();
        /**
         * 类型
         */
        this.type = 'Arc';
        this.transformMatrix.elements[8] = normal.x;
        this.transformMatrix.elements[9] = normal.y;
        this.transformMatrix.elements[10] = normal.z;
        axisX.normalize();
        this.updateMatrixByX(axisX, false);
        if (radius <= 0)
            radius = 1;
        this.radius = radius;
        this.center = center;
        while (angle > 2 * Math.PI || angle < 0) {
            if (angle > 2 * Math.PI)
                angle -= 2 * Math.PI;
            if (angle < 0)
                angle += 2 * Math.PI;
        }
        this.angle = angle;
    }
    /**
     * 设置属性
     * @param key
     * @param value
     * @returns
     */
    set(key, value) {
        if (key == "center") {
            if (!(value instanceof Point))
                return;
            this.center = value;
        }
        else if (key == "radius") {
            if (typeof value !== 'number')
                return;
            this.radius = value;
        }
        else if (key == "angle") {
            if (typeof value !== 'number')
                return;
            let angle = value;
            while (angle > 2 * Math.PI || angle < 0) {
                if (angle > 2 * Math.PI)
                    angle -= 2 * Math.PI;
                if (angle < 0)
                    angle += 2 * Math.PI;
            }
            this.angle = angle;
        }
    }
    /**
     * 两点和圆心确定圆弧，点都是在同一平面下，适用于圆弧已经定好后更改起点终点和圆心
     * @param start
     * @param end
     * @param center
     * @param normal 参考法向量，用于判断圆弧的方向
     */
    setFromTwoPointAndCenter(start, end, center, normal) {
        const radius = center.distanceTo(start);
        if (radius <= 1e-6)
            return false;
        this.center = center; // 设置圆心
        if (_vec2.copy(end).sub(center).length() < 1e-6) {
            // 终点和圆心重合
            end = start.clone();
        }
        if (start.distanceTo(end) < 1e-6) {
            // 起点等于终点，约定角度为2Pi
            this.angle = 0;
            _vec1.copy(start).sub(center).normalize();
            this.setXYZ(_vec1, normal);
            this.radius = radius;
            return true;
        }
        _vec1.copy(start).sub(center).normalize(); // center到start连线的方向向量
        _vec2.copy(end).sub(center).normalize(); // center到end连线的方向向量
        this.angle = _vec2.angleTo(_vec1);
        let _normal = _vec1.clone().cross(_vec2).normalize(); // 计算法向量
        if (_normal.dot(normal) < 0) {
            // 方向不一致，圆心角大于180° 需要倒转法向量
            _normal.multiplyScalar(-1);
            this.angle = Math.PI * 2 - this.angle;
        }
        this.setXYZ(_vec1, _normal); // 设置局部坐标系
        this.radius = radius;
        return true;
    }
    /**
     * 三点确定圆弧
     * @param start
     * @param mid
     * @param end
     */
    setByThreePoint(start, mid, end) {
        // start到mid连线的方向向量
        _startToMid.copy(mid).sub(start).normalize();
        if (_startToMid.length() < 1e-6)
            return;
        // mid到end连线的方向向量
        _midToEnd.copy(end).sub(mid).normalize();
        if (_midToEnd.length() < 1e-6)
            return;
        // 法向量
        _normal.copy(_startToMid).cross(_midToEnd).normalize();
        // start和mid连线的中点
        _point1.copy(start).add(mid).multiplyScalar(0.5);
        // mid和end连线的中点
        _point2.copy(end).add(mid).multiplyScalar(0.5);
        // start和mid连线的中垂线
        _plumbLine1.setFromDirection(_point1, _normal.clone().cross(_startToMid));
        // mid和end连线的中垂线
        _plumbLine2.setFromDirection(_point2, _normal.clone().cross(_midToEnd));
        // 中垂线交点为圆心
        let center = GeoIntersection.StraightLineIntersectStraightLine(_plumbLine1, _plumbLine2);
        if (center == null)
            return;
        let radius = center.distanceTo(start);
        if (radius < 1e-6)
            radius = 1e-5;
        _vec1.copy(start).sub(center).normalize(); // center到start连线的方向向量 x轴
        _vec2.copy(end).sub(center).normalize(); // center到end连线的方向向量
        _vec3.copy(mid).sub(center).normalize(); // center到mid连线的方向向量
        // 计算圆心角
        let angle = _vec2.angleTo(_vec1);
        let tmpAngle = _startToMid.angleTo(_midToEnd); // 计算这个夹角，防止cross平行判断
        if (tmpAngle > Math.PI / 2) {
            // 如果夹角大于90度 说明是大角圆弧
            angle = Math.PI * 2 - angle;
        }
        // _vec4.crossVectors(_vec1, _vec3); // 起点到中点
        // _vec5.crossVectors(_vec2, _vec3); // 端点到中点
        // if (_vec4.dot(_vec5) < 0) {
        //     angle = Math.PI * 2 - angle;
        // }
        // 设置圆弧
        this.setXYZ(_vec1, _normal);
        this.center = center;
        this.radius = radius;
        this.angle = angle;
    }
    /**
     * 两点加半径设置圆弧
     * @param start 起点
     * @param end 端点
     * @param radius 半径
     * @param normal
     * @returns true表示可以构成圆弧、false表示无法构成
     */
    setByTwoPointAndRadius(start, end, radius, normal) {
        let dist = start.distanceTo(end);
        if (dist > 2 * radius) {
            return false;
        }
        // 起点到端点
        _vec1.subVectors(end, start).normalize();
        // 起点到端点连线中点
        _vec2.addVectors(start, end).multiplyScalar(0.5);
        // 圆心到连线中点的垂直距离
        let halfChordDist = dist / 2;
        let h = Math.sqrt(radius ** 2 - halfChordDist ** 2);
        // 起点到端点连线的中垂线方向向量
        let plumbLineDir = normal.clone().cross(_vec1).normalize();
        // 判断顺时针或逆时针
        let towardCenter = plumbLineDir.clone();
        // 圆心的位置
        let center = towardCenter.multiplyScalar(h).add(_vec2);
        this.setFromTwoPointAndCenter(start, end, center, normal);
        return true;
    }
    /**
     * 两点和矢高定义圆弧
     * @param startPoint
     * @param endPoint
     * @param h
     */
    setByEndPointChordHeight(startPoint, endPoint, h, normal) {
        // 起点到端点的距离
        let dist = startPoint.distanceTo(endPoint);
        if (dist < 1e-6)
            return;
        // 圆弧半径
        let radius = ((dist * 0.5) ** 2 + h ** 2) / (2 * h);
        if (radius < 1e-6)
            return;
        // 起点到端点连线的单位向量
        let dir = _vec1.copy(endPoint).sub(startPoint).normalize();
        // 起点到端点连线的中点
        let pHalf = _point.copy(startPoint).add(endPoint).multiplyScalar(0.5);
        // 起点到端点连线的中垂线的方向向量（指向圆心）
        let towardCenter = _vec2.copy(normal).cross(dir).normalize();
        if (h > radius) {
            towardCenter.negate();
        }
        // 圆弧圆心
        let center = towardCenter.multiplyScalar(Math.abs(radius - h)).add(pHalf);
        // 圆心角
        let angle = _point1.copy(startPoint).sub(center).angleTo(_point2.copy(endPoint).sub(center));
        if (_point1.cross(_point2).dot(normal) < 0) {
            angle = Math.PI * 2 - angle;
        }
        _point1.copy(startPoint).sub(center);
        this.setXYZ(_point1, normal);
        this.center = center;
        this.radius = radius;
        this.angle = angle;
    }
    /**
     * 获得弧线上某一点切线的方向向量
     * @param point
     * @returns
     */
    getCutLineDir(point, target) {
        if (target == null) {
            target = new Vector3();
        }
        this.getNormalizeZ(target);
        _p$1.subVectors(point, this.center);
        return target.cross(_p$1).normalize();
    }
    /**
     * 计算象限点
     * @returns
     */
    getQuadrantPoints() {
        let quadrantPoints = [];
        let x_dir = this.getX();
        let y_dir = this.getY();
        let point1 = this.center.add(x_dir);
        let point2 = this.center.add(y_dir);
        let point3 = this.center.sub(x_dir);
        let point4 = this.center.sub(y_dir);
        let t1 = this.getPointParameter(point1);
        let t2 = this.getPointParameter(point2);
        let t3 = this.getPointParameter(point3);
        let t4 = this.getPointParameter(point4);
        if (t1 >= 0 && t1 <= 1) {
            quadrantPoints.push(point1);
        }
        if (t2 >= 0 && t2 <= 1) {
            quadrantPoints.push(point2);
        }
        if (t3 >= 0 && t3 <= 1) {
            quadrantPoints.push(point3);
        }
        if (t4 >= 0 && t4 <= 1) {
            quadrantPoints.push(point4);
        }
        return quadrantPoints;
    }
    /**
     * 计算插值点
     * @param t 0-1
     * @param target
     * @returns
     */
    getPointAt(t, target = null) {
        if (target == null) {
            target = new Point();
        }
        // let deltaAngle = this.getDeltaAngle(this.endAngle);
        const angle = t * this.angle;
        let x = Math.cos(angle);
        let y = Math.sin(angle);
        target.set(x, y, 0).applyMatrix4(this.transformMatrix);
        return target;
    }
    /**
     * 获取圆弧上的点
     * @param division
     * @returns
     */
    getPoints(division) {
        let points = [];
        for (let d = 0; d <= division; d++) {
            points.push(this.getPointAt(d / division));
        }
        return points;
    }
    /**
     * 计算一个点在圆弧（圆周）上到起点的角度占总角度的比例
     * @param point
     * @returns t 0-1
     */
    getPointParameter(point) {
        // 将点变换到局部坐标系下
        let p = point.clone().applyMatrix4(this.transformMatrix.clone().invert());
        // 判断点是否在圆周上
        if (Math.abs(p.z) > 1e-6)
            return null;
        if (Math.abs(p.length() - 1) > 1e-6)
            return null;
        // 计算比例
        let theta = Math.acos(p.x);
        if (p.y < 0)
            theta = Math.PI * 2 - theta;
        let t = theta / this.angle;
        return t;
    }
    /**
     * 获取中心点
     * @param target 用以防止对象重复创建
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        this.getPointAt(0.5, target); //弧中点
        return target;
    }
    /**
     * 计算圆弧的凸度
     * @returns
     */
    getBulge() {
        return Math.tan(this.angle / 4);
    }
    /**
     * 获取曲线长度
     * @returns
     */
    getPerimeter() {
        return this.radius * Math.abs(this.angle);
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Arc().copy(this);
    }
    /**
     * 复制
     * @param arc
     * @returns
     */
    copy(arc) {
        super.copy(arc);
        this.angle = arc.angle;
        return this;
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        let data = Object.assign({ type: this.type, angle: this.angle }, super.toJson());
        return data;
    }
    /**
     * 解析json数据成圆弧对象
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        this.angle = json.angle;
        return this;
    }
    /**
     * 判断相等
     * @param arc
     * @returns
     */
    equals(arc) {
        if (!this.center.equals(arc.center))
            return false;
        if (!this.normal.equals(arc.normal))
            return false;
        if (!this.getX().equals(arc.getX()))
            return false;
        if (this.angle != arc.angle)
            return false;
        return true;
    }
    /**
     * 获取特征点列表
     * @returns
     */
    getFeaturePoints() {
        return [
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1), geoType: this.type, uvtPosition: 1 },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: this.getPointAt(0.5), geoType: this.type, uvtPosition: 0.5 },
            { type: FEATURE_POINT_TYPE.CENTER, point: this.center, geoType: this.type },
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let min_x = 0;
        let min_y = 0;
        let max_x = 0;
        let max_y = 0;
        // let min = new Point(0, 0, 0);
        // let max = new Point(0, 0, 0);
        if (this.angle < Math.PI / 2) {
            min_x = Math.cos(this.angle);
            min_y = 0;
            max_x = 1;
            max_y = Math.sin(this.angle);
        }
        else if (this.angle >= Math.PI / 2 && this.angle < Math.PI) {
            min_x = Math.cos(this.angle);
            min_y = 0;
            max_x = 1;
            max_y = 1;
        }
        else if (this.angle >= Math.PI && this.angle < Math.PI * 3 / 2) {
            min_x = -1;
            min_y = Math.sin(this.angle);
            max_x = 1;
            max_y = 1;
        }
        else {
            min_x = -1;
            min_y = -1;
            max_x = 1;
            max_y = 1;
        }
        let box = new Box3().setFromPoints([new Vector3(min_x, min_y, 0), new Vector3(max_x, max_y, 0)]);
        box.applyMatrix4(this.transformMatrix);
        return {
            min: box.min,
            max: box.max,
        };
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        return [
            { type: FEATURE_POINT_TYPE.CENTER, point: this.center, geoType: this.type },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1), geoType: this.type, uvtPosition: 1 },
        ];
    }
    /**
     * 通过控制点修改几何
     */
    setByControlPoint(controlPoints = [], vec) {
        let res = super.setByControlPoint(controlPoints, vec); // 如果有center，直接move，不执行下面的
        if (res)
            return true;
        let height = this.getChordHeight();
        let normal = this.normal;
        for (let cp of controlPoints) {
            // 没有center的情况
            if (cp.uvtPosition == null || cp.point == null)
                continue;
            _p$1.copy(cp.point).add(vec);
            if (cp.uvtPosition == 0) {
                // 改起点
                this.setByEndPointChordHeight(_p$1, this.getPointAt(1), height, normal);
            }
            else if (cp.uvtPosition == 1) {
                // 改终点
                this.setByEndPointChordHeight(this.getPointAt(0), _p$1, height, normal);
            }
        }
        return true;
    }
    /**
     * 计算圆弧矢高
     */
    getChordHeight() {
        return this.radius * (1 - Math.cos(this.angle / 2));
    }
    /**
     * 反向
     */
    reverse() {
        const endDir = this.getPointAt(1).sub(this.center);
        this.setXYZ(endDir.normalize(), this.getNormalizeZ().multiplyScalar(-1));
        return this;
    }
}
const _vec1 = new Vector3();
const _vec2 = new Vector3();
const _vec3 = new Vector3();
const _normal = new Vector3();
const _point = new Point();
const _point1 = new Point();
const _point2 = new Point();
const _p$1 = new Point();
const _startToMid = new Vector3();
const _midToEnd = new Vector3();
const _plumbLine1 = new StraightLine();
const _plumbLine2 = new StraightLine();/**
 * 圆
 */
class Circle extends Arc {
    constructor(center = new Point(), radius = 1, normal = new Vector3(0, 0, 1), axisX = new Vector3(1, 0, 0)) {
        super(center, radius, 2 * Math.PI, normal, axisX);
        this.type = 'Circle';
    }
    /**
     * 抽象成json数据
     * @returns
     */
    toJson() {
        let data = super.toJson();
        data = Object.assign(Object.assign({}, data), { type: this.type });
        return data;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Circle().copy(this);
    }
    /**
     * 获取圆上的点
     * @param division
     * @returns
     */
    getPoints(division) {
        let points = [];
        for (let d = 0; d <= division; d++) {
            points.push(this.getPointAt(d / division));
        }
        return points;
    }
    /**
     * 获取特征点列表
     * @returns
     */
    getFeaturePoints() {
        return [
            { type: FEATURE_POINT_TYPE.CENTER, point: this.center, geoType: this.type },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: this.getPointAt(0.25), geoType: this.type, uvtPosition: 0.25 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: this.getPointAt(0.5), geoType: this.type, uvtPosition: 0.5 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: this.getPointAt(0.75), geoType: this.type, uvtPosition: 0.75 },
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let min = new Point(-1, -1, 0);
        let max = new Point(1, 1, 0);
        min.applyMatrix4(this.transformMatrix);
        max.applyMatrix4(this.transformMatrix);
        return {
            min: min,
            max: max,
        };
    }
    /**
     * 获取中心点
     * @param target 用以防止对象重复创建
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        this.getP(target);
        return target;
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        return [
            { type: FEATURE_POINT_TYPE.CENTER, point: this.center, geoType: this.type },
        ];
    }
    /**
     * 通过控制点修改几何
     */
    setByControlPoint(controlPoints = [], vec) {
        return super.setByControlPoint(controlPoints, vec);
    }
}/**
 * 椭圆弧
 * 局部坐标系下为起始角度为0，终止角度为angle，a轴长度等于b轴长度等于1的圆弧
 * 局部坐标系x轴等于椭圆弧的a轴方向与a轴长度
 * 局部坐标系y轴等于椭圆弧的b轴方向与b轴长度
 */
class EllipseArc extends Curve {
    get center() {
        return new Point().copy(this.getP());
    }
    set center(value) {
        this.setP(value);
    }
    get normal() {
        return this.getNormalizeZ();
    }
    get axisX() {
        return this.getNormalizeX();
    }
    set axisA(a) {
        if (a.length() < 1e-5) {
            a = this.getNormalizeX().multiplyScalar(1e-5);
        }
        this.transformMatrix.elements[0] = a.x;
        this.transformMatrix.elements[1] = a.y;
        this.transformMatrix.elements[2] = a.z;
        this.setXYZ(this.getX(), this.getNormalizeZ());
    }
    set axisB(b) {
        if (b.length() < 1e-5) {
            b = this.getNormalizeY().multiplyScalar(1e-5);
        }
        this.transformMatrix.elements[4] = b.x;
        this.transformMatrix.elements[5] = b.y;
        this.transformMatrix.elements[6] = b.z;
    }
    /**
     * 获取椭圆弧上一点切线方向
     * @param point
     */
    getCutLineDir(point, target) {
        if (target == null) {
            target = new Vector3();
        }
        const xAxis = this.getX().normalize();
        const yAxis = this.getY().normalize();
        const localOffset = new Vector3(point.x - this.center.x, point.y - this.center.y, point.z - this.center.z);
        const a = this.getX().length();
        const b = this.getY().length();
        const xOffset = localOffset.dot(xAxis) / a;
        const yOffset = localOffset.dot(yAxis) / b;
        const t = Math.atan2(yOffset, xOffset);
        const dx = -a * Math.sin(t);
        const dy = b * Math.cos(t);
        target
            .addScaledVector(xAxis, dx)
            .addScaledVector(yAxis, dy)
            .normalize();
        return target;
    }
    /**
     * 椭圆弧
     * 局部坐标系下为起始角度为0，终止角度为angle，a轴长度等于b轴长度等于1的圆弧
     * 局部坐标系x轴等于椭圆弧的a轴方向与a轴长度
     * 局部坐标系y轴等于椭圆弧的b轴方向与b轴长度
     */
    constructor(center = new Point(0, 0, 0), axisX = new Vector3(1, 0, 0), axisY = new Vector3(0, 1, 0), angle = 0) {
        super();
        this.type = 'EllipseArc';
        let normal = (axisX.clone().cross(axisY)).normalize();
        this.transformMatrix.set(axisX.x, axisY.x, normal.x, center.x, axisX.y, axisY.y, normal.y, center.y, axisX.z, axisY.z, normal.z, center.z, 0, 0, 0, 1);
        this.angle = angle;
    }
    set(center = new Point(0, 0, 0), axisX = new Vector3(1, 0, 0), axisY = new Vector3(0, 1, 0), angle = 0) {
        let normal = (axisX.clone().cross(axisY)).normalize();
        this.transformMatrix.set(axisX.x, axisY.x, normal.x, center.x, axisX.y, axisY.y, normal.y, center.y, axisX.z, axisY.z, normal.z, center.z, 0, 0, 0, 1);
        this.angle = angle;
        return this;
    }
    // overwrite
    // t: 沿曲线的比例
    getPointAt(t, target = null) {
        if (target === null) {
            target = new Point();
        }
        const angle = t * this.angle;
        let x = Math.cos(angle);
        let y = Math.sin(angle);
        target.set(x, y, 0).applyMatrix4(this.transformMatrix);
        return target;
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.center);
        return target;
    }
    clone() {
        return new EllipseArc().copy(this);
    }
    copy(arc) {
        this.transformMatrix.copy(arc.transformMatrix);
        this.angle = arc.angle;
        return this;
    }
    // overwrite
    toJson() {
        let data = Object.assign({ version: "1.0", angle: this.angle, type: this.type }, super.toJson());
        return data;
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let res = [];
        res.push({ type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 });
        res.push({ type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1), geoType: this.type, uvtPosition: 1 });
        // 构造椭圆
        let ellipse = new Ellipse(this.getP(), this.getX(), this.getY());
        res.push(...ellipse.getControlPoints());
        return res;
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let min = new Point();
        let max = new Point();
        if (this.angle < Math.PI / 2) {
            min.x = Math.cos(this.angle);
            min.y = 0;
            max.x = 1;
            max.y = Math.sin(this.angle);
        }
        else if (this.angle >= Math.PI / 2 && this.angle < Math.PI) {
            min.x = Math.cos(this.angle);
            min.y = 0;
            max.x = 1;
            max.y = 1;
        }
        else if (this.angle >= Math.PI && this.angle < Math.PI * 3 / 2) {
            min.x = -1;
            min.y = Math.sin(this.angle);
            max.x = 1;
            max.y = 1;
        }
        else {
            min.x = -1;
            min.y = -1;
            max.x = 1;
            max.y = 1;
        }
        min.applyMatrix4(this.transformMatrix);
        max.applyMatrix4(this.transformMatrix);
        return {
            min: min,
            max: max,
        };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let res = [];
        res.push({ type: FEATURE_POINT_TYPE.QUADRANTAL, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 });
        res.push({ type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0), geoType: this.type, uvtPosition: 0 });
        res.push({ type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1), geoType: this.type, uvtPosition: 1 });
        res.push({ type: FEATURE_POINT_TYPE.MIDDLE, point: this.getPointAt(0.5), geoType: this.type, uvtPosition: 0.5 });
        if (this.angle > Math.PI / 2) {
            let ratio = Math.PI / 2 / this.angle;
            let point = this.getPointAt(ratio);
            res.push({ type: FEATURE_POINT_TYPE.QUADRANTAL, point: point, geoType: this.type, uvtPosition: ratio });
        }
        if (this.angle > Math.PI) {
            let ratio = Math.PI / this.angle;
            let point = this.getPointAt(ratio);
            res.push({ type: FEATURE_POINT_TYPE.QUADRANTAL, point: point, geoType: this.type, uvtPosition: ratio });
        }
        if (this.angle > Math.PI * 3 / 2) {
            let ratio = Math.PI * 3 / 2 / this.angle;
            let point = this.getPointAt(ratio);
            res.push({ type: FEATURE_POINT_TYPE.QUADRANTAL, point: point, geoType: this.type, uvtPosition: ratio });
        }
        return res;
    }
    // overwrite
    fromJson(json) {
        super.fromJson(json);
        this.angle = json.angle;
        return this;
    }
    reverse() {
        // TODO 椭圆弧反向
        return this;
    }
    // overwrite
    equals(arc) {
        const eplison = 1e-6;
        for (let i = 0; i < 16; i++) {
            if (Math.abs(this.transformMatrix.elements[i] - arc.transformMatrix.elements[i]) > eplison)
                return false;
        }
        if (Math.abs(this.angle - arc.angle) > eplison)
            return false;
        return true;
    }
}/**
 * 椭圆
 * 局部坐标系定义同EllipseArc
 */
class Ellipse extends EllipseArc {
    constructor(center = new Point(0, 0, 0), axisX = new Vector3(1, 0, 0), axisY = new Vector3(0, 1, 0)) {
        super(center, axisX, axisY, Math.PI * 2);
        this.type = "Ellipse";
    }
    getValue(key) {
        if (key === 'center') {
            return this.center;
        }
        else {
            return this.center;
        }
    }
    // 圆心
    getCenter(target = null) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.center);
        return target;
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let point1 = this.getPointAt(0);
        let point2 = this.getPointAt(0.25);
        let point3 = this.getPointAt(0.5);
        let point4 = this.getPointAt(0.75);
        return [
            // 控制点
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getP(), geoType: this.type }, // 夹点
            // 夹点
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point1, geoType: this.type, uvtPosition: 0 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point2, geoType: this.type, uvtPosition: 0.25 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point3, geoType: this.type, uvtPosition: 0.5 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point4, geoType: this.type, uvtPosition: 0.75 },
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        // let aLength = this.getX().length()
        // let bLength = this.getY().length()
        let min = new Point(1, 1, 0).applyMatrix4(this.transformMatrix);
        let max = new Point(-1, -1, 0).applyMatrix4(this.transformMatrix);
        return {
            min: min,
            max: max,
        };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let point1 = this.getPointAt(0);
        let point2 = this.getPointAt(0.25);
        let point3 = this.getPointAt(0.5);
        let point4 = this.getPointAt(0.75);
        return [
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point1, geoType: this.type, uvtPosition: 0 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point2, geoType: this.type, uvtPosition: 0.25 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point3, geoType: this.type, uvtPosition: 0.5 },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point4, geoType: this.type, uvtPosition: 0.75 },
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getP(), geoType: this.type },
        ];
    }
}/**
 * 用于根据基线的string字段，生成基线类的文件
 */var BaseCurveTrans=/*#__PURE__*/Object.freeze({__proto__:null,Arc:Arc,Circle:Circle,Ellipse:Ellipse,EllipseArc:EllipseArc,Line:Line});/**
 *
 */
class RevolveSurface extends Surface {
    constructor(baseCurve, axis = new StraightLine(), angle = 2 * Math.PI, axisX = new Vector3(1, 0, 0)) {
        super();
        this.type = 'RevolveSurface';
        // 设置旋转轴方向
        const zAxis = axis.direction.clone().normalize();
        // 旋转轴起点
        const origin = axis.origin.clone();
        // 设置x向量xAxis
        const anyAxis = axisX.clone();
        // 如果方向接近平行，选择另一个方向
        if (Math.abs(zAxis.dot(anyAxis)) > 0.999) {
            anyAxis.set(0, 1, 0);
        }
        if (Math.abs(zAxis.dot(anyAxis)) > 0.999) {
            anyAxis.set(1, 0, 0);
        }
        const yAxis = zAxis.clone().cross(anyAxis).normalize();
        // 生成yAxis
        const xAxis = yAxis.clone().cross(zAxis).normalize();
        // 构造旋转体的变换矩阵
        this.transformMatrix.set(xAxis.x, yAxis.x, zAxis.x, origin.x, xAxis.y, yAxis.y, zAxis.y, origin.y, xAxis.z, yAxis.z, zAxis.z, origin.z, 0, 0, 0, 1);
        this.angle = angle;
        // 将curve的局部坐标系转换到旋转体的局部坐标系下
        if (baseCurve) {
            this.baseCurve = baseCurve.clone();
            this.baseCurve.transformMatrix.premultiply(this.transformMatrix.clone().invert());
        }
    }
    /**
     * 返回特征点
     * @returns
     */
    getFeaturePoints() {
        const points = this.baseCurve.getFeaturePoints();
        const revolvePoints = [];
        const origin = this.getP();
        const direction = this.getZ().normalize();
        points.forEach(point => {
            point.point.applyMatrix4(this.transformMatrix);
            revolvePoints.push({
                type: point.type, point: point.point.clone().sub(origin).applyAxisAngle(direction, this.angle * 1).add(origin), geoType: point.geoType
            });
        });
        return [
            ...points,
            ...revolvePoints
        ];
    }
    /**
     * 获取控制点
     * @returns
     */
    getControlPoints() {
        const points = this.baseCurve.getControlPoints();
        points.forEach(point => {
            point.point.applyMatrix4(this.transformMatrix);
        });
        return [
            ...points
        ];
    }
    copy(surface) {
        super.copy(surface);
        this.baseCurve = surface.baseCurve.clone();
        this.angle = surface.angle;
        return this;
    }
    clone() {
        return new RevolveSurface().copy(this);
    }
    equals(surface) {
        return this.baseCurve.equals(surface.baseCurve) && this.transformMatrix.equals(surface.transformMatrix) && this.angle === surface.angle;
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        this.getP(target);
        return target;
    }
    getBoundingBox() {
        // 初始化最小值和最大值为无穷
        const min = new Point(Infinity, Infinity, Infinity);
        const max = new Point(-Infinity, -Infinity, -Infinity);
        const numSamples = 15; // 采样点数
        // 获取基准曲线上的点
        const basePoints = this.baseCurve.getPoints(numSamples);
        // 遍历旋转角度，生成旋转后的点
        for (let i = 0; i <= numSamples; i++) {
            const theta = (i / numSamples) * this.angle;
            const rotationMatrix = new Matrix4().makeRotationAxis(this.getZ().normalize(), // 旋转轴方向
            theta);
            // 遍历基准曲线上的点并计算旋转后的点
            for (const point of basePoints) {
                const rotatedPoint = point.clone().applyMatrix4(rotationMatrix);
                rotatedPoint.applyMatrix4(this.transformMatrix); // 转换到世界坐标系
                // 更新最小值和最大值
                min.x = Math.min(min.x, rotatedPoint.x);
                min.y = Math.min(min.y, rotatedPoint.y);
                min.z = Math.min(min.z, rotatedPoint.z);
                max.x = Math.max(max.x, rotatedPoint.x);
                max.y = Math.max(max.y, rotatedPoint.y);
                max.z = Math.max(max.z, rotatedPoint.z);
            }
        }
        return {
            min: min,
            max: max
        };
    }
    /**
     * 获取曲面插值点
     * @param t 底线上的比例
     * @param u 占旋转角度的比例
     * @param target
     * @returns
     */
    getPointAt(t, u, target = null) {
        if (target == null) {
            target = new Point();
        }
        const origin = this.getP();
        const direction = this.getZ().normalize();
        this.baseCurve.getPointAt(t, target);
        target.applyMatrix4(this.transformMatrix); //需要乘以旋转面的局部坐标
        return target.sub(origin).applyAxisAngle(direction, this.angle * u).add(origin);
        // return target.applyAxisAngle(direction, this.angle * u);
    }
    // overwrite
    toJson() {
        return Object.assign({ version: "1.0", baseCurve: this.baseCurve.toJson(), angle: this.angle, type: this.type }, super.toJson());
    }
    // overwrite
    fromJson(json) {
        super.fromJson(json);
        this.baseCurve = new BaseCurveTrans[json.baseCurve.type]().fromJson(json.baseCurve);
        this.angle = json.angle;
        return this;
    }
    getSideEdges() {
        let res = [];
        // 底线
        let baseC = this.baseCurve.clone();
        baseC.transformMatrix.premultiply(this.transformMatrix);
        res.push(baseC);
        // 旋转后的底线
        let baseRC = this.baseCurve.clone();
        baseRC.transformMatrix.premultiply(this.transformMatrix);
        baseRC.rotate(this.getP(), this.getNormalizeZ(), this.angle);
        res.push(baseRC);
        // 旋转的弧线
        let arc1 = new Arc();
        let arc2 = new Arc();
        let downPoint1 = this.getPointAt(0, 0);
        let downPoint2 = this.getPointAt(0, 1);
        let upPoint1 = this.getPointAt(1, 0);
        let upPoint2 = this.getPointAt(1, 1);
        let center1 = new Point();
        let center2 = new Point();
        let sLine = new StraightLine(this.getP(), this.getNormalizeZ());
        GeoRelation.StraightLineClosestPointToPoint(sLine, downPoint1, center1);
        GeoRelation.StraightLineClosestPointToPoint(sLine, upPoint1, center2);
        if (arc1.setFromTwoPointAndCenter(downPoint1, downPoint2, center1, this.getNormalizeZ())) {
            res.push(arc1);
        }
        if (arc2.setFromTwoPointAndCenter(upPoint1, upPoint2, center2, this.getNormalizeZ())) {
            res.push(arc2);
        }
        return res;
    }
}/**
 * 圆锥侧面
 * 局部坐标系原点为圆的圆心
 * 局部坐标系x轴方向与长度表示圆的x方向与半径长度
 * 用局部坐标系的z轴表示旋转轴
 */
class ConeSideSurface extends RevolveSurface {
    /**
     * @param baseCenter 底面圆心
     * @param vecX 含半径长度的x方向
     * @param vecZ 含高度的z方向
     */
    constructor(baseCenter = new Point(), vecX = new Vector3(1, 0, 0), vecZ = new Vector3(0, 0, 1)) {
        let radius = vecX.length(); //底面圆半径
        let circle = new Circle(baseCenter, radius, vecZ, vecX.normalize());
        const point1 = baseCenter.clone().add(vecZ.clone()); //圆锥顶点
        const point2 = circle.getPointAt(0.5); //底面圆上一点
        const baseCurve = new Line(point1, point2); //旋转基线
        const axis = new StraightLine(baseCenter, vecZ); //旋转轴
        super(baseCurve, axis, 2 * Math.PI);
        this.type = 'ConeSideSurface';
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        // 获取顶点
        let topPoint = this.baseCurve.getPointAt(0);
        // 获取底面上一点
        let bottomPoint = this.baseCurve.getPointAt(1);
        // 获取旋转轴
        let axis = new StraightLine(this.getP(), this.getNormalizeZ());
        // 获取旋转轴距离底面点最近的点
        let crossPoint = GeoRelation.StraightLineClosestPointToPoint(axis, bottomPoint);
        // 获取半径
        let raidus = crossPoint.distanceTo(bottomPoint);
        // 获取象限点
        let sidePoint1 = bottomPoint.clone().add(this.getNormalizeX().multiplyScalar(raidus));
        let sidePoint2 = bottomPoint.clone().add(this.getNormalizeY().multiplyScalar(raidus));
        let sidePoint3 = bottomPoint.clone().add(this.getNormalizeX().multiplyScalar(-raidus));
        let sidePoint4 = bottomPoint.clone().add(this.getNormalizeY().multiplyScalar(-raidus));
        return [
            // 控制点
            // 夹点
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint1, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint2, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint3, geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint4, geoType: this.type, uvtPosition: new Point(0, -1, 0) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint, geoType: this.type, uvtPosition: new Point(0, 0, 1) },
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let min = new Point(0, 0, 0);
        let max = new Point(0, 0, 0);
        return {
            min: min,
            max: max,
        };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        // 获取顶点
        let topPoint = this.baseCurve.getPointAt(0);
        // 获取底面上一点
        let bottomPoint = this.baseCurve.getPointAt(1);
        // 获取旋转轴
        let axis = new StraightLine(this.getP(), this.getNormalizeZ());
        // 获取旋转轴距离底面点最近的点
        let crossPoint = GeoRelation.StraightLineClosestPointToPoint(axis, bottomPoint);
        // 获取半径
        let raidus = crossPoint.distanceTo(bottomPoint);
        // 获取象限点
        let sidePoint1 = bottomPoint.clone().add(this.getNormalizeX().multiplyScalar(raidus));
        let sidePoint2 = bottomPoint.clone().add(this.getNormalizeY().multiplyScalar(raidus));
        let sidePoint3 = bottomPoint.clone().add(this.getNormalizeX().multiplyScalar(-raidus));
        let sidePoint4 = bottomPoint.clone().add(this.getNormalizeY().multiplyScalar(-raidus));
        return [
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint1, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint2, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint3, geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint4, geoType: this.type, uvtPosition: new Point(0, -1, 0) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint, geoType: this.type, uvtPosition: new Point(0, 0, 1) },
        ];
    }
}/**
 * 椭圆基面
 * 局部坐标系下椭圆为长轴为1，短轴也为1的椭圆
 * 局部坐标系原点为椭圆的圆心
 * 局部坐标系x轴方向与长度表示椭圆的x方向与a轴长度
 * 局部坐标系y轴方向与长度表示椭圆的y方向与b轴长度
 * 用局部坐标系的z轴表示矩形所在平面的法向量
 */
class EllipseSurface extends Surface {
    /**
     * 椭圆基面
     * 局部坐标系下椭圆为长轴为1，短轴也为1的椭圆
     * 局部坐标系原点为椭圆的圆心
     * 局部坐标系x轴方向与长度表示椭圆的x方向与a轴长度
     * 局部坐标系y轴方向与长度表示椭圆的y方向与b轴长度
     * 用局部坐标系的z轴表示矩形所在平面的法向量
     * @param basePoint 基点位置
     * @param vecX 含a轴长度的x方向
     * @param vecY 含b轴长度的y方向
     */
    constructor(basePoint = new Point(), vecX = new Vector3(1, 0, 0), vecY = new Vector3(0, 1, 0)) {
        super();
        this.type = 'EllipseSurface';
        let normal = (vecX.clone().cross(vecY.clone())).normalize();
        this.transformMatrix.set(vecX.x, vecY.x, normal.x, basePoint.x, vecX.y, vecY.y, normal.y, basePoint.y, vecX.z, vecY.z, normal.z, basePoint.z, 0, 0, 0, 1);
    }
    /**
     * 获取圆面上的插值点
     * @param t 角度（x轴）方向插值比例
     * @param u 半径（y轴）方向插值比例
     * @param target 插值点引用对象
     * @returns
     */
    getPointAt(t, u, target) {
        if (target == null) {
            target = new Point();
        }
        let angle = t * Math.PI * 2;
        // 统一化，使用局部坐标点加上变换矩阵的方法
        target.set(Math.cos(angle), Math.sin(angle), 0).multiplyScalar(u).applyMatrix4(this.transformMatrix);
        return target;
    }
    /**
     * 复制
     * @param ellipse
     * @returns
     */
    copy(ellipse) {
        this.transformMatrix.copy(ellipse.transformMatrix);
        return this;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new EllipseSurface().copy(this);
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let point1 = this.getPointAt(0, 1);
        let point2 = this.getPointAt(0.25, 1);
        let point3 = this.getPointAt(0.5, 1);
        let point4 = this.getPointAt(0.75, 1);
        return [
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point1, geoType: this.type, uvtPosition: new Point2(0, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point2, geoType: this.type, uvtPosition: new Point2(0.25, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point3, geoType: this.type, uvtPosition: new Point2(0.5, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point4, geoType: this.type, uvtPosition: new Point2(0.75, 1) },
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getP(), geoType: this.type },
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        // let aLength = this.getX().length()
        // let bLength = this.getY().length()
        let min = new Point(1, 1, 0).applyMatrix4(this.transformMatrix);
        let max = new Point(-1, -1, 0).applyMatrix4(this.transformMatrix);
        return {
            min: min,
            max: max,
        };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let point1 = this.getPointAt(0, 1);
        let point2 = this.getPointAt(0.25, 1);
        let point3 = this.getPointAt(0.5, 1);
        let point4 = this.getPointAt(0.75, 1);
        return [
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point1, geoType: this.type, uvtPosition: new Point2(0, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point2, geoType: this.type, uvtPosition: new Point2(0.25, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point3, geoType: this.type, uvtPosition: new Point2(0.5, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: point4, geoType: this.type, uvtPosition: new Point2(0.75, 1) },
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getP(), geoType: this.type },
        ];
    }
    toJson() {
        let data = Object.assign({ version: "1.1", type: this.type }, super.toJson());
        return data;
    }
    getSideEdges() {
        let res = [new Ellipse(this.getP(), this.getX(), this.getY())];
        return res;
    }
}const ConeSurfaceType = {
    Bottom: 0,
    Side: 1
};
/**
 * 圆锥
 */
class Cone extends Solid {
    /**
     * 底面圆心
     */
    get center() {
        return new Point().copy(this.getP());
    }
    /**
     * 设置底面圆心
     */
    set center(value) {
        this.setP(value);
    }
    /**
     * 法向量
     */
    get normal() {
        return this.getNormalizeZ();
    }
    /**
     * 需要保证z与x轴方向不同，所以建议以后设置normal时都调用这个方法
     * @param nx
     * @param ny
     * @param nz
     */
    setNormal(nx, ny, nz) {
        this.setXYZ(this.axisX, new Vector3(nx, ny, nz));
    }
    /**
     * x轴方向(带长度)
     */
    get axisX() {
        return new Vector3().copy(this.getX());
    }
    /**
     * 底面半径
     */
    get radius() {
        return this.scaleX;
    }
    /**
     * 设置底面半径
     */
    set radius(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale, scale, 1);
    }
    /**
     * 高
     */
    get height() {
        return this.scaleZ;
    }
    /**
     * 设置高
     */
    set height(value) {
        let scale = value / this.scaleZ;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), 1, 1, scale);
    }
    // /**
    //  * 局部坐标系x轴
    //  */
    // get axisX() {
    //     return this.getX();
    // }
    /**
     * 构造函数
     * @param center 圆心
     * @param radius 半径
     * @param height 高
     * @param normal 法向量
     * @param axisX 局部坐标系x轴
     */
    constructor(center = new Point(), radius = 1, height = 0, normal = new Vector3(0, 0, 1), axisX = new Vector3(1, 0, 0)) {
        super();
        /**
         * 类型
         */
        this.type = 'Cone';
        /**
         * 圆锥表面索引，以局部坐标系尺度来看，0代表底面，1代表侧面
         */
        this.surfaceIndex = [ConeSurfaceType.Bottom, ConeSurfaceType.Side];
        this.transformMatrix.elements[8] = normal.x;
        this.transformMatrix.elements[9] = normal.y;
        this.transformMatrix.elements[10] = normal.z;
        axisX.normalize();
        this.updateMatrixByX(axisX, false);
        if (radius <= 0)
            radius = 1;
        this.radius = radius;
        this.center = center;
        this.height = height;
        //this.setXYZ(this.getX(), normal);
    }
    /**
     * 设置属性
     * @param  center
     * @param  radius
     * @param  height
     * @param  normal
     * @returns
     */
    set(center, radius, height, normal) {
        this.center = center;
        this.radius = radius;
        this.height = height;
        this.setXYZ(this.getX(), normal);
        return this;
    }
    /**
     * 复制
     * @param cone
     * @returns
     */
    copy(cone) {
        super.copy(cone);
        return this;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Cone().copy(this);
    }
    /**
     * 判断相等
     * @param cone
     * @returns
     */
    equals(cone) {
        // if (!this.center.equals(cone.center)) return false;
        // if (Math.abs(this.radius - cone.radius) > 1e-6) return false;
        // if (Math.abs(this.height - cone.height) > 1e-6) return false;
        // if (!this.normal.equals(cone.normal)) return false;
        for (let i = 0; i < 16; i++) {
            if (Math.abs(this.transformMatrix.elements[i] - cone.transformMatrix.elements[i]) > 1e-6)
                return false;
        }
        return true;
    }
    /**
     * 获取中心点
     * @param target
     * @returns
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.center);
        target.add(this.normal.clone().multiplyScalar(this.height / 2));
        return target;
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        let res = []; //返回的数组
        this.surfaceIndex.forEach(index => {
            let surface = this.getSurface(index);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index) {
        switch (index) {
            case ConeSurfaceType.Bottom:
                return new EllipseSurface(this.getP(), this.getX(), this.getY());
            case ConeSurfaceType.Side:
                return new ConeSideSurface(this.center, this.getX(), this.getZ());
            default:
                return null;
        }
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let raidus = this.getX().length();
        let sidePoint1 = this.getP().add(this.getNormalizeX().multiplyScalar(raidus));
        let sidePoint2 = this.getP().add(this.getNormalizeY().multiplyScalar(raidus));
        let sidePoint3 = this.getP().add(this.getNormalizeX().multiplyScalar(-raidus));
        let sidePoint4 = this.getP().add(this.getNormalizeY().multiplyScalar(-raidus));
        let topPoint = this.getP().add(this.getZ());
        return [
            // 控制点
            // 夹点
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint1, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint2, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint3, geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint4, geoType: this.type, uvtPosition: new Point(0, -1, 0) },
            { type: FEATURE_POINT_TYPE.FACE_CENTER, point: this.getP(), geoType: this.type, uvtPosition: new Point(0, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint, geoType: this.type, uvtPosition: new Point(0, 0, 1) },
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let raidus = this.getX().length();
        let sidePoint1 = this.getP().add(this.getNormalizeX().multiplyScalar(raidus));
        let sidePoint2 = this.getP().add(this.getNormalizeY().multiplyScalar(raidus));
        let sidePoint3 = this.getP().add(this.getNormalizeX().multiplyScalar(-raidus));
        let sidePoint4 = this.getP().add(this.getNormalizeY().multiplyScalar(-raidus));
        let topPoint1 = sidePoint1.clone().add(this.getZ());
        let topPoint2 = sidePoint2.clone().add(this.getZ());
        let topPoint3 = sidePoint3.clone().add(this.getZ());
        let topPoint4 = sidePoint4.clone().add(this.getZ());
        let box = new Box3();
        box.setFromPoints([sidePoint1, sidePoint2, sidePoint3, sidePoint4, topPoint1, topPoint2, topPoint3, topPoint4]);
        return {
            min: box.min,
            max: box.max,
        };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let raidus = this.getX().length();
        let sidePoint1 = this.getP().add(this.getNormalizeX().multiplyScalar(raidus));
        let sidePoint2 = this.getP().add(this.getNormalizeY().multiplyScalar(raidus));
        let sidePoint3 = this.getP().add(this.getNormalizeX().multiplyScalar(-raidus));
        let sidePoint4 = this.getP().add(this.getNormalizeY().multiplyScalar(-raidus));
        let topPoint = this.getP().add(this.getZ());
        return [
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint1, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint2, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint3, geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint4, geoType: this.type, uvtPosition: new Point(0, -1, 0) },
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getP(), geoType: this.type, uvtPosition: new Point(0, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint, geoType: this.type, uvtPosition: new Point(0, 0, 1) },
        ];
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        return Object.assign({ type: this.type }, super.toJson());
    }
    /**
     * 解析json数据成圆弧对象
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        return this;
    }
}const _dirDelta = new Vector3();
/**
 * 拉伸面
 * 高度并入z轴缩放
 * 拉伸面的底面的局部坐标系三个轴的方向必须为
 * (1, 0, 0)
 * (0, 1, 0)
 * (0, 0, 1)
 */
class ExtrudeSurface extends Surface {
    set offsetDirection(v) {
        if (v != null) {
            this._offsetDirection.copy(v);
            this._offsetDirection.applyMatrix4(this.transformMatrix.clone().invert());
            this._offsetDirection.normalize();
        }
    }
    get offsetDirection() {
        return this._offsetDirection;
    }
    /**
     * 拉伸方向
     */
    get direction() {
        return new Vector3().copy(this.getZ());
    }
    /**
     * 设置拉伸高度
     */
    set height(value) {
        let scale = value / this.scaleZ;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), 1, 1, scale);
    }
    /**
     * 拉伸高度
     */
    get height() {
        return this.scaleZ;
    }
    /**
     * 构造函数
     * @param baseCurve 基准线
     * @param direction 拉伸向量含高度
     */
    constructor(baseCurve, direction = new Vector3(0, 0, 1)) {
        super();
        /**
         * 类型
         */
        this.type = 'ExtrudeSurface';
        /**
         * 相对于底面的水平方向偏移向量(单位向量)
         */
        this._offsetDirection = new Vector3(0, 0, 0);
        /**
         * 偏移比例， 偏移方向与高度的比值
         */
        this.offsetRatio = 0;
        /**
         * 判断偏移方向的正反
         */
        this.initBiggerThanZero = true;
        if (baseCurve) {
            this.baseCurve = baseCurve.clone();
            if (this.baseCurve.type == "Line") {
                let tmpLine = new Line();
                tmpLine.copy(baseCurve);
                tmpLine.setXYZ(this.baseCurve.getNormalizeX(), direction.clone().normalize(), false);
                this.baseCurve.transformMatrix.set(tmpLine.scaleX, 0, 0, 0, 0, tmpLine.scaleY, 0, 0, 0, 0, tmpLine.scaleZ, 0, 0, 0, 0, 1);
                this.transformMatrix.set(1 / tmpLine.scaleX, 0, 0, 0, 0, 1 / tmpLine.scaleY, 0, 0, 0, 0, 1 / tmpLine.scaleZ, 0, 0, 0, 0, 1).premultiply(tmpLine.transformMatrix);
            }
            else {
                this.baseCurve.transformMatrix.set(baseCurve.scaleX, 0, 0, 0, 0, baseCurve.scaleY, 0, 0, 0, 0, baseCurve.scaleZ, 0, 0, 0, 0, 1);
                this.transformMatrix.set(1 / baseCurve.scaleX, 0, 0, 0, 0, 1 / baseCurve.scaleY, 0, 0, 0, 0, 1 / baseCurve.scaleZ, 0, 0, 0, 0, 1).premultiply(baseCurve.transformMatrix);
            }
            const length = direction.length();
            const lengthZ = this.scaleZ;
            this.transformMatrix.elements[8] = this.transformMatrix.elements[8] * length / lengthZ;
            this.transformMatrix.elements[9] = this.transformMatrix.elements[9] * length / lengthZ;
            this.transformMatrix.elements[10] = this.transformMatrix.elements[10] * length / lengthZ;
            let bottomNormal = baseCurve.getNormalizeZ();
            let directionNormal = direction.clone().normalize();
            let t = bottomNormal.dot(directionNormal);
            if (Math.abs(t - 1) > 1e-6) {
                // 底面法向量与拉伸方向不一致,设置偏移变量
                if (Math.abs(t) < 1e-6) { //垂直情况，不做处理
                    return;
                }
                let origin = this.getP(); // 拉伸面局部坐标系原点的世界坐标
                let pointInDirectionNormal = origin.clone().add(directionNormal);
                let pointInBottomNormal = bottomNormal.clone().multiplyScalar(t).add(origin);
                let offset = new Vector3().subVectors(pointInDirectionNormal, pointInBottomNormal);
                this.offsetDirection = offset;
                this.offsetRatio = offset.length() / Math.abs(t);
                this.initBiggerThanZero = (this.transformMatrix.elements[8] + this.transformMatrix.elements[9] + this.transformMatrix.elements[10]) >= 0;
            }
        }
    }
    /**
     * 复制
     * @param surface
     * @returns
     */
    copy(surface) {
        this.baseCurve = surface.baseCurve.clone();
        this.transformMatrix.copy(surface.transformMatrix);
        this.height = surface.height;
        this.offsetDirection = surface.offsetDirection;
        this.offsetRatio = surface.offsetRatio;
        this.initBiggerThanZero = surface.initBiggerThanZero;
        return this;
    }
    setHeight(height, normal) {
        if (this.getNormalizeX().dot(normal) < 0) {
            height *= -1;
        }
        this.height = height;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new ExtrudeSurface().copy(this);
    }
    /**
     * 判断相等
     */
    equals(Surface) {
        let isEquals = this.baseCurve.equals(Surface.baseCurve);
        if (!isEquals)
            return false;
        for (let i = 0; i < this.transformMatrix.elements.length; i++) {
            if (this.transformMatrix.elements[i] !== Surface.transformMatrix.elements[i]) {
                isEquals = false;
                break;
            }
        }
        return isEquals;
    }
    /**
     * 计算插值点
     * @param t 底线的比例
     * @param u 沿着拉伸方向的比例
     * @param target
     * @returns
     */
    getPointAt(t, u, target = null) {
        if (target == null) {
            target = new Point();
        }
        this.baseCurve.getPointAt(t, target);
        target.applyMatrix4(this.transformMatrix);
        this.getZ(_dirDelta);
        _dirDelta.multiplyScalar(u);
        target.add(_dirDelta);
        // 乘以横向偏移
        const sum = this.transformMatrix.elements[8] + this.transformMatrix.elements[9] + this.transformMatrix.elements[10];
        const sign = ((this.initBiggerThanZero && sum >= 0) || (!this.initBiggerThanZero && sum < 0)) ? 1 : -1;
        _dirDelta.copy(this._offsetDirection).multiplyScalar(this.offsetRatio * this.scaleZ * u * sign);
        target.add(_dirDelta);
        return target;
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        const data = this.baseCurve.getBoundingBox();
        let baseMin = data.min.applyMatrix4(this.transformMatrix);
        let baseMax = data.max.applyMatrix4(this.transformMatrix);
        let dirMin = baseMin.clone().add(this.getZ());
        let dirMax = baseMax.clone().add(this.getZ());
        let min = new Vector3(Math.min(dirMin.x, baseMin.x), Math.min(dirMin.y, baseMin.y), Math.min(dirMin.z, baseMin.z));
        let max = new Vector3(Math.max(dirMax.x, baseMax.x), Math.max(dirMax.y, baseMax.y), Math.max(dirMax.z, baseMax.z));
        return {
            min: min,
            max: max,
        };
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        return Object.assign(Object.assign({}, super.toJson()), { version: "1.1", baseCurve: this.baseCurve.toJson(), offsetDirection: {
                x: this.offsetDirection.x,
                y: this.offsetDirection.y,
                z: this.offsetDirection.z
            }, offsetRatio: this.offsetRatio, type: this.type, initBiggerThanZero: this.initBiggerThanZero });
    }
    /**
     * 解析json数据
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        this.baseCurve = new BaseCurveTrans[json.baseCurve.type]().fromJson(json.baseCurve);
        if (json.offsetDirection) {
            this._offsetDirection.set(json.offsetDirection.x, json.offsetDirection.y, json.offsetDirection.z);
        }
        this.offsetRatio = json.offsetRatio || 0;
        this.initBiggerThanZero = json.initBiggerThanZero;
        return this;
    }
    getSideEdges() {
        let res = [];
        res.push(this.baseCurve.clone());
        res.push(this.baseCurve.clone().move(this.direction));
        res.push(new Line(this.getPointAt(0, 0), this.getPointAt(0, 1)));
        res.push(new Line(this.getPointAt(1, 0), this.getPointAt(1, 1)));
        return res;
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let bottomPointList = this.baseCurve.getControlPoints();
        bottomPointList.forEach(item => {
            item.point.applyMatrix4(this.transformMatrix);
            let uv = item.uvtPosition;
            let newUv = new Point2(uv, 0);
            item.uvtPosition = newUv;
        });
        let topCenter = this.baseCurve.getP().applyMatrix4(this.transformMatrix).add(this.getZ());
        return [
            // 
            // 顶部拉伸夹点
            { type: FEATURE_POINT_TYPE.MIDDLE, point: topCenter, geoType: this.type, uvtPosition: new Point(0.5, 0.5, 1) },
            // 底面夹点
            ...bottomPointList,
        ];
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let bottomPointList = this.baseCurve.getFeaturePoints();
        bottomPointList.forEach(item => {
            item.point.applyMatrix4(this.transformMatrix);
            let uv = item.uvtPosition;
            let newUv = new Point2(uv, 0);
            item.uvtPosition = newUv;
        });
        let topPointList = this.baseCurve.getFeaturePoints();
        topPointList.forEach(item => {
            item.point.applyMatrix4(this.transformMatrix);
            item.point.add(this.getZ());
            let uv = item.uvtPosition;
            let newUv = new Point2(uv, 1);
            item.uvtPosition = newUv;
        });
        return [
            ...bottomPointList,
            ...topPointList
        ];
    }
}
const _tmpMatrix = new Matrix4();const CylinderSurfaceType = {
    Top: 0,
    Bottom: 1,
    Side: 2
};
/**
 * 圆柱
 * 局部坐标系下定义为底面半径为1，高度为1的圆柱
 * 其高度等于z的长度，底面的半径等于x轴的长度
 */
class Cylinder extends Solid {
    get center() {
        return new Point().copy(this.getP());
    }
    set center(value) {
        this.setP(value);
    }
    get normal() {
        return new Vector3().copy(this.getZ());
    }
    get axisX() {
        return new Vector3().copy(this.getX());
    }
    get height() {
        return this.getZ().length();
    }
    set height(h) {
        let normal = this.getNormalizeZ();
        if (Math.abs(h) < 1e-5) {
            h = 1e-5;
        }
        this.transformMatrix.elements[8] = normal.x * h;
        this.transformMatrix.elements[9] = normal.y * h;
        this.transformMatrix.elements[10] = normal.z * h;
    }
    get radius() {
        return this.getX().length();
    }
    set radius(r) {
        if (Math.abs(r) < 1e-5) {
            r = 1e-5;
        }
        let midX = this.getNormalizeX();
        this.transformMatrix.elements[0] = midX.x * r;
        this.transformMatrix.elements[1] = midX.y * r;
        this.transformMatrix.elements[2] = midX.z * r;
    }
    constructor(center = new Point(), radius = 0, height = 1, normal = new Vector3(0, 0, 1), axisX = new Vector3(1, 0, 0)) {
        super();
        /*1.初始定义与构造*/
        this.type = 'Cylinder';
        this.surfaceIndex = [CylinderSurfaceType.Top, CylinderSurfaceType.Bottom, CylinderSurfaceType.Side];
        let vecY = normal.clone().normalize().cross(axisX.normalize());
        this.transformMatrix.set(axisX.x * radius, vecY.x, normal.x * height, center.x, axisX.y * radius, vecY.y, normal.y * height, center.y, axisX.z * radius, vecY.z, normal.z * height, center.z, 0, 0, 0, 1);
    }
    /**
     * 需要保证z与x轴方向不同，所以建议以后设置normal时都调用这个方法
     * @param nx
     * @param ny
     * @param nz
     */
    setNormal(nx, ny, nz) {
        this.setXYZ(this.axisX, new Vector3(nx, ny, nz));
    }
    /**
     * 需要保证z与x轴方向不同，所以建议以后设置normal时都调用这个方法
     * @param nx
     * @param ny
     * @param nz
     */
    setNormalFromVec(v) {
        this.setXYZ(this.axisX, v);
    }
    /*2.复制、克隆与判断相等*/
    copy(cylinder) {
        this.transformMatrix.copy(cylinder.transformMatrix);
        return this;
    }
    clone() {
        return new Cylinder().copy(this);
    }
    equals(cylinder) {
        const eplison = 1e-6;
        // 相等方法
        for (let i = 0; i < 16; i++) {
            if (Math.abs(this.transformMatrix.elements[i] - cylinder.transformMatrix.elements[i]) > eplison)
                return false;
        }
        return true;
    }
    /**
     * 获取圆柱中心
     * @param target
     * @returns
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        this.getP(target);
        let normal = this.getZ();
        target.add(normal.multiplyScalar(0.5));
        return target;
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        let res = []; //返回的数组
        this.surfaceIndex.forEach(index => {
            let surface = this.getSurface(index);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index) {
        let radius = this.getX().length();
        if (index == CylinderSurfaceType.Top) {
            return new EllipseSurface(this.getP().add(this.getZ()), this.getX(), this.getNormalizeY().multiplyScalar(radius));
        }
        else if (index == CylinderSurfaceType.Bottom) {
            return new EllipseSurface(this.getP(), this.getX(), this.getNormalizeY().multiplyScalar(radius).negate());
        }
        else if (index == CylinderSurfaceType.Side) {
            return new ExtrudeSurface(new Circle(this.center, this.getX().length(), this.getNormalizeZ(), this.getNormalizeX()), this.getZ());
        }
        return null;
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let raidus = this.getX().length();
        let sidePoint1 = this.getP().add(this.getNormalizeX().multiplyScalar(raidus));
        let sidePoint2 = this.getP().add(this.getNormalizeY().multiplyScalar(raidus));
        let sidePoint3 = this.getP().add(this.getNormalizeX().multiplyScalar(-raidus));
        let sidePoint4 = this.getP().add(this.getNormalizeY().multiplyScalar(-raidus));
        return [
            // 控制点
            // 夹点
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint1, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint2, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint3, geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint4, geoType: this.type, uvtPosition: new Point(0, -1, 0) },
            { type: FEATURE_POINT_TYPE.FACE_CENTER, point: this.getP(), geoType: this.type, uvtPosition: new Point(0, 0, 0) },
            { type: FEATURE_POINT_TYPE.FACE_CENTER, point: this.getP().add(this.getZ()), geoType: this.type, uvtPosition: new Point(0, 0, 1) },
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getP().add(this.getZ().multiplyScalar(0.5)), geoType: this.type }
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let raidus = this.getX().length();
        let xDir = this.getX();
        let yDir = this.getNormalizeY().multiplyScalar(raidus);
        let bottomPoint1 = this.getP().add(xDir).add(yDir);
        let bottomPoint2 = this.getP().add(xDir.clone().negate()).add(yDir);
        let bottomPoint3 = this.getP().add(xDir.clone().negate()).add(yDir.clone().negate());
        let bottomPoint4 = this.getP().add(xDir).add(yDir.clone().negate());
        let topPoint1 = bottomPoint1.clone().add(this.getZ());
        let topPoint2 = bottomPoint2.clone().add(this.getZ());
        let topPoint3 = bottomPoint3.clone().add(this.getZ());
        let topPoint4 = bottomPoint4.clone().add(this.getZ());
        let box = new Box3();
        box.setFromPoints([bottomPoint1, bottomPoint2, bottomPoint3, bottomPoint4, topPoint1, topPoint2, topPoint3, topPoint4]);
        return {
            min: box.min,
            max: box.max,
        };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let raidus = this.getX().length();
        let sidePoint1 = this.getP().add(this.getNormalizeX().multiplyScalar(raidus));
        let sidePoint2 = this.getP().add(this.getNormalizeY().multiplyScalar(raidus));
        let sidePoint3 = this.getP().add(this.getNormalizeX().multiplyScalar(-raidus));
        let sidePoint4 = this.getP().add(this.getNormalizeY().multiplyScalar(-raidus));
        return [
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint1, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint2, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint3, geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint4, geoType: this.type, uvtPosition: new Point(0, -1, 0) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint1.clone().add(this.getZ()), geoType: this.type, uvtPosition: new Point(1, 0, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint2.clone().add(this.getZ()), geoType: this.type, uvtPosition: new Point(0, 1, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint3.clone().add(this.getZ()), geoType: this.type, uvtPosition: new Point(-1, 0, 1) },
            { type: FEATURE_POINT_TYPE.QUADRANTAL, point: sidePoint4.clone().add(this.getZ()), geoType: this.type, uvtPosition: new Point(0, -1, 1) },
            { type: FEATURE_POINT_TYPE.FACE_CENTER, point: this.getP(), geoType: this.type, uvtPosition: new Point(0, 0, 0) },
            { type: FEATURE_POINT_TYPE.FACE_CENTER, point: this.getP().add(this.getZ()), geoType: this.type, uvtPosition: new Point(0, 0, 1) },
        ];
    }
    toJson() {
        return Object.assign({ type: this.type }, super.toJson());
    }
}/**
 * 矩形基面
 * 用局部坐标系基点表示矩形左下角，
 * 用局部坐标系的x轴表示矩形的长边的方向与长度
 * 用局部坐标系的y轴表示矩形的宽边的方向与长度
 * 用局部坐标系的z轴表示矩形所在平面的法向量
 */
class RectFace extends Plane {
    /**
     * 矩形基面
     * 用局部坐标系基点表示矩形左下角，
     * 用局部坐标系的x轴表示矩形的长边的方向与长度
     * 用局部坐标系的y轴表示矩形的宽边的方向与长度
     * 用局部坐标系的z轴表示矩形所在平面的法向量
     * @param basePoint 基点
     * @param vecX x轴，含方向与长度信息
     * @param vecY y轴，含方向与长度信息
     */
    constructor(basePoint = new Point(), vecX = new Vector3(1, 0, 0), vecY = new Vector3(0, 1, 0)) {
        super();
        this.type = 'RectFace';
        let dirZ = vecX.clone().cross(vecY).normalize();
        this.transformMatrix.set(vecX.x, vecY.x, dirZ.x, basePoint.x, vecX.y, vecY.y, dirZ.y, basePoint.y, vecX.z, vecY.z, dirZ.z, basePoint.z, 0, 0, 0, 1);
    }
    clone() {
        return new RectFace().copy(this);
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        this.getPointAt(0.5, 0.5, target);
        return target;
    }
    /**
     * 获取矩形面上的插值点
     * @param t 长度（x轴）方向插值比例
     * @param u 宽度（y轴）方向插值比例
     * @param target 插值点引用对象
     * @returns
     */
    getPointAt(t, u, target) {
        if (target == null) {
            target = new Point();
        }
        // const matArr = this.transformMatrix.elements;
        // target.set(
        //     matArr[12] + t * matArr[0] + u * matArr[4], 
        //     matArr[13] + t * matArr[1] + u * matArr[5], 
        //     matArr[14] + t * matArr[2] + u * matArr[6]
        // );
        // return target;
        // 统一化，使用局部坐标点加上变换矩阵的方法
        target.set(t, u, 0).applyMatrix4(this.transformMatrix);
        return target;
    }
    toJson() {
        let data = Object.assign({ version: "1.1", type: this.type }, super.toJson());
        return data;
    }
    getSideEdges() {
        let res = [];
        let point1 = this.getPointAt(0, 0);
        let point2 = this.getPointAt(1, 0);
        let point3 = this.getPointAt(1, 1);
        let point4 = this.getPointAt(0, 1);
        res.push(new Line(point1, point2));
        res.push(new Line(point2, point3));
        res.push(new Line(point3, point4));
        res.push(new Line(point4, point1));
        return res;
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let point1 = this.getPointAt(0, 0);
        let point2 = this.getPointAt(1, 0);
        let point3 = this.getPointAt(1, 1);
        let point4 = this.getPointAt(0, 1);
        let minX = Math.min(point1.x, point2.x, point3.x, point4.x);
        let minY = Math.min(point1.y, point2.y, point3.y, point4.y);
        let minZ = Math.min(point1.z, point2.z, point3.z, point4.z);
        let maxX = Math.max(point1.x, point2.x, point3.x, point4.x);
        let maxY = Math.max(point1.y, point2.y, point3.y, point4.y);
        let maxZ = Math.max(point1.z, point2.z, point3.z, point4.z);
        return {
            min: new Vector3(minX, minY, minZ),
            max: new Vector3(maxX, maxY, maxZ),
        };
    }
    /**
     * 获取特征点
     * @returns
     */
    getFeaturePoints() {
        return [
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0, 0), geoType: this.type },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1, 0), geoType: this.type },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1, 1), geoType: this.type },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0, 1), geoType: this.type },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: this.getPointAt(0.5, 0), geoType: this.type },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: this.getPointAt(1, 0.5), geoType: this.type },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: this.getPointAt(0.5, 1), geoType: this.type },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: this.getPointAt(0, 0.5), geoType: this.type },
        ];
    }
    /**
     * 获取控制点
     * @returns
     */
    getControlPoints() {
        return [
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0, 0), geoType: this.type },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1, 0), geoType: this.type },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(1, 1), geoType: this.type },
            { type: FEATURE_POINT_TYPE.END, point: this.getPointAt(0, 1), geoType: this.type },
        ];
    }
}/**
 * 平面任意面域
 */
class RegionFace extends Plane {
    /**
     * x轴设置方法
     */
    set axisX(value) {
        this.setXYZ(value, this.normal);
    }
    /**
     * x轴获取方法
     */
    get axisX() {
        return this.getX();
    }
    /**
     * 构造方法
     * @param edges 边数组
     * @param axisX x轴
     * @param basePoint 基点
     */
    constructor(edges = [], axisX = new Vector3(1, 0, 0), basePoint = null) {
        super();
        /**
         * 类型
         */
        this.type = 'RegionFace';
        /**
         * 边点列表
         */
        this.vertexList = [];
        /**
         * 边数组
         */
        this.edges = [];
        /**
         * 洞数组
         */
        this.holes = [];
        //初始化边
        this.edges = edges.map(edge => edge.clone());
        //初始化x轴向量
        if (axisX)
            this.axisX = axisX.normalize();
        //初始化normal与洞数据结构
        this.generate();
        //指定基点，如果没有基点，则将所有顶点的平均值作为基点
        if (basePoint) {
            this.setP(basePoint);
        }
        else {
            const P = new Point();
            this.vertexList.forEach(point => {
                P.add(point);
            });
            if (this.vertexList.length > 0) {
                P.multiplyScalar(1 / this.vertexList.length);
            }
            this.setP(P);
        }
        let inverseMatrix = this.transformMatrix.clone().invert();
        this.edges.forEach(edge => {
            edge.transformMatrix.premultiply(inverseMatrix);
        });
        this.holes.forEach(hole => {
            hole.transformMatrix.premultiply(inverseMatrix);
        });
    }
    /**
     * 设置底面
     * @param edges
     * @param axisX
     */
    setRegionFace(edges = [], holes = [], axisX) {
        if (edges.length > 0) {
            this.edges = edges.map(edge => edge.clone());
        }
        if (holes.length > 0) {
            this.holes = holes.map(hole => hole.clone());
        }
        if (axisX)
            this.axisX = axisX.normalize();
        this.generate();
        let inverseMatrix = this.transformMatrix.clone().invert();
        this.edges.forEach(edge => {
            edge.transformMatrix.premultiply(inverseMatrix);
        });
        this.holes.forEach(hole => {
            hole.transformMatrix.premultiply(inverseMatrix);
        });
    }
    /**
     * 复制
     * @param regionFace
     * @returns
     */
    copy(regionFace) {
        super.copy(regionFace);
        if (this.edges.length === regionFace.edges.length) {
            this.edges.forEach((edge, idx) => {
                if (edge.type === regionFace.edges[idx].type) {
                    edge.copy(regionFace.edges[idx]);
                }
                else {
                    this.edges[idx] = regionFace.edges[idx].clone();
                }
            });
        }
        else {
            let edges = regionFace.edges.map(item => item.clone());
            this.edges = [...edges];
        }
        if (this.holes.length === regionFace.holes.length) {
            this.holes.forEach((hole, idx) => {
                hole.copy(regionFace.holes[idx]);
            });
        }
        else {
            this.holes = regionFace.holes.map(item => item.clone());
        }
        if (this.vertexList.length === regionFace.vertexList.length) {
            this.vertexList.forEach((vertex, idx) => {
                vertex.copy(regionFace.vertexList[idx]);
            });
        }
        else {
            this.vertexList = regionFace.vertexList.map(vertex => vertex.clone());
        }
        this.generate(false);
        return this;
    }
    clone() {
        return new RegionFace().copy(this);
    }
    /**
     * 生成面域，顶点、包围盒与
     */
    generate(init = true) {
        if (this.vertexList.length === 0) {
            for (let i = 0; i < this.edges.length; i++) {
                let edge = this.edges[i];
                let endPoints = edge.getEndPoints();
                if (endPoints.length > 0) {
                    this.vertexList.push(endPoints[0]);
                }
            }
            this.holes.forEach(hole => {
                hole.edges.forEach(edge => {
                    let endPoints = edge.getEndPoints();
                    if (endPoints.length > 0) {
                        this.vertexList.push(endPoints[0]);
                    }
                });
            });
        }
        if (init) {
            if (this.edges.length === 1) {
                if (this.edges[0] instanceof Circle) {
                    let circle = this.edges[0];
                    this.setFromNormalAndCoplanarPoint(circle.normal, circle.center);
                }
                else if (this.edges[0] instanceof Ellipse) {
                    let ell = this.edges[0];
                    this.setFromNormalAndCoplanarPoint(ell.normal, ell.getCenter());
                }
            }
            else if (this.vertexList.length === 2) {
                if (this.edges[0] instanceof Arc) {
                    let arc = this.edges[0];
                    this.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
                }
                else if (this.edges[1] instanceof Arc) {
                    let arc = this.edges[1];
                    this.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
                }
            }
            else if (this.vertexList.length > 2) {
                this.setFromCoplanarPoints(this.vertexList[0], this.vertexList[1], this.vertexList[2]);
            }
            for (let i = 0; i < this.edges.length; i++) {
                const l = this.edges[i];
                if (l.type === "Line") {
                    l.setXYZ(l.getNormalizeX(), this.getNormalizeZ());
                }
            }
        }
    }
    /**
     * 获取特征点
     * @returns
     */
    getFeaturePoints() {
        const res = [];
        if (this.edges.length === 1) {
            res.push(...this.edges[0].getFeaturePoints());
            // if (this.edges[0] instanceof Circle || this.edges[0].type === 'Circle') {
            //     let circle = this.edges[0] as Circle;
            //     return [
            //         { type: FEATURE_POINT_TYPE.CENTER, point: circle.center.applyMatrix4(this.transformMatrix), geoType: this.type },
            //     ]
            // }
        }
        else {
            this.edges.forEach(edge => {
                res.push({ type: FEATURE_POINT_TYPE.END, point: edge.getPointAt(0).applyMatrix4(this.transformMatrix), geoType: this.type });
                res.push({ type: FEATURE_POINT_TYPE.MIDDLE, point: edge.getPointAt(0.5).applyMatrix4(this.transformMatrix), geoType: this.type });
            });
        }
        // hole
        return [...res];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        // if (this.edges.length === 1) {
        //     if (this.edges[0] instanceof Circle) {
        //         let circle = this.edges[0];
        //         return circle.getBoundingBox();
        //     }
        // }
        let min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
        let max = new Vector3(Number.MIN_VALUE, Number.MIN_VALUE, Number.MIN_VALUE);
        for (const edge of this.edges) {
            const box = edge.getBoundingBox();
            min.x = Math.min(min.x, box.min.x);
            min.y = Math.min(min.y, box.min.y);
            min.z = Math.min(min.z, box.min.z);
            max.x = Math.max(max.x, box.max.x);
            max.y = Math.max(max.y, box.max.y);
            max.z = Math.max(max.z, box.max.z);
        }
        min.applyMatrix4(this.transformMatrix);
        max.applyMatrix4(this.transformMatrix);
        return { min, max };
    }
    /**
     * 获取控制点
     * @returns
     */
    getControlPoints() {
        if (this.edges.length === 1) {
            if (this.edges[0] instanceof Circle) {
                let circle = this.edges[0];
                return [
                    { type: FEATURE_POINT_TYPE.CENTER, point: circle.center, geoType: this.type },
                ];
            }
        }
        this.edges.map(edge => {
            return [
                { type: FEATURE_POINT_TYPE.END, point: edge.getPointAt(0), geoType: this.type }
            ];
        });
        return [];
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        if (this.edges.length === 1 && (this.edges[0].type === 'Circle' || this.edges[0].type === 'Ellipse')) {
            target.copy(this.edges[0].getCenter());
            target.applyMatrix4(this.transformMatrix);
            return target;
        }
        let count = 0;
        let sum = new Point();
        this.vertexList.forEach(item => {
            sum.add(item);
            count++;
        });
        if (count === 0)
            return target;
        target.copy(sum).divideScalar(count);
        target.applyMatrix4(this.transformMatrix);
        return target;
    }
    /**
     * 获取多边形基点
     * @param target 传入点（可避免重复创建对象）
     * @returns
     */
    getBasePoint(target) {
        if (target == null) {
            target = new Point();
        }
        this.getP(target);
        return target;
    }
    //todo
    isPointInRegionFace(point) {
        // 假设curve已经成环且排好序了
        return false;
    }
    // 面积
    getArea() {
        // 边界围成的面积
        let edgeArea = this.getAreaByEdges(this.edges);
        // 洞的面积
        let holeArea = 0;
        this.holes.forEach(hole => {
            holeArea += this.getAreaByEdges(hole.edges);
        });
        return edgeArea - holeArea;
    }
    //TODO 如何从外部传入分割值
    // _prevVector: Vector3 = new Vector3();
    // _nextVector: Vector3 = new Vector3();
    getAreaByEdges(curves) {
        // const _prevVector: Vector3 = new Vector3();
        // const _nextVector: Vector3 = new Vector3();
        // // 由有序的边得到的顶点
        // let points: Point[] = [];
        // let noRepeatPoints: Point[] = [];
        // curves.forEach(edge => {
        //     points.push(...edge.getPoints());
        // })
        // // 对顶点去重
        // points.forEach(point => {
        //     if (!noRepeatPoints.some(item => item.equals(point))) {
        //         noRepeatPoints.push(point);
        //     }
        // })
        // /**         
        //  * 计算多边形面积，参考文献：[1]韦品瑚,晓伟.三维空间多边形的面积计算[J].数学通报,1984(02):18-21. 引理2
        //  * 空间多边形A1A2...An的面积S=1/2 * |A1A2 * A1A3 + A1A3 * A1A4 + ... + A1An-1 * A1An|
        //  */
        // if (noRepeatPoints.length < 3) return 0;
        // // 第一个点
        // let firstPoint = noRepeatPoints[0];
        // // 矢量积
        // let vectorProduct = new Vector3();
        // for (let i = 2; i <= noRepeatPoints.length - 1; i++) {
        //     // A1Ai-1
        //     _prevVector.subVectors(noRepeatPoints[i - 1], firstPoint);
        //     // A1Ai
        //     _nextVector.subVectors(noRepeatPoints[i], firstPoint);
        //     // A1Ai-1 * A1Ai
        //     vectorProduct.add(_prevVector.cross(_nextVector));
        // }
        // return vectorProduct.length() / 2
        return 0;
    }
    //TODO 重写获取点的方法
    getPoints() {
        let points = [];
        // for (let i = 0; i < this.edges.length; i++) {
        //     let midList = [...this.edges[i].getPoints()]
        //     points.push(...midList)
        // }
        // for (let i = 0; i < this.holes.length; i++) {
        //     points.push(...this.holes[i].getPoints())
        // }
        return points;
    }
    //TODO 重写获取点的方法
    getPointsNoLoop() {
        let points = [];
        // for (let i = 0; i < this.edges.length; i++) {
        //     let midList = [...this.edges[i].getPoints()]
        //     // if (this.edges[i].type == 'Circle') {
        //     midList.pop()
        //     // }
        //     points.push(...midList)
        // }
        // for (let i = 0; i < this.holes.length; i++) {
        //     points.push(...this.holes[i].getPointsNoLoop())
        // }
        return points;
    }
    // generateRings(rings: Ring[], polygon: RegionFace) {
    //     let ring: Ring = [];
    //     polygon.edges.forEach(edge => {
    //         //TODO 需要移动至外部
    //         let points: Point[] = [];
    //         for (let d = 0; d <= division; d++) {
    //             points.push(this.getPointAt(d / division));
    //         }
    //         let points = edge.getPoints();
    //         for (let i = 0; i < points.length - 1; i++) {
    //             ring.push(new Line(points[i].clone(), points[i + 1].clone()));
    //         }
    //     });
    //     rings.push(ring);
    //     polygon.holes.forEach(polygon => {
    //         this.generateRings(rings, polygon);
    //     })
    // }
    // /**
    //  * 转化为一般面域
    //  * @returns 
    //  */
    // toGeneralRegion(): GeneralRegion {
    //     let res = new GeneralRegion();
    //     res.normal = this.normal;
    //     let rings: Ring[] = [];
    //     return this;
    // }
    // overwrite
    toJson() {
        let data = Object.assign(Object.assign({}, super.toJson()), { version: "1.0", type: this.type, edges: this.edges.map(edge => edge.toJson()), holes: this.holes.map(hole => hole.toJson()) });
        return data;
    }
    fromJson(json) {
        super.fromJson(json);
        //生成边
        this.edges = json.edges.map(item => {
            let edge = new BaseCurveTrans[item.type]().fromJson(item);
            return edge;
        });
        //没有洞的情况，直接生成
        if (json.holes == null || json.holes.length === 0) {
            this.holes = [];
            this.generate(false);
            return this;
        }
        //生成洞
        this.holes = json.holes.map(jsonhole => {
            let hole = new RegionFace().fromJson(jsonhole);
            return hole;
        });
        this.generate(false);
        return this;
    }
    getSideEdges() {
        let res = [];
        let regionNormal = this.getNormalizeZ();
        for (let i = 0; i < this.edges.length; i++) {
            let edge = this.edges[i].clone();
            // 将线的法向量设置为面域的法向量
            if (edge.getNormalizeZ().dot(regionNormal) < -1e-6) {
                edge.reverse();
            }
            edge.transformMatrix.premultiply(this.transformMatrix);
            res.push(edge);
        }
        for (let i = 0; i < this.holes.length; i++) {
            res.push(...this.holes[i].getSideEdges());
        }
        return res;
    }
}/**
 * 球面
 * 其局部坐标系定义为半径为1，基点为(0,0,0)的面
 */
class Spherical extends Surface {
    // radius: number;
    // center: Point;
    get center() {
        return new Point().copy(this.getP());
    }
    set center(value) {
        this.setP(value);
    }
    /**
     * 半径
     */
    get radius() {
        return this.scaleX;
    }
    /**
     * 设置半径
     */
    set radius(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale);
    }
    constructor(center = new Point(), radius = 0) {
        super();
        /*1.初始定义与构造*/
        this.type = 'Spherical';
        this.center = center;
        this.radius = radius;
        this.transformMatrix.set(radius, 0, 0, center.x, 0, radius, 0, center.y, 0, 0, radius, center.z, 0, 0, 0, 1);
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.center);
        return target;
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        const min = new Point(-1, -1, -1);
        const max = new Point(1, 1, 1);
        min.applyMatrix4(this.transformMatrix);
        max.applyMatrix4(this.transformMatrix);
        return {
            min: min,
            max: max,
        };
    }
    /**
     * 获取特征点
     * @returns
     */
    getFeaturePoints() {
        return [];
    }
    /**
     * 获取控制点
     * @returns
     */
    getControlPoints() {
        return [];
    }
    getPointAt(t, u, target) {
        if (target == null) {
            target = new Point();
        }
        // 将 t 和 u 转换为经纬度角度
        const theta = t * Math.PI * 2; // θ：绕 Z 轴的角度 (0 ~ 2π)
        const phi = u * Math.PI; // φ：从北极到南极的角度 (0 ~ π)
        // 球面参数化公式计算局部坐标系点
        const x = Math.sin(phi) * Math.cos(theta);
        const y = Math.sin(phi) * Math.sin(theta);
        const z = Math.cos(phi);
        // 统一化，使用局部坐标点加上变换矩阵的方法
        target.set(x, y, z).applyMatrix4(this.transformMatrix);
        return target;
    }
    clone() {
        return new Spherical().copy(this);
    }
    equals(spherical) {
        if (!this.center.equals(spherical.center))
            return false;
        if (Math.abs(this.radius - spherical.radius) > 1e-6)
            return false;
        return true;
    }
    toJson() {
        return Object.assign({ version: "1.0", type: this.type }, super.toJson());
    }
}class SweeperSurface extends Surface {
    /**
     * 构造函数
     * @param baseCurve 扫掠基线(二维曲线)
     * @param sweepPath 扫掠路径(只实现二维曲线)
     * @param basePoint 扫掠基线基点
     * @param angle 沿轴修正角度
     */
    constructor(baseCurve, sweepPath, basePoint, angle = 0) {
        super();
        /**
         * 几何类型
         */
        this.type = 'SweeperSurface';
        if (baseCurve) {
            this.baseCurve = baseCurve.clone();
        }
        if (sweepPath) {
            this.sweepPath = sweepPath.clone();
        }
        if (basePoint == null) {
            // 未指定扫掠基点，进行默认处理
            this.basePoint = SweeperSurface.getCurveCenterPoint(baseCurve);
        }
        else {
            this.basePoint = basePoint.clone();
        }
        // 对基线进行平移变换，将基线变换到原点位置
        const transM = new Matrix4(1, 0, 0, -this.basePoint.x, 0, 1, 0, -this.basePoint.y, 0, 0, 1, -this.basePoint.z, 0, 0, 0, 1);
        if (this.baseCurve) {
            this.baseCurve.transformMatrix.premultiply(transM);
        }
        this.angle = angle;
    }
    /**
     * 获取曲线的默认基点
     * @param curve 曲线
     * @param type 曲线类型
     * @returns 返回曲线基点
     */
    static getCurveCenterPoint(curve) {
        if (curve == null) {
            return new Point(0, 0, 0);
        }
        // 只对几种特殊曲线做处理
        if (curve instanceof Line$1) { // 对直线做特殊处理
            const pointStart = curve.getEndPoints()[0];
            const pointEnd = curve.getEndPoints()[1];
            return new Point((pointStart.x + pointEnd.x) / 2, (pointStart.y + pointEnd.y) / 2, (pointStart.z + pointEnd.z) / 2);
        }
        if (curve instanceof Circle) { // 对圆做特殊处理
            return curve.center;
        }
        if (curve instanceof Ellipse) { // 对椭圆做特殊处理
            return curve.center;
        }
        // 其他默认返回起始点
        return curve.getEndPoints()[0];
    }
    /**
     * 获取当前点
     * @param t 扫掠路径插值位置
     * @param u 扫掠基线插值位置
     * @param target 获取点
     * @returns
     */
    getPointAt(t, u, target) {
        if (target == null) {
            target = new Point();
        }
        // 获取路径上的点
        this.sweepPath.getPointAt(t, targetPath);
        // 获取路径上点的切向量
        this.sweepPath.getCutLineDir(targetPath, direct);
        // 构造方位变换矩阵 
        // 扫掠方向向量
        this.baseCurve.getNormalizeZ(normal);
        // 计算法向量和切向量之间的变换矩阵
        direct.normalize();
        normal.normalize();
        const rotationMatrix = this.getRotationMatrix(normal, direct);
        // 构造平移变换矩阵2
        const transM = new Matrix4(1, 0, 0, targetPath.x, 0, 1, 0, targetPath.y, 0, 0, 1, targetPath.z, 0, 0, 0, 1);
        const baseCurveClone = this.baseCurve.clone();
        baseCurveClone.transformMatrix.premultiply(rotationMatrix);
        // 沿扫掠方向向量旋转修正y轴，将y轴和扫掠路径z轴对齐
        const rotaMY = this.getRotationMatrix(baseCurveClone.getNormalizeY(), this.sweepPath.getNormalizeZ(), direct);
        baseCurveClone.transformMatrix.premultiply(rotaMY);
        // 根据偏移量修正y轴
        baseCurveClone.rotate(new Point(0, 0, 0), baseCurveClone.getNormalizeZ(), this.angle);
        baseCurveClone.transformMatrix.premultiply(transM);
        baseCurveClone.getPointAt(u, target);
        return target;
    }
    /**
     * 构造旋转矩阵
     * @param vecOrigin 物体原先方向
     * @param vecTarget 旋转后物体方向
     * @param axis 用户指定旋转轴(当物体旋转角度为PI的时候才有效果)
     * @returns
     */
    getRotationMatrix(vecOrigin, vecTarget, axis) {
        vecOrigin.normalize();
        vecTarget.normalize();
        // 计算旋转轴
        const myAxis = new Vector3().crossVectors(vecOrigin, vecTarget);
        // 计算旋转角度
        const angle = Math.acos(vecOrigin.dot(vecTarget));
        // 构造旋转矩阵
        if (Math.abs(angle - Math.PI) < Number.EPSILON && axis != null) {
            // 判断旋转角度是否为pi，在旋转角度为pi时用户可以自定义旋转轴
            return new Matrix4().makeRotationAxis(axis.normalize(), angle);
        }
        return new Matrix4().makeRotationAxis(myAxis.normalize(), angle);
    }
    copy(geo) {
        super.copy(geo);
        this.baseCurve = geo.baseCurve.clone();
        this.sweepPath = geo.sweepPath.clone();
        this.basePoint = geo.basePoint.clone();
        this.angle = geo.angle;
        return this;
    }
    clone() {
        return new SweeperSurface().copy(this);
    }
    getBoundingBox() {
        // 初始化最小值和最大值为无穷
        const min = new Point(Infinity, Infinity, Infinity);
        const max = new Point(-Infinity, -Infinity, -Infinity);
        const numSamples = 15; // 采样点数
        for (let i = 0; i < numSamples; i++) {
            for (let j = 0; j < numSamples; j++) {
                this.getPointAt(i / numSamples, j / numSamples, point);
                // 更新最小值和最大值
                min.x = Math.min(min.x, point.x);
                min.y = Math.min(min.y, point.y);
                min.z = Math.min(min.z, point.z);
                max.x = Math.max(max.x, point.x);
                max.y = Math.max(max.y, point.y);
                max.z = Math.max(max.z, point.z);
            }
        }
        return {
            min: min,
            max: max
        };
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        return Object.assign(Object.assign({}, super.toJson()), { version: "1.1", type: this.type, baseCurve: this.baseCurve.toJson(), sweepPath: this.sweepPath.toJson(), basePoint: {
                x: this.basePoint.x,
                y: this.basePoint.y,
                z: this.basePoint.z,
            }, angle: this.angle });
    }
    /**
     * 解析json数据
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        this.baseCurve = new BaseCurveTrans[json.baseCurve.type]().fromJson(json.baseCurve);
        this.sweepPath = new BaseCurveTrans[json.sweepPath.type]().fromJson(json.sweepPath);
        this.basePoint = new Point(json.basePoint.x, json.basePoint.y, json.basePoint.z);
        this.angle = json.angle;
        return this;
    }
}
const normal = new Vector3();
const direct = new Vector3();
const targetPath = new Point();
const point = new Point();/**
 * 梯形
 * 其局部坐标系定义为底边长度为1，高度为1梯形,x轴为底边方向,y轴为底边高度方向,z轴为法向量方向,原点为底边一端点  todo修改顶边定义
 */
class Trapezoid extends Surface {
    /**
     * 端点
     */
    get center() {
        return this.getP();
    }
    /**
     * 设置端点
     */
    set center(value) {
        this.setP(value);
    }
    /**
     * 法向量
     */
    get normal() {
        return this.getNormalizeZ();
    }
    /**
     * 底边长度
     */
    get length() {
        return this.scaleX;
    }
    /**
     * 设置底边长度,同时因为顶边长度储存格式为底边的比例，所以也需要更新Z轴缩放比例
     */
    set length(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale, 1, 1 / scale);
    }
    /**
     * 高度
     */
    get height() {
        return this.scaleY;
    }
    /**
     * 设置底边上的高度
     */
    set height(value) {
        let scale = value / this.scaleY;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), 1, scale, 1);
    }
    /**
    * 顶边长度，以底边长度为基准计算比例存入在Z轴上，以避免后续对矩阵的修改导致顶边长度的变化
    */
    get length2() {
        return this.scaleZ * this.length_2;
    }
    /**
     * 设置顶边上的高度
     */
    set length2(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.length_2 = scale;
    }
    /**
     * 构造函数
        * @param center 底边端点
        * @param length 底边长度
        * @param height 高度
        * @param length2 顶边长度
        * @param normal 法向量
        * @param axisX x轴方向
        * @param angle 端点所在角的角度
     */
    constructor(center = new Point(), length = 1, height = 1, length2 = 1, normal = new Vector3(0, 0, 1), axisX = new Vector3(1, 0, 0), angle = Math.PI / 2) {
        super();
        /**
         * 类型
         */
        this.type = 'Trapezoid';
        this.transformMatrix.elements[8] = normal.x;
        this.transformMatrix.elements[9] = normal.y;
        this.transformMatrix.elements[10] = normal.z;
        axisX.normalize();
        this.updateMatrixByX(axisX, false);
        if (length <= 0)
            length = 1;
        this.length = length;
        if (height <= 0)
            height = 1;
        this.height = height;
        if (length2 <= 0)
            length2 = 1;
        this.length2 = length2;
        this.center = center;
        while (angle > 2 * Math.PI || angle < 0) {
            if (angle > 2 * Math.PI)
                angle -= 2 * Math.PI;
            if (angle < 0)
                angle += 2 * Math.PI;
        }
        this.angle = angle;
    }
    /**
     * 设置属性
     * @param key
     * @param value
     * @returns
     */
    set(key, value) {
        if (key == "center") {
            if (!(value instanceof Point))
                return;
            this.center = value;
        }
        else if (key == "length") {
            if (typeof value !== 'number')
                return;
            this.length = value;
        }
        else if (key == "height") {
            if (typeof value !== 'number')
                return;
            this.height = value;
        }
        else if (key == "length2") {
            if (typeof value !== 'number')
                return;
            this.length2 = value;
        }
        else if (key == "angle") {
            if (typeof value !== 'number')
                return;
            let angle = value;
            while (angle > 2 * Math.PI || angle < 0) {
                if (angle > 2 * Math.PI)
                    angle -= 2 * Math.PI;
                if (angle < 0)
                    angle += 2 * Math.PI;
            }
            this.angle = angle;
        }
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Trapezoid().copy(this);
    }
    /**
     * 复制
     * @param trapezoid
     * @returns
     */
    copy(trapezoid) {
        super.copy(trapezoid);
        this.angle = trapezoid.angle;
        return this;
    }
    /**
     * 获取梯形上的插值点（只能获得端点）
     * @param u u=0时为左边端点，u=1时为右边端点
     * @param v v=0时为底边端点，v=1时为顶边端点
     * 其余情况均返回原点
     * @param target 插值点引用对象
     * @returns
     */
    getPointAt(u, v, target) {
        if (target == null) {
            target = new Point().copy(this.getP());
        }
        if (u == 0 && v == 0) {
            target.copy(this.getP());
        }
        else if (u == 1 && v == 0) {
            target.set(1, 0, 0).applyMatrix4(this.transformMatrix);
        }
        else if (u == 0 && v == 1) {
            {
                let dx = 1 / Math.tan(this.angle);
                target.set(dx, 1, 0).applyMatrix4(this.transformMatrix);
            }
        }
        else if (u == 1 && v == 1) {
            let dx = 1 / Math.tan(this.angle);
            target.set(this.length2 + dx, 1, 0).applyMatrix4(this.transformMatrix);
        }
        return target;
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        let data = Object.assign({ type: this.type, angle: this.angle }, super.toJson());
        return data;
    }
    /**
     * 解析json数据成三角形对象
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        this.angle = json.angle;
        return this;
    }
    /**
     * 判断相等
     * @param Trapezoid
     * @returns
     */
    equals(trapezoid) {
        if (!this.matrixEquals(trapezoid.transformMatrix))
            return false;
        if (this.angle != trapezoid.angle)
            return false;
        return true;
    }
    getSideEdges() {
        let res = [];
        let point1 = this.getPointAt(0, 0);
        let point2 = this.getPointAt(1, 0);
        let point3 = this.getPointAt(1, 1);
        let point4 = this.getPointAt(0, 1);
        res.push(new Line(point1, point2));
        res.push(new Line(point2, point3));
        res.push(new Line(point3, point4));
        res.push(new Line(point4, point1));
        return res;
    }
}/**
 * 三角形
 * 其局部坐标系定义为底边长度为1，高度为1三角形,x轴为底边方向,y轴为高度方向,z轴为法向量方向,原点为底边一端点
 */
class Triangle extends Surface {
    /**
     * 端点
     */
    get center() {
        return this.getP();
    }
    /**
     * 设置端点
     */
    set center(value) {
        this.setP(value);
    }
    /**
     * 法向量
     */
    get normal() {
        return this.getNormalizeZ();
    }
    /**
     * 底边长度
     */
    get length() {
        return this.scaleX;
    }
    /**
     * 设置底边长度
     */
    set length(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale, 1, 1);
    }
    /**
     * 高度
     */
    get height() {
        return this.scaleY;
    }
    /**
     * 设置底边上的高度
     */
    set height(value) {
        let scale = value / this.scaleY;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), 1, scale, 1);
    }
    /**
     * 构造函数
        * @param first 第一点 A
        * @param second 第二点 B
        * @param third 第三点 C
        * 第一点A为原点，第二点B减去第一点作为A BA x轴， BA叉乘CA作为Z轴
     */
    constructor(first = new Point(), second = new Point(), third = new Point()) {
        super();
        /**
         * 类型
         */
        this.type = 'Triangle';
        this.setByThreePoints(first, second, third);
    }
    /**
     * 设置属性
     * @param key
     * @param value
     * @returns
     */
    set(key, value) {
        if (key == "center") {
            if (!(value instanceof Point))
                return;
            this.center = value;
        }
        else if (key == "length") {
            if (typeof value !== 'number')
                return;
            this.length = value;
        }
        else if (key == "height") {
            if (typeof value !== 'number')
                return;
            this.height = value;
        }
        else if (key == "angle") {
            if (typeof value !== 'number')
                return;
            let angle = value;
            while (angle > 2 * Math.PI || angle < 0) {
                if (angle > 2 * Math.PI)
                    angle -= 2 * Math.PI;
                if (angle < 0)
                    angle += 2 * Math.PI;
            }
            this.angle = angle;
        }
    }
    /**
     * 根据三点生成三角形
     * @param p1 原点
     * @param p2 底边端点
     * @param p3 顶点
     * @returns
     */
    setByThreePoints(p1, p2, p3) {
        //根据三点计算三角形信息
        _v1$1.subVectors(p2, p1);
        let s1 = _v1$1.length();
        if (s1 < 1e-6) {
            _v1$1.set(1, 0, 0);
            s1 = 1e-5;
        }
        _v1$1.normalize();
        _v2Old.subVectors(p3, p1);
        let s2 = _v2Old.length();
        if (s2 < 1e-6) {
            if (Math.abs(_v1$1.dot(_v3.set(0, 1, 0))) > 1 - 1 / 64) {
                _v2.set(0, 0, 1);
            }
            else {
                _v2.set(0, 1, 0);
            }
        }
        else {
            _v2.copy(_v2Old);
        }
        //v3
        _v3.crossVectors(_v1$1, _v2).normalize();
        //v2
        _v2.crossVectors(_v3, _v1$1).normalize();
        //s2
        if (s2 < 1e-6) {
            s2 = 1e-5;
        }
        else {
            s2 = _v2.dot(_v2Old);
        }
        this.transformMatrix.set(_v1$1.x * s1, _v2.x * s2, _v3.x, p1.x, _v1$1.y * s1, _v2.y * s2, _v3.y, p1.y, _v1$1.z * s1, _v2.z * s2, _v3.z, p1.z, 0, 0, 0, 1);
        let theta = Math.acos(_v1$1.dot(_v2Old) / _v1$1.length() / _v2Old.length());
        let mark = _v1$1.clone().cross(_v2Old).dot(_v3);
        if (mark < 0) {
            this.angle = 2 * Math.PI - theta;
        }
        this.angle = theta;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Triangle().copy(this);
    }
    /**
     * 复制
     * @param triangle
     * @returns
     */
    copy(triangle) {
        super.copy(triangle);
        this.angle = triangle.angle;
        return this;
    }
    /**
     * 获取三角形上的插值点（只能获得端点）
     * @param u u=0时为左边端点，u=1时为右边端点
     * @param v v=0时为底边端点，v=1时为顶边端点
     * 其余情况均返回原点
     * @param target 插值点引用对象
     * @returns
     */
    //todo 有问题·，因为xy坐标轴缩放不一样
    getPointAt(u, v, target) {
        if (target == null) {
            target = new Point().copy(this.getP());
        }
        if (u == 0 && v == 0) {
            target.copy(this.getP());
        }
        else if (u == 1 && v == 0) {
            target.set(1, 0, 0).applyMatrix4(this.transformMatrix);
        }
        else if (u == 0 && v == 1 || u == 1 && v == 1) {
            let dx = 1 / Math.tan(this.angle);
            target.set(dx, 1, 0).applyMatrix4(this.transformMatrix);
        }
        return target;
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        let data = Object.assign({ type: this.type, angle: this.angle }, super.toJson());
        return data;
    }
    /**
     * 解析json数据成三角形对象
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        this.angle = json.angle;
        return this;
    }
    /**
     * 判断相等
     * @param triangle
     * @returns
     */
    equals(triangle) {
        if (!this.matrixEquals(triangle.transformMatrix))
            return false;
        if (this.angle != triangle.angle)
            return false;
        return true;
    }
    getSideEdges() {
        let res = [];
        let point1 = this.getPointAt(0, 0);
        let point2 = this.getPointAt(1, 0);
        let point3 = this.getPointAt(0, 1);
        res.push(new Line(point1, point2));
        res.push(new Line(point2, point3));
        res.push(new Line(point3, point1));
        return res;
    }
}
const _v1$1 = new Vector3();
const _v2 = new Vector3();
const _v2Old = new Vector3();
const _v3 = new Vector3();var BaseFaceTrans=/*#__PURE__*/Object.freeze({__proto__:null,ConeSideSurface:ConeSideSurface,EllipseSurface:EllipseSurface,ExtrudeSurface:ExtrudeSurface,GeneralRegion:GeneralRegion,RectFace:RectFace,RegionFace:RegionFace,RevolveSurface:RevolveSurface,Spherical:Spherical,SweeperSurface:SweeperSurface,Trapezoid:Trapezoid,Triangle:Triangle});const ExtrudeSolidType = {
    Top: 0,
    Bottom: 1
};
/**
 * 拉伸体
 * 默认往z轴方向拉伸，
 * 拉伸体的底面的局部坐标系三个轴的方向必须为
 * (1, 0, 0)
 * (0, 1, 0)
 * (0, 0, 1)
 */
class ExtrudeSolid extends Solid {
    set offsetDirection(v) {
        if (v != null) {
            this._offsetDirection.copy(v);
            this._offsetDirection.applyMatrix4(this.transformMatrix.clone().invert());
            this._offsetDirection.normalize();
        }
    }
    get offsetDirection() {
        return this._offsetDirection;
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        this.surfaceIndex = [ExtrudeSolidType.Top, ExtrudeSolidType.Bottom];
        let sideCurves = this.bottomFace.getSideEdges();
        for (let i = 0; i < sideCurves.length; i++) {
            this.surfaceIndex.push(i + 2);
        }
        let res = []; // 返回的数组
        this.surfaceIndex.forEach(index => {
            let surface = this.getSurface(index, sideCurves);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    setHeight(height, normal) {
        if (this.getNormalizeX().dot(normal) < 0) {
            height *= -1;
        }
        this.height = height;
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let bottomRes = this.bottomFace.getControlPoints();
        return [
            ...bottomRes,
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getP().add(this.getZ()), geoType: this.type, uvtPosition: new Point(0, 0, 1) }
        ];
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let bottomRes = this.bottomFace.getFeaturePoints();
        bottomRes.forEach(item => {
            item.point.applyMatrix4(this.transformMatrix);
            let uv = item.uvtPosition;
            if (!uv) {
                uv = new Point2(0, 0);
            }
            let newUv = new Point(uv.x, uv.y, 0);
            item.uvtPosition = newUv;
        });
        let topBottomRes = this.bottomFace.getFeaturePoints();
        topBottomRes.forEach(item => {
            item.point.applyMatrix4(this.transformMatrix);
            item.point.add(this.getZ());
            let uv = item.uvtPosition;
            if (!uv) {
                uv = new Point2(0, 0);
            }
            let newUv = new Point(uv.x, uv.y, 1);
            item.uvtPosition = newUv;
        });
        return [
            ...bottomRes,
            ...topBottomRes
        ];
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index, sideCurves = []) {
        if (index == ExtrudeSolidType.Top) {
            const sum = this.transformMatrix.elements[8] + this.transformMatrix.elements[9] + this.transformMatrix.elements[10];
            const sign = ((this.initBiggerThanZero && sum >= 0) || (!this.initBiggerThanZero && sum < 0)) ? 1 : -1;
            const top = this.bottomFace.clone();
            top.move(new Vector3(0, 0, 1));
            if (this._offsetDirection != null) {
                _v1.copy(this._offsetDirection).multiplyScalar(this.offsetRatio * this.scaleZ * sign);
                top.move(_v1);
            }
            top.scale(top.getP(), this.scaleFactor);
            top.transformMatrix.premultiply(this.transformMatrix);
            return top;
        }
        else if (index == ExtrudeSolidType.Bottom) {
            const bottom = this.bottomFace.clone();
            bottom.transformMatrix.premultiply(this.transformMatrix);
            return bottom;
        }
        else {
            let actualIndex = index - 2;
            // 边线数组
            if (sideCurves.length === 0) {
                sideCurves = this.bottomFace.getSideEdges();
            }
            let extrudeSurface = new ExtrudeSurface(sideCurves[actualIndex], new Vector3(0, 0, 1));
            extrudeSurface.transformMatrix.premultiply(this.transformMatrix);
            return extrudeSurface;
        }
    }
    /**
     * 拉伸方向
     */
    get direction() {
        return new Vector3().copy(this.getZ());
    }
    /**
     * 设置高度
     */
    set height(value) {
        if (Math.abs(value) < 1e-6) {
            const sign = Math.sign(value);
            if (sign === 0) {
                value = 1e-4;
            }
            else {
                value = Math.sign(value) * 1e-4;
            }
        }
        const z = this.getNormalizeZ();
        z.multiplyScalar(value);
        this.transformMatrix.elements[8] = z.x;
        this.transformMatrix.elements[9] = z.y;
        this.transformMatrix.elements[10] = z.z;
        // let scale = value / this.height;
        // if (scale < 1e-4) {
        //     scale = 1;
        // }
        // const p = this.getP();
        // _transformMatrix.set(
        //     1, 0, 0, 0,
        //     0, 1, 0, 0,
        //     0, 0, scale, p.z * (1 - scale),
        //     0, 0, 0, 1
        // )
        // this.transformMatrix.multiply(_transformMatrix);
    }
    /**
     * 高度
     */
    get height() {
        return this.getZ().length();
    }
    /**
     * 构造函数
     * @param bottomFace 底面
     * @param offsetDirection 含距离
     */
    constructor(bottomFace = new Surface(), height = 1, offsetDirection = new Vector3()) {
        super();
        /**
         * 类型
         */
        this.type = 'ExtrudeSolid';
        /**
         * 顶面相对于底面的缩放系数
         */
        this.scaleFactor = 1;
        /**
         * 圆弧中间的竖线
         */
        this.bottomMiddlePoints = [];
        /**
         * 是否需要侧面中线
         */
        this._hasMiddleCurve = true;
        /**
         * 面类型数组
         */
        this.surfaceIndex = [ExtrudeSolidType.Top, ExtrudeSolidType.Bottom];
        /**
         * 相对于底面的水平方向偏移向量(单位向量)
         */
        this._offsetDirection = new Vector3(0, 0, 1);
        /**
         * 偏移比例， 偏移方向与高度的比值
         */
        this.offsetRatio = 1;
        /**
         * 判断偏移方向的正反
         */
        this.initBiggerThanZero = true;
        if (bottomFace) {
            this.bottomFace = bottomFace.clone();
            this.bottomFace.transformMatrix.set(bottomFace.scaleX, 0, 0, 0, 0, bottomFace.scaleY, 0, 0, 0, 0, bottomFace.scaleZ, 0, 0, 0, 0, 1);
            this.transformMatrix.set(1 / bottomFace.scaleX, 0, 0, 0, 0, 1 / bottomFace.scaleY, 0, 0, 0, 0, 1 / bottomFace.scaleZ, 0, 0, 0, 0, 1).premultiply(bottomFace.transformMatrix);
            const lengthZ = this.scaleZ;
            this.transformMatrix.elements[8] = this.transformMatrix.elements[8] * height / lengthZ;
            this.transformMatrix.elements[9] = this.transformMatrix.elements[9] * height / lengthZ;
            this.transformMatrix.elements[10] = this.transformMatrix.elements[10] * height / lengthZ;
            this.offsetDirection = offsetDirection.clone().normalize();
            this.offsetRatio = offsetDirection.length() / height;
            this.initBiggerThanZero = (this.transformMatrix.elements[8] + this.transformMatrix.elements[9] + this.transformMatrix.elements[10]) >= 0;
            let curves = bottomFace.getSideEdges();
            for (let i = 0; i < curves.length; i++) {
                this.surfaceIndex.push(i + 2);
            }
        }
    }
    /**
     * 复制
     * @param solid
     * @returns
     */
    copy(solid) {
        this.bottomFace = solid.bottomFace.clone();
        this.transformMatrix.copy(solid.transformMatrix);
        this.scaleFactor = solid.scaleFactor;
        this.offsetDirection = solid.offsetDirection;
        this.offsetRatio = solid.offsetRatio;
        this.initBiggerThanZero = solid.initBiggerThanZero;
        return this;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new ExtrudeSolid(this.bottomFace.clone(), this.height).copy(this);
    }
    /**
     * 判断是否相等
     * @param solid
     * @returns
     */
    equals(solid) {
        let isEquals = solid.bottomFace.equals(this.bottomFace);
        if (!isEquals)
            return false;
        isEquals = true;
        for (let i = 0; i < this.transformMatrix.elements.length; i++) {
            if (this.transformMatrix.elements[i] !== solid.transformMatrix.elements[i]) {
                isEquals = false;
                break;
            }
        }
        return isEquals;
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        let res = Object.assign(Object.assign({}, super.toJson()), { version: "1.0", 
            // baseCurves: this.baseCurves.map(item => item.toJson()),
            bottomFace: this.bottomFace.toJson(), scaleFactor: this.scaleFactor, initBiggerThanZero: this.initBiggerThanZero, type: this.type });
        if (this._offsetDirection != null) {
            res = Object.assign(Object.assign({}, res), { _offsetDirection: {
                    x: this._offsetDirection.x,
                    y: this._offsetDirection.y,
                    z: this._offsetDirection.z
                } });
        }
        return res;
    }
    /**
     * 解析json数据
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        this.bottomFace = new BaseFaceTrans[json.bottomFace.type]().fromJson(json.bottomFace);
        this.scaleFactor = (json === null || json === void 0 ? void 0 : json.scaleFactor) || 1;
        if (json.offsetDirection) {
            this.offsetDirection.set(json.offsetDirection.x, json.offsetDirection.y, json.offsetDirection.z);
        }
        this.offsetRatio = json.offsetRatio || 1;
        this.initBiggerThanZero = json.initBiggerThanZero;
        return this;
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        this.bottomFace.getCenter(target);
        target.add(this.direction.normalize().clone().multiplyScalar(this.height / 2));
        return target;
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let top = this.getSurface(ExtrudeSolidType.Top);
        let bottom = this.getSurface(ExtrudeSolidType.Bottom);
        let topData = top.getBoundingBox();
        let bottomData = bottom.getBoundingBox();
        let min = new Vector3(Math.min(topData.min.x, bottomData.min.x), Math.min(topData.min.y, bottomData.min.y), Math.min(topData.min.z, bottomData.min.z));
        let max = new Vector3(Math.max(topData.max.x, bottomData.max.x), Math.max(topData.max.y, bottomData.max.y), Math.max(topData.max.z, bottomData.max.z));
        return {
            min: min,
            max: max
        };
    }
}
const _v1 = new Vector3();
// const _transformMatrix = new Matrix4();
/**
 * 多边形
 */
class Polygon extends Curve {
    /**
     * 基点
     */
    get basePoint() {
        return this.getP();
    }
    /**
     * 获取基元列表
     * @returns
     */
    getGeoElements() {
        const res = [...this.edges];
        this.holes.forEach(hole => res.push(...hole.getGeoElements()));
        return [...res];
    }
    /**
     * 设置基点
     */
    set basePoint(value) {
        this.setP(value);
    }
    /**
     * 设置平面法向量
     */
    set normal(value) {
        this.setXYZ(this.getNormalizeX(), value);
    }
    /**
     * 平面法向量
     */
    get normal() {
        return this.getNormalizeZ();
    }
    /**
     * 获取基元索引列表 用于绑定
     */
    getGeoElementsIdxList() {
        const arr = [];
        for (let i = 0; i < this.edges.length; i++) {
            arr.push(i);
        }
        let start = (arr[arr.length - 1] + 1) || 0;
        for (let i = 0; i < this.holes.length; i++) {
            this.holes[i].getGeoElementsIdxList().forEach(idx => {
                arr.push(start + idx);
                start++;
            });
        }
        return arr;
    }
    // /**
    //  * 获取多边形上的点
    //  * @param t 
    //  * @param target 
    //  */
    // getPointAt(t, target = null) {
    //     if (target == null) {
    //         target = new Point();
    //     }
    //     if (this.edges.length == 1) {
    //         return this.edges[0].getPointAt(t, target);
    //     } else {
    //         // 向下取整
    //         const index = Math.floor(t * this.edges.length);
    //         const edgeIdx = index % this.edges.length;
    //         const delta = 1 / this.edges.length;
    //         const startT = edgeIdx * delta;
    //         let endT;
    //         if (edgeIdx === this.edges.length - 1) {
    //             endT = 1;
    //         } else {
    //             endT = (edgeIdx + 1) * delta;
    //         }
    //         // 把t映射到当前边上
    //         const tOnEdge = (t - startT) / (endT - startT);
    //         return this.edges[edgeIdx].getPointAt(tOnEdge, target);
    //     }
    // }
    /**
     * 构造函数
     * @param edges 边列表
     * @param axisX X轴方向
     * @param basePoint 基点
     */
    constructor(edges = [], axisX = new Vector3(1, 0, 0), basePoint = null) {
        super();
        this.type = 'Polygon';
        /**
         * 点列表
         */
        this.vertexList = [];
        /**
         * 边列表
         */
        this.edges = [];
        /**
         * 洞列表
         */
        this.holes = [];
        this.edges = [...edges];
        if (axisX)
            this.axisX = axisX.normalize();
        this.generate();
        if (basePoint) {
            this.basePoint = basePoint;
        }
        else {
            this.basePoint = new Point();
            this.vertexList.forEach(point => {
                this.basePoint.add(point);
            });
            if (this.vertexList.length > 0) {
                this.basePoint.multiplyScalar(1 / this.vertexList.length);
            }
        }
    }
    /**
     * 设置多边形
     * @param edges
     * @param axisX
     */
    setPolygon(edges = [], axisX) {
        this.edges = [...edges];
        if (axisX)
            this.axisX = axisX.normalize();
        this.generate();
    }
    /**
     * 设置X轴方向
     */
    set axisX(value) {
        this.setXYZ(value, this.normal);
    }
    /**
     * 获取X轴方向
     */
    get axisX() {
        return this.getX();
    }
    copy(polygon) {
        if (this.edges.length === polygon.edges.length) {
            this.edges.forEach((edge, idx) => {
                if (edge.type === polygon.edges[idx].type) {
                    edge.copy(polygon.edges[idx]);
                }
                else {
                    this.edges[idx] = polygon.edges[idx].clone();
                }
            });
        }
        else {
            let edges = polygon.edges.map(item => item.clone());
            this.edges = [...edges];
        }
        if (this.holes.length === polygon.holes.length) {
            this.holes.forEach((hole, idx) => {
                hole.copy(polygon.holes[idx]);
            });
        }
        else {
            this.holes = polygon.holes.map(item => item.clone());
        }
        if (this.vertexList.length === polygon.vertexList.length) {
            this.vertexList.forEach((vertex, idx) => {
                vertex.copy(polygon.vertexList[idx]);
            });
        }
        else {
            this.vertexList = polygon.vertexList.map(vertex => vertex.clone());
        }
        //TODO,等插值点移过来再处理
        // if (this._polygonPoints.length === polygon._polygonPoints.length) {
        //     this._polygonPoints.forEach((vertex, idx) => {
        //         vertex.copy(polygon._polygonPoints[idx]);
        //     })
        // } else {
        //     this._polygonPoints = polygon._polygonPoints.map(vertex => vertex.clone());
        // }
        this.axisX = polygon.axisX;
        this.generate();
        this.basePoint.copy(polygon.basePoint);
        return this;
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Polygon().copy(this);
    }
    /**
     * 生成多边形
     */
    generate() {
        if (this.vertexList.length === 0) {
            for (let i = 0; i < this.edges.length; i++) {
                let edge = this.edges[i];
                let endPoints = edge.getEndPoints();
                if (endPoints.length > 0) {
                    this.vertexList.push(endPoints[0]);
                }
            }
            this.holes.forEach(hole => {
                hole.edges.forEach(edge => {
                    let endPoints = edge.getEndPoints();
                    if (endPoints.length > 0) {
                        this.vertexList.push(endPoints[0]);
                    }
                });
            });
        }
        if (this.edges.length === 1) {
            if (this.edges[0] instanceof Circle) {
                let circle = this.edges[0];
                this.setFromNormalAndCoplanarPoint(circle.normal, circle.center);
            }
            else if (this.edges[0] instanceof Ellipse) {
                let ell = this.edges[0];
                this.setFromNormalAndCoplanarPoint(ell.normal, ell.getCenter());
            }
        }
        else if (this.vertexList.length === 2) {
            if (this.edges[0] instanceof Arc) {
                let arc = this.edges[0];
                this.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
            }
            else if (this.edges[1] instanceof Arc) {
                let arc = this.edges[1];
                this.setFromNormalAndCoplanarPoint(arc.normal, arc.center);
            }
        }
        else if (this.vertexList.length > 2) {
            this.setFromCoplanarPoints(this.vertexList[0], this.vertexList[1], this.vertexList[2]);
        }
    }
    /**
     * 根据平面法向量和平面上一点构造平面
     * @param normal
     * @param point
     * @returns
     */
    setFromNormalAndCoplanarPoint(normal, point) {
        if (normal.length() < 1e-6) {
            normal.set(0, 0, 1);
        }
        else {
            normal.normalize();
        }
        this.normal = normal;
        this.setP(point);
        return this;
    }
    /**
 * 根据平面上三个点构造平面
 * @param a
 * @param b
 * @param c
 * @returns
 */
    setFromCoplanarPoints(a, b, c) {
        const normal = _vector1.subVectors(c, b).cross(_vector2.subVectors(a, b)).normalize();
        // Q: should an error be thrown if normal is zero (e.g. degenerate plane)?
        this.setFromNormalAndCoplanarPoint(normal, a);
        return this;
    }
    //TODO 暂时不需要该方法
    // getCenter(target?: Point) {
    //     if (target == null) {
    //         target = new Point();
    //     }
    //     if (this.edges.length === 1 && (this.edges[0].type === 'Circle' || this.edges[0].type === 'Ellipse')) {
    //         target.copy(this.edges[0].getCenter());
    //         return target;
    //     }
    //     let count = 0;
    //     let sum = new Point();
    //     this.vertexList.forEach(item => {
    //         sum.add(item);
    //         count++;
    //     })
    //     if (count === 0) return target;
    //     target.copy(sum).divideScalar(count);
    //     return target;
    // }
    /**
     * 获取基点
     * @param target
     * @returns
     */
    getBasePoint(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.basePoint);
        return target;
    }
    //todo
    isPointInPolygon(point) {
        // 假设curve已经成环且排好序了
        return false;
    }
    /**
     * 获取面积
     * @returns
     */
    getArea() {
        // 边界围成的面积
        let edgeArea = this.getAreaByEdges(this.edges);
        // 洞的面积
        let holeArea = 0;
        this.holes.forEach(hole => {
            holeArea += this.getAreaByEdges(hole.edges);
        });
        return edgeArea - holeArea;
    }
    //TODO 如何从外部传入分割值
    // _prevVector: Vector3 = new Vector3();
    // _nextVector: Vector3 = new Vector3();
    getAreaByEdges(curves) {
        // const _prevVector: Vector3 = new Vector3();
        // const _nextVector: Vector3 = new Vector3();
        // // 由有序的边得到的顶点
        // let points: Point[] = [];
        // let noRepeatPoints: Point[] = [];
        // curves.forEach(edge => {
        //     points.push(...edge.getPoints());
        // })
        // // 对顶点去重
        // points.forEach(point => {
        //     if (!noRepeatPoints.some(item => item.equals(point))) {
        //         noRepeatPoints.push(point);
        //     }
        // })
        // /**         
        //  * 计算多边形面积，参考文献：[1]韦品瑚,晓伟.三维空间多边形的面积计算[J].数学通报,1984(02):18-21. 引理2
        //  * 空间多边形A1A2...An的面积S=1/2 * |A1A2 * A1A3 + A1A3 * A1A4 + ... + A1An-1 * A1An|
        //  */
        // if (noRepeatPoints.length < 3) return 0;
        // // 第一个点
        // let firstPoint = noRepeatPoints[0];
        // // 矢量积
        // let vectorProduct = new Vector3();
        // for (let i = 2; i <= noRepeatPoints.length - 1; i++) {
        //     // A1Ai-1
        //     _prevVector.subVectors(noRepeatPoints[i - 1], firstPoint);
        //     // A1Ai
        //     _nextVector.subVectors(noRepeatPoints[i], firstPoint);
        //     // A1Ai-1 * A1Ai
        //     vectorProduct.add(_prevVector.cross(_nextVector));
        // }
        // return vectorProduct.length() / 2
        return 0;
    }
    //TODO 重写获取点的方法
    getPoints() {
        let points = [];
        // for (let i = 0; i < this.edges.length; i++) {
        //     let midList = [...this.edges[i].getPoints()]
        //     points.push(...midList)
        // }
        // for (let i = 0; i < this.holes.length; i++) {
        //     points.push(...this.holes[i].getPoints())
        // }
        return points;
    }
    //TODO 重写获取点的方法
    getPointsNoLoop() {
        let points = [];
        // for (let i = 0; i < this.edges.length; i++) {
        //     let midList = [...this.edges[i].getPoints()]
        //     // if (this.edges[i].type == 'Circle') {
        //     midList.pop()
        //     // }
        //     points.push(...midList)
        // }
        // for (let i = 0; i < this.holes.length; i++) {
        //     points.push(...this.holes[i].getPointsNoLoop())
        // }
        return points;
    }
    /**
     * 抽象成json
     * @returns
     */
    toJson() {
        let data = {
            version: "1.0",
            edges: this.edges.map(edge => edge.toJson()),
            holes: this.holes.map(hole => hole.toJson()),
            axisX: {
                x: this.axisX.x,
                y: this.axisX.y,
                z: this.axisX.z,
            },
            basePoint: {
                x: this.basePoint.x,
                y: this.basePoint.y,
                z: this.basePoint.z,
            }
        };
        return data;
    }
    /**
     * 从json中解析
     * @param json
     * @returns
     */
    fromJson(json) {
        //TODO
        return this;
    }
}
const _vector1 = new Vector3();
const _vector2 = new Vector3();const BoxSurfaceType = {
    Top: 0,
    Bottom: 1,
    Left: 2,
    Right: 3,
    Front: 4,
    Back: 5
};
/**
 * 长方体。默认局部坐标系下的立方体只是一个原点为（0， 0， 0）， 三条边方向分别为xyz轴，长度均为1的坐标轴
 * 定义方式：将长方体的长宽高分别存入，局部坐标系的xyz轴中，长方体的基点存入局部坐标系的基点
 */
class Box extends Solid {
    /**
     *
     * @param basePoint 立方体基点（左下角）
     * @param vecX 立方体x轴，含长度与x轴方向
     * @param vecY 立方体y轴，含宽度与y轴方向
     * @param vecZ 立方体z轴，含高度与z轴方向
     */
    constructor(basePoint = new Point(), vecX = new Vector3(1, 0, 0), vecZ = new Vector3(0, 1, 0), vecY = null) {
        super();
        /**
         * 几何类型
         */
        this.type = 'Box';
        /**
         * 长方体表面索引，以局部坐标系尺度来看，0~5分别对应上下左右前后
         */
        this.surfaceIndex = [BoxSurfaceType.Top, BoxSurfaceType.Bottom, BoxSurfaceType.Left, BoxSurfaceType.Right, BoxSurfaceType.Front, BoxSurfaceType.Back];
        let vecTmpY = vecY; // 立方体y轴，如果有传入设置为传入的值，否则设置为z叉乘x
        if (vecY == null) {
            vecTmpY = vecZ.clone().cross(vecX).normalize();
        }
        this.transformMatrix.set(vecX.x, vecTmpY.x, vecZ.x, basePoint.x, vecX.y, vecTmpY.y, vecZ.y, basePoint.y, vecX.z, vecTmpY.z, vecZ.z, basePoint.z, 0, 0, 0, 1);
    }
    clone() {
        return new Box().copy(this);
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        let res = []; //返回的数组
        this.surfaceIndex.forEach(index => {
            let surface = this.getSurface(index);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index) {
        switch (index) {
            case BoxSurfaceType.Top:
                return new RectFace(new Point(1, 1, 1).applyMatrix4(this.transformMatrix), this.getX().multiplyScalar(-1), this.getY().multiplyScalar(-1));
            case BoxSurfaceType.Bottom:
                return new RectFace(this.getP(), this.getX(), this.getY());
            case BoxSurfaceType.Left:
                return new RectFace(this.getP(), this.getZ(), this.getY());
            case BoxSurfaceType.Right:
                return new RectFace(new Point(1, 1, 1).applyMatrix4(this.transformMatrix), this.getY().multiplyScalar(-1), this.getZ().multiplyScalar(-1));
            case BoxSurfaceType.Front:
                return new RectFace(this.getP(), this.getX(), this.getZ());
            case BoxSurfaceType.Back:
                return new RectFace(new Point(1, 1, 1).applyMatrix4(this.transformMatrix), this.getX().multiplyScalar(-1), this.getZ().multiplyScalar(-1));
            default:
                return null;
        }
    }
    /**
     * 获取中心点
     * @param target
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.set(0.5, 0.5, 0.5).applyMatrix4(this.transformMatrix);
        return target;
    }
    /**
     * 判断两个立方体是否相等，本质上是判断两个立方体的矩阵是否相等
     * @param box
     * @returns
     */
    equals(box) {
        const eplison = 1e-6;
        // TODO 矩阵相等方法，后续需要提取出去
        for (let i = 0; i < 16; i++) {
            if (Math.abs(this.transformMatrix.elements[i] - box.transformMatrix.elements[i]) > eplison)
                return false;
        }
        return true;
    }
    /**
     * 获取控制点 可用于stretch
     * @returns
     */
    getControlPoints() {
        let bottomPoint1 = this.getP();
        let bottomPoint2 = this.getP().add(this.getX());
        let bottomPoint3 = this.getP().add(this.getX()).add(this.getY());
        let bottomPoint4 = this.getP().add(this.getY());
        let bottomCenter = this.getP().add(this.getX().multiplyScalar(0.5)).add(this.getY().multiplyScalar(0.5));
        let topCenter = bottomCenter.clone().add(this.getZ());
        return [
            // stretch点
            // 夹点
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint1, geoType: this.type, uvtPosition: new Point(0, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint2, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint3, geoType: this.type, uvtPosition: new Point(1, 1, 0) },
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint4, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.FACE_CENTER, point: bottomCenter, geoType: this.type, uvtPosition: new Point(0.5, 0.5, 0) },
            { type: FEATURE_POINT_TYPE.FACE_CENTER, point: topCenter, geoType: this.type, uvtPosition: new Point(0.5, 0.5, 1) },
            { type: FEATURE_POINT_TYPE.CENTER, point: new Point().addVectors(bottomCenter, topCenter).multiplyScalar(0.5), geoType: this.type }
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        let bottomPoint1 = this.getP();
        let bottomPoint2 = this.getP().add(this.getX());
        let bottomPoint3 = this.getP().add(this.getX()).add(this.getY());
        let bottomPoint4 = this.getP().add(this.getY());
        let topPoint1 = bottomPoint1.clone().add(this.getZ());
        let topPoint2 = bottomPoint2.clone().add(this.getZ());
        let topPoint3 = bottomPoint3.clone().add(this.getZ());
        let topPoint4 = bottomPoint4.clone().add(this.getZ());
        let box = new Box3();
        box.setFromPoints([bottomPoint1, bottomPoint2, bottomPoint3, bottomPoint4, topPoint1, topPoint2, topPoint3, topPoint4]);
        return {
            min: box.min,
            max: box.max,
        };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let bottomPoint1 = this.getP();
        let bottomPoint2 = this.getP().add(this.getX());
        let bottomPoint3 = this.getP().add(this.getX()).add(this.getY());
        let bottomPoint4 = this.getP().add(this.getY());
        let topPoint1 = bottomPoint1.clone().add(this.getZ());
        let topPoint2 = bottomPoint2.clone().add(this.getZ());
        let topPoint3 = bottomPoint3.clone().add(this.getZ());
        let topPoint4 = bottomPoint4.clone().add(this.getZ());
        let res = [
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint1, geoType: this.type, uvtPosition: new Point(0, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint2, geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint3, geoType: this.type, uvtPosition: new Point(1, 1, 0) },
            { type: FEATURE_POINT_TYPE.END, point: bottomPoint4, geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint1, geoType: this.type, uvtPosition: new Point(0, 0, 1) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint2, geoType: this.type, uvtPosition: new Point(1, 0, 1) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint3, geoType: this.type, uvtPosition: new Point(1, 1, 1) },
            { type: FEATURE_POINT_TYPE.END, point: topPoint4, geoType: this.type, uvtPosition: new Point(0, 1, 1) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(bottomPoint1, bottomPoint2).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(0.5, 0, 0) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(bottomPoint2, bottomPoint3).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(1, 0.5, 0) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(bottomPoint3, bottomPoint4).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(0.5, 1, 0) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(bottomPoint4, bottomPoint1).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(0, 0.5, 0) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(topPoint1, topPoint2).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(0.5, 0, 1) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(topPoint2, topPoint3).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(1, 0.5, 1) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(topPoint3, topPoint4).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(0.5, 1, 1) },
            { type: FEATURE_POINT_TYPE.MIDDLE, point: new Vector3().addVectors(topPoint4, topPoint1).multiplyScalar(0.5), geoType: this.type, uvtPosition: new Point(0, 0.5, 1) },
        ];
        return res;
    }
    toJson() {
        let data = Object.assign({ version: "1.1", type: this.type }, super.toJson());
        return data;
    }
}const PyramidSurfaceType = {
    Bottom: 0
};
/**
 * 棱锥
 *
 */
class Pyramid extends Solid {
    /**
     * 底面中心
     */
    get center() {
        return new Point().copy(this.getP());
    }
    /**
     * 设置底面中心
     */
    set center(value) {
        this.setP(value);
    }
    /**
     * 底面指向顶部的方向单位向量
     */
    get normal() {
        return new Vector3().copy(this.getZ().normalize());
    }
    /**
     * 需要保证z与x轴方向不同，所以建议以后设置normal时都调用这个方法
     * @param nx
     * @param ny
     * @param nz
     */
    setNormal(nx, ny, nz) {
        this.setXYZ(this.getX(), new Vector3(nx, ny, nz));
    }
    /**
     * 底面中心指向第一个顶点的方向单位向量
     */
    get firstDir() {
        return new Vector3().copy(this.getX().normalize());
    }
    /**
     * 棱锥高
     */
    get height() {
        return this.scaleZ;
    }
    /**
     * 设置棱锥高
     */
    set height(value) {
        let scale = value / this.scaleZ;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), 1, 1, scale);
    }
    /**
     * 底面外接圆半径
     */
    get radius() {
        return this.scaleX;
    }
    /**
     * 设置底面外接圆半径
     */
    set radius(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.ratio = this.ratio / scale;
        this.scale(this.getP(), scale, scale, 1);
    }
    /**
     * 顶面外接圆半径
     */
    get topRadius() {
        return this.radius * this.ratio;
    }
    /**
     * 设置顶面外接圆半径
     */
    set topRadius(value) {
        let scale = value / this.radius;
        this.ratio = scale;
    }
    /**
     * 构造函数
     * @param center 底面中心
     * @param radius 底面外接圆半径
     * @param height 棱锥高度
     * @param edge 棱锥侧面数量
     * @param topRadius 棱台顶面外接圆半径
     * @param normal 法向量
     * @param firstDir
     */
    constructor(center = new Point(), radius = 0, height = 0, edge = 4, topRadius = 0, normal = new Vector3(0, 0, 1), firstDir = new Vector3(1, 0, 0)) {
        super();
        /**
         * 类型
         */
        this.type = 'Pyramid';
        /**
         * 棱锥表面索引
         */
        this.surfaceIndex = [PyramidSurfaceType.Bottom];
        this.transformMatrix.elements[8] = normal.x;
        this.transformMatrix.elements[9] = normal.y;
        this.transformMatrix.elements[10] = normal.z;
        firstDir.normalize();
        this.updateMatrixByX(firstDir, false);
        if (radius <= 0)
            radius = 1;
        this.center = center;
        this.radius = radius;
        this.height = height;
        this.edge = edge;
        this.topRadius = topRadius;
        for (let i = 0; i < this.edge; i++) {
            this.surfaceIndex.push(i + 1);
        }
        if (this.topRadius > 1e-6)
            this.surfaceIndex.push(this.edge + 1);
    }
    /**
     * 设置属性
     * @param center 底面中心
     * @param radius 底面外接圆半径
     * @param height 棱锥高度
     * @param edge 棱锥侧面数量
     * @param topRadius 棱台顶面外接圆半径
     * @param normal 法向量
     * @returns
     */
    set(center, radius, height, edge, normal) {
        this.center = center;
        this.radius = radius;
        this.topRadius = this.topRadius;
        this.height = height;
        this.edge = edge;
        this.setXYZ(this.getX(), normal);
        return this;
    }
    /**
     * 复制
     * @param pyramid
     * @returns
     */
    copy(pyramid) {
        super.copy(pyramid);
        this.edge = pyramid.edge;
        this.ratio = pyramid.ratio;
        return this;
    }
    /**
     * 克隆
     * @param
     * @returns
     */
    clone() {
        return new Pyramid().copy(this);
    }
    equals(pyramid) {
        for (let i = 0; i < 16; i++) {
            if (Math.abs(this.transformMatrix.elements[i] - pyramid.transformMatrix.elements[i]) > 1e-6)
                return false;
        }
        if (this.edge != pyramid.edge)
            return false;
        if (Math.abs(this.topRadius - pyramid.topRadius) > 1e-6)
            return false;
        return false;
    }
    /**
     * 获取中心点
     * @param target
     * @returns
     */
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.center);
        target.add(this.normal.clone().multiplyScalar(this.height / 2));
        return target;
    }
    /**
     * 抽象成json数据格式
     * @returns
     */
    toJson() {
        return Object.assign(Object.assign({ version: "1.0", type: this.type }, super.toJson()), { edge: this.edge, ratio: this.ratio });
    }
    /**
     * 解析json数据成圆弧对象
     * @param json
     * @returns
     */
    fromJson(json) {
        super.fromJson(json);
        this.edge = json.edge;
        this.ratio = json.ratio;
        return this;
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        let res = []; //返回的数组
        this.surfaceIndex.forEach(index => {
            let surface = this.getSurface(index);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index) {
        let points = this.getBottomPoints();
        let topPoints = this.getTopPoints();
        //底面
        if (index == PyramidSurfaceType.Bottom) {
            let edges = [];
            for (let i = 1; i <= this.edge; i++) {
                const edge = new Line(points[i - 1], points[i % this.edge]);
                edges.push(edge);
            }
            return new RegionFace(edges, this.firstDir, this.center);
        }
        else if (index == this.edge + 1) { //顶面
            if (this.topRadius < 1e-6)
                return null;
            let edges = [];
            for (let i = 1; i <= this.edge; i++) {
                const edge = new Line(topPoints[i - 1], topPoints[i % this.edge]);
                edges.push(edge);
            }
            return new RegionFace(edges, this.firstDir, topPoints[0]);
        }
        else { //侧面
            let bottomPoint1 = points[index - 1];
            let bottomPoint2 = points[index % this.edge];
            let tempEdges = [];
            if (this.topRadius < 1e-6) { //三角形
                tempEdges.push(new Line(bottomPoint1, bottomPoint2));
                tempEdges.push(new Line(bottomPoint2, topPoints[0]));
                tempEdges.push(new Line(topPoints[0], bottomPoint1));
                return new RegionFace(tempEdges, this.firstDir);
                //return new Triangle(topPoints[0], bottomPoint1, bottomPoint2);
            }
            else { // 返回梯形
                let topPoint1 = topPoints[index - 1];
                let topPoint2 = topPoints[index % this.edge];
                tempEdges.push(new Line(bottomPoint1, bottomPoint2));
                tempEdges.push(new Line(bottomPoint2, topPoint2));
                tempEdges.push(new Line(topPoint2, topPoint1));
                tempEdges.push(new Line(topPoint1, bottomPoint1));
                return new RegionFace(tempEdges, this.firstDir);
            }
        }
    }
    rotate_point(origin, point, rotateAxis, angle) {
        origin.sub(point).applyAxisAngle(rotateAxis, angle).add(point);
        return origin.clone();
    }
    /**
     * 获取底面端点
     * @param
     * @returns
     */
    getBottomPoints() {
        let points = [];
        let firstPoint = this.center.clone().add(this.firstDir.clone().multiplyScalar(this.radius));
        let angleSegment = Math.PI * 2 / this.edge;
        points.push(firstPoint);
        for (let i = 1; i < this.edge; i++) {
            let tmpPoint = this.rotate_point(firstPoint.clone(), this.center, this.normal, angleSegment * i);
            points.push(tmpPoint);
        }
        return points;
    }
    /**
     * 获取顶面端点
     * @param
     * @returns
     */
    getTopPoints() {
        if (this.topRadius < 1e-6) {
            return [this.center.clone().add(this.normal.clone().multiplyScalar(this.height))];
        }
        else {
            let points = [];
            let angleSegment = Math.PI * 2 / this.edge;
            let firstTopPoint = this.center.clone().add(this.firstDir.clone().multiplyScalar(this.topRadius))
                .add(this.normal.clone().multiplyScalar(this.height));
            points.push(firstTopPoint);
            for (let i = 1; i < this.edge; i++) {
                let tmpPoint = this.rotate_point(firstTopPoint.clone(), this.center, this.normal, angleSegment * i);
                points.push(tmpPoint);
            }
            return points;
        }
    }
}const _normalMatrix = new Matrix3();
/**
 * 射线
 * 其局部坐标系定义为起点为基点p，方向为局部x轴
 */
class Ray extends Curve {
    /**
     * 端点
     */
    get origin() {
        return new Point().copy(this.getP());
    }
    /**
     * 设置端点
     */
    set origin(value) {
        this.setP(value);
    }
    /**
     * 方向
     */
    get direction() {
        return this.getZ();
    }
    /**
     * 获取切线方向
     * @param point
     */
    getCutLineDir(point, target) {
        if (target == null) {
            target = new Vector3();
        }
        this.getNormalizeX(target);
        return target;
    }
    /**
     * 构造函数
     * @param origin
     * @param direction
     */
    constructor(origin = new Point(), direction = new Vector3(0, 0, -1)) {
        super();
        /**
         * 类型
         */
        this.type = 'Ray';
        this.origin = origin;
        this.setXYZ(this.getX(), direction);
    }
    /**
     * 设置端点和方向
     * @param origin
     * @param direction
     * @returns
     */
    set(origin, direction) {
        this.origin = origin;
        this.setXYZ(this.getX(), direction);
        return this;
    }
    /**
     * 应用矩阵
     * @param matrix4
     * @returns
     */
    applyMatrix4(matrix4) {
        const normalMatrix = _normalMatrix.getNormalMatrix(matrix4);
        this.origin.applyMatrix4(matrix4);
        this.direction.applyMatrix3(normalMatrix).normalize();
        return this;
    }
    /**
     * 判定相等
     * @param ray
     * @returns
     */
    equals(ray) {
        return ray.origin.equals(this.origin) && ray.direction.equals(this.direction);
    }
    /**
     * 克隆一个新的射线对象
     * @returns
     */
    clone() {
        return new Ray().copy(this);
    }
    /**
     * 获取插值点
     * @param t 0-1
     * @returns
     */
    getPointAt(t, target = null) {
        if (target == null) {
            target = new Point();
        }
        return target.copy(this.direction).multiplyScalar(t).add(this.origin);
    }
    /**
     * 改变观察方向
     * @param v
     * @returns
     */
    lookAt(v) {
        let dir = this.direction.copy(v).sub(this.origin).normalize();
        this.setXYZ(this.getX(), dir);
        return this;
    }
    /**
     * 转成json格式数据
     * @returns
     */
    toJson() {
        return Object.assign({ version: "1.1", type: this.type }, super.toJson());
    }
}// const _rotateDivition: number = 16;
// const _tmpArc: Arc = new Arc()
// const  _tmpPoint = new Point();
//todo较多内容待实现
const RevolveSolidType = {
    Top: 0,
    Bottom: 1,
};
/**
 * 旋转体
 * 局部坐标系为：基点为(0,0,0), 起点在x轴
 */
class RevolveSolid extends Solid {
    get center() {
        return new Point().copy(this.getP());
    }
    set center(value) {
        this.setP(value);
    }
    get axis() {
        //axis是旋转轴z轴
        return this.getZ();
    }
    constructor(bottomFace, axis = new StraightLine(), angle = 2 * Math.PI) {
        super();
        this.type = 'RevolveSolid';
        // // 拉伸面的基准线
        // baseCurves: Curve[] = [];
        /**
         * 面类型数组
         */
        this.surfaceIndex = [RevolveSolidType.Top, RevolveSolidType.Bottom];
        // 设置旋转轴方向
        const zAxis = axis.direction.clone().normalize();
        // 旋转轴起点
        const origin = axis.origin.clone();
        // 设置x向量xAxis
        const anyAxis = new Vector3(1, 0, 0);
        // 如果方向接近平行，选择另一个方向
        if (Math.abs(zAxis.dot(anyAxis)) > 0.999) {
            anyAxis.set(0, 1, 0);
        }
        const xAxis = anyAxis.cross(zAxis).normalize();
        // 生成yAxis
        const yAxis = zAxis.clone().cross(xAxis).normalize();
        // 构造旋转体的变换矩阵
        this.transformMatrix.set(xAxis.x, yAxis.x, zAxis.x, origin.x, xAxis.y, yAxis.y, zAxis.y, origin.y, xAxis.z, yAxis.z, zAxis.z, origin.z, 0, 0, 0, 1);
        this.angle = angle;
        // 将bottomFace的局部坐标系转换到旋转体的局部坐标系下
        if (bottomFace) {
            this.bottomFace = bottomFace;
            // bottomFace.transformMatrix.premultiply(this.transformMatrix.clone().invert());
            let curves = bottomFace.getSideEdges();
            for (let i = 0; i < curves.length; i++) {
                this.surfaceIndex.push(i + 2);
            }
        }
    }
    getGeoElements() {
        this.surfaceIndex = [RevolveSolidType.Top, RevolveSolidType.Bottom];
        let sideCurves = this.bottomFace.getSideEdges();
        for (let i = 0; i < sideCurves.length; i++) {
            this.surfaceIndex.push(i + 2);
        }
        let res = []; // 返回的数组
        this.surfaceIndex.forEach(index => {
            let surface = this.getSurface(index, sideCurves);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    copy(solid) {
        super.copy(solid);
        this.bottomFace = solid.bottomFace;
        this.angle = solid.angle;
        return this;
    }
    clone() {
        return new RevolveSolid().copy(this);
    }
    equals(solid) {
        return this.bottomFace.equals(solid.bottomFace) && this.axis.equals(solid.axis) && this.angle === solid.angle;
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.center);
        return target;
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index, sideCurves = []) {
        if (index === RevolveSolidType.Bottom) {
            const bottom = this.bottomFace.clone();
            // bottom.transformMatrix.premultiply(this.transformMatrix);
            return bottom;
        }
        else if (index === RevolveSolidType.Top) {
            const top = this.bottomFace.clone();
            // top.transformMatrix.premultiply(this.transformMatrix);
            top.rotate(this.getP(), this.getNormalizeZ(), this.angle);
            return top;
        }
        else {
            // 侧面的旋转面
            let actualIndex = index - 2;
            // 边线数组
            if (sideCurves.length === 0) {
                sideCurves = this.bottomFace.getSideEdges();
            }
            // 获取旋转轴
            this.getNormalizeZ(_direction);
            this.getP(_origin);
            _axis.setFromDirection(_origin, _direction);
            const side = sideCurves[actualIndex].clone();
            // side.transformMatrix.premultiply(this.transformMatrix);
            let surface = new RevolveSurface(side, _axis, this.angle);
            // surface.transformMatrix.premultiply(this.transformMatrix);
            return surface;
        }
    }
    /**
     * 返回特征点
     * @returns
     */
    getFeaturePoints() {
        const featurePoints = [];
        const bottomPoints = this.bottomFace.getFeaturePoints();
        bottomPoints.forEach(p => {
            p.point.applyMatrix4(this.transformMatrix);
        });
        featurePoints.push(...bottomPoints);
        // const els = this.getGeoElements();
        // const featurePoints = [];
        // els.forEach(el => {
        //     const points = el.getFeaturePoints()
        //     if (points) {
        //         featurePoints.push(...points);
        //     }
        // })
        return featurePoints;
    }
    /**
     * 获取控制点
     * @returns
     */
    getControlPoints() {
        let points = [];
        points.push(...this.bottomFace.getControlPoints());
        return points;
    }
    getBoundingBox() {
        // 初始化最小值和最大值为无穷
        const min = new Point(Infinity, Infinity, Infinity);
        const max = new Point(-Infinity, -Infinity, -Infinity);
        const edgeSample = 4; // 边线采样点数
        const numSamples = 15; // 旋转面采样点数
        // 获取底面上的所有顶点
        const edges = this.bottomFace.edges;
        const basePoints = [];
        for (let i = 0; i < edges.length; i++) {
            const edge = edges[i];
            const points = edge.getPoints(edgeSample);
            basePoints.push(...points);
        }
        // 遍历旋转角度，生成旋转后的点
        for (let i = 0; i <= numSamples; i++) {
            const theta = (i / numSamples) * this.angle;
            // 构造旋转矩阵
            const rotationMatrix = new Matrix4().makeRotationAxis(this.axis.clone().normalize(), // 旋转轴方向
            theta);
            // 遍历底面上的点，计算旋转后的点
            for (const point of basePoints) {
                // 点先应用旋转变换
                const rotatedPoint = point.clone().applyMatrix4(rotationMatrix);
                // 转换到世界坐标系
                // rotatedPoint.applyMatrix4(this.transformMatrix);
                // 更新最小值和最大值
                min.x = Math.min(min.x, rotatedPoint.x);
                min.y = Math.min(min.y, rotatedPoint.y);
                min.z = Math.min(min.z, rotatedPoint.z);
                max.x = Math.max(max.x, rotatedPoint.x);
                max.y = Math.max(max.y, rotatedPoint.y);
                max.z = Math.max(max.z, rotatedPoint.z);
            }
        }
        return { min, max };
    }
    // overwrite
    toJson() {
        return Object.assign({ version: "1.0", bottomFace: this.bottomFace.toJson(), angle: this.angle, type: this.type }, super.toJson());
    }
    // overwrite
    fromJson(json) {
        super.fromJson(json);
        this.bottomFace = new RegionFace().fromJson(json.bottomFace);
        this.angle = json.angle;
        return this;
    }
}
const _direction = new Vector3();
const _origin = new Vector3();
const _axis = new StraightLine();/**
 * 球体
 * 其局部坐标系定义为半径为1，起点为(0,0,0)的标准球体
 */
class Sphere extends Solid {
    get center() {
        return new Point().copy(this.getP());
    }
    set center(value) {
        this.setP(value);
    }
    /**
     * 半径
     */
    get radius() {
        return this.scaleX;
    }
    /**
     * 设置半径
     */
    set radius(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale);
    }
    constructor(center = new Point(), radius = 0) {
        super();
        /*1.初始定义与构造*/
        this.type = 'Sphere';
        // radius: number;
        // center: Point;
        //线框模型三个大圆的normal，为固定的
        this.xDir = new Vector3(1, 0, 0);
        this.yDir = new Vector3(0, 1, 0);
        this.zDir = new Vector3(0, 0, 1);
        /**
         * 球体表面索引，只有一个球面
         */
        this.surfaceIndex = [0];
        this.center = center;
        this.radius = radius;
        this.transformMatrix.set(radius, 0, 0, center.x, 0, radius, 0, center.y, 0, 0, radius, center.z, 0, 0, 0, 1);
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        const min = new Point(-1, -1, -1);
        const max = new Point(1, 1, 1);
        min.applyMatrix4(this.transformMatrix);
        max.applyMatrix4(this.transformMatrix);
        return {
            min: min,
            max: max,
        };
    }
    /**
     * 获取特征点
     * @returns
     */
    getFeaturePoints() {
        const featurePoints = [];
        featurePoints.push({ type: FEATURE_POINT_TYPE.CENTER, point: this.center, geoType: this.type });
        return featurePoints;
    }
    /**
     * 返回控制点
     * @returns
     */
    getControlPoints() {
        const featurePoints = [];
        const spherical = this.getGeoElements()[0];
        featurePoints.push(...[
            { type: FEATURE_POINT_TYPE.END, point: spherical.getPointAt(0, 0.5), geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: spherical.getPointAt(0.25, 0.5), geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.END, point: spherical.getPointAt(0.5, 0.5), geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: spherical.getPointAt(0.75, 0.5), geoType: this.type, uvtPosition: new Point(0, -1, 0) },
        ]);
        featurePoints.push({ type: FEATURE_POINT_TYPE.CENTER, point: this.center, geoType: this.type, uvtPosition: new Point(0, 0, 0) });
        return featurePoints;
    }
    /*2.复制、克隆与判断相等*/
    copy(sphere) {
        this.center = sphere.center;
        this.radius = sphere.radius;
        this.transformMatrix = sphere.transformMatrix;
        return this;
    }
    clone() {
        return new Sphere().copy(this);
    }
    equals(sphere) {
        if (!this.center.equals(sphere.center))
            return false;
        if (Math.abs(this.radius - sphere.radius) > 1e-6)
            return false;
        return true;
    }
    /*4.获取图元上的各种信息*/
    //线框模型的五个圆的几何信息
    // private _xCircle: Circle = new Circle();
    // private _yCircle: Circle = new Circle();
    // private _zCircle: Circle = new Circle();
    // private _topCircle: Circle = new Circle();
    // private _bottomCircle: Circle = new Circle();
    //重新设置线框模型的几何信息   TODO
    // resetHelpCircle() {
    //     this._xCircle.center = this.center;
    //     this._xCircle.radius = this.radius;
    //     this._xCircle.setXYZ(this.yDir, this.xDir);
    //     this._yCircle.center = this.center;
    //     this._yCircle.radius = this.radius;
    //     this._yCircle.setXYZ(this._yCircle.axisX, this.xDir);
    //     this._zCircle.center = this.center;
    //     this._zCircle.radius = this.radius;
    //     this._zCircle.setXYZ(this._zCircle.axisX, this.zDir);
    //     this._topCircle.radius = this.radius / Math.sqrt(2);
    //     this._topCircle.center = this.center.clone().add(this.zDir.clone().multiplyScalar(this._topCircle.radius));
    //     this._topCircle.setXYZ(this._topCircle.axisX, this.zDir);
    //     this._bottomCircle.radius = this.radius / Math.sqrt(2);
    //     this._bottomCircle.center = this.center.clone().add(this.zDir.clone().multiplyScalar(-this._bottomCircle.radius));
    //     this._bottomCircle.setXYZ(this._bottomCircle.axisX, this.zDir);
    // }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.center);
        return target;
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        let res = []; //返回的数组
        this.surfaceIndex.forEach(_ => {
            let surface = new Spherical(this.center, this.radius);
            res.push(surface);
        });
        return res;
    }
    toJson() {
        return Object.assign({ version: "1.0", type: this.type }, super.toJson());
    }
    fromJson(json) {
        super.fromJson(json);
        return this;
    }
}const WedgeSurfaceType = {
    Bottom: 0,
    Left: 1,
    Right: 2,
    Front: 3,
    Back: 4
};
/**
 * 楔形
 * 其局部坐标系定义为底边长度为1，宽度为1，高度为1楔形,
 * x轴为底边方向
 * y轴为宽度方向
 * z轴为法向量方向
 * 原点为底面矩阵左下角
 */
class Wedge extends Solid {
    get dirX() {
        return new Vector3().copy(this.getX());
    }
    get dirY() {
        return new Vector3().copy(this.getY());
    }
    get normal() {
        return new Vector3().copy(this.getZ());
    }
    get basePoint() {
        return new Point().copy(this.getP());
    }
    set basePoint(value) {
        this.setP(value);
    }
    get length() {
        return this.scaleX;
    }
    set length(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale, 1, 1);
    }
    get width() {
        return this.scaleY;
    }
    set width(value) {
        let scale = value / this.scaleY;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), 1, scale, 1);
    }
    get height() {
        return this.scaleZ;
    }
    set height(value) {
        let scale = value / this.scaleZ;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), 1, 1, scale);
    }
    constructor(basePoint = new Point(), length = 0, width = 0, heigth = 0, dirX = new Vector3(1, 0, 0), normal = new Vector3(0, 0, 1)) {
        super();
        this.type = 'Wedge';
        this.surfaceIndex = [WedgeSurfaceType.Bottom, WedgeSurfaceType.Left, WedgeSurfaceType.Right, WedgeSurfaceType.Front, WedgeSurfaceType.Back];
        this.basePoint = basePoint;
        this.length = length;
        this.width = width;
        this.height = heigth;
        this.setXYZ(dirX, normal);
    }
    set(basePoint, length, width, heigth, dirX, normal) {
        this.basePoint = basePoint;
        this.length = length;
        this.width = width;
        this.height = heigth;
        this.setXYZ(dirX, normal);
        return this;
    }
    copy(box) {
        this.basePoint = box.basePoint;
        this.length = box.length;
        this.width = box.width;
        this.height = box.height;
        this.setXYZ(box.dirX, box.normal);
        return this;
    }
    clone() {
        return new Wedge().copy(this);
    }
    equals(wedge) {
        if (!this.matrixEquals(wedge.transformMatrix))
            return false;
        return true;
    }
    getCenter(target) {
        if (target == null) {
            target = new Point();
        }
        target.copy(this.dirX.clone().multiplyScalar(this.length).add(this.dirY.clone().multiplyScalar(this.width)).multiplyScalar(0.5)).add(this.basePoint);
        return target;
    }
    /**
     * 获取特征点
     * @returns
     */
    getFeaturePoints() {
        return [];
    }
    /**
     * 获取控制点
     * @returns
     */
    getControlPoints() {
        return [
            { type: FEATURE_POINT_TYPE.CENTER, point: this.getSurface(WedgeSurfaceType.Bottom).getPointAt(0.5, 0.5), geoType: this.type }
        ];
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        const bottom = this.getSurface(WedgeSurfaceType.Bottom);
        const left = this.getSurface(WedgeSurfaceType.Left);
        const bottomData = bottom.getBoundingBox();
        const leftData = left.getBoundingBox();
        let min = new Vector3(Math.min(leftData.min.x, bottomData.min.x), Math.min(leftData.min.y, bottomData.min.y), Math.min(leftData.min.z, bottomData.min.z));
        let max = new Vector3(Math.max(leftData.max.x, bottomData.max.x), Math.max(leftData.max.y, bottomData.max.y), Math.max(leftData.max.z, bottomData.max.z));
        return {
            min: min,
            max: max
        };
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        let res = []; //返回的数组
        this.surfaceIndex.forEach(index => {
            let surface = this.getSurface(index);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    /**
     * 根据索引值获取表面
     * @param index
     * @returns
     */
    getSurface(index) {
        switch (index) {
            case WedgeSurfaceType.Bottom:
                return new RectFace(this.getP(), this.getX(), this.getY());
            case WedgeSurfaceType.Left:
                return new RectFace(this.getP(), this.getZ(), this.getY());
            case WedgeSurfaceType.Right:
                let vecX = new Vector3(1, 0, -1).applyMatrix4(this.transformMatrix).sub(this.getP());
                return new RectFace(new Point(0, 0, 1).applyMatrix4(this.transformMatrix), vecX, this.getY());
            case WedgeSurfaceType.Front:
                return new Triangle(this.getP(), new Point(1, 0, 0).applyMatrix4(this.transformMatrix), new Point(0, 0, 1).applyMatrix4(this.transformMatrix));
            case WedgeSurfaceType.Back:
                return new Triangle(new Point(0, 1, 0).applyMatrix4(this.transformMatrix), new Point(0, 1, 1).applyMatrix4(this.transformMatrix), new Point(1, 1, 0).applyMatrix4(this.transformMatrix));
            default:
                return null;
        }
    }
    toJson() {
        let data = Object.assign({ version: "1.0", basePoint: {
                x: this.basePoint.x,
                y: this.basePoint.y,
                z: this.basePoint.z,
            }, length: this.length, width: this.width, height: this.height, dirX: {
                x: this.dirX.x,
                y: this.dirX.y,
                z: this.dirX.z,
            }, dirY: {
                x: this.dirY.x,
                y: this.dirY.y,
                z: this.dirY.z,
            }, normal: {
                x: this.normal.x,
                y: this.normal.y,
                z: this.normal.z,
            } }, super.toJson());
        return data;
    }
    fromJson(json) {
        this.basePoint = new Point(json.basePoint.x, json.basePoint.y, json.basePoint.z);
        this.length = json.length;
        this.width = json.width;
        this.height = json.height;
        this.setXYZ(new Vector3(json.dirX.x, json.dirX.y, json.dirX.z), new Vector3(json.normal.x, json.normal.y, json.normal.z));
        return this;
    }
}/**
 * 圆环体
 */
class Torus extends Solid {
    /**
     * 平面法向量
     */
    get normal() {
        return this.getNormalizeZ();
    }
    /**
     * 圆环半径
     */
    get radius() {
        return this.scaleX;
    }
    getGeoElements() {
        let res = []; //返回的数组
        const line = new StraightLine(this.center, this.normal);
        this.getP(_p).add(this.getX());
        const rev = new RevolveSurface(new Circle(_p, this.segmentRadius, this.getNormalizeY(), this.getNormalizeX()), line);
        res.push(rev);
        return res;
    }
    /**
     * 设置圆环半径
     */
    set radius(value) {
        let scale = value / this.scaleX;
        if (scale == 0)
            scale = 1;
        this.scale(this.getP(), scale);
    }
    /**
     * 设置圆环截面半径，重新计算旋转体的底面
     * @param value
     */
    setSegmentRadius(value) {
        this.segmentRadius = value;
    }
    /**
     * 圆心
     */
    get center() {
        return this.getP();
    }
    /**
     * 设置圆心
     */
    set center(value) {
        this.setP(value);
    }
    /**
     * 构造方法
     * @param center 圆环圆心
     * @param radius 圆环半径
     * @param normal 法向量
     * @param axisX x轴
     * @param segmentRadius 截面半径
     */
    constructor(center = new Point(), radius = 1, normal = new Vector3(0, 0, 1), axisX = new Vector3(1, 0, 0), segmentRadius = 0) {
        super();
        /**
         * 类型
         */
        this.type = "Torus";
        /**
         * 面索引
         */
        this.surfaceIndex = [0];
        this.transformMatrix.elements[8] = normal.x;
        this.transformMatrix.elements[9] = normal.y;
        this.transformMatrix.elements[10] = normal.z;
        axisX = axisX.normalize();
        this.updateMatrixByX(axisX, false);
        if (radius <= 0)
            radius = 1;
        this.radius = radius;
        this.center = center;
        this.segmentRadius = segmentRadius;
    }
    /**
     * 复制
     * @param torus
     * @returns
     */
    copy(torus) {
        this.transformMatrix.copy(torus.transformMatrix);
        this.segmentRadius = torus.segmentRadius;
        return this;
    }
    scale(point, factorX, factorY, factorZ) {
        super.scale(point, factorX, factorY, factorZ);
        this.setSegmentRadius(this.segmentRadius * factorX);
        return this;
    }
    /**
     * 获取特征点
     * @returns
     */
    getFeaturePoints() {
        const featurePoints = [];
        return featurePoints;
    }
    /**
     * 获取控制点
     * @returns
     */
    getControlPoints() {
        const featurePoints = [];
        const revolve = this.getGeoElements()[0];
        const origin = this.getP();
        const direction = this.getZ().normalize();
        const baseCenter = revolve.baseCurve.center;
        baseCenter.applyMatrix4(revolve.transformMatrix); //需要乘以旋转面的局部坐标
        featurePoints.push(...[
            { type: FEATURE_POINT_TYPE.END, point: baseCenter.clone().sub(origin).applyAxisAngle(direction, 0).add(origin), geoType: this.type, uvtPosition: new Point(1, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: baseCenter.clone().sub(origin).applyAxisAngle(direction, revolve.angle * 0.25).add(origin), geoType: this.type, uvtPosition: new Point(0, 1, 0) },
            { type: FEATURE_POINT_TYPE.END, point: baseCenter.clone().sub(origin).applyAxisAngle(direction, revolve.angle * 0.5).add(origin), geoType: this.type, uvtPosition: new Point(-1, 0, 0) },
            { type: FEATURE_POINT_TYPE.END, point: baseCenter.clone().sub(origin).applyAxisAngle(direction, revolve.angle * 0.75).add(origin), geoType: this.type, uvtPosition: new Point(0, -1, 0) },
        ]);
        featurePoints.push({ type: FEATURE_POINT_TYPE.CENTER, point: this.center, geoType: this.type, uvtPosition: new Point(0, 0, 0) });
        return featurePoints;
    }
    /**
     * 获取包围盒
     * @returns
     */
    getBoundingBox() {
        return this.getGeoElements()[0].getBoundingBox();
    }
    /**
     * 克隆
     * @returns
     */
    clone() {
        return new Torus().copy(this);
    }
    /**
     * 判断是否相等
     * @param box
     * @returns
     */
    equals(torus) {
        if (torus.segmentRadius !== this.segmentRadius)
            return false;
        return this.matrixEquals(torus.transformMatrix);
    }
    /**
     * 抽象成json
     * @returns
     */
    toJson() {
        let data = Object.assign({ version: "1.0", type: this.type, segmentRadius: this.segmentRadius }, super.toJson());
        return data;
    }
    fromJson(json) {
        super.fromJson(json);
        this.segmentRadius = json.segmentRadius;
        return this;
    }
}
const _p = new Point();const SweeperSolidType = {
    Top: 0,
    Bottom: 1,
};
class SweeperSolid extends Solid {
    /**
     * 构造函数
     * @param baseSurface 扫掠基面
     * @param sweepPath 扫掠路径
     */
    constructor(baseSurface, sweepPath, basePoint) {
        super();
        /**
         * 几何类型
         */
        this.type = 'SweeperSolid';
        /**
         * 顶面相对于底面的缩放系数
         */
        this.scaleFactor = 1;
        /**
         * 面类型数组
         */
        this.surfaceIndex = [SweeperSolidType.Top, SweeperSolidType.Bottom];
        if (baseSurface) {
            this.baseSurface = baseSurface.clone();
        }
        if (sweepPath) {
            this.sweepPath = sweepPath.clone(); // 将曲线设置为多段的曲线
        }
        //未指定基点，对基点进行默认选择操作
        if (basePoint == null) {
            this.basePoint = baseSurface ? baseSurface.getCenter() : new Point();
        }
        else {
            this.basePoint = basePoint.clone();
        }
        this.surfaceIndex = [SweeperSolidType.Top, SweeperSolidType.Bottom];
        if (baseSurface) {
            let curves = baseSurface.getSideEdges();
            for (let i = 0; i < curves.length; i++) {
                this.surfaceIndex.push(i + 2);
            }
        }
    }
    /**
     * 获取基元数组
     * @returns
     */
    getGeoElements() {
        this.surfaceIndex = [SweeperSolidType.Top, SweeperSolidType.Bottom];
        let sideCurves = this.baseSurface.getSideEdges();
        for (let i = 0; i < sideCurves.length; i++) {
            this.surfaceIndex.push(i + 2);
        }
        let res = []; // 返回的数组
        this.surfaceIndex.map(index => {
            const surface = this.getSurface(index);
            if (surface) {
                res.push(surface);
            }
        });
        return res;
    }
    /**
     * 根据索引值获得表面
     * @param index
     */
    getSurface(index) {
        if (index === SweeperSolidType.Top) { // 当获取的表面是顶面时，对顶面进行旋转并将顶面平移到扫掠路径对应的终点
            // 获取扫掠基面
            let top = this.baseSurface.clone();
            // 获取扫掠路径终点的位置
            const endPoint = this.sweepPath.getEndPoints()[0];
            // 获取路径终点处切向量
            const endTangent = this.sweepPath.getCutLineDir(endPoint);
            top = this.getEndSurface(top, endPoint, endTangent);
            return top;
        }
        else if (index === SweeperSolidType.Bottom) { // 当获取的表面是底面
            // 对底面进行旋转并将顶面平移到扫掠路径对应的起点
            // 获取扫掠基面
            let top = this.baseSurface.clone();
            // 获取扫掠路径起点的位置
            const startPoint = this.sweepPath.getEndPoints()[1];
            // 获取路径起点处切向量
            const startTangent = this.sweepPath.getCutLineDir(startPoint);
            top = this.getEndSurface(top, startPoint, startTangent);
            return top;
        }
        else { // 当获取的表面是扫掠侧面
            // 通过底面边和扫掠路径创建扫掠面
            const top = this.baseSurface.clone();
            const edges = top.getSideEdges();
            // 创建扫掠面
            const edge = edges[index - 2];
            let angle;
            if (top.getNormalizeX().cross(edge.getNormalizeX()).z > 0) {
                angle = Math.acos(top.getNormalizeX().dot(edge.getNormalizeX()));
            }
            else {
                angle = -Math.acos(top.getNormalizeX().dot(edge.getNormalizeX()));
            }
            const sweeperSurface = new SweeperSurface(edge, this.sweepPath, this.basePoint, angle);
            return sweeperSurface;
        }
    }
    /**
     * 构造旋转矩阵
     * @param vecOrigin 物体原先方向
     * @param vecTarget 旋转后物体方向
     * @param axis 用户指定旋转轴(当物体旋转角度为PI的时候才有效果)
     * @returns
     */
    getRotationMatrix(vecOrigin, vecTarget, axis) {
        vecOrigin.normalize();
        vecTarget.normalize();
        // 计算旋转轴
        const myAxis = new Vector3().crossVectors(vecOrigin, vecTarget);
        // 计算旋转角度
        const angle = Math.acos(vecOrigin.dot(vecTarget));
        // 构造旋转矩阵
        if (Math.abs(angle - Math.PI) < Number.EPSILON && axis != null) {
            // 判断旋转角度是否为pi，在旋转角度为pi时用户可以自定义旋转轴
            return new Matrix4().makeRotationAxis(axis.normalize(), angle);
        }
        return new Matrix4().makeRotationAxis(myAxis.normalize(), angle);
    }
    /**
     * 获取扫掠体两端的面
     * @param bottom 基面
     * @param point 端点坐标
     * @param tangent 端点处切向量
     * @returns
     */
    getEndSurface(bottom, point, tangent) {
        // 获取扫掠基面对应的坐标点
        const basePoint = this.basePoint.clone();
        // 获取基面法向量
        const baseNormal = bottom.getNormalizeZ();
        /**
         * 获取到顶面需要进行三步变换:
         * 1. 将原有底面平移到原点处
         * 2. 对底面进行旋转，将底面法向量旋转到扫掠路径终点切向量的方向
         * 3. 将底面平移到扫掠路径终点处
         * 由上面三步可知，需要构造三个变换矩阵
         */
        // 构造平移变换矩阵1，将底面平移到原点
        const transM1 = new Matrix4(1, 0, 0, -basePoint.x, 0, 1, 0, -basePoint.y, 0, 0, 1, -basePoint.z, 0, 0, 0, 1);
        // 构造旋转变换矩阵
        const rotationMatrix = this.getRotationMatrix(baseNormal, tangent);
        // 构造平移变换矩阵2，将底面平移到扫掠路径位置
        const transM2 = new Matrix4(1, 0, 0, point.x, 0, 1, 0, point.y, 0, 0, 1, point.z, 0, 0, 0, 1);
        // 对原始矩阵进行变换
        bottom.transformMatrix.premultiply(transM1);
        bottom.transformMatrix.premultiply(rotationMatrix);
        //旋转后平面的y轴需要和曲线的z轴对齐
        const bottomY = bottom.getNormalizeY();
        const sweepZ = this.sweepPath.getNormalizeZ();
        const rotaMY = this.getRotationMatrix(bottomY, sweepZ, tangent);
        bottom.transformMatrix.premultiply(rotaMY);
        bottom.transformMatrix.premultiply(transM2);
        return bottom;
    }
    copy(geo) {
        super.copy(geo);
        this.baseSurface = geo.baseSurface.clone();
        this.sweepPath = geo.sweepPath.clone();
        this.basePoint = geo.basePoint.clone();
        this.scaleFactor = geo.scaleFactor;
        this.surfaceIndex = [...geo.surfaceIndex];
        return this;
    }
    clone() {
        return new SweeperSolid().copy(this);
    }
    getBoundingBox() {
        const min = new Point(Infinity, Infinity, Infinity);
        const max = new Point(-Infinity, -Infinity, -Infinity);
        let boxs = [0, 1].map(index => {
            return this.getSurface(index).getBoundingBox();
        });
        boxs.forEach(box => {
            min.x = Math.min(min.x, box.min.x);
            min.y = Math.min(min.y, box.min.y);
            min.z = Math.min(min.z, box.min.z);
            max.x = Math.max(max.x, box.max.x);
            max.y = Math.max(max.y, box.max.y);
            max.z = Math.max(max.z, box.max.z);
        });
        return { min, max };
    }
    /**
    * 获取特征点列表
    * @returns
    */
    getFeaturePoints() {
        let bottomRes = this.getSurface(0).getFeaturePoints();
        let topBottomRes = this.getSurface(1).getFeaturePoints();
        bottomRes.forEach(item => {
            item.point.applyMatrix4(this.transformMatrix);
            let uv = item.uvtPosition;
            if (!uv) {
                uv = new Point2(0, 0);
            }
            let newUv = new Point(uv.x, uv.y, 0);
            item.uvtPosition = newUv;
        });
        topBottomRes.forEach(item => {
            item.point.applyMatrix4(this.transformMatrix);
            item.point.add(this.getZ());
            let uv = item.uvtPosition;
            if (!uv) {
                uv = new Point2(0, 0);
            }
            let newUv = new Point(uv.x, uv.y, 1);
            item.uvtPosition = newUv;
        });
        return [
            ...bottomRes,
            ...topBottomRes
        ];
    }
    toJson() {
        return Object.assign(Object.assign({}, super.toJson()), { version: "1.1", type: this.type, baseSurface: this.baseSurface.toJson(), sweepPath: this.sweepPath.toJson(), basePoint: {
                x: this.basePoint.x,
                y: this.basePoint.y,
                z: this.basePoint.z,
            }, scaleFactor: this.scaleFactor });
    }
    fromJson(json) {
        super.fromJson(json);
        this.baseSurface = new BaseFaceTrans[json.baseSurface.type]().fromJson(json.baseSurface);
        this.sweepPath = new BaseCurveTrans[json.sweepPath.type]().fromJson(json.sweepPath);
        this.basePoint = new Point(json.basePoint.x, json.basePoint.y, json.basePoint.z);
        this.scaleFactor = json.scaleFactor;
        return this;
    }
}function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}var EarClip = {exports: {}};EarClip.exports = earcut;
EarClip.exports.default = earcut;

function earcut(data, holeIndices, dim) {

    dim = dim || 2;

    var hasHoles = holeIndices && holeIndices.length,
        outerLen = hasHoles ? holeIndices[0] * dim : data.length,
        outerNode = linkedList(data, 0, outerLen, dim, true),
        triangles = [];

    if (!outerNode || outerNode.next === outerNode.prev) return triangles;

    var minX, minY, maxX, maxY, x, y, invSize;

    if (hasHoles) outerNode = eliminateHoles(data, holeIndices, outerNode, dim);

    // if the shape is not too simple, we'll use z-order curve hash later; calculate polygon bbox
    if (data.length > 80 * dim) {
        minX = maxX = data[0];
        minY = maxY = data[1];

        for (var i = dim; i < outerLen; i += dim) {
            x = data[i];
            y = data[i + 1];
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x > maxX) maxX = x;
            if (y > maxY) maxY = y;
        }

        // minX, minY and invSize are later used to transform coords into integers for z-order calculation
        invSize = Math.max(maxX - minX, maxY - minY);
        invSize = invSize !== 0 ? 1 / invSize : 0;
    }

    earcutLinked(outerNode, triangles, dim, minX, minY, invSize);

    return triangles;
}

// create a circular doubly linked list from polygon points in the specified winding order
function linkedList(data, start, end, dim, clockwise) {
    var i, last;

    if (clockwise === (signedArea(data, start, end, dim) > 0)) {
        for (i = start; i < end; i += dim) last = insertNode(i, data[i], data[i + 1], last);
    } else {
        for (i = end - dim; i >= start; i -= dim) last = insertNode(i, data[i], data[i + 1], last);
    }

    if (last && equals(last, last.next)) {
        removeNode(last);
        last = last.next;
    }

    return last;
}

// eliminate colinear or duplicate points
function filterPoints(start, end) {
    if (!start) return start;
    if (!end) end = start;

    var p = start,
        again;
    do {
        again = false;

        if (!p.steiner && (equals(p, p.next) || area(p.prev, p, p.next) === 0)) {
            removeNode(p);
            p = end = p.prev;
            if (p === p.next) break;
            again = true;

        } else {
            p = p.next;
        }
    } while (again || p !== end);

    return end;
}

// main ear slicing loop which triangulates a polygon (given as a linked list)
function earcutLinked(ear, triangles, dim, minX, minY, invSize, pass) {
    if (!ear) return;

    // interlink polygon nodes in z-order
    if (!pass && invSize) indexCurve(ear, minX, minY, invSize);

    var stop = ear,
        prev, next;

    // iterate through ears, slicing them one by one
    while (ear.prev !== ear.next) {
        prev = ear.prev;
        next = ear.next;

        if (invSize ? isEarHashed(ear, minX, minY, invSize) : isEar(ear)) {
            // cut off the triangle
            triangles.push(prev.i / dim);
            triangles.push(ear.i / dim);
            triangles.push(next.i / dim);

            removeNode(ear);

            // skipping the next vertex leads to less sliver triangles
            ear = next.next;
            stop = next.next;

            continue;
        }

        ear = next;

        // if we looped through the whole remaining polygon and can't find any more ears
        if (ear === stop) {
            // try filtering points and slicing again
            if (!pass) {
                earcutLinked(filterPoints(ear), triangles, dim, minX, minY, invSize, 1);

            // if this didn't work, try curing all small self-intersections locally
            } else if (pass === 1) {
                ear = cureLocalIntersections(filterPoints(ear), triangles, dim);
                earcutLinked(ear, triangles, dim, minX, minY, invSize, 2);

            // as a last resort, try splitting the remaining polygon into two
            } else if (pass === 2) {
                splitEarcut(ear, triangles, dim, minX, minY, invSize);
            }

            break;
        }
    }
}

// check whether a polygon node forms a valid ear with adjacent nodes
function isEar(ear) {
    var a = ear.prev,
        b = ear,
        c = ear.next;

    if (area(a, b, c) >= 0) return false; // reflex, can't be an ear

    // now make sure we don't have other points inside the potential ear
    var p = ear.next.next;

    while (p !== ear.prev) {
        if (pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
            area(p.prev, p, p.next) >= 0) return false;
        p = p.next;
    }

    return true;
}

function isEarHashed(ear, minX, minY, invSize) {
    var a = ear.prev,
        b = ear,
        c = ear.next;

    if (area(a, b, c) >= 0) return false; // reflex, can't be an ear

    // triangle bbox; min & max are calculated like this for speed
    var minTX = a.x < b.x ? (a.x < c.x ? a.x : c.x) : (b.x < c.x ? b.x : c.x),
        minTY = a.y < b.y ? (a.y < c.y ? a.y : c.y) : (b.y < c.y ? b.y : c.y),
        maxTX = a.x > b.x ? (a.x > c.x ? a.x : c.x) : (b.x > c.x ? b.x : c.x),
        maxTY = a.y > b.y ? (a.y > c.y ? a.y : c.y) : (b.y > c.y ? b.y : c.y);

    // z-order range for the current triangle bbox;
    var minZ = zOrder(minTX, minTY, minX, minY, invSize),
        maxZ = zOrder(maxTX, maxTY, minX, minY, invSize);

    var p = ear.prevZ,
        n = ear.nextZ;

    // look for points inside the triangle in both directions
    while (p && p.z >= minZ && n && n.z <= maxZ) {
        if (p !== ear.prev && p !== ear.next &&
            pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
            area(p.prev, p, p.next) >= 0) return false;
        p = p.prevZ;

        if (n !== ear.prev && n !== ear.next &&
            pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, n.x, n.y) &&
            area(n.prev, n, n.next) >= 0) return false;
        n = n.nextZ;
    }

    // look for remaining points in decreasing z-order
    while (p && p.z >= minZ) {
        if (p !== ear.prev && p !== ear.next &&
            pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, p.x, p.y) &&
            area(p.prev, p, p.next) >= 0) return false;
        p = p.prevZ;
    }

    // look for remaining points in increasing z-order
    while (n && n.z <= maxZ) {
        if (n !== ear.prev && n !== ear.next &&
            pointInTriangle(a.x, a.y, b.x, b.y, c.x, c.y, n.x, n.y) &&
            area(n.prev, n, n.next) >= 0) return false;
        n = n.nextZ;
    }

    return true;
}

// go through all polygon nodes and cure small local self-intersections
function cureLocalIntersections(start, triangles, dim) {
    var p = start;
    do {
        var a = p.prev,
            b = p.next.next;

        if (!equals(a, b) && intersects(a, p, p.next, b) && locallyInside(a, b) && locallyInside(b, a)) {

            triangles.push(a.i / dim);
            triangles.push(p.i / dim);
            triangles.push(b.i / dim);

            // remove two nodes involved
            removeNode(p);
            removeNode(p.next);

            p = start = b;
        }
        p = p.next;
    } while (p !== start);

    return filterPoints(p);
}

// try splitting polygon into two and triangulate them independently
function splitEarcut(start, triangles, dim, minX, minY, invSize) {
    // look for a valid diagonal that divides the polygon into two
    var a = start;
    do {
        var b = a.next.next;
        while (b !== a.prev) {
            if (a.i !== b.i && isValidDiagonal(a, b)) {
                // split the polygon in two by the diagonal
                var c = splitPolygon(a, b);

                // filter colinear points around the cuts
                a = filterPoints(a, a.next);
                c = filterPoints(c, c.next);

                // run earcut on each half
                earcutLinked(a, triangles, dim, minX, minY, invSize);
                earcutLinked(c, triangles, dim, minX, minY, invSize);
                return;
            }
            b = b.next;
        }
        a = a.next;
    } while (a !== start);
}

// link every hole into the outer loop, producing a single-ring polygon without holes
function eliminateHoles(data, holeIndices, outerNode, dim) {
    var queue = [],
        i, len, start, end, list;

    for (i = 0, len = holeIndices.length; i < len; i++) {
        start = holeIndices[i] * dim;
        end = i < len - 1 ? holeIndices[i + 1] * dim : data.length;
        list = linkedList(data, start, end, dim, false);
        if (list === list.next) list.steiner = true;
        queue.push(getLeftmost(list));
    }

    queue.sort(compareX);

    // process holes from left to right
    for (i = 0; i < queue.length; i++) {
        outerNode = eliminateHole(queue[i], outerNode);
        outerNode = filterPoints(outerNode, outerNode.next);
    }

    return outerNode;
}

function compareX(a, b) {
    return a.x - b.x;
}

// find a bridge between vertices that connects hole with an outer ring and and link it
function eliminateHole(hole, outerNode) {
    var bridge = findHoleBridge(hole, outerNode);
    if (!bridge) {
        return outerNode;
    }

    var bridgeReverse = splitPolygon(bridge, hole);

    // filter collinear points around the cuts
    var filteredBridge = filterPoints(bridge, bridge.next);
    filterPoints(bridgeReverse, bridgeReverse.next);

    // Check if input node was removed by the filtering
    return outerNode === bridge ? filteredBridge : outerNode;
}

// David Eberly's algorithm for finding a bridge between hole and outer polygon
function findHoleBridge(hole, outerNode) {
    var p = outerNode,
        hx = hole.x,
        hy = hole.y,
        qx = -Infinity,
        m;

    // find a segment intersected by a ray from the hole's leftmost point to the left;
    // segment's endpoint with lesser x will be potential connection point
    do {
        if (hy <= p.y && hy >= p.next.y && p.next.y !== p.y) {
            var x = p.x + (hy - p.y) * (p.next.x - p.x) / (p.next.y - p.y);
            if (x <= hx && x > qx) {
                qx = x;
                if (x === hx) {
                    if (hy === p.y) return p;
                    if (hy === p.next.y) return p.next;
                }
                m = p.x < p.next.x ? p : p.next;
            }
        }
        p = p.next;
    } while (p !== outerNode);

    if (!m) return null;

    if (hx === qx) return m; // hole touches outer segment; pick leftmost endpoint

    // look for points inside the triangle of hole point, segment intersection and endpoint;
    // if there are no points found, we have a valid connection;
    // otherwise choose the point of the minimum angle with the ray as connection point

    var stop = m,
        mx = m.x,
        my = m.y,
        tanMin = Infinity,
        tan;

    p = m;

    do {
        if (hx >= p.x && p.x >= mx && hx !== p.x &&
                pointInTriangle(hy < my ? hx : qx, hy, mx, my, hy < my ? qx : hx, hy, p.x, p.y)) {

            tan = Math.abs(hy - p.y) / (hx - p.x); // tangential

            if (locallyInside(p, hole) &&
                (tan < tanMin || (tan === tanMin && (p.x > m.x || (p.x === m.x && sectorContainsSector(m, p)))))) {
                m = p;
                tanMin = tan;
            }
        }

        p = p.next;
    } while (p !== stop);

    return m;
}

// whether sector in vertex m contains sector in vertex p in the same coordinates
function sectorContainsSector(m, p) {
    return area(m.prev, m, p.prev) < 0 && area(p.next, m, m.next) < 0;
}

// interlink polygon nodes in z-order
function indexCurve(start, minX, minY, invSize) {
    var p = start;
    do {
        if (p.z === null) p.z = zOrder(p.x, p.y, minX, minY, invSize);
        p.prevZ = p.prev;
        p.nextZ = p.next;
        p = p.next;
    } while (p !== start);

    p.prevZ.nextZ = null;
    p.prevZ = null;

    sortLinked(p);
}

// Simon Tatham's linked list merge sort algorithm
// http://www.chiark.greenend.org.uk/~sgtatham/algorithms/listsort.html
function sortLinked(list) {
    var i, p, q, e, tail, numMerges, pSize, qSize,
        inSize = 1;

    do {
        p = list;
        list = null;
        tail = null;
        numMerges = 0;

        while (p) {
            numMerges++;
            q = p;
            pSize = 0;
            for (i = 0; i < inSize; i++) {
                pSize++;
                q = q.nextZ;
                if (!q) break;
            }
            qSize = inSize;

            while (pSize > 0 || (qSize > 0 && q)) {

                if (pSize !== 0 && (qSize === 0 || !q || p.z <= q.z)) {
                    e = p;
                    p = p.nextZ;
                    pSize--;
                } else {
                    e = q;
                    q = q.nextZ;
                    qSize--;
                }

                if (tail) tail.nextZ = e;
                else list = e;

                e.prevZ = tail;
                tail = e;
            }

            p = q;
        }

        tail.nextZ = null;
        inSize *= 2;

    } while (numMerges > 1);

    return list;
}

// z-order of a point given coords and inverse of the longer side of data bbox
function zOrder(x, y, minX, minY, invSize) {
    // coords are transformed into non-negative 15-bit integer range
    x = 32767 * (x - minX) * invSize;
    y = 32767 * (y - minY) * invSize;

    x = (x | (x << 8)) & 0x00FF00FF;
    x = (x | (x << 4)) & 0x0F0F0F0F;
    x = (x | (x << 2)) & 0x33333333;
    x = (x | (x << 1)) & 0x55555555;

    y = (y | (y << 8)) & 0x00FF00FF;
    y = (y | (y << 4)) & 0x0F0F0F0F;
    y = (y | (y << 2)) & 0x33333333;
    y = (y | (y << 1)) & 0x55555555;

    return x | (y << 1);
}

// find the leftmost node of a polygon ring
function getLeftmost(start) {
    var p = start,
        leftmost = start;
    do {
        if (p.x < leftmost.x || (p.x === leftmost.x && p.y < leftmost.y)) leftmost = p;
        p = p.next;
    } while (p !== start);

    return leftmost;
}

// check if a point lies within a convex triangle
function pointInTriangle(ax, ay, bx, by, cx, cy, px, py) {
    return (cx - px) * (ay - py) - (ax - px) * (cy - py) >= 0 &&
           (ax - px) * (by - py) - (bx - px) * (ay - py) >= 0 &&
           (bx - px) * (cy - py) - (cx - px) * (by - py) >= 0;
}

// check if a diagonal between two polygon nodes is valid (lies in polygon interior)
function isValidDiagonal(a, b) {
    return a.next.i !== b.i && a.prev.i !== b.i && !intersectsPolygon(a, b) && // dones't intersect other edges
           (locallyInside(a, b) && locallyInside(b, a) && middleInside(a, b) && // locally visible
            (area(a.prev, a, b.prev) || area(a, b.prev, b)) || // does not create opposite-facing sectors
            equals(a, b) && area(a.prev, a, a.next) > 0 && area(b.prev, b, b.next) > 0); // special zero-length case
}

// signed area of a triangle
function area(p, q, r) {
    return (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);
}

// check if two points are equal
function equals(p1, p2) {
    return p1.x === p2.x && p1.y === p2.y;
}

// check if two segments intersect
function intersects(p1, q1, p2, q2) {
    var o1 = sign(area(p1, q1, p2));
    var o2 = sign(area(p1, q1, q2));
    var o3 = sign(area(p2, q2, p1));
    var o4 = sign(area(p2, q2, q1));

    if (o1 !== o2 && o3 !== o4) return true; // general case

    if (o1 === 0 && onSegment(p1, p2, q1)) return true; // p1, q1 and p2 are collinear and p2 lies on p1q1
    if (o2 === 0 && onSegment(p1, q2, q1)) return true; // p1, q1 and q2 are collinear and q2 lies on p1q1
    if (o3 === 0 && onSegment(p2, p1, q2)) return true; // p2, q2 and p1 are collinear and p1 lies on p2q2
    if (o4 === 0 && onSegment(p2, q1, q2)) return true; // p2, q2 and q1 are collinear and q1 lies on p2q2

    return false;
}

// for collinear points p, q, r, check if point q lies on segment pr
function onSegment(p, q, r) {
    return q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) && q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y);
}

function sign(num) {
    return num > 0 ? 1 : num < 0 ? -1 : 0;
}

// check if a polygon diagonal intersects any polygon segments
function intersectsPolygon(a, b) {
    var p = a;
    do {
        if (p.i !== a.i && p.next.i !== a.i && p.i !== b.i && p.next.i !== b.i &&
                intersects(p, p.next, a, b)) return true;
        p = p.next;
    } while (p !== a);

    return false;
}

// check if a polygon diagonal is locally inside the polygon
function locallyInside(a, b) {
    return area(a.prev, a, a.next) < 0 ?
        area(a, b, a.next) >= 0 && area(a, a.prev, b) >= 0 :
        area(a, b, a.prev) < 0 || area(a, a.next, b) < 0;
}

// check if the middle point of a polygon diagonal is inside the polygon
function middleInside(a, b) {
    var p = a,
        inside = false,
        px = (a.x + b.x) / 2,
        py = (a.y + b.y) / 2;
    do {
        if (((p.y > py) !== (p.next.y > py)) && p.next.y !== p.y &&
                (px < (p.next.x - p.x) * (py - p.y) / (p.next.y - p.y) + p.x))
            inside = !inside;
        p = p.next;
    } while (p !== a);

    return inside;
}

// link two polygon vertices with a bridge; if the vertices belong to the same ring, it splits polygon into two;
// if one belongs to the outer ring and another to a hole, it merges it into a single ring
function splitPolygon(a, b) {
    var a2 = new Node(a.i, a.x, a.y),
        b2 = new Node(b.i, b.x, b.y),
        an = a.next,
        bp = b.prev;

    a.next = b;
    b.prev = a;

    a2.next = an;
    an.prev = a2;

    b2.next = a2;
    a2.prev = b2;

    bp.next = b2;
    b2.prev = bp;

    return b2;
}

// create a node and optionally link it with previous one (in a circular doubly linked list)
function insertNode(i, x, y, last) {
    var p = new Node(i, x, y);

    if (!last) {
        p.prev = p;
        p.next = p;

    } else {
        p.next = last.next;
        p.prev = last;
        last.next.prev = p;
        last.next = p;
    }
    return p;
}

function removeNode(p) {
    p.next.prev = p.prev;
    p.prev.next = p.next;

    if (p.prevZ) p.prevZ.nextZ = p.nextZ;
    if (p.nextZ) p.nextZ.prevZ = p.prevZ;
}

function Node(i, x, y) {
    // vertex index in coordinates array
    this.i = i;

    // vertex coordinates
    this.x = x;
    this.y = y;

    // previous and next vertex nodes in a polygon ring
    this.prev = null;
    this.next = null;

    // z-order curve value
    this.z = null;

    // previous and next nodes in z-order
    this.prevZ = null;
    this.nextZ = null;

    // indicates whether this is a steiner point
    this.steiner = false;
}

// return a percentage difference between the polygon area and its triangulation area;
// used to verify correctness of triangulation
earcut.deviation = function (data, holeIndices, dim, triangles) {
    var hasHoles = holeIndices && holeIndices.length;
    var outerLen = hasHoles ? holeIndices[0] * dim : data.length;

    var polygonArea = Math.abs(signedArea(data, 0, outerLen, dim));
    if (hasHoles) {
        for (var i = 0, len = holeIndices.length; i < len; i++) {
            var start = holeIndices[i] * dim;
            var end = i < len - 1 ? holeIndices[i + 1] * dim : data.length;
            polygonArea -= Math.abs(signedArea(data, start, end, dim));
        }
    }

    var trianglesArea = 0;
    for (i = 0; i < triangles.length; i += 3) {
        var a = triangles[i] * dim;
        var b = triangles[i + 1] * dim;
        var c = triangles[i + 2] * dim;
        trianglesArea += Math.abs(
            (data[a] - data[c]) * (data[b + 1] - data[a + 1]) -
            (data[a] - data[b]) * (data[c + 1] - data[a + 1]));
    }

    return polygonArea === 0 && trianglesArea === 0 ? 0 :
        Math.abs((trianglesArea - polygonArea) / polygonArea);
};

function signedArea(data, start, end, dim) {
    var sum = 0;
    for (var i = start, j = end - dim; i < end; i += dim) {
        sum += (data[j] - data[i]) * (data[i + 1] + data[j + 1]);
        j = i;
    }
    return sum;
}

// turn a polygon in a multi-dimensional array form (e.g. as in GeoJSON) into a form Earcut accepts
earcut.flatten = function (data) {
    var dim = data[0][0].length,
        result = {vertices: [], holes: [], dimensions: dim},
        holeIndex = 0;

    for (var i = 0; i < data.length; i++) {
        for (var j = 0; j < data[i].length; j++) {
            for (var d = 0; d < dim; d++) result.vertices.push(data[i][j][d]);
        }
        if (i > 0) {
            holeIndex += data[i - 1].length;
            result.holes.push(holeIndex);
        }
    }
    return result;
};

var EarClipExports = EarClip.exports;
var earcut$1 = /*@__PURE__*/getDefaultExportFromCjs(EarClipExports);/**
 * 通过耳切法返回三角剖分数组
 * @param coords 耳切法顶点坐标（二维坐标，铺开，比如有点A(xa, ya), B(xb, yb), C(xc, yc)可以写成[xa, ya, xb, yb, xc, yc]）
 * @param holesIndics 耳切法所需洞的开始索引，比如第一个洞的顶点从数组data的索引20开始，第二个洞的顶点从数组data1索引34开始，则写成[20, 34]
 */
function triangulationByEarcut(coords, holesIndics) {
    return earcut$1(coords, holesIndics, 2);
}export{Arc,BaseUtils,BoolOpeType,BoolOperation,Box,BoxSurfaceType,Circle,ClosedInterval,Cone,ConeSideSurface,ConeSurfaceType,Curve,Cylinder,CylinderSurfaceType,Ellipse,EllipseArc,EllipseSurface,ExtrudeSolid,ExtrudeSolidType,ExtrudeSurface,FEATURE_POINT_TYPE,GeneralRegion,GeneralSolid,GeoIntersection,GeoMeasurement,GeoRelation,GeometryBase,Line,Plane,Point,Point2,Polygon,PolygonIndex,Pyramid,PyramidSurfaceType,Ray,RectFace,RegionFace,RevolveSolid,RevolveSolidType,RevolveSurface,Solid,Sphere,Spherical,StraightLine,Surface,SweeperSolid,SweeperSolidType,SweeperSurface,Torus,Triangle,Wedge,WedgeSurfaceType,_tmpMatrix,triangulationByEarcut};